import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Separator } from "./components/ui/separator";
import { Checkbox } from "./components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./components/ui/dropdown-menu";
import { toast, Toaster } from "sonner";
import { 
  Users, 
  FolderOpen, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar as CalendarIcon, 
  Plus, 
  Edit, 
  Trash2,
  BarChart3,
  Building2,
  UserCheck,
  Globe,
  Mail,
  Phone,
  MapPin,
  PieChart,
  Target,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Download,
  Settings,
  LogOut,
  User,
  Lock,
  Eye,
  EyeOff,
  Upload,
  Save,
  Shield,
  FileText,
  Clock,
  Send,
  Copy,
  Receipt,
  MessageSquare,
  ArrowLeft,
  Check,
  Globe2,
  Languages,
  Bell,
  Search,
  HelpCircle,
  ChevronDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Activity,
  MoreHorizontal,
  Crown,
  Ticket
} from "lucide-react";
import { format } from "date-fns";
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';

// Support System Components
import SupportTickets from './components/SupportTickets';
import AddSupportTicket from './components/AddSupportTicket';
import SupportTicketDetailEnhanced from './components/SupportTicketDetailEnhanced';
import UnifiedTasks from './components/UnifiedTasks';
import ProjectTaskViewEnhanced from './components/ProjectTaskViewEnhanced';
import EnhancedTaskDialog from './components/EnhancedTaskDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component - Sidebar with Submenus
const Navigation = () => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [appSettings, setAppSettings] = useState({
    appName: 'BizManager',
    logo: null
  });
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchAppSettings();
    if (user && !user.isEmployee) {
      fetchNotifications();
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/general`);
      setAppSettings({
        appName: response.data.appName || 'BizManager',
        logo: response.data.logo ? `${API}/settings/logo` : null
      });
    } catch (error) {
      console.error("Error fetching app settings:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/admin/notifications/${notificationId}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };
  
  const navItems = [
    { path: "/", label: "Dashboard", icon: BarChart3 },
    // Employee-specific menu items
    { path: "/my-profile", label: "My Profile", icon: User },
    { path: "/my-projects", label: "My Projects", icon: Building2 },
    { path: "/my-tasks", label: "My Tasks", icon: CheckCircle },
    { path: "/my-settings", label: "Settings", icon: Settings },
    // Manager-specific menu items
    { path: "/manager-dashboard", label: "Dashboard", icon: BarChart3 },
    { path: "/manager-team", label: "My Team", icon: Users },
    { path: "/manager-projects", label: "Team Projects", icon: Building2 },
    { path: "/manager-tasks", label: "Team Tasks", icon: CheckCircle },
    { path: "/manager-settings", label: "Settings", icon: Settings },
    // Client-specific menu items
    { path: "/client-dashboard", label: "Dashboard", icon: BarChart3 },
    { path: "/client-projects", label: "Projects", icon: Building2 },
    { path: "/client-tasks", label: "Tasks", icon: CheckCircle },
    { path: "/client-invoices", label: "Invoices", icon: Receipt },
    { path: "/client-support", label: "Support", icon: HelpCircle },
    { path: "/client-settings", label: "Settings", icon: Settings },
    { 
      path: "/leads", 
      label: "Leads", 
      icon: Target,
      subItems: [
        { path: "/leads/list", label: "Leads List" },
        { path: "/leads/status", label: "Status" }
      ]
    },
    { path: "/clients", label: "Clients", icon: Building2 },
    { path: "/employees", label: "Employees", icon: Users },
    { 
      path: "/projects", 
      label: "Projects", 
      icon: FolderOpen,
      subItems: [
        { path: "/projects", label: "Projects" },
        { path: "/projects/weekly-planner", label: "Weekly Planner" }
      ]
    },
    { 
      path: "/finances", 
      label: "Finances", 
      icon: DollarSign,
      subItems: [
        { path: "/finances/management", label: "Financial Management" },
        { path: "/finances/categories", label: "Categories" },
        { path: "/finances/online-payments", label: "Online Payments" }
      ]
    },
    { 
      path: "/sales", 
      label: "Sales", 
      icon: ShoppingCart,
      subItems: [
        { path: "/sales/products", label: "Products" },
        { path: "/sales/sales", label: "Sales" },
        { path: "/sales/expenses", label: "Expenses" },
        { path: "/sales/invoices", label: "Invoices" }
      ]
    },
    { path: "/analytics", label: "Analytics", icon: PieChart },
    { 
      path: "/communications", 
      label: "Communications", 
      icon: MessageSquare,
      subItems: [
        { path: "/communications/mails", label: "Mails" },
        { path: "/communications/templates", label: "Templates" },
        { path: "/communications/marketing", label: "Marketing" },
        { path: "/communications/chat", label: "Chat" }
      ]
    },
    { 
      path: "/settings", 
      label: "Settings", 
      icon: Settings,
      subItems: [
        { path: "/settings/general", label: "General Settings" },
        { path: "/settings/team-types", label: "Team Types" },
        { path: "/settings/roles", label: "Roles" },
        { path: "/settings/language", label: "Language Management" },
        { path: "/settings/admin", label: "Admin" },
        { path: "/settings/reset-db", label: "Reset DB" }
      ]
    },
    { 
      path: "/support", 
      label: "Support", 
      icon: HelpCircle,
      subItems: [
        { path: "/support/tickets", label: "Tickets" },
        { path: "/support/tasks", label: "Tasks" }
      ]
    },
  ];

  const toggleExpanded = (path) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const isPathActive = (path, subItems = null) => {
    if (subItems) {
      return location.pathname.startsWith(path);
    }
    return location.pathname === path || (path !== "/" && location.pathname.startsWith(path));
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <nav className="fixed left-0 top-0 h-full w-64 bg-slate-900 text-white shadow-lg z-50 overflow-y-auto flex flex-col">
      {/* Logo/Brand */}
      <div className="flex items-center space-x-3 p-6 border-b border-slate-700">
        {appSettings.logo ? (
          <img
            src={appSettings.logo}
            alt="Logo"
            className="w-8 h-8 object-contain"
          />
        ) : (
          <Building2 className="h-8 w-8 text-emerald-400" />
        )}
        <h1 className="text-xl font-bold">{appSettings.appName}</h1>
      </div>
      
      {/* User Profile */}
      <div className="p-4 border-t border-slate-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              {user?.isClient ? (
                <>
                  <p className="text-sm font-medium text-white truncate">
                    {user.contacts?.[0]?.name || user.contact_person || user.name}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {user.company_name || user.name}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </>
              )}
            </div>
          </div>
          
          {/* Notification Bell - Only for Admin users (not employees or clients) */}
          {user && !user.isEmployee && !user.isClient && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) {
                    fetchNotifications();
                  }
                }}
                className="text-slate-300 hover:text-white hover:bg-slate-700 relative p-2"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              
              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute bottom-full right-0 mb-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b bg-slate-50">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900">Notifications</h3>
                      <Badge variant="secondary" className="text-xs">
                        {unreadCount} unread
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.slice(0, 10).map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-3 border-b hover:bg-slate-50 cursor-pointer ${
                            !notification.is_read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          }`}
                          onClick={() => {
                            if (!notification.is_read) {
                              markNotificationAsRead(notification.id);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">
                                {notification.title}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-2">
                                {new Date(notification.created_at).toLocaleString()}
                              </p>
                            </div>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1 ml-2"></div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-slate-500">
                        <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>
                  
                  {notifications.length > 10 && (
                    <div className="p-3 border-t bg-slate-50 text-center">
                      <Button variant="ghost" size="sm" className="text-xs">
                        View all notifications
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Navigation Menu */}
      <div className="flex-1 py-6">
        <div className="space-y-2 px-4">
          {navItems.filter(item => {
            // For employee users (but not managers), show only specific menu items
            if (user?.isEmployee && !user?.isManager) {
              const employeeAllowedPaths = ['/', '/my-profile', '/my-projects', '/my-tasks', '/my-settings'];
              return employeeAllowedPaths.includes(item.path);
            }
            // For manager users, show manager-specific menu items
            if (user?.isManager) {
              const managerAllowedPaths = ['/manager-dashboard', '/manager-team', '/manager-projects', '/manager-tasks', '/manager-settings'];
              return managerAllowedPaths.includes(item.path);
            }
            // For client users, show only client-specific menu items
            if (user?.isClient) {
              const clientAllowedPaths = ['/client-dashboard', '/client-projects', '/client-tasks', '/client-invoices', '/client-support', '/client-settings'];
              return clientAllowedPaths.includes(item.path);
            }
            // For admin users, exclude employee-specific, manager-specific and client-specific items
            if (item.path === '/my-profile' || item.path === '/my-projects' || item.path === '/my-tasks' || item.path === '/my-settings' ||
                item.path === '/manager-dashboard' || item.path === '/manager-team' || item.path === '/manager-projects' || item.path === '/manager-tasks' || item.path === '/manager-settings' ||
                item.path === '/client-dashboard' || item.path === '/client-projects' || item.path === '/client-tasks' || 
                item.path === '/client-invoices' || item.path === '/client-support' || item.path === '/client-settings') {
              return false;
            }
            return true; // Show all other items for admin users
          }).map((item) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path, item.subItems);
            const isExpanded = expandedMenus[item.path] || isActive;
            
            return (
              <div key={item.path}>
                {/* Main Menu Item */}
                {item.subItems ? (
                  <button
                    onClick={() => toggleExpanded(item.path)}
                    className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-emerald-600 text-white shadow-lg"
                        : "text-slate-300 hover:text-white hover:bg-slate-700"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-emerald-600 text-white shadow-lg"
                        : "text-slate-300 hover:text-white hover:bg-slate-700"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                )}
                
                {/* Submenu Items */}
                {item.subItems && isExpanded && (
                  <div className="mt-2 ml-4 space-y-1">
                    {item.subItems.filter(subItem => {
                      // For employees, only show General Settings in the Settings menu
                      if (user?.isEmployee && item.path === '/settings') {
                        return subItem.path === '/settings/general';
                      }
                      return true; // Show all subitems for admin users
                    }).map((subItem) => {
                      const isSubActive = location.pathname === subItem.path;
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`flex items-center space-x-3 w-full px-4 py-2 rounded-lg transition-colors text-sm ${
                            isSubActive
                              ? "bg-emerald-500 text-white"
                              : "text-slate-400 hover:text-white hover:bg-slate-600"
                          }`}
                        >
                          <div className="w-2 h-2 rounded-full bg-current opacity-60"></div>
                          <span>{subItem.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Logout Section */}
      <div className="p-4 border-t border-slate-700">
        <button
          onClick={handleLogout}
          className="flex items-center space-x-3 w-full px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Logout</span>
        </button>
        <div className="text-xs text-slate-400 text-center mt-2">
          {appSettings.appName} v2.0
        </div>
      </div>
    </nav>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [worldClockCountries, setWorldClockCountries] = useState([]);

  // Country timezone mapping
  const countryTimezones = {
    'United States (New York)': { timezone: 'America/New_York', offset: 'GMT-5' },
    'United States (Los Angeles)': { timezone: 'America/Los_Angeles', offset: 'GMT-8' },
    'United Kingdom': { timezone: 'Europe/London', offset: 'GMT+0' },
    'Germany': { timezone: 'Europe/Berlin', offset: 'GMT+1' },
    'France': { timezone: 'Europe/Paris', offset: 'GMT+1' },
    'Japan': { timezone: 'Asia/Tokyo', offset: 'GMT+9' },
    'China': { timezone: 'Asia/Shanghai', offset: 'GMT+8' },
    'Australia (Sydney)': { timezone: 'Australia/Sydney', offset: 'GMT+10' },
    'India': { timezone: 'Asia/Kolkata', offset: 'GMT+5:30' },
    'Indonesia': { timezone: 'Asia/Jakarta', offset: 'GMT+7' },
    'Singapore': { timezone: 'Asia/Singapore', offset: 'GMT+8' },
    'Philippines': { timezone: 'Asia/Manila', offset: 'GMT+8' },
    'South Korea': { timezone: 'Asia/Seoul', offset: 'GMT+9' },
    'Thailand': { timezone: 'Asia/Bangkok', offset: 'GMT+7' },
    'United Arab Emirates': { timezone: 'Asia/Dubai', offset: 'GMT+4' },
    'Brazil': { timezone: 'America/Sao_Paulo', offset: 'GMT-3' },
    'Argentina': { timezone: 'America/Argentina/Buenos_Aires', offset: 'GMT-3' },
    'Mexico': { timezone: 'America/Mexico_City', offset: 'GMT-6' },
    'Canada': { timezone: 'America/Toronto', offset: 'GMT-5' },
    'Russia (Moscow)': { timezone: 'Europe/Moscow', offset: 'GMT+3' },
    'South Africa': { timezone: 'Africa/Johannesburg', offset: 'GMT+2' },
    'Egypt': { timezone: 'Africa/Cairo', offset: 'GMT+2' },
    'Turkey': { timezone: 'Europe/Istanbul', offset: 'GMT+3' },
    'New Zealand': { timezone: 'Pacific/Auckland', offset: 'GMT+12' }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchWorldClockSettings();
  }, []);

  // Update world clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorldClockSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/general`);
      const countries = response.data.worldClockCountries || ['Indonesia', 'India', 'Argentina'];
      const countryData = countries.map(country => ({
        name: country,
        timezone: countryTimezones[country]?.timezone || 'UTC',
        offset: countryTimezones[country]?.offset || 'GMT+0'
      }));
      setWorldClockCountries(countryData);
    } catch (error) {
      console.error("Error fetching world clock settings:", error);
      // Use default countries if settings fetch fails
      setWorldClockCountries([
        { name: 'Indonesia', timezone: 'Asia/Jakarta', offset: 'GMT+7' },
        { name: 'India', timezone: 'Asia/Kolkata', offset: 'GMT+5:30' },
        { name: 'Argentina', timezone: 'America/Argentina/Buenos_Aires', offset: 'GMT-3' }
      ]);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!dashboardData) {
    return <div className="flex justify-center items-center h-64">Failed to load data</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-slate-900 mb-8">Business Dashboard</h1>
      
      {/* World Clock */}
      {worldClockCountries.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              World Clock
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-4 ${
              worldClockCountries.length === 1 ? 'grid-cols-1' :
              worldClockCountries.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
              worldClockCountries.length === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
              worldClockCountries.length === 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4' :
              worldClockCountries.length === 5 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5' :
              worldClockCountries.length === 6 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6' :
              'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            }`}>
              {worldClockCountries.map((country, index) => (
                <div key={country.name} className="bg-slate-50 rounded-lg p-4 text-center border border-slate-200 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 mb-3 h-10 flex items-center justify-center leading-tight px-1">
                    <div className="text-center break-words hyphens-auto" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word'
                    }}>
                      {country.name}
                    </div>
                  </div>
                  <div className={`text-3xl font-bold mb-2 h-9 flex items-center justify-center leading-none ${
                    index === 0 ? 'text-blue-600' : 
                    index === 1 ? 'text-green-600' : 
                    index === 2 ? 'text-purple-600' :
                    index === 3 ? 'text-red-600' :
                    index === 4 ? 'text-orange-600' : 'text-cyan-600'
                  }`}>
                    {currentTime.toLocaleTimeString('en-US', {
                      timeZone: country.timezone,
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div className="text-xs text-slate-500 font-medium h-4 flex items-center justify-center">
                    {country.offset}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Balance</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              dashboardData.current_balance >= 0 ? "text-blue-600" : "text-red-600"
            }`}>
              ${dashboardData.current_balance?.toFixed(2) || "0.00"}
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Opening: ${dashboardData.opening_balance?.toFixed(2) || "0.00"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Sales</CardTitle>
            <ShoppingCart className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${dashboardData.monthly_sales_revenue?.toFixed(2) || "0.00"}
            </div>
            <p className="text-xs text-slate-600 mt-1">
              {dashboardData.total_sales_count || 0} sales this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <Target className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.total_products || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
            <Building2 className="h-4 w-4 text-slate-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.total_clients}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <FolderOpen className="h-4 w-4 text-slate-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.active_projects}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Profit</CardTitle>
            <BarChart3 className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              dashboardData.total_profit >= 0 ? "text-green-600" : "text-red-600"
            }`}>
              ${dashboardData.total_profit.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${dashboardData.monthly_revenue.toFixed(2)}
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Top: {dashboardData.top_revenue_category}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Expenses</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              ${dashboardData.monthly_expenses.toFixed(2)}
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Top: {dashboardData.top_expense_category}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Profit</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              dashboardData.monthly_profit >= 0 ? "text-green-600" : "text-red-600"
            }`}>
              ${dashboardData.monthly_profit.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/clients">
              <Button variant="outline" className="w-full justify-start">
                <Building2 className="h-4 w-4 mr-2" />
                Add New Client
              </Button>
            </Link>
            <Link to="/projects">
              <Button variant="outline" className="w-full justify-start">
                <FolderOpen className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            </Link>
            <Link to="/finances">
              <Button variant="outline" className="w-full justify-start">
                <DollarSign className="h-4 w-4 mr-2" />
                Add Financial Record
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Business Insights</CardTitle>
            <CardDescription>Key metrics and trends</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Profit Margin:</span>
              <span className="font-medium">
                {dashboardData.monthly_revenue > 0 
                  ? ((dashboardData.monthly_profit / dashboardData.monthly_revenue) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Revenue per Employee:</span>
              <span className="font-medium">
                ${dashboardData.total_employees > 0 
                  ? (dashboardData.monthly_revenue / dashboardData.total_employees).toFixed(0)
                  : 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Projects per Client:</span>
              <span className="font-medium">
                {dashboardData.total_clients > 0 
                  ? (dashboardData.active_projects / dashboardData.total_clients).toFixed(1)
                  : 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Employee Management Component
const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [skillFilter, setSkillFilter] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    country: true,
    team: true,
    status: true,
    salary: true,
    sellingHour: true,
    costHour: true,
    experience: true,
    phone: true,
    actions: true
  });
  
  // Column visibility collapse state
  const [isColumnVisibilityExpanded, setIsColumnVisibilityExpanded] = useState(false);
  
  // Employee filters collapse state
  const [isEmployeeFiltersExpanded, setIsEmployeeFiltersExpanded] = useState(false);
  
  // Additional filter states
  const [nameFilter, setNameFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [employeesPerPage] = useState(50);
  
  // Summary state for active employees
  const [employeeSummary, setEmployeeSummary] = useState({
    active_employees_count: 0,
    total_active_salary: 0,
    total_active_cost_per_hour: 0
  });

  // Team types state
  const [teamTypes, setTeamTypes] = useState([]);
  
  // Managers state
  const [managers, setManagers] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    cost_per_hour: "",
    selling_per_hour: "",
    custom_rate: "",
    email: "",
    phone: "",
    date_of_joining: new Date(),
    monthly_salary: "",
    designation: "",
    country: "",
    initial_experience: "",
    status: "Active",
    team_type_id: "",
    manager_id: ""
  });

  useEffect(() => {
    fetchEmployees();
    fetchTeamTypes();
    fetchManagers();
  }, [currentPage]);

  const fetchTeamTypes = async () => {
    try {
      const response = await axios.get(`${API}/team-types`);
      setTeamTypes(response.data);
    } catch (error) {
      console.error("Error fetching team types:", error);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/employees/managers`);
      setManagers(response.data);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  const getDefaultTeamId = () => {
    const defaultTeam = teamTypes.find(team => team.is_default);
    return defaultTeam ? defaultTeam.id : "";
  };

  const resetEmployeeForm = () => {
    setFormData({
      name: "",
      cost_per_hour: "",
      selling_per_hour: "",
      custom_rate: "",
      email: "",
      phone: "",
      date_of_joining: new Date(),
      monthly_salary: "",
      designation: "",
      country: "",
      initial_experience: "",
      status: "Active",
      team_type_id: getDefaultTeamId(),
      manager_id: ""
    });
    setEditingEmployee(null);
  };

  const fetchEmployees = async () => {
    try {
      // First, fetch all employees if we have filters other than skill, or fetch paginated data
      let allEmployees = [];
      let needsClientSideFiltering = nameFilter || countryFilter || teamFilter;
      
      if (needsClientSideFiltering) {
        // If we need client-side filtering, fetch all employees first
        const allResponse = await axios.get(`${API}/employees`, {
          params: skillFilter ? { skill: skillFilter } : {}
        });
        allEmployees = allResponse.data || [];
        
        // Apply client-side filters
        let filteredEmployees = allEmployees;
        
        if (nameFilter) {
          filteredEmployees = filteredEmployees.filter(emp => 
            emp.name?.toLowerCase().includes(nameFilter.toLowerCase())
          );
        }
        
        if (countryFilter) {
          filteredEmployees = filteredEmployees.filter(emp => 
            emp.country?.toLowerCase().includes(countryFilter.toLowerCase())
          );
        }
        
        if (teamFilter) {
          filteredEmployees = filteredEmployees.filter(emp => 
            emp.team_name?.toLowerCase().includes(teamFilter.toLowerCase())
          );
        }
        
        // Apply pagination manually
        const totalFiltered = filteredEmployees.length;
        const startIndex = (currentPage - 1) * employeesPerPage;
        const endIndex = startIndex + employeesPerPage;
        const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);
        
        setEmployees(paginatedEmployees);
        setTotalPages(Math.ceil(totalFiltered / employeesPerPage));
        setTotalEmployees(totalFiltered);
        
        // Calculate summary for filtered employees
        const activeEmployees = filteredEmployees.filter(emp => (emp.status || 'Active') === 'Active');
        const totalSalary = activeEmployees.reduce((sum, emp) => sum + (emp.monthly_salary || emp.salary || 0), 0);
        const totalCost = activeEmployees.reduce((sum, emp) => sum + (emp.cost_per_hour || 0), 0);
        
        setEmployeeSummary({
          total_active_salary: totalSalary,
          total_active_cost_per_hour: totalCost,
          active_employees_count: activeEmployees.length
        });
        
      } else {
        // Use normal pagination for skill-only or no filters
        const params = {
          page: currentPage,
          limit: employeesPerPage
        };
        
        if (skillFilter) {
          params.skill = skillFilter;
        }
        
        const response = await axios.get(`${API}/employees/paginated`, { params });
        
        if (response.data.employees) {
          setEmployees(response.data.employees);
          setTotalPages(response.data.pagination.total_pages);
          setTotalEmployees(response.data.pagination.total_employees);
          
          if (response.data.summary) {
            setEmployeeSummary(response.data.summary);
          }
        } else {
          setEmployees(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    }
  };

  const handleSkillFilterChange = (e) => {
    const skill = e.target.value;
    setSkillFilter(skill);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearSkillFilter = () => {
    setSkillFilter("");
    setCurrentPage(1); // Reset to first page when filter is cleared
  };

  const clearAllFilters = () => {
    setNameFilter("");
    setCountryFilter("");
    setTeamFilter("");
    setSkillFilter("");
    setCurrentPage(1); // Reset to first page when filters are cleared
  };

  // useEffect to trigger filtering when any filter changes
  useEffect(() => {
    fetchEmployees();
  }, [currentPage, skillFilter, nameFilter, countryFilter, teamFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        cost_per_hour: parseFloat(formData.cost_per_hour),
        selling_per_hour: parseFloat(formData.selling_per_hour),
        custom_rate: formData.custom_rate ? parseFloat(formData.custom_rate) : null,
        email: formData.email || null,
        phone: formData.phone || null,
        date_of_joining: format(formData.date_of_joining, "yyyy-MM-dd"),
        monthly_salary: parseFloat(formData.monthly_salary),
        designation: formData.designation,
        country: formData.country,
        initial_experience: formData.initial_experience ? parseFloat(formData.initial_experience) : null,
        position: formData.designation, // For backward compatibility
        status: formData.status, // Add status field to API request
        team_type_id: formData.team_type_id || undefined,
        manager_id: formData.manager_id || null // Add manager_id field to API request
      };

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, data);
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/employees`, data);
        toast.success("Employee added successfully");
      }

      setIsDialogOpen(false);
      resetEmployeeForm();
      fetchEmployees(skillFilter);
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error("Failed to save employee");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      cost_per_hour: employee.cost_per_hour.toString(),
      selling_per_hour: employee.selling_per_hour.toString(),
      custom_rate: employee.custom_rate ? employee.custom_rate.toString() : "",
      email: employee.email || "",
      phone: employee.phone || "",
      date_of_joining: employee.date_of_joining ? new Date(employee.date_of_joining) : new Date(),
      monthly_salary: employee.monthly_salary ? employee.monthly_salary.toString() : "",
      designation: employee.designation || "",
      country: employee.country || "",
      initial_experience: employee.initial_experience ? employee.initial_experience.toString() : "",
      status: employee.status || "Active",
      team_type_id: employee.team_type_id || "",
      manager_id: employee.manager_id || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        await axios.delete(`${API}/employees/${id}`);
        toast.success("Employee deleted successfully");
        fetchEmployees(skillFilter);
      } catch (error) {
        console.error("Error deleting employee:", error);
        toast.error("Failed to delete employee");
      }
    }
  };

  const handlePromoteToManager = async (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    const employeeName = employee ? employee.name : 'this employee';
    
    if (window.confirm(`Are you sure you want to promote ${employeeName} to manager? This will give them manager privileges and allow them to manage team members.`)) {
      try {
        await axios.put(`${API}/employees/${employeeId}`, { isManager: true });
        toast.success(`${employeeName} has been successfully promoted to manager!`);
        
        // Refresh both employees and managers data
        await fetchEmployees(skillFilter);
        await fetchManagers();
        
        // Update the specific employee in the local state for immediate visual feedback
        setEmployees(prevEmployees => 
          prevEmployees.map(emp => 
            emp.id === employeeId 
              ? { ...emp, isManager: true }
              : emp
          )
        );
        
      } catch (error) {
        console.error("Error promoting employee to manager:", error);
        const errorMessage = error.response?.data?.detail || "Failed to promote employee to manager";
        toast.error(errorMessage);
      }
    }
  };

  // Column visibility handlers
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const showAllColumns = () => {
    setVisibleColumns({
      name: true,
      country: true,
      team: true,
      status: true,
      salary: true,
      sellingHour: true,
      costHour: true,
      experience: true,
      phone: true,
      actions: true
    });
  };

  const hideAllColumns = () => {
    setVisibleColumns({
      name: true, // Always keep name column visible
      country: false,
      team: false,
      status: false,
      salary: false,
      sellingHour: false,
      costHour: false,
      experience: false,
      phone: false,
      actions: true // Always keep actions column visible
    });
  };

  const columnOptions = [
    { key: 'name', label: 'Name', required: true },
    { key: 'country', label: 'Country' },
    { key: 'team', label: 'Team' },
    { key: 'status', label: 'Status' },
    { key: 'salary', label: 'Salary' },
    { key: 'sellingHour', label: 'Selling/Hour' },
    { key: 'costHour', label: 'Cost/Hour' },
    { key: 'experience', label: 'Experience' },
    { key: 'phone', label: 'Phone' },
    { key: 'actions', label: 'Actions', required: true }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Employee Management</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => resetEmployeeForm()}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>
                {editingEmployee ? "Edit Employee" : "Add New Employee"}
              </DialogTitle>
              <DialogDescription>
                Enter the employee details below.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              <div className="flex-1 overflow-y-auto space-y-6 pr-2 mb-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="designation">Designation *</Label>
                    <Input
                      id="designation"
                      value={formData.designation}
                      onChange={(e) => setFormData({...formData, designation: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="initial_experience">Initial Experience (Years)</Label>
                    <Input
                      id="initial_experience"
                      type="number"
                      step="0.1"
                      value={formData.initial_experience}
                      onChange={(e) => setFormData({...formData, initial_experience: e.target.value})}
                      placeholder="e.g., 3.5"
                    />
                  </div>
                </div>
              </div>

              {/* Employment Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Employment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Date of Joining *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date_of_joining ? format(formData.date_of_joining, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.date_of_joining}
                          onSelect={(date) => setFormData({...formData, date_of_joining: date || new Date()})}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="monthly_salary">Monthly Salary ($) *</Label>
                    <Input
                      id="monthly_salary"
                      type="number"
                      step="0.01"
                      value={formData.monthly_salary}
                      onChange={(e) => setFormData({...formData, monthly_salary: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="team_type">Team Type</Label>
                    <select
                      id="team_type"
                      value={formData.team_type_id}
                      onChange={(e) => setFormData({...formData, team_type_id: e.target.value})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select Team Type</option>
                      {teamTypes.map((teamType) => (
                        <option key={teamType.id} value={teamType.id}>
                          {teamType.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="manager">Assign Manager</Label>
                    <select
                      id="manager"
                      value={formData.manager_id}
                      onChange={(e) => setFormData({...formData, manager_id: e.target.value})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">No Manager (Direct Report)</option>
                      {managers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} - {manager.designation || 'Manager'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Rates & Billing */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Rates & Billing</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cost_per_hour">Cost per Hour ($) *</Label>
                    <Input
                      id="cost_per_hour"
                      type="number"
                      step="0.01"
                      value={formData.cost_per_hour}
                      onChange={(e) => setFormData({...formData, cost_per_hour: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="selling_per_hour">Selling Rate per Hour ($) *</Label>
                    <Input
                      id="selling_per_hour"
                      type="number"
                      step="0.01"
                      value={formData.selling_per_hour}
                      onChange={(e) => setFormData({...formData, selling_per_hour: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="custom_rate">Custom Rate ($)</Label>
                    <Input
                      id="custom_rate"
                      type="number"
                      step="0.01"
                      value={formData.custom_rate}
                      onChange={(e) => setFormData({...formData, custom_rate: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              </div>
              
              {/* Sticky Footer with Submit Button */}
              <div className="flex-shrink-0 pt-4 border-t bg-white">
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                  {editingEmployee ? "Update Employee" : "Create Employee"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Blocks for Active Employees */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Total Active Salary</h3>
                <p className="text-sm text-slate-500">Monthly salary of all active employees</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600">
                  ${employeeSummary.total_active_salary ? employeeSummary.total_active_salary.toFixed(2) : '0.00'}
                </div>
                <div className="text-sm text-slate-500">
                  {employeeSummary.active_employees_count} active employees
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Total Active Cost/Hour</h3>
                <p className="text-sm text-slate-500">Combined cost per hour of all active employees</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-orange-600">
                  ${employeeSummary.total_active_cost_per_hour ? employeeSummary.total_active_cost_per_hour.toFixed(2) : '0.00'}
                </div>
                <div className="text-sm text-slate-500">
                  {employeeSummary.active_employees_count} active employees
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsEmployeeFiltersExpanded(!isEmployeeFiltersExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-purple-600" />
              Employee Filters
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isEmployeeFiltersExpanded ? 'rotate-180' : ''}`} />
          </CardTitle>
          <CardDescription>Search and filter employees by name, country, team, or skills</CardDescription>
        </CardHeader>
        {isEmployeeFiltersExpanded && (
          <CardContent>
          <div className="space-y-4">
            {/* First Row - Name, Country, Team */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="name-filter">Filter by Name</Label>
                <Input
                  id="name-filter"
                  type="text"
                  placeholder="Search by employee name..."
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="country-filter">Filter by Country</Label>
                <Input
                  id="country-filter"
                  type="text"
                  placeholder="e.g., USA, India, Canada..."
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="team-filter">Filter by Team</Label>
                <Input
                  id="team-filter"
                  type="text"
                  placeholder="Search by team name..."
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                />
              </div>
            </div>
            
            {/* Second Row - Skills */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <Label htmlFor="skill-filter">Filter by Skills</Label>
                <Input
                  id="skill-filter"
                  type="text"
                  placeholder="e.g., React, Python, Project Management..."
                  value={skillFilter}
                  onChange={handleSkillFilterChange}
                />
              </div>
              <div>
                {(skillFilter || nameFilter || countryFilter || teamFilter) && (
                  <Button onClick={clearAllFilters} variant="outline">
                    <X className="h-4 w-4 mr-1" />
                    Clear All Filters
                  </Button>
                )}
              </div>
            </div>
            
            {/* Active Filters Display */}
            {(skillFilter || nameFilter || countryFilter || teamFilter) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <span className="text-sm text-slate-600">Active filters:</span>
                {nameFilter && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Name: "{nameFilter}"
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1 hover:bg-transparent"
                      onClick={() => setNameFilter("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                )}
                {countryFilter && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Country: "{countryFilter}"
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1 hover:bg-transparent"
                      onClick={() => setCountryFilter("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                )}
                {teamFilter && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Team: "{teamFilter}"
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1 hover:bg-transparent"
                      onClick={() => setTeamFilter("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                )}
                {skillFilter && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Skill: "{skillFilter}"
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1 hover:bg-transparent"
                      onClick={() => setSkillFilter("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Column Visibility Controls */}
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsColumnVisibilityExpanded(!isColumnVisibilityExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-indigo-600" />
              Column Visibility
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isColumnVisibilityExpanded ? 'rotate-180' : ''}`} />
          </CardTitle>
          <CardDescription>Choose which columns to show or hide in the employee table</CardDescription>
        </CardHeader>
        {isColumnVisibilityExpanded && (
          <CardContent>
            <div className="space-y-4">
              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button onClick={showAllColumns} variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-1" />
                  Show All
                </Button>
                <Button onClick={hideAllColumns} variant="outline" size="sm">
                  <EyeOff className="h-4 w-4 mr-1" />
                  Hide All
                </Button>
              </div>
              
              {/* Column Checkboxes */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {columnOptions.map((column) => (
                  <div key={column.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`column-${column.key}`}
                      checked={visibleColumns[column.key]}
                      onCheckedChange={() => column.required ? null : toggleColumn(column.key)}
                      disabled={column.required}
                    />
                    <Label 
                      htmlFor={`column-${column.key}`}
                      className={`text-sm cursor-pointer ${column.required ? 'text-gray-400' : 'text-gray-700'}`}
                    >
                      {column.label}
                      {column.required && <span className="text-xs text-gray-400 ml-1">(required)</span>}
                    </Label>
                  </div>
                ))}
              </div>
              
              {/* Column Count Info */}
              <div className="text-xs text-gray-500">
                Showing {Object.values(visibleColumns).filter(Boolean).length} of {columnOptions.length} columns
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Employee Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Employees ({totalEmployees} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  {visibleColumns.name && <th className="text-left p-3 font-semibold">Name</th>}
                  {visibleColumns.country && <th className="text-left p-3 font-semibold">Country</th>}
                  {visibleColumns.team && <th className="text-center p-3 font-semibold">Team</th>}
                  {visibleColumns.status && <th className="text-center p-3 font-semibold">Status</th>}
                  {visibleColumns.salary && <th className="text-right p-3 font-semibold">Salary</th>}
                  {visibleColumns.sellingHour && <th className="text-right p-3 font-semibold">Selling/Hour</th>}
                  {visibleColumns.costHour && <th className="text-right p-3 font-semibold">Cost/Hour</th>}
                  {visibleColumns.experience && <th className="text-right p-3 font-semibold">Experience</th>}
                  {visibleColumns.phone && <th className="text-left p-3 font-semibold">Phone</th>}
                  {visibleColumns.actions && <th className="text-center p-3 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {employees.length > 0 ? employees.map((employee, index) => (
                  <tr key={employee.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                    {visibleColumns.name && (
                      <td className="p-3 font-medium">
                        <div>
                          <div className="font-semibold flex items-center gap-2">
                            {employee.name}
                            {employee.isManager && (
                              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                Manager
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-500">
                            {employee.designation || employee.position || 'N/A'}
                            {employee.manager_name && (
                              <span className="text-blue-600"> • Reports to: {employee.manager_name}</span>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.country && (
                      <td className="p-3">{employee.country || 'N/A'}</td>
                    )}
                    {visibleColumns.team && (
                      <td className="p-3 text-center">
                        {employee.team_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {employee.team_name}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">No Team</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (employee.status || 'Active') === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employee.status || 'Active'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.salary && (
                      <td className="p-3 text-right font-bold text-green-600">
                        ${employee.monthly_salary ? employee.monthly_salary.toFixed(2) : employee.salary ? employee.salary.toFixed(2) : '0.00'}
                      </td>
                    )}
                    {visibleColumns.sellingHour && (
                      <td className="p-3 text-right font-semibold text-blue-600">
                        ${employee.selling_per_hour ? employee.selling_per_hour.toFixed(2) : '0.00'}
                      </td>
                    )}
                    {visibleColumns.costHour && (
                      <td className="p-3 text-right font-semibold text-orange-600">
                        ${employee.cost_per_hour ? employee.cost_per_hour.toFixed(2) : '0.00'}
                      </td>
                    )}
                    {visibleColumns.experience && (
                      <td className="p-3 text-right font-semibold text-purple-600">
                        {employee.total_experience ? `${employee.total_experience.toFixed(1)} yrs` : 'N/A'}
                      </td>
                    )}
                    {visibleColumns.phone && (
                      <td className="p-3">{employee.phone || 'N/A'}</td>
                    )}
                    {visibleColumns.actions && (
                      <td className="p-3">
                        <div className="flex items-center justify-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => window.location.href = `/employees/${employee.id}`}
                                className="cursor-pointer"
                              >
                                <UserCheck className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleEdit(employee)}
                                className="cursor-pointer"
                              >
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Employee
                              </DropdownMenuItem>
                              {!employee.isManager && (
                                <DropdownMenuItem
                                  onClick={() => handlePromoteToManager(employee.id)}
                                  className="cursor-pointer text-emerald-600"
                                >
                                  <Crown className="mr-2 h-4 w-4" />
                                  Promote to Manager
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(employee.id)}
                                className="cursor-pointer text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Employee
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    )}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="p-8 text-center text-slate-500">
                      No employees found. Add your first employee to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalEmployees}
        itemsPerPage={employeesPerPage}
        itemName="employees"
      />
    </div>
  );
};

// Project Management Component
// ProjectDetail Component
const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user context for role-based restrictions
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  
  // Check if current user is an employee or client
  const isEmployee = user?.isEmployee || false;
  const isClient = user?.isClient || false;
  const isManager = user?.isManager || false;

  // Task management states
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Enhanced task creation states
  const [taskForm, setTaskForm] = useState({ 
    task: "",
    description: "",
    links: [],
    documents: []
  });
  const [newTaskLink, setNewTaskLink] = useState("");
  const [uploadingTaskDocs, setUploadingTaskDocs] = useState(false);
  
  // Employee assignment states
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);

  // Task assignment states
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState({
    taskIndex: '',
    employeeIds: [], // Changed from employeeId to employeeIds array
    startDate: null,
    endDate: null
  });

  // Manager team management
  const fetchManagerTeam = async () => {
    if (isManager && user?.id && project) {
      try {
        const teamResponse = await axios.get(`${API}/managers/${user.id}/team`);
        const teamMembers = teamResponse.data.team_members || [];
        
        // Filter team members that are not already assigned to the project
        const availableTeamMembers = teamMembers.filter(member => 
          !project.employee_ids || !project.employee_ids.includes(member.id)
        );
        
        setAvailableEmployees(availableTeamMembers);
      } catch (error) {
        console.error('Error fetching manager team:', error);
        setAvailableEmployees([]);
      }
    }
  };

  // Get manager name helper function
  const getManagerName = (managerId) => {
    if (!managerId) return 'Not Assigned';
    const manager = employees.find(emp => emp.id === managerId && emp.isManager);
    return manager ? manager.name : 'Unknown Manager';
  };

  // Get client contact person name helper function
  const getClientContactPerson = (client) => {
    if (!client) return 'Unknown Contact';
    
    // If contact_person property exists, use it
    if (client.contact_person) {
      return client.contact_person;
    }
    
    // If contacts array exists, get primary contact or first contact
    if (client.contacts && client.contacts.length > 0) {
      const primaryContact = client.contacts.find(contact => contact.is_primary);
      if (primaryContact) {
        return primaryContact.name;
      }
      // Fallback to first contact
      return client.contacts[0].name;
    }
    
    // Fallback to other possible fields
    return client.contact_name || client.primary_contact || 'No Contact Person';
  };

  // Helper function to check if current user is assigned to this project
  const isUserAssignedToProject = () => {
    if (!user || !project) return false;
    
    // Check if user is the project manager
    if (project.manager_id === user.id) return true;
    
    // Check if user is in the assigned employees list (primary access control)
    if (project.employee_ids && project.employee_ids.includes(user.id)) return true;
    
    return false;
  };

  // Access control enforcement - check when user and project are loaded
  useEffect(() => {
    // Only run access control after project data is fully loaded and user is authenticated
    if (user && project && project.id && isEmployee && !loading) {
      console.log('Access control check:', {
        userId: user.id,
        isEmployee,
        managerId: project.manager_id,
        employeeIds: project.employee_ids,
        isAssigned: isUserAssignedToProject()
      });
      
      // For employees: check if they have access to this project
      if (!isUserAssignedToProject()) {
        console.log('Access denied for user:', user.id);
        toast.error("Access denied. You are not assigned to this project.");
        navigate('/employee-dashboard');
        return;
      }
    }
  }, [user, project, isEmployee, navigate, loading]);

  // Document management states
  const [documents, setDocuments] = useState([]);
  const [demoLinks, setDemoLinks] = useState([]);
  const [linkForm, setLinkForm] = useState({ title: "", url: "" });

  // Payment management states
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({});
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    description: '',
    reference_number: ''
  });

  // Notes management states
  const [notes, setNotes] = useState([]);
  const [noteForm, setNoteForm] = useState({ content: "" });
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);

  // Activity logs management states
  const [projectLogs, setProjectLogs] = useState([]);

  // Activity logging function
  const logProjectActivity = async (activity) => {
    try {
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      
      // Get proper user name based on role
      let userName = 'Unknown User';
      if (user) {
        if (user.isClient) {
          // For clients, use contact name
          userName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        } else if (user.isEmployee) {
          userName = user.name || 'Employee User';
        } else {
          userName = user.name || 'Admin User';
        }
      }
      
      const logData = {
        activity,
        created_by: user?.id,
        created_by_name: userName,
        created_at: new Date().toISOString()
      };
      
      await axios.post(`${API}/projects/${id}/logs`, logData);
      
      // Add to local state for immediate update
      setProjectLogs(prev => [logData, ...prev]);
    } catch (error) {
      console.error('Error logging project activity:', error);
    }
  };

  useEffect(() => {
    fetchProjectDetail();
    fetchEmployees();
    fetchPayments();
    fetchPaymentSummary();
    fetchProjectLogs();
  }, [id]);

  const fetchProjectLogs = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/logs`);
      setProjectLogs(response.data || []);
    } catch (error) {
      console.error('Error fetching project logs:', error);
      setProjectLogs([]);
    }
  };

  const fetchProjectDetail = async () => {
    try {
      setLoading(true);
      const [projectRes, clientsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/clients`)
      ]);
      
      const projectData = projectRes.data;
      setProject(projectData);
      
      // Load task assignments and notes from backend with proper date conversion
      const taskAssignments = (projectData.task_assignments || []).map(assignment => ({
        taskIndex: assignment.task_index,
        taskName: assignment.task_name,
        employeeId: assignment.employee_id,
        employeeName: assignment.employee_name || assignment.employeeName, // Handle both formats
        startDate: assignment.start_date ? new Date(assignment.start_date + 'T00:00:00') : 
                  (assignment.due_date ? new Date(assignment.due_date + 'T00:00:00') : null), // Fallback to due_date for old records
        endDate: assignment.end_date ? new Date(assignment.end_date + 'T00:00:00') : 
                (assignment.due_date ? new Date(assignment.due_date + 'T00:00:00') : null), // Fallback to due_date for old records  
        status: assignment.status || "To Do",
        assignedAt: assignment.assigned_at ? new Date(assignment.assigned_at) : new Date()
      }));
      setTaskAssignments(taskAssignments);
      
      const notes = (projectData.notes || []).map(note => ({
        id: note.id,
        content: note.content,
        createdAt: note.created_at ? new Date(note.created_at) : new Date(),
        created_by: note.created_by,
        created_by_name: note.created_by_name
      }));
      setNotes(notes);

      // Load documents and demo links from backend
      const documents = (projectData.documents || []).map(doc => ({
        id: doc.id,
        name: doc.name,
        size: doc.size,
        type: doc.type,
        file_path: doc.file_path,
        uploadedAt: doc.uploaded_at ? new Date(doc.uploaded_at) : new Date()
      }));
      setDocuments(documents);

      const demoLinks = (projectData.demo_links || []).map(link => ({
        id: link.id,
        title: link.title,
        url: link.url,
        type: link.type || "custom",
        createdAt: link.created_at ? new Date(link.created_at) : new Date()
      }));
      setDemoLinks(demoLinks);
      
      // Find the client for this project
      const projectClient = clientsRes.data.find(c => c.id === projectData.client_id);
      setClient(projectClient);
      
      // Store project data temporarily to run access check
      setProject(projectData);
      
    } catch (error) {
      console.error("Error fetching project detail:", error);
      setError("Failed to load project details");
      toast.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      const allEmployees = response.data;
      setEmployees(allEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  useEffect(() => {
    if (project && employees.length > 0) {
      let assignedEmployees = employees.filter(emp => 
        project.employee_ids && project.employee_ids.includes(emp.id)
      );
      
      // Auto-include project manager in assigned employees if not already included
      if (project.manager_id) {
        const projectManager = employees.find(emp => emp.id === project.manager_id);
        if (projectManager && !assignedEmployees.find(emp => emp.id === project.manager_id)) {
          assignedEmployees.push(projectManager);
        }
      }
      
      setProjectEmployees(assignedEmployees);

      // Set available employees (not yet assigned to project)
      if (isManager && user?.id) {
        // For managers, only show their team members as available
        fetchManagerTeam();
      } else {
        // For non-managers, show all available employees
        const available = employees.filter(emp => 
          !project.employee_ids || !project.employee_ids.includes(emp.id)
        );
        setAvailableEmployees(available);
      }
    }
  }, [project, employees, isManager, user]);

  // Task management functions
  const addTaskLink = () => {
    if (newTaskLink.trim()) {
      setTaskForm(prev => ({
        ...prev,
        links: [...prev.links, newTaskLink.trim()]
      }));
      setNewTaskLink("");
    }
  };

  const removeTaskLink = (index) => {
    setTaskForm(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const handleTaskFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingTaskDocs(true);
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        uploadedFiles.push({
          name: file.name,
          url: response.data.url,
          type: file.type,
          size: file.size
        });
      }
      
      setTaskForm(prev => ({
        ...prev,
        documents: [...prev.documents, ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Error uploading task files:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      setUploadingTaskDocs(false);
    }
  };

  const removeTaskDocument = (index) => {
    setTaskForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const openTaskDialog = (task = null, index = -1) => {
    if (task && index >= 0) {
      // Editing existing task
      setEditingTask({ task, index });
      
      // Find existing task assignment data
      const existingAssignment = project.task_assignments?.find(
        ta => ta.task_index === index
      );
      
      setTaskForm({ 
        task: task,
        description: existingAssignment?.description || "",
        links: existingAssignment?.links || [],
        documents: existingAssignment?.documents || []
      });
    } else {
      // Adding new task
      setEditingTask(null);
      setTaskForm({ 
        task: "",
        description: "",
        links: [],
        documents: []
      });
    }
    setIsTaskDialogOpen(true);
  };

  const handleAddTask = async () => {
    if (!taskForm.task.trim()) return;

    try {
      // Add task name to tasks array
      const updatedTasks = [...(project.tasks || []), taskForm.task.trim()];
      
      // Create enhanced task assignment with description, links, and documents
      const newTaskIndex = updatedTasks.length - 1;
      const taskAssignmentData = {
        task_index: newTaskIndex,
        task_name: taskForm.task.trim(),
        employee_id: "unassigned", // Default value for unassigned tasks
        employee_name: "Unassigned", // Default value for unassigned tasks
        description: taskForm.description || "",
        links: taskForm.links || [],
        documents: taskForm.documents || [],
        notes: [],
        logs: [],
        status: "To Do",
        assigned_at: new Date().toISOString()
      };

      // Update project with both tasks and task assignment
      await axios.put(`${API}/projects/${id}`, { 
        tasks: updatedTasks,
        task_assignments: [...(project.task_assignments || []), taskAssignmentData]
      });
      
      setProject({ 
        ...project, 
        tasks: updatedTasks,
        task_assignments: [...(project.task_assignments || []), taskAssignmentData]
      });
      
      setTaskForm({ 
        task: "",
        description: "",
        links: [],
        documents: []
      });
      setIsTaskDialogOpen(false);
      toast.success("Task added successfully");
    } catch (error) {
      console.error("Error adding task:", error);
      toast.error("Failed to add task");
    }
  };

  const handleEditTask = async () => {
    if (!taskForm.task.trim()) return;

    try {
      // Update task name
      const updatedTasks = [...(project.tasks || [])];
      updatedTasks[editingTask.index] = taskForm.task.trim();
      
      // Update or create task assignment data
      let updatedTaskAssignments = [...(project.task_assignments || [])];
      const existingAssignmentIndex = updatedTaskAssignments.findIndex(
        ta => ta.task_index === editingTask.index
      );
      
      const taskAssignmentData = {
        task_index: editingTask.index,
        task_name: taskForm.task.trim(),
        description: taskForm.description || "",
        links: taskForm.links || [],
        documents: taskForm.documents || [],
        updated_at: new Date().toISOString()
      };
      
      if (existingAssignmentIndex >= 0) {
        // Update existing assignment
        updatedTaskAssignments[existingAssignmentIndex] = {
          ...updatedTaskAssignments[existingAssignmentIndex],
          ...taskAssignmentData
        };
      } else {
        // Create new assignment
        updatedTaskAssignments.push({
          ...taskAssignmentData,
          employee_id: "unassigned",
          employee_name: "Unassigned",
          notes: [],
          logs: [],
          status: "To Do",
          assigned_at: new Date().toISOString()
        });
      }

      await axios.put(`${API}/projects/${id}`, { 
        tasks: updatedTasks,
        task_assignments: updatedTaskAssignments
      });
      
      setProject({ 
        ...project, 
        tasks: updatedTasks,
        task_assignments: updatedTaskAssignments
      });
      
      setTaskForm({ 
        task: "",
        description: "",
        links: [],
        documents: []
      });
      setEditingTask(null);
      setIsTaskDialogOpen(false);
      toast.success("Task updated successfully");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskIndex) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      // Step 1: Delete all task assignments for this task
      await axios.delete(`${API}/projects/${id}/task-assignments/${taskIndex}`);
      
      // Step 2: Get current project state to ensure we have latest data
      const response = await axios.get(`${API}/projects/${id}`);
      const currentProject = response.data;
      
      // Step 3: Remove the task from tasks list
      const updatedTasks = currentProject.tasks.filter((_, index) => index !== taskIndex);
      
      // Step 4: Fix assignment indexes for tasks that come after the deleted task
      const fixedAssignments = (currentProject.task_assignments || []).map(assignment => {
        if (assignment.task_index > taskIndex) {
          return { ...assignment, task_index: assignment.task_index - 1 };
        }
        return assignment;
      });
      
      // Step 5: Update project with both updated tasks and fixed assignments in one call
      await axios.put(`${API}/projects/${id}`, {
        ...currentProject,
        tasks: updatedTasks,
        task_assignments: fixedAssignments
      });
      
      // Step 6: Refresh project data to get latest state
      await fetchProjectDetail();
      
      toast.success("Task deleted successfully");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(`Failed to delete task: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Employee management functions
  const handleAddEmployee = async (employeeId) => {
    try {
      const updatedEmployeeIds = [...(project.employee_ids || []), employeeId];
      await axios.put(`${API}/projects/${id}`, { employee_ids: updatedEmployeeIds });
      
      setProject({ ...project, employee_ids: updatedEmployeeIds });
      toast.success("Employee added to project");
      fetchProjectDetail(); // Refresh to update employee lists
    } catch (error) {
      console.error("Error adding employee:", error);
      toast.error("Failed to add employee");
    }
  };

  const handleRemoveEmployee = async (employeeId) => {
    // Prevent managers from removing themselves
    if (isManager && user?.id === employeeId) {
      toast.error("You cannot remove yourself from the project");
      return;
    }

    if (!window.confirm("Are you sure you want to remove this employee from the project?")) return;

    try {
      const updatedEmployeeIds = project.employee_ids.filter(id => id !== employeeId);
      await axios.put(`${API}/projects/${id}`, { employee_ids: updatedEmployeeIds });
      
      setProject({ ...project, employee_ids: updatedEmployeeIds });
      toast.success("Employee removed from project");
      fetchProjectDetail(); // Refresh to update employee lists
    } catch (error) {
      console.error("Error removing employee:", error);
      toast.error("Failed to remove employee");
    }
  };

  // Group task assignments by task index for display
  // For employees, filter to show only tasks assigned to them
  const filteredTaskAssignments = isEmployee && !isManager ? 
    taskAssignments.filter(assignment => 
      assignment.employeeId === user?.id
    ) : taskAssignments;

  const groupedTaskAssignments = filteredTaskAssignments.reduce((groups, assignment) => {
    const taskIndex = assignment.taskIndex;
    if (!groups[taskIndex]) {
      groups[taskIndex] = {
        taskIndex,
        taskName: assignment.taskName,
        employees: [],
        startDate: assignment.startDate,
        endDate: assignment.endDate,
        status: assignment.status
      };
    } else {
      // Update date range for multiple assignments to the same task
      const currentGroup = groups[taskIndex];
      if (assignment.startDate && (!currentGroup.startDate || assignment.startDate < currentGroup.startDate)) {
        currentGroup.startDate = assignment.startDate;
      }
      if (assignment.endDate && (!currentGroup.endDate || assignment.endDate > currentGroup.endDate)) {
        currentGroup.endDate = assignment.endDate;
      }
    }
    groups[taskIndex].employees.push({
      employeeId: assignment.employeeId,
      employeeName: assignment.employeeName,
      status: assignment.status
    });
    return groups;
  }, {});

  const groupedAssignmentsArray = Object.values(groupedTaskAssignments);
  
  // Filter out placeholder assignments for the "Assigned Tasks" section
  // For assigned employees: show ALL tasks (assigned and unassigned)
  // For non-assigned users: only show tasks that have at least one valid employee assignment
  let validAssignedTasks;
  
  if (isEmployee && isUserAssignedToProject()) {
    // For assigned employees: show ALL project tasks (both with and without assignments)
    const allProjectTasks = [];
    
    // First, add all tasks that have assignments (from groupedAssignmentsArray)
    allProjectTasks.push(...groupedAssignmentsArray);
    
    // Then, add tasks that have NO assignments at all
    if (project?.tasks) {
      project.tasks.forEach((task, taskIndex) => {
        // Check if this task already has assignments in groupedAssignmentsArray
        const hasAssignments = groupedAssignmentsArray.some(group => group.taskIndex === taskIndex);
        
        if (!hasAssignments) {
          // This task has no assignments, create an entry for it
          allProjectTasks.push({
            taskIndex: taskIndex,
            taskName: task.name || task.task_name || `Task ${taskIndex + 1}`,
            employees: [], // No employees assigned
            status: task.status || 'To Do'
          });
        }
      });
    }
    
    validAssignedTasks = allProjectTasks;
  } else {
    // For non-assigned users: only show tasks that have valid employee assignments
    validAssignedTasks = groupedAssignmentsArray.filter(group => {
      return group.employees.some(employee => {
        const employeeName = employee.employeeName || employee.employee_name;
        const employeeId = employee.employeeId || employee.employee_id;
        
        return employeeName && employeeName.trim() && 
               employeeName.toLowerCase() !== 'unassigned' &&
               employeeName.toLowerCase() !== 'none' &&
               employeeName.toLowerCase() !== 'null' &&
               employeeId && employeeId.trim() &&
               employeeId.toLowerCase() !== 'unassigned' &&
               employeeId.toLowerCase() !== 'none' &&
               employeeId.toLowerCase() !== 'null';
      });
    });
  }

  // Note: Removed allProjectTasks since we're now using a simpler approach

  // Task assignment functions
  const handleCurrentAssignmentChange = (field, value) => {
    setCurrentAssignment(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeSelection = (employeeId) => {
    setCurrentAssignment(prev => {
      const currentEmployeeIds = prev.employeeIds || [];
      const isSelected = currentEmployeeIds.includes(employeeId);
      
      if (isSelected) {
        // Remove employee if already selected
        return {
          ...prev,
          employeeIds: currentEmployeeIds.filter(id => id !== employeeId)
        };
      } else {
        // Add employee if not selected
        return {
          ...prev,
          employeeIds: [...currentEmployeeIds, employeeId]
        };
      }
    });
  };

  const assignTaskToEmployee = async () => {
    const { taskIndex, employeeIds, startDate, endDate } = currentAssignment;
    
    if (!taskIndex && taskIndex !== 0) {
      toast.error("Please select a task");
      return;
    }
    
    if (!employeeIds || employeeIds.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }

    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }

    if (!endDate) {
      toast.error("Please select an end date");
      return;
    }

    const taskName = project.tasks[taskIndex];
    
    try {
      // Remove existing assignments for this task
      const existingAssignments = taskAssignments.filter(
        assignment => assignment.taskIndex === taskIndex
      );

      // Create assignments for each selected employee
      const newAssignments = [];
      const assignmentsForState = [];

      for (const employeeId of employeeIds) {
        const employee = projectEmployees.find(emp => emp.id === employeeId);
        
        const newAssignment = {
          task_index: parseInt(taskIndex),
          task_name: taskName,
          employee_id: employeeId,
          employee_name: employee?.name || 'Unknown',
          start_date: startDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
          end_date: endDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
          status: "To Do" // Default status when task is assigned
        };

        newAssignments.push(newAssignment);

        const assignmentForState = {
          taskIndex: parseInt(taskIndex),
          taskName,
          employeeId,
          employeeName: employee?.name || 'Unknown',
          startDate,
          endDate,
          status: "To Do",
          assignedAt: new Date()
        };

        assignmentsForState.push(assignmentForState);
      }

      // Save all assignments to backend
      for (const assignment of newAssignments) {
        await axios.post(`${API}/projects/${id}/task-assignments`, assignment);
      }

      // Update state - remove old assignments for this task and add new ones
      setTaskAssignments(prev => {
        const filtered = prev.filter(assignment => assignment.taskIndex !== taskIndex);
        return [...filtered, ...assignmentsForState];
      });

      const employeeNames = assignmentsForState.map(a => a.employeeName).join(', ');
      toast.success(`Task "${taskName}" assigned to ${employeeNames}`);

      // Reset form
      setCurrentAssignment({
        taskIndex: '',
        employeeIds: [],
        startDate: null,
        endDate: null
      });
    } catch (error) {
      console.error("Error assigning task:", error);
      toast.error("Failed to assign task");
    }
  };

  const removeTaskAssignment = async (taskIndex) => {
    if (!window.confirm("Are you sure you want to remove this task assignment?")) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/task-assignments/${taskIndex}`);
      setTaskAssignments(prev => prev.filter(assignment => assignment.taskIndex !== taskIndex));
      toast.success("Task assignment removed");
    } catch (error) {
      console.error("Error removing task assignment:", error);
      toast.error("Failed to remove task assignment");
    }
  };

  const getTaskAssignment = (taskIndex) => {
    // Get all assignments for this task index
    const allAssignments = taskAssignments.filter(assignment => assignment.taskIndex === taskIndex);
    
    if (!allAssignments || allAssignments.length === 0) {
      return null;
    }
    
    // Filter out placeholder/invalid assignments
    const validAssignments = allAssignments.filter(assignment => {
      const employeeName = assignment.employeeName || assignment.employee_name;
      const employeeId = assignment.employeeId || assignment.employee_id;
      
      return employeeName && employeeName.trim() && 
             employeeName.toLowerCase() !== 'unassigned' &&
             employeeName.toLowerCase() !== 'none' &&
             employeeName.toLowerCase() !== 'null' &&
             employeeId && employeeId.trim() &&
             employeeId.toLowerCase() !== 'unassigned' &&
             employeeId.toLowerCase() !== 'none' &&
             employeeId.toLowerCase() !== 'null';
    });
    
    // If we have valid assignments, return the first one with combined info
    if (validAssignments.length > 0) {
      const firstValid = validAssignments[0];
      
      // If multiple valid assignments, combine employee names
      if (validAssignments.length > 1) {
        const employeeNames = validAssignments
          .map(a => a.employeeName || a.employee_name)
          .filter(name => name && name.trim())
          .slice(0, 3); // Show max 3 names
        
        const displayName = employeeNames.length > 2 
          ? `${employeeNames.slice(0, 2).join(', ')} +${employeeNames.length - 2} more`
          : employeeNames.join(', ');
        
        return {
          ...firstValid,
          employeeName: displayName,
          isMultipleAssignment: true
        };
      }
      
      return firstValid;
    }
    
    // If no valid assignments, return the first assignment (might be placeholder)
    return allAssignments[0];
  };

  // Task status update function
  const updateTaskStatus = async (taskIndex, status) => {
    try {
      await axios.put(`${API}/projects/${id}/task-assignments/${taskIndex}/status`, { status });
      
      // Find task name for logging
      const taskAssignment = taskAssignments.find(assignment => assignment.taskIndex === taskIndex);
      const taskName = taskAssignment?.task_name || project.tasks?.[taskIndex] || `Task ${taskIndex + 1}`;
      
      // Update local state
      setTaskAssignments(prev => prev.map(assignment => 
        assignment.taskIndex === taskIndex 
          ? { ...assignment, status }
          : assignment
      ));
      
      // Log activity for status update
      await logProjectActivity(`Task status updated: "${taskName}" changed to ${status}`);
      
      toast.success(`Task status updated to ${status}`);
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error("Failed to update task status");
    }
  };

  // Notes management functions
  const addProjectNote = async () => {
    if (!noteForm.content.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      // Get proper user name based on role
      let userName = 'Unknown User';
      if (user) {
        if (user.isClient) {
          // For clients, use contact name
          userName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        } else if (user.isEmployee) {
          userName = user.name || 'Employee User';
        } else {
          userName = user.name || 'Admin User';
        }
      }

      const newNote = {
        content: noteForm.content.trim(),
        created_by: user?.id,
        created_by_name: userName
      };

      await axios.post(`${API}/projects/${id}/notes`, newNote);

      // Add to local state for immediate update (latest first)
      const noteForState = {
        id: Date.now().toString(),
        content: noteForm.content.trim(),
        createdAt: new Date(),
        created_by: user?.id,
        created_by_name: userName
      };

      setNotes(prev => [noteForState, ...prev]); // Latest first
      setNoteForm({ content: "" });
      setIsNoteDialogOpen(false);
      
      // Log activity for note addition
      await logProjectActivity(`Note added: ${noteForm.content.substring(0, 50)}${noteForm.content.length > 50 ? '...' : ''}`);
      
      toast.success("Note added successfully");
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  const removeProjectNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to remove this note?")) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/notes/${noteId}`);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast.success("Note removed");
    } catch (error) {
      console.error("Error removing note:", error);
      toast.error("Failed to remove note");
    }
  };

  // Kanban helper functions
  const getKanbanColumns = () => {
    const columns = {
      "To Do": { title: "To Do", tasks: [], color: "bg-gray-100" },
      "In Progress": { title: "In Progress", tasks: [], color: "bg-blue-100" },
      "Review": { title: "Review", tasks: [], color: "bg-yellow-100" },
      "Completed": { title: "Completed", tasks: [], color: "bg-green-100" }
    };

    // Categorize assigned tasks based on their actual status
    taskAssignments.forEach(assignment => {
      const status = assignment.status || "To Do";
      if (columns[status]) {
        columns[status].tasks.push(assignment);
      }
    });

    return columns;
  };

  // Document management functions
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    for (const file of files) {
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.png', '.jpg', '.jpeg', '.gif'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        toast.error(`File type ${fileExt} is not allowed for ${file.name}`);
        continue;
      }

      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);

        // Upload file to backend
        const response = await axios.post(`${API}/projects/${id}/documents`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });

        // Add to local state for immediate update
        const docForState = {
          id: response.data.document.id,
          name: file.name,
          size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
          type: file.name.split('.').pop().toLowerCase(),
          uploadedAt: new Date(),
          file_path: response.data.document.file_path
        };
        setDocuments(prev => [...prev, docForState]);
        
        // Log activity for document upload
        await logProjectActivity(`Document added: ${file.name}`);
        
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }
    
    if (files.length > 0) {
      toast.success(`File upload completed`);
    }
    
    // Reset file input
    event.target.value = '';
  };

  const removeDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to remove this document?")) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/documents/${docId}`);
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
      toast.success("Document removed");
    } catch (error) {
      console.error("Error removing document:", error);
      toast.error("Failed to remove document");
    }
  };

  const addDemoLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      toast.error("Please fill in both title and URL");
      return;
    }

    try {
      const newLink = {
        title: linkForm.title.trim(),
        url: linkForm.url.trim(),
        type: "custom"
      };

      await axios.post(`${API}/projects/${id}/demo-links`, newLink);

      // Add to local state for immediate update
      const linkForState = {
        id: Date.now(),
        title: linkForm.title.trim(),
        url: linkForm.url.trim(),
        type: "custom",
        createdAt: new Date()
      };

      setDemoLinks(prev => [...prev, linkForState]);
      setLinkForm({ title: "", url: "" });
      
      // Log activity for link addition
      await logProjectActivity(`Link added: ${linkForm.title.trim()}`);
      
      toast.success("Demo link added successfully");
    } catch (error) {
      console.error("Error adding demo link:", error);
      toast.error("Failed to add demo link");
    }
  };

  const downloadProjectDocument = async (doc) => {
    try {
      // Download file from backend
      const downloadUrl = `${API}/projects/${id}/documents/${doc.id}/download`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Log activity for document download
      await logProjectActivity(`Document downloaded: ${doc.name}`);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const removeDemoLink = async (linkId) => {
    if (!window.confirm("Are you sure you want to remove this demo link?")) return;
    
    try {
      await axios.delete(`${API}/projects/${id}/demo-links/${linkId}`);
      setDemoLinks(prev => prev.filter(link => link.id !== linkId));
      toast.success("Demo link removed");
    } catch (error) {
      console.error("Error removing demo link:", error);
      toast.error("Failed to remove demo link");
    }
  };



  // Payment management functions
  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };

  const fetchPaymentSummary = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/payment-summary`);
      setPaymentSummary(response.data);
    } catch (error) {
      console.error("Error fetching payment summary:", error);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      const paymentData = {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      };

      if (editingPayment) {
        await axios.put(`${API}/projects/${id}/payments/${editingPayment.id}`, paymentData);
        toast.success("Payment updated successfully");
      } else {
        await axios.post(`${API}/projects/${id}/payments`, paymentData);
        toast.success("Payment added successfully");
      }

      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        description: '',
        reference_number: ''
      });
      setEditingPayment(null);
      setIsPaymentDialogOpen(false);
      
      // Refresh payments and summary
      await fetchPayments();
      await fetchPaymentSummary();
      
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      amount: payment.amount.toString(),
      payment_date: payment.payment_date,
      payment_method: payment.payment_method,
      description: payment.description || '',
      reference_number: payment.reference_number || ''
    });
    setIsPaymentDialogOpen(true);
  };

  const handleDeletePayment = async (paymentId) => {
    if (!confirm("Are you sure you want to delete this payment?")) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${id}/payments/${paymentId}`);
      toast.success("Payment deleted successfully");
      
      // Refresh payments and summary
      await fetchPayments();
      await fetchPaymentSummary();
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: "bg-yellow-100 text-yellow-800",
      active: "bg-green-100 text-green-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getBillingTypeColor = (billingType) => {
    const colors = {
      one_time: "bg-purple-100 text-purple-800",
      monthly_retainer: "bg-indigo-100 text-indigo-800",
      annual_fee: "bg-pink-100 text-pink-800",
      custom_development: "bg-orange-100 text-orange-800"
    };
    return colors[billingType] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading project details...</div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex flex-col justify-center items-center h-64">
          <div className="text-red-500 mb-4">{error || "Project not found"}</div>
          {!isManager && !isClient && (
            <Button 
              onClick={() => window.location.href = isEmployee ? '/my-projects' : '/projects'} 
              variant="outline"
            >
              Back to Projects
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Main content container with max width */}
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            {!isManager && !isClient && (
              <Button 
                onClick={() => window.location.href = isEmployee ? '/my-projects' : '/projects'} 
                variant="ghost" 
                className="mb-2 text-gray-600 hover:text-gray-800"
              >
                ← Back to Projects
              </Button>
            )}
            <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-gray-600 mt-1">Project Details</p>
          </div>
          <div className="flex space-x-2">
            <Badge className={getStatusColor(project.status)}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </Badge>
            <Badge className={getBillingTypeColor(project.billing_type)}>
              {project.billing_type.replace('_', ' ').split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')}
            </Badge>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${isEmployee ? 'grid-cols-7' : 'grid-cols-8'}`}>
            <TabsTrigger value="info">Project Info</TabsTrigger>
            <TabsTrigger value="assigned">Assigned</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="activity">Activity Logs</TabsTrigger>
            {!isEmployee && !isManager && <TabsTrigger value="payments">Payments</TabsTrigger>}
          </TabsList>

          {/* Project Info Tab */}
          <TabsContent value="info" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Project Information */}
              <div className="lg:col-span-2 space-y-6">
                {/* Basic Information */}
                <Card>
                  <CardHeader>
                    <CardTitle>Project Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {project.description && (
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Description</Label>
                        <p className="text-gray-600 mt-1">{project.description}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Client</Label>
                        <p className="text-gray-600 mt-1">{client?.company_name || 'Unknown Client'}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Project Manager</Label>
                        <p className="text-gray-600 mt-1">{getManagerName(project.manager_id)}</p>
                      </div>
                    </div>
                    
                    {(!isEmployee && !isManager && !isClient) && (
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Budget</Label>
                        <p className="text-gray-600 mt-1 font-medium">${project.budget?.toLocaleString() || '0'}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Start Date</Label>
                        <p className="text-gray-600 mt-1">
                          {project.start_date ? format(new Date(project.start_date + 'T00:00:00'), "MMMM dd, yyyy") : 'Not set'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700">End Date</Label>
                        <p className="text-gray-600 mt-1">
                          {project.end_date ? format(new Date(project.end_date + 'T00:00:00'), "MMMM dd, yyyy") : 'Not set'}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Created</Label>
                      <p className="text-gray-600 mt-1">
                        {project.created_at ? format(new Date(project.created_at), "MMMM dd, yyyy 'at' h:mm a") : 'Unknown'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Client Information */}
                {client && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Client Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Company</Label>
                        <p className="text-gray-600 mt-1">{client.company_name}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Contact Person</Label>
                        <p className="text-gray-600 mt-1">{getClientContactPerson(client)}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Country</Label>
                        <p className="text-gray-600 mt-1">{client.country}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column - Project Statistics */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Project Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <Badge className={getStatusColor(project.status)}>
                        {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Billing Type</span>
                      <Badge className={getBillingTypeColor(project.billing_type)}>
                        {project.billing_type.replace('_', ' ').split(' ').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Tasks</span>
                      <span className="font-medium">{project.tasks?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Team Size</span>
                      <span className="font-medium">{projectEmployees.length} employees</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Assigned Tab */}
          <TabsContent value="assigned" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Assigned Employees */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Assigned Employees ({projectEmployees.length})</CardTitle>
                    {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                      <Button 
                        onClick={() => setIsEmployeeDialogOpen(true)} 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Add Employee
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {projectEmployees.length > 0 ? (
                    <div className="space-y-3">
                      {projectEmployees.map((employee) => (
                        <div key={employee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                              {employee.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{employee.name}</p>
                              <p className="text-sm text-gray-500">{employee.email}</p>
                            </div>
                          </div>
                          {!isEmployee && !isClient && !(isManager && user?.id === employee.id) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveEmployee(employee.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No employees assigned to this project.</p>
                  )}
                </CardContent>
              </Card>

              {/* Available Employees - Visible for Admin and Assigned Employees/Managers */}
              {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Available Employees ({availableEmployees.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {availableEmployees.length > 0 ? (
                      <div className="space-y-3">
                        {availableEmployees.map((employee) => (
                          <div key={employee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-medium">
                                {employee.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{employee.name}</p>
                                <p className="text-sm text-gray-500">{employee.email}</p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddEmployee(employee.id)}
                              className="text-emerald-600 hover:text-emerald-700"
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">All employees are assigned to this project.</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="mt-6">
            <div className="space-y-6">
              {/* Project Tasks */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Project Tasks ({project.tasks?.length || 0})</CardTitle>
                    {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                      <Button 
                        onClick={() => openTaskDialog()} 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Add Task
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {project.tasks && project.tasks.length > 0 ? (
                    <div className="space-y-3">
                      {project.tasks.map((task, index) => {
                        const assignment = getTaskAssignment(index);
                        return (
                          <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="text-gray-700 font-medium">{task}</p>
                                {assignment && (
                                  <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                                    <span className="flex items-center space-x-1">
                                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                      <span>Assigned to {assignment.employeeName}</span>
                                    </span>
                                    <span>
                                      {assignment.startDate && assignment.endDate ? (
                                        <>Start: {format(new Date(assignment.startDate), "MMM dd, yyyy")} - End: {format(new Date(assignment.endDate), "MMM dd, yyyy")}</>
                                      ) : (
                                        <span className="text-orange-600">Dates not set</span>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                              <div className="flex space-x-2 ml-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openTaskDialog(task, index)}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {!isEmployee && !isClient && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTask(index)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-gray-500 text-sm">No tasks created yet.</p>
                      {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                        <Button 
                          onClick={() => openTaskDialog()} 
                          variant="outline" 
                          size="sm" 
                          className="mt-3"
                        >
                          Add First Task
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Task Assignment Section - For Admin and Assigned Employees/Managers */}
              {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Assign Task to Employee</CardTitle>
                    <CardDescription>Select a task, assign it to a team member, and set start and end dates</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Select Task */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Select Task</Label>
                        <Select 
                          value={currentAssignment.taskIndex.toString()} 
                          onValueChange={(value) => handleCurrentAssignmentChange('taskIndex', parseInt(value))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a task" />
                          </SelectTrigger>
                          <SelectContent>
                            {project.tasks && project.tasks.length > 0 ? (
                              project.tasks.map((task, index) => (
                                <SelectItem key={index} value={index.toString()}>
                                  {index + 1}. {task}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="no-tasks" disabled>
                                No tasks available - Please add tasks first
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Select Employee(s) */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Select Employee(s)</Label>
                        <div className="border border-gray-300 rounded-md p-2 bg-white min-h-[40px] max-h-[120px] overflow-y-auto">
                          {projectEmployees.length > 0 ? (
                            <div className="space-y-1">
                              {projectEmployees.map((employee) => (
                                <div key={employee.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`employee-${employee.id}`}
                                    checked={currentAssignment.employeeIds.includes(employee.id)}
                                    onCheckedChange={() => handleEmployeeSelection(employee.id)}
                                  />
                                  <Label 
                                    htmlFor={`employee-${employee.id}`}
                                    className="text-sm cursor-pointer flex-1"
                                  >
                                    {employee.name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No employees available</p>
                          )}
                        </div>
                        {currentAssignment.employeeIds.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {currentAssignment.employeeIds.length} employee(s) selected
                          </p>
                        )}
                      </div>

                      {/* Date Assignment Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Start Date */}
                        <div>
                          <Label htmlFor="start-date" className="text-sm font-medium">Start Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                <span>
                                  {currentAssignment.startDate 
                                    ? format(currentAssignment.startDate, "MMM dd, yyyy") 
                                    : "Select start date"
                                  }
                                </span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={currentAssignment.startDate}
                                onSelect={(date) => handleCurrentAssignmentChange('startDate', date)}
                                disabled={(date) => {
                                  if (project.end_date) {
                                    return date > new Date(project.end_date);
                                  }
                                  return false;
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        {/* End Date */}
                        <div>
                          <Label htmlFor="end-date" className="text-sm font-medium">End Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                <span>
                                  {currentAssignment.endDate 
                                    ? format(currentAssignment.endDate, "MMM dd, yyyy") 
                                    : "Select end date"
                                  }
                                </span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={currentAssignment.endDate}
                                onSelect={(date) => handleCurrentAssignmentChange('endDate', date)}
                                disabled={(date) => {
                                  if (project.end_date) {
                                    return date > new Date(project.end_date);
                                  }
                                  // End date must be after start date
                                  if (currentAssignment.startDate) {
                                    return date < currentAssignment.startDate;
                                  }
                                  return false;
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          {project.end_date && (
                            <p className="text-xs text-gray-400 mt-1">
                              Must be before: {format(new Date(project.end_date + 'T00:00:00'), "MMM dd, yyyy")}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button 
                        onClick={assignTaskToEmployee}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={!currentAssignment.taskIndex && currentAssignment.taskIndex !== 0 || !currentAssignment.employeeIds.length || !currentAssignment.startDate || !currentAssignment.endDate}
                      >
                        Assign Task
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Assigned Tasks Table */}
              {(validAssignedTasks.length > 0 || (isEmployee && isUserAssignedToProject())) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Assigned Tasks ({validAssignedTasks.length})</CardTitle>
                    <CardDescription>Overview of all task assignments with team members</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Task
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Assigned Employees
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date Range
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {validAssignedTasks.map((group, index) => (
                            <tr key={group.taskIndex} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2">
                                  <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                                    {group.taskIndex + 1}
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">{group.taskName}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {(() => {
                                  // Filter out employees with null/undefined/empty names or "Unassigned"
                                  const validEmployees = (group.employees || []).filter(employee => 
                                    employee.employeeName && 
                                    employee.employeeName.trim() !== '' && 
                                    employee.employeeName.toLowerCase() !== 'unassigned'
                                  );
                                  
                                  if (validEmployees.length > 0) {
                                    return (
                                      <>
                                        <div className="flex flex-wrap gap-2">
                                          {validEmployees.map((employee, empIndex) => (
                                            <div key={empIndex} className="flex items-center space-x-1 bg-emerald-50 rounded-full px-2 py-1">
                                              <div className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                                                {employee.employeeName.charAt(0).toUpperCase()}
                                              </div>
                                              <span className="text-xs text-emerald-700 font-medium">{employee.employeeName}</span>
                                            </div>
                                          ))}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">{validEmployees.length} employee(s) assigned</p>
                                      </>
                                    );
                                  } else {
                                    return <span className="text-xs text-gray-400 italic">No employees assigned</span>;
                                  }
                                })()}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {(() => {
                                  try {
                                    if (group.startDate && group.endDate) {
                                      return `${format(new Date(group.startDate), "MMM dd")} - ${format(new Date(group.endDate), "MMM dd, yyyy")}`;
                                    } else if (group.startDate || group.endDate) {
                                      return `${format(new Date(group.startDate || group.endDate), "MMM dd, yyyy")}`;
                                    } else {
                                      return 'No dates set';
                                    }
                                  } catch (error) {
                                    console.error('Date formatting error:', error, 'startDate:', group.startDate, 'endDate:', group.endDate);
                                    return 'Date formatting error';
                                  }
                                })()}
                              </td>
                              <td className="px-4 py-3">
                                {!isClient ? (
                                  <Select 
                                    value={group.status || "To Do"} 
                                    onValueChange={(value) => updateTaskStatus(group.taskIndex, value)}
                                  >
                                    <SelectTrigger className="w-32 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="To Do">
                                        <span className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                          <span>To Do</span>
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="In Progress">
                                        <span className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                          <span>In Progress</span>
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="In Testing">
                                        <span className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                          <span>In Testing</span>
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="Resolved">
                                        <span className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                          <span>Resolved</span>
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="Published (Prod)">
                                        <span className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                          <span>Published (Prod)</span>
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="Closed">
                                        <span className="flex items-center space-x-2">
                                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                                          <span>Closed</span>
                                        </span>
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium bg-gray-100">
                                    <div className={`w-2 h-2 rounded-full ${
                                      group.status === 'Closed' ? 'bg-gray-500' :
                                      group.status === 'Published (Prod)' ? 'bg-purple-500' :
                                      group.status === 'Resolved' ? 'bg-green-500' :
                                      group.status === 'In Testing' ? 'bg-yellow-500' :
                                      group.status === 'In Progress' ? 'bg-blue-500' : 'bg-red-500'
                                    }`}></div>
                                    <span>{group.status || "To Do"}</span>
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex space-x-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/projects/${id}/tasks/${group.taskIndex}`)}
                                    className="text-blue-600 hover:text-blue-700"
                                    title="View Task Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {!isEmployee && !isClient && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeTaskAssignment(group.taskIndex)}
                                      className="text-red-600 hover:text-red-700"
                                      title="Remove Task Assignment"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Kanban Tab */}
          <TabsContent value="kanban" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Kanban Board</CardTitle>
                <CardDescription>Task status overview and workflow progress</CardDescription>
              </CardHeader>
              <CardContent>
                {taskAssignments.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {Object.entries(getKanbanColumns()).map(([columnKey, column]) => (
                      <div key={columnKey} className={`${column.color} rounded-lg p-4 min-h-96`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-800">{column.title}</h3>
                          <Badge variant="secondary" className="text-xs">
                            {column.tasks.length}
                          </Badge>
                        </div>
                        
                        <div className="space-y-3">
                          {column.tasks.map((task, index) => (
                            <div key={index} className="bg-white rounded-lg p-3 shadow-sm border">
                              <div className="space-y-2">
                                {/* Task Title */}
                                <div className="flex items-start space-x-2">
                                  <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                    {task.taskIndex + 1}
                                  </div>
                                  <h4 className="font-medium text-sm text-gray-900 leading-tight">{task.taskName}</h4>
                                </div>
                                
                                {/* Assigned Employee */}
                                <div className="flex items-center space-x-2 mt-2">
                                  <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                                    {task.employeeName.charAt(0)}
                                  </div>
                                  <span className="text-xs text-gray-600">{task.employeeName}</span>
                                </div>
                                
                                {/* Due Date */}
                                <div className="flex items-center space-x-1 mt-2">
                                  <CalendarIcon className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-500">
                                    {task.startDate && task.endDate ? 
                                      `${format(new Date(task.startDate), "MMM dd")} - ${format(new Date(task.endDate), "MMM dd")}` : 
                                      'No dates'
                                    }
                                  </span>
                                </div>
                                
                                {/* Status Badge */}
                                <div className="mt-2">
                                  <Badge 
                                    className={`text-xs ${
                                      task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                      task.status === 'Review' ? 'bg-yellow-100 text-yellow-700' :
                                      task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {task.status || 'To Do'}
                                  </Badge>
                                </div>
                                
                                {/* Timeline Indicator */}
                                <div className="mt-2">
                                  {(() => {
                                    if (!task.endDate) {
                                      return <Badge className="bg-gray-100 text-gray-700 text-xs">No End Date</Badge>;
                                    }
                                    
                                    const endDate = new Date(task.endDate);
                                    const today = new Date();
                                    const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                                    
                                    if (diffDays < 0) {
                                      return <Badge className="bg-red-100 text-red-700 text-xs">Overdue</Badge>;
                                    } else if (diffDays <= 3) {
                                      return <Badge className="bg-orange-100 text-orange-700 text-xs">Due Soon</Badge>;
                                    } else if (diffDays <= 7) {
                                      return <Badge className="bg-yellow-100 text-yellow-700 text-xs">This Week</Badge>;
                                    } else {
                                      return <Badge className="bg-green-100 text-green-700 text-xs">On Track</Badge>;
                                    }
                                  })()}
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {column.tasks.length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm">
                              No tasks in {column.title.toLowerCase()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Assigned Yet</h3>
                    <p className="text-gray-500 text-sm mb-4">Assign tasks to team members to see them in the Kanban board</p>
                    <Button 
                      onClick={() => setActiveTab("tasks")}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Go to Tasks Tab
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Project Notes</CardTitle>
                    <CardDescription>Timeline-based project notes and updates</CardDescription>
                  </div>
                  {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                    <Button 
                      onClick={() => setIsNoteDialogOpen(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Add Note
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {notes.length > 0 ? (
                  <div className="space-y-4">
                    {notes.map((note, index) => (
                      <div key={note.id} className="relative">
                        {/* Timeline Line */}
                        {index < notes.length - 1 && (
                          <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                        )}
                        
                        {/* Note Card */}
                        <div className="flex space-x-4">
                          {/* Timeline Dot */}
                          <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </div>
                          
                          {/* Note Content */}
                          <div className="flex-1 bg-white border rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="text-xs text-gray-500">
                                  {format(new Date(note.createdAt), "MMMM dd, yyyy 'at' h:mm a")}
                                </p>
                                <p className="text-xs text-blue-600 font-medium">
                                  Added by: {note.created_by_name || 'Unknown User'}
                                </p>
                              </div>
                              {!isEmployee && !isClient && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeProjectNote(note.id)}
                                  className="text-red-600 hover:text-red-700 opacity-60 hover:opacity-100"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{note.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h3>
                    <p className="text-gray-500 text-sm mb-4">Add project notes to track important updates and decisions</p>
                    {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && (
                      <Button 
                        onClick={() => setIsNoteDialogOpen(true)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Add First Note
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Documents & Links</CardTitle>
                <CardDescription>Upload documents and add demo/reference links for this project</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Document Upload Section */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Documents</Label>
                  {(!isClient || (isEmployee && isUserAssignedToProject())) && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <div className="space-y-2">
                        <div className="mx-auto w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Drop files here or click to browse</p>
                          <p className="text-xs text-gray-400">PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, Images up to 10MB</p>
                        </div>
                      </div>
                      <input
                        type="file"
                        id="file-upload"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <Button 
                        variant="outline" 
                        className="mt-3"
                        onClick={() => document.getElementById('file-upload').click()}
                      >
                        Choose Files
                      </Button>
                    </div>
                  )}
                  
                  {/* Uploaded documents */}
                  <div className="mt-4 space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded flex items-center justify-center ${
                            doc.type === 'pdf' ? 'bg-blue-100' : doc.type === 'xlsx' || doc.type === 'xls' ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            <svg className={`w-4 h-4 ${
                              doc.type === 'pdf' ? 'text-blue-600' : doc.type === 'xlsx' || doc.type === 'xls' ? 'text-green-600' : 'text-gray-600'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {doc.type === 'pdf' ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              )}
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                            <p className="text-xs text-gray-500">{doc.size} • Uploaded {format(new Date(doc.uploadedAt), "MMM dd, yyyy")}</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => downloadProjectDocument(doc)}
                          >
                            Download
                          </Button>
                          {!isEmployee && !isClient && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600"
                              onClick={() => removeDocument(doc.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Demo Links Section */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Demo Links (Optional)</Label>
                  <div className="space-y-3">
                    {/* Add New Link Form */}
                    {(!isClient || (isEmployee && isUserAssignedToProject())) && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 border border-gray-200 rounded-lg">
                        <div>
                          <Input 
                            placeholder="Link Title (e.g., Live Demo)"
                            value={linkForm.title}
                            onChange={(e) => setLinkForm({...linkForm, title: e.target.value})}
                          />
                        </div>
                        <div>
                          <Input 
                            placeholder="URL (e.g., https://demo.example.com)"
                            value={linkForm.url}
                            onChange={(e) => setLinkForm({...linkForm, url: e.target.value})}
                          />
                        </div>
                        <div>
                          <Button 
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            onClick={addDemoLink}
                          >
                            Add Link
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Existing Demo Links */}
                    <div className="space-y-2">
                      {demoLinks.map((link) => (
                        <div key={link.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                          link.type === 'live' ? 'bg-blue-50 border-blue-200' : 
                          link.type === 'staging' ? 'bg-green-50 border-green-200' : 
                          'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded flex items-center justify-center ${
                              link.type === 'live' ? 'bg-blue-100' : 
                              link.type === 'staging' ? 'bg-green-100' : 
                              'bg-gray-100'
                            }`}>
                              <svg className={`w-4 h-4 ${
                                link.type === 'live' ? 'text-blue-600' : 
                                link.type === 'staging' ? 'text-green-600' : 
                                'text-gray-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{link.title}</p>
                              <p className={`text-xs ${
                                link.type === 'live' ? 'text-blue-600' : 
                                link.type === 'staging' ? 'text-green-600' : 
                                'text-gray-600'
                              }`}>{link.url}</p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => window.open(link.url, '_blank')}
                            >
                              Visit
                            </Button>
                            {((!isEmployee && !isClient) || (isEmployee && isUserAssignedToProject())) && <Button size="sm" variant="ghost">Edit</Button>}
                            {!isEmployee && !isClient && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600"
                                onClick={() => removeDemoLink(link.id)}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="activity" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl font-semibold text-gray-900">Project Activity Logs</CardTitle>
                    <CardDescription>Timeline of all project activities and changes</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {projectLogs.length > 0 ? (
                  <div className="space-y-4">
                    {projectLogs.map((log, index) => (
                      <div key={log.id || index} className="relative">
                        {/* Timeline Line */}
                        {index < projectLogs.length - 1 && (
                          <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                        )}
                        
                        {/* Log Card */}
                        <div className="flex space-x-4">
                          {/* Timeline Dot */}
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V9a2 2 0 00-2-2h-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </div>
                          
                          {/* Log Content */}
                          <div className="flex-1 bg-white border rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="text-xs text-gray-500">
                                  {format(new Date(log.created_at), "MMMM dd, yyyy 'at' h:mm a")}
                                </p>
                                <p className="text-xs text-blue-600 font-medium">
                                  By: {log.created_by_name || 'Unknown User'}
                                </p>
                              </div>
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{log.activity}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V9a2 2 0 00-2-2h-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Logs Yet</h3>
                    <p className="text-gray-500 text-sm">Project activity will appear here as actions are performed</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Project Payments</CardTitle>
                    <CardDescription>
                      Track payments and outstanding balance for this project
                    </CardDescription>
                  </div>
                  {!isClient && (
                    <Button 
                      onClick={() => setIsPaymentDialogOpen(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Payment
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Payment Summary */}
                {paymentSummary && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-slate-900">
                        ${paymentSummary.budget?.toLocaleString() || '0'}
                      </div>
                      <div className="text-sm text-slate-600">Project Budget</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-600">
                        ${paymentSummary.total_paid?.toLocaleString() || '0'}
                      </div>
                      <div className="text-sm text-slate-600">Total Paid</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        ${paymentSummary.remaining_balance?.toLocaleString() || '0'}
                      </div>
                      <div className="text-sm text-slate-600">Remaining Balance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {paymentSummary.payment_percentage || 0}%
                      </div>
                      <div className="text-sm text-slate-600">Payment Progress</div>
                    </div>
                  </div>
                )}

                {/* Payment Progress Bar */}
                {paymentSummary && paymentSummary.budget > 0 && (
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Payment Progress</span>
                      <span>{paymentSummary.payment_percentage || 0}% Complete</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(paymentSummary.payment_percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Payments List */}
                <div className="space-y-4">
                  {payments.length > 0 ? (
                    payments.map((payment) => (
                      <div key={payment.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-4 mb-2">
                              <div className="text-lg font-semibold text-slate-900">
                                ${payment.amount.toLocaleString()}
                              </div>
                              <div className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                {payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1)}
                              </div>
                            </div>
                            <div className="text-sm text-slate-600 space-y-1">
                              <div>
                                <strong>Date:</strong> {new Date(payment.payment_date).toLocaleDateString()}
                              </div>
                              {payment.description && (
                                <div>
                                  <strong>Description:</strong> {payment.description}
                                </div>
                              )}
                              {payment.reference_number && (
                                <div>
                                  <strong>Reference:</strong> {payment.reference_number}
                                </div>
                              )}
                              <div className="text-xs text-slate-500">
                                Received by {payment.received_by} on {new Date(payment.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          {!isClient && !isManager && (
                            <div className="flex space-x-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditPayment(payment)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeletePayment(payment.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <DollarSign className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No payments recorded yet.</p>
                      <p className="text-sm">Add your first payment to start tracking project finances.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Payment Management Dialog */}
        <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingPayment ? "Edit Payment" : "Add Payment"}</DialogTitle>
              <DialogDescription>
                {editingPayment ? "Update payment details" : "Record a new payment for this project"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="amount">Payment Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <Label htmlFor="payment_date">Payment Date *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(value) => setPaymentForm({...paymentForm, payment_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="reference_number">Reference Number</Label>
                <Input
                  id="reference_number"
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                  placeholder="Check number, transaction ID, etc."
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                  placeholder="Additional notes about this payment..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddPayment}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
              >
                {editingPayment ? "Update Payment" : "Add Payment"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Enhanced Task Management Dialog */}
        <EnhancedTaskDialog
          isOpen={isTaskDialogOpen}
          onClose={() => setIsTaskDialogOpen(false)}
          taskForm={taskForm}
          setTaskForm={setTaskForm}
          editingTask={editingTask}
          handleAddTask={handleAddTask}
          handleEditTask={handleEditTask}
          newTaskLink={newTaskLink}
          setNewTaskLink={setNewTaskLink}
          addTaskLink={addTaskLink}
          removeTaskLink={removeTaskLink}
          handleTaskFileUpload={handleTaskFileUpload}
          removeTaskDocument={removeTaskDocument}
          uploadingTaskDocs={uploadingTaskDocs}
        />

        {/* Employee Assignment Dialog */}
        <Dialog open={isEmployeeDialogOpen} onOpenChange={setIsEmployeeDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Employee to Project</DialogTitle>
              <DialogDescription>
                Select employees to add to this project
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {availableEmployees.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableEmployees.map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium">
                          {employee.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{employee.name}</p>
                          <p className="text-xs text-gray-500">{employee.email}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          handleAddEmployee(employee.id);
                          setIsEmployeeDialogOpen(false);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">
                  All employees are already assigned to this project.
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Notes Dialog */}
        <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Project Note</DialogTitle>
              <DialogDescription>
                Add a new note to track project progress and updates
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="note-content">Note Content</Label>
                <Textarea
                  id="note-content"
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({content: e.target.value})}
                  placeholder="Enter your project note here..."
                  rows={4}
                  className="resize-none"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={addProjectNote}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!noteForm.content.trim()}
                >
                  Add Note
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Employee Profile Restricted Component (for employee login)
const EmployeeProfileRestricted = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");

  // Additional states for employee functionality
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [bankInfo, setBankInfo] = useState(null);
  const [projects, setProjects] = useState([]); // Only assigned projects
  const [attendance, setAttendance] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);

  // Form states
  const [leaveForm, setLeaveForm] = useState({
    start_date: '',
    end_date: '',
    leave_type: 'vacation',
    reason: '',
    days_requested: 0
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [bankInfoForm, setBankInfoForm] = useState({
    bank_name: '',
    account_number: '',
    routing_number: '',
    account_type: 'checking',
    additional_info: ''
  });

  // Document management states to match admin interface
  const [newDocumentForm, setNewDocumentForm] = useState({
    title: '',
    category: 'general',
    description: ''
  });

  useEffect(() => {
    // Ensure employee can only access their own profile
    if (user?.id !== id) {
      window.location.href = `/employee-profile/${user.id}`;
      return;
    }
    fetchEmployeeData();
  }, [id, user]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      
      // Fetch employee profile first
      console.log("Fetching employee profile for ID:", id);
      const profileRes = await axios.get(`${API}/auth/employee-profile/${id}`);
      console.log("Profile response:", profileRes.data);
      
      if (!profileRes.data) {
        throw new Error("No profile data received");
      }
      
      setEmployeeProfile({ employee: profileRes.data });
      console.log("Employee profile set successfully");
      
      // Fetch other data in parallel (with error handling for optional data)
      const currentYear = new Date().getFullYear();
      
      try {
        const leaveReqRes = await axios.get(`${API}/employees/${id}/leave-requests/${currentYear}`);
        console.log("Leave requests response:", leaveReqRes.data);
        // Handle both array and object with leave_requests property
        if (leaveReqRes.data?.leave_requests) {
          setLeaveRequests(leaveReqRes.data.leave_requests);
        } else if (Array.isArray(leaveReqRes.data)) {
          setLeaveRequests(leaveReqRes.data);
        } else {
          setLeaveRequests([]);
        }
      } catch (error) {
        console.warn("Failed to fetch leave requests:", error);
        setLeaveRequests([]);
      }

      try {
        const shiftsRes = await axios.get(`${API}/employees/${id}/shifts`);
        setShifts(shiftsRes.data || []);
      } catch (error) {
        console.warn("Failed to fetch shifts:", error);
        setShifts([]);
      }

      try {
        const paymentsRes = await axios.get(`${API}/employees/${id}/payments`);
        setPayments(paymentsRes.data || []);
      } catch (error) {
        console.warn("Failed to fetch payments:", error);
        setPayments([]);
      }

      try {
        const docsRes = await axios.get(`${API}/employees/${id}/documents`);
        setDocuments(docsRes.data || []);
      } catch (error) {
        console.warn("Failed to fetch documents:", error);
        setDocuments([]);
      }

      try {
        const bankRes = await axios.get(`${API}/employees/${id}/bank-info`);
        console.log("Bank info response:", bankRes.data);
        // Handle both array and single object responses
        if (Array.isArray(bankRes.data)) {
          setBankInfo(bankRes.data.length > 0 ? bankRes.data[0] : null);
        } else if (bankRes.data) {
          setBankInfo(bankRes.data);
        } else {
          setBankInfo(null);
        }
      } catch (error) {
        console.warn("Failed to fetch bank info:", error);
        setBankInfo(null);
      }
      
      try {
        // Use dedicated employee projects endpoint
        const projectsRes = await axios.get(`${API}/employees/${id}/projects`);
        setProjects(projectsRes.data || []);
      } catch (error) {
        console.warn("Failed to fetch employee projects:", error);
        setProjects([]);
      }

      try {
        const attendanceRes = await axios.get(`${API}/employees/${id}/attendance`);
        setAttendance(attendanceRes.data || []);
        
        // Check today's attendance
        const today = new Date().toISOString().split('T')[0];
        const todayRecord = attendanceRes.data?.find(record => 
          record.date === today || new Date(record.date).toISOString().split('T')[0] === today
        );
        setTodayAttendance(todayRecord);
      } catch (error) {
        console.warn("Failed to fetch attendance:", error);
        setAttendance([]);
        setTodayAttendance(null);
      }

    } catch (error) {
      console.error("Critical error fetching employee profile:", error);
      toast.error(`Failed to load profile: ${error.message}`);
      // Don't set employeeProfile to null if profile fetch fails
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveRequest = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const leaveData = {
        employee_id: id,
        start_date: leaveForm.start_date,
        end_date: leaveForm.end_date,
        leave_type: leaveForm.leave_type,
        reason: leaveForm.reason,
        days_requested: parseInt(leaveForm.days_requested) || 1
      };
      
      console.log("Submitting leave request:", leaveData);
      
      await axios.post(`${API}/employees/${id}/leave-request`, leaveData);
      toast.success("Leave request submitted successfully");
      setLeaveForm({
        start_date: '',
        end_date: '',
        leave_type: 'vacation',
        reason: '',
        days_requested: 0
      });
      fetchEmployeeData();
    } catch (error) {
      console.error("Error submitting leave request:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to submit leave request");
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }

    try {
      await axios.post(`${API}/auth/employee-change-password?employee_id=${id}`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success("Password changed successfully");
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);

    try {
      setUploadingDoc(true);
      await axios.post(`${API}/employees/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded successfully");
      fetchEmployeeData();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
      event.target.value = '';
    }
  };

  const handleBankInfoSave = async () => {
    try {
      if (bankInfo) {
        await axios.put(`${API}/employees/${id}/bank-info`, bankInfoForm);
        toast.success("Bank information updated successfully");
      } else {
        await axios.post(`${API}/employees/${id}/bank-info`, bankInfoForm);
        toast.success("Bank information added successfully");
      }
      fetchEmployeeData();
    } catch (error) {
      console.error("Error saving bank info:", error);
      toast.error("Failed to save bank information");
    }
  };

  const handleMarkAttendance = async (type) => {
    try {
      const response = await axios.post(`${API}/employees/${id}/attendance`, {
        type: type, // 'check_in' or 'check_out'
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      toast.success(`${type === 'check_in' ? 'Checked in' : 'Checked out'} successfully`);
      fetchEmployeeData(); // Refresh to get updated attendance
    } catch (error) {
      console.error("Error marking attendance:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  const handleDocumentUploadWithForm = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!newDocumentForm.title.trim()) {
      toast.error("Please enter a document title");
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', newDocumentForm.title);

    try {
      setUploadingDoc(true);
      await axios.post(`${API}/employees/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded successfully");
      setNewDocumentForm({
        title: '',
        category: 'general',
        description: ''
      });
      fetchEmployeeData();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
      event.target.value = '';
    }
  };

  // Set initial bank info form when data loads
  useEffect(() => {
    if (bankInfo) {
      setBankInfoForm({
        bank_name: bankInfo.bank_name || '',
        account_number: bankInfo.account_number || '',
        routing_number: bankInfo.routing_number || '',
        account_type: bankInfo.account_type || 'checking',
        additional_info: bankInfo.additional_info || ''
      });
    }
  }, [bankInfo]);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading your profile...</div>;
  }

  if (!employeeProfile) {
    return <div className="max-w-7xl mx-auto p-6">Profile not found</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Welcome, {employeeProfile.employee.name}</h1>
          <p className="text-slate-600">{employeeProfile.employee.designation || 'Employee'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setActiveTab('password')}>
            <Lock className="h-4 w-4 mr-2" />
            Change Password
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="shifts">Shifts</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leave">Leave Requests</TabsTrigger>
          <TabsTrigger value="financial">Financial History</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="bankinfo">Bank Info</TabsTrigger>
          <TabsTrigger value="projects">My Projects</TabsTrigger>
        </TabsList>

        {/* Profile Tab - Hide sensitive financial info */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <div className="font-medium">{employeeProfile.employee.name}</div>
                </div>
                <div>
                  <Label>Email</Label>
                  <div>{employeeProfile.employee.email || 'Not provided'}</div>
                </div>
                <div>
                  <Label>Phone</Label>
                  <div>{employeeProfile.employee.phone || 'Not provided'}</div>
                </div>
                <div>
                  <Label>Designation</Label>
                  <div>{employeeProfile.employee.designation || 'Not specified'}</div>
                </div>
                <div>
                  <Label>Country</Label>
                  <div>{employeeProfile.employee.country || 'Not specified'}</div>
                </div>
                <div>
                  <Label>Date of Joining</Label>
                  <div>{employeeProfile.employee.date_of_joining || 'Not specified'}</div>
                </div>
                <div>
                  <Label>Monthly Salary</Label>
                  <div>${employeeProfile.employee.monthly_salary || 'Not specified'}</div>
                </div>
                <div>
                  <Label>Total Experience</Label>
                  <div>{employeeProfile.employee.total_experience ? `${employeeProfile.employee.total_experience.toFixed(1)} years` : 'Not specified'}</div>
                </div>
              </div>
              

            </CardContent>
          </Card>
        </TabsContent>

        {/* Shifts Tab - Detailed View like Admin */}
        <TabsContent value="shifts">
          <Card>
            <CardHeader>
              <CardTitle>My Work Shifts</CardTitle>
              <CardDescription>View your assigned work shifts and schedules</CardDescription>
            </CardHeader>
            <CardContent>
              {shifts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-semibold">Shift Name</th>
                        <th className="text-left p-3 font-semibold">Time</th>
                        <th className="text-left p-3 font-semibold">Days</th>
                        <th className="text-left p-3 font-semibold">Timezone</th>
                        <th className="text-left p-3 font-semibold">Valid From</th>
                        <th className="text-left p-3 font-semibold">Valid To</th>
                        <th className="text-center p-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.map((shift, index) => (
                        <tr key={shift.id || index} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <td className="p-3">
                            <span className="font-medium text-slate-900">{shift.shift_name || 'Unnamed Shift'}</span>
                          </td>
                          <td className="p-3">
                            <span className="text-slate-700">
                              {shift.start_time} - {shift.end_time}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-slate-600">
                              {shift.working_days ? shift.working_days.join(', ') : 'Not specified'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-slate-600">{shift.timezone || 'UTC'}</span>
                          </td>
                          <td className="p-3">
                            <span className="text-slate-600">
                              {shift.valid_from ? new Date(shift.valid_from).toLocaleDateString() : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-slate-600">
                              {shift.valid_to ? new Date(shift.valid_to).toLocaleDateString() : 'Ongoing'}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <Badge 
                              variant={shift.is_active ? "default" : "secondary"}
                              className={shift.is_active ? "bg-green-600" : "bg-gray-500"}
                            >
                              {shift.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                      <Clock className="h-8 w-8 text-slate-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Shifts Assigned</h3>
                  <p className="text-slate-500">You don't have any work shifts assigned yet. Contact your manager for shift assignments.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Management Tab */}
        <TabsContent value="attendance">
          <div className="space-y-6">
            {/* Check In/Out Buttons */}
            <Card>
              <CardHeader>
                <CardTitle>Mark Attendance</CardTitle>
                <CardDescription>Click the buttons below to mark your check-in and check-out times</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 justify-center">
                  <Button 
                    onClick={() => handleMarkAttendance('check_in')}
                    className="bg-green-600 hover:bg-green-700 px-8 py-3 text-lg"
                    size="lg"
                  >
                    <Clock className="h-5 w-5 mr-2" />
                    Check In
                  </Button>
                  <Button 
                    onClick={() => handleMarkAttendance('check_out')}
                    className="bg-red-600 hover:bg-red-700 px-8 py-3 text-lg"
                    size="lg"
                  >
                    <Clock className="h-5 w-5 mr-2" />
                    Check Out
                  </Button>
                </div>
                
                {/* Today's Status */}
                {todayAttendance && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium text-slate-900 mb-2">Today's Status</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Check-in:</span>
                        <span className="ml-2 font-medium">
                          {todayAttendance.check_in_time ? new Date(todayAttendance.check_in_time).toLocaleTimeString() : 'Not marked'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-600">Check-out:</span>
                        <span className="ml-2 font-medium">
                          {todayAttendance.check_out_time ? new Date(todayAttendance.check_out_time).toLocaleTimeString() : 'Not marked'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attendance History Table */}
            <Card>
              <CardHeader>
                <CardTitle>Attendance History</CardTitle>
                <CardDescription>Your attendance records for the current month</CardDescription>
              </CardHeader>
              <CardContent>
                {attendance.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Date</th>
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Check In</th>
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Check Out</th>
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Total Hours</th>
                          <th className="border border-slate-300 px-4 py-3 text-center font-medium text-slate-700">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendance.slice(0, 30).map((record, index) => (
                          <tr key={record.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-300 px-4 py-3 font-medium">
                              {new Date(record.date).toLocaleDateString()}
                            </td>
                            <td className="border border-slate-300 px-4 py-3">
                              {record.check_in_time ? new Date(record.check_in_time).toLocaleTimeString() : '-'}
                            </td>
                            <td className="border border-slate-300 px-4 py-3">
                              {record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString() : '-'}
                            </td>
                            <td className="border border-slate-300 px-4 py-3">
                              {record.total_hours ? `${(record.total_hours || 0).toFixed(2)}h` : '-'}
                            </td>
                            <td className="border border-slate-300 px-4 py-3 text-center">
                              <Badge 
                                variant={
                                  record.check_in_time && record.check_out_time ? "default" :
                                  record.check_in_time ? "secondary" : "destructive"
                                }
                                className={
                                  record.check_in_time && record.check_out_time ? "bg-green-600" :
                                  record.check_in_time ? "bg-yellow-600" : "bg-red-600"
                                }
                              >
                                {record.check_in_time && record.check_out_time ? "Complete" :
                                 record.check_in_time ? "Incomplete" : "Absent"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No Attendance Records</h3>
                    <p className="text-slate-500">Start marking your attendance to see your records here.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leave Requests Tab - Can apply but not see balance */}
        <TabsContent value="leave" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Apply for Leave</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={leaveForm.start_date}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      setLeaveForm({...leaveForm, start_date: startDate});
                      // Auto-calculate days if both dates are selected
                      if (startDate && leaveForm.end_date) {
                        const start = new Date(startDate);
                        const end = new Date(leaveForm.end_date);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        setLeaveForm(prev => ({...prev, days_requested: diffDays}));
                      }
                    }}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={leaveForm.end_date}
                    onChange={(e) => {
                      const endDate = e.target.value;
                      setLeaveForm({...leaveForm, end_date: endDate});
                      // Auto-calculate days if both dates are selected
                      if (leaveForm.start_date && endDate) {
                        const start = new Date(leaveForm.start_date);
                        const end = new Date(endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        setLeaveForm(prev => ({...prev, days_requested: diffDays}));
                      }
                    }}
                  />
                </div>
                <div>
                  <Label>Leave Type</Label>
                  <select
                    value={leaveForm.leave_type}
                    onChange={(e) => setLeaveForm({...leaveForm, leave_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="vacation">Vacation</option>
                    <option value="sick">Sick Leave</option>
                    <option value="personal">Personal</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div>
                  <Label>Days Requested</Label>
                  <Input
                    type="number"
                    value={leaveForm.days_requested}
                    onChange={(e) => setLeaveForm({...leaveForm, days_requested: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea
                  value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                  placeholder="Please provide a reason for your leave request"
                />
              </div>
              <Button onClick={handleLeaveRequest}>Submit Leave Request</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>My Leave Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {leaveRequests.length > 0 ? (
                <div className="space-y-4">
                  {leaveRequests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">
                            {request.start_date} to {request.end_date} ({request.days_requested} days)
                          </p>
                          <p className="text-sm text-slate-600">Type: {request.leave_type}</p>
                          <p className="text-sm text-slate-500">Reason: {request.reason}</p>
                        </div>
                        <Badge variant={
                          request.status === 'approved' ? 'default' :
                          request.status === 'rejected' ? 'destructive' : 'secondary'
                        }>
                          {request.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No leave requests yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial History Tab - Read Only (Same as Admin but without Add/Delete) */}
        <TabsContent value="financial">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Payment History ({payments.length})</CardTitle>
                  <CardDescription>View your payment records and history</CardDescription>
                </div>
                <div className="text-lg font-bold text-green-600">
                  Total: ${payments.reduce((sum, payment) => sum + (payment.amount || 0), 0).toFixed(2)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {payments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-left p-3 font-semibold">Type</th>
                        <th className="text-right p-3 font-semibold">Amount</th>
                        <th className="text-left p-3 font-semibold">Period</th>
                        <th className="text-left p-3 font-semibold">Notes</th>
                        <th className="text-center p-3 font-semibold">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, index) => (
                        <tr key={payment.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                          <td className="p-3">{payment.payment_date}</td>
                          <td className="p-3">
                            <Badge variant="outline">{payment.payment_type}</Badge>
                          </td>
                          <td className="p-3 text-right font-bold text-green-600">${payment.amount.toFixed(2)}</td>
                          <td className="p-3">
                            {payment.period_start && payment.period_end 
                              ? `${new Date(payment.period_start).toLocaleDateString()} to ${new Date(payment.period_end).toLocaleDateString()}`
                              : payment.period_start 
                                ? new Date(payment.period_start).toLocaleDateString()
                                : '-'
                            }
                          </td>
                          <td className="p-3">{payment.notes || '-'}</td>
                          <td className="p-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Download receipt functionality
                                const downloadUrl = `${API}/employees/${id}/payments/${payment.id}/receipt`;
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = `receipt-${payment.id}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Receipt
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                      <Clock className="h-8 w-8 text-slate-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Payment History</h3>
                  <p className="text-slate-500">Your payment records will appear here once payments are processed.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab - Exact Copy of Admin Interface (Without Delete) */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
              <CardDescription>
                Upload and manage your documents
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Upload Document Section */}
              <div className="mb-6 p-4 border rounded-lg bg-slate-50">
                <Label className="text-base font-medium mb-4 block">Upload New Document</Label>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="employee-document-title">Document Title</Label>
                    <Input
                      id="employee-document-title"
                      placeholder="Enter document title..."
                      value={newDocumentForm.title}
                      onChange={(e) => setNewDocumentForm({...newDocumentForm, title: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="employee-document-file">Select File</Label>
                    <Input
                      id="employee-document-file"
                      type="file"
                      onChange={handleDocumentUploadWithForm}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.xls"
                      disabled={uploadingDoc}
                    />
                  </div>
                  <Button
                    onClick={() => document.getElementById('employee-document-file').click()}
                    disabled={uploadingDoc || !newDocumentForm.title.trim()}
                    className="w-full"
                  >
                    {uploadingDoc ? (
                      <>
                        <Upload className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Documents List */}
              <div className="space-y-4">
                {documents && documents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-medium text-slate-700">Title</th>
                          <th className="text-left p-3 font-medium text-slate-700">File Name</th>
                          <th className="text-left p-3 font-medium text-slate-700">Upload Date</th>
                          <th className="text-left p-3 font-medium text-slate-700">Size</th>
                          <th className="text-right p-3 font-medium text-slate-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc, index) => (
                          <tr key={doc.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                            <td className="p-3 font-medium">{doc.title}</td>
                            <td className="p-3">{doc.filename || doc.file_name}</td>
                            <td className="p-3">{new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}</td>
                            <td className="p-3">{doc.file_size ? (doc.file_size / 1024).toFixed(1) + ' KB' : 'N/A'}</td>
                            <td className="p-3 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Download functionality
                                  const downloadUrl = `${API}/employees/${id}/documents/${doc.id}/download`;
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.download = doc.filename || doc.file_name || doc.title;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Upload className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No documents found for this employee.</p>
                    <p className="text-sm">Upload your first document to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Info Tab - Exact Copy of Admin Interface */}
        <TabsContent value="bankinfo">
          <Card>
            <CardHeader>
              <CardTitle>Bank Information</CardTitle>
              <CardDescription>
                Manage your bank details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label htmlFor="employee-bank-name">Bank Name</Label>
                  <Input
                    id="employee-bank-name"
                    placeholder="Enter bank name..."
                    value={bankInfoForm.bank_name}
                    onChange={(e) => setBankInfoForm(prev => ({ ...prev, bank_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="employee-additional-info">Additional Information</Label>
                  <Textarea
                    id="employee-additional-info"
                    placeholder="Enter account numbers, routing numbers, SWIFT codes, or any other bank information..."
                    rows={6}
                    value={bankInfoForm.additional_info}
                    onChange={(e) => setBankInfoForm(prev => ({ ...prev, additional_info: e.target.value }))}
                  />
                </div>
                <Button onClick={handleBankInfoSave} disabled={!bankInfoForm.bank_name.trim()}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Bank Information
                </Button>
              </div>

              {/* Display Saved Bank Info in Table Format */}
              {bankInfo && (
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-slate-900">Saved Bank Information</h3>
                    <div className="text-sm text-slate-500">
                      Last updated: {new Date(bankInfo.updated_at).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Bank Name</th>
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Bank Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-4 py-3 font-medium">{bankInfo.bank_name}</td>
                          <td className="border border-slate-300 px-4 py-3 whitespace-pre-wrap">
                            {bankInfo.additional_info || 'No additional information provided'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-800">
                        Only one bank account can be saved per employee. Use the form above to update this information.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab - Only assigned projects, no budget info */}
        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <CardTitle>My Projects</CardTitle>
              <CardDescription>Projects you are assigned to</CardDescription>
            </CardHeader>
            <CardContent>
              {projects.length > 0 ? (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div key={project.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{project.name}</h3>
                          <p className="text-sm text-slate-600">{project.description}</p>
                          <p className="text-sm text-slate-500">
                            Status: <Badge variant="secondary">{project.status}</Badge>
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Navigate to full project detail page
                            window.location.href = `/projects/${project.id}`;
                          }}
                        >
                          View Project
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No projects assigned yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Password Change Dialog */}
      {activeTab === 'password' && (
        <Dialog open={true} onOpenChange={() => setActiveTab('profile')}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                />
              </div>
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setActiveTab('profile')}>
                Cancel
              </Button>
              <Button onClick={handlePasswordChange}>
                Change Password
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// My Tasks Component for Employees (filtered version of UnifiedTasks)
const EmployeeMyTasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [itemsPerPage] = useState(50);

  // Helper function to truncate text with hover tooltip
  const TruncatedText = ({ text, maxLength = 15, className = "" }) => {
    if (!text) return <span className={className}>-</span>;
    
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text;
    
    return (
      <span 
        className={`${className} ${shouldTruncate ? 'cursor-help' : ''}`}
        title={shouldTruncate ? text : undefined}
      >
        {displayText}
      </span>
    );
  };

  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [typeFilter, statusFilter, user, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [typeFilter, statusFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('task_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      // Remove team_id parameter - let frontend do all filtering
      // This ensures we get all tasks and can properly filter for team assignments
      
      // Add pagination parameters
      const skip = (currentPage - 1) * itemsPerPage;
      params.append('skip', skip.toString());
      params.append('limit', itemsPerPage.toString());
      
      const response = await axios.get(`${API}/tasks/unified?${params.toString()}`);
      
      // Additional filtering: only show tasks assigned to this specific employee
      let filteredTasks = response.data;
      if (user?.id) {
        filteredTasks = response.data.filter(task => {
          // For project tasks: check all assigned employee IDs
          if (task.type === 'Project') {
            // Check the new all_assigned_employee_ids field for multi-employee tasks
            if (task.all_assigned_employee_ids && Array.isArray(task.all_assigned_employee_ids)) {
              return task.all_assigned_employee_ids.includes(user.id);
            }
            // Fallback to old logic for backward compatibility
            return task.assigned_employee_id === user.id || task.person === user.name;
          }
          // For support tasks: show if assigned to employee OR to employee's team
          else if (task.type === 'Support') {
            return task.assigned_employee_id === user.id || 
                   task.team_assigned === user.team_type_id ||
                   task.team_id === user.team_type_id; // Include team assignments
          }
          return false;
        });
      }
      
      setTasks(filteredTasks);
      
      // Get total count for pagination (need to fetch more to count properly)
      const totalResponse = await axios.get(`${API}/tasks/unified?${new URLSearchParams({
        ...(typeFilter && { task_type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
        // Remove team_id parameter from total count call too
        limit: '1000' // Get a large number to count total
      }).toString()}`);
      
      // Apply same filtering to total count
      let totalFiltered = totalResponse.data;
      if (user?.id) {
        totalFiltered = totalResponse.data.filter(task => {
          // For project tasks: check all assigned employee IDs
          if (task.type === 'Project') {
            // Check the new all_assigned_employee_ids field for multi-employee tasks
            if (task.all_assigned_employee_ids && Array.isArray(task.all_assigned_employee_ids)) {
              return task.all_assigned_employee_ids.includes(user.id);
            }
            // Fallback to old logic for backward compatibility
            return task.assigned_employee_id === user.id || task.person === user.name;
          }
          // For support tasks: show if assigned to employee OR to employee's team
          else if (task.type === 'Support') {
            return task.assigned_employee_id === user.id || 
                   task.team_assigned === user.team_type_id ||
                   task.team_id === user.team_type_id; // Include team assignments
          }
          return false;
        });
      }
      setTotalTasks(totalFiltered.length);
    } catch (error) {
      console.error('Error fetching employee tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'Project':
        return <FolderOpen className="h-4 w-4 text-blue-500" />;
      case 'Support':
        return <FileText className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTaskTypeBadgeClass = (type) => {
    switch (type) {
      case 'Project':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Support':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'In testing':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Published (Prod)':
        return <CheckCircle className="h-4 w-4 text-purple-500" />;
      case 'Closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTaskView = (task) => {
    if (task.type === 'Support') {
      window.location.href = `/support/tickets/${task.ticket_id}`;
    } else if (task.type === 'Project') {
      window.location.href = `/projects/${task.project_id}/tasks/${task.task_index}`;
    }
  };

  const filteredTasks = tasks.filter(task =>
    task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.team_name && task.team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.assigned_employee_name && task.assigned_employee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.project_name && task.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.client_name && task.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Tasks</h1>
          <p className="text-gray-600 mt-1">Tasks assigned to you or your team</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="Project">Project</option>
              <option value="Support">Support</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="To Do">To Do</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="In testing">In testing</option>
              <option value="Resolved">Resolved</option>
              <option value="Published (Prod)">Published (Prod)</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading your tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500">
              {searchTerm || typeFilter || statusFilter
                ? "No tasks match your current filters."
                : "No tasks assigned to you or your team at the moment."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    {/* Type Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getTaskTypeIcon(task.type)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTaskTypeBadgeClass(task.type)}`}>
                          {task.type}
                        </span>
                      </div>
                    </td>
                    
                    {/* ID Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-blue-600">
                        {task.type === 'Support' ? 
                          `#${task.ticket_id?.slice(-8) || task.id.slice(-8)}` : 
                          `T${task.task_index + 1}`
                        }
                      </div>
                    </td>
                    
                    {/* Task Name Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.task_name} 
                        maxLength={20}
                        className="font-medium text-gray-900"
                      />
                    </td>
                    
                    {/* Client Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.client_name} 
                        maxLength={12}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Team Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.team_name} 
                        maxLength={10}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Person Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {task.assigned_employee_name ? (
                          <>
                            <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {task.assigned_employee_name.charAt(0)}
                            </div>
                            <TruncatedText 
                              text={task.assigned_employee_name} 
                              maxLength={10}
                              className="text-gray-900"
                            />
                          </>
                        ) : (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </div>
                    </td>
                    
                    {/* Status Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(task.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeClass(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    </td>
                    
                    {/* Created Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(task.created_at).toLocaleDateString()}
                    </td>
                    
                    {/* Action Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleTaskView(task)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filteredTasks.length > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTasks)} of {totalTasks} tasks assigned to you or your team
          </div>
          
          {Math.ceil(totalTasks / itemsPerPage) > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalTasks / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalTasks / itemsPerPage);
                  let pageNumber;
                  
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === pageNumber
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalTasks / itemsPerPage)))}
                disabled={currentPage === Math.ceil(totalTasks / itemsPerPage)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Employee Settings Component
const EmployeeSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate password change if provided
      if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
        toast.error("New passwords don't match");
        return;
      }

      const updateData = {
        name: formData.name,
        email: formData.email
      };

      // Only include password data if changing password
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      const response = await axios.put(`${API}/employees/${user.id}`, updateData);
      
      if (response.data) {
        // Update user data in localStorage
        const updatedUser = { ...user, name: formData.name, email: formData.email };
        localStorage.setItem('bizmanager_user', JSON.stringify(updatedUser));
        
        toast.success("Profile updated successfully");
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Profile Settings</CardTitle>
          <CardDescription>Update your personal information and password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Password Change */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
              <p className="text-sm text-gray-600">Leave blank if you don't want to change your password</p>
              
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Employee Dashboard Component
const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    tasks: [],
    projects: [],
    metrics: {
      totalTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      overdueTasksCount: 0,
      completedThisWeek: 0,
      productivityPercentage: 0
    },
    todaysTasks: [],
    upcomingDeadlines: [],
    recentActivity: []
  });
  
  // Attendance states (copied from employee profile)
  const [todayAttendance, setTodayAttendance] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch employee's tasks
      const tasksResponse = await axios.get(`${API}/tasks/unified?team_id=${user?.team_type_id}`);
      const allTasks = tasksResponse.data || [];
      
      // Filter tasks assigned to this employee or their team
      const employeeTasks = allTasks.filter(task => 
        task.assigned_employee_id === user.id || 
        (task.team_id === user.team_type_id)
      );
      
      // Fetch employee's projects
      const projectsResponse = await axios.get(`${API}/employees/${user.id}/projects`);
      const employeeProjects = projectsResponse.data || [];

      // Calculate metrics
      const totalTasks = employeeTasks.length;
      const completedTasks = employeeTasks.filter(task => 
        task.status === 'Completed' || task.status === 'Resolved' || task.status === 'Closed'
      ).length;
      const inProgressTasks = employeeTasks.filter(task => 
        task.status === 'In Progress'
      ).length;
      
      // Get tasks for today (tasks with recent activity or due today)
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const todaysTasks = employeeTasks.filter(task => {
        const taskDate = new Date(task.created_at).toISOString().split('T')[0];
        return taskDate === todayStr || task.status === 'In Progress';
      }).slice(0, 5);

      // Get overdue tasks (this is a simplified calculation)
      const overdueTasksCount = employeeTasks.filter(task => 
        task.status !== 'Completed' && task.status !== 'Resolved' && task.status !== 'Closed'
      ).length;

      // Calculate completed this week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const completedThisWeek = employeeTasks.filter(task => {
        const isCompleted = task.status === 'Completed' || task.status === 'Resolved';
        const taskDate = new Date(task.created_at);
        return isCompleted && taskDate >= weekAgo;
      }).length;

      // Calculate productivity percentage
      const productivityPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

      // Get upcoming deadlines (next 7 days)
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      const upcomingDeadlines = employeeTasks.filter(task => {
        if (!task.created_at) return false;
        const taskDate = new Date(task.created_at);
        return taskDate <= nextWeek && (task.status !== 'Completed' && task.status !== 'Resolved');
      }).slice(0, 5);

      // Recent activity (last 10 tasks with recent updates)
      const recentActivity = employeeTasks
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setDashboardData({
        tasks: employeeTasks,
        projects: employeeProjects,
        metrics: {
          totalTasks,
          completedTasks,
          inProgressTasks,
          overdueTasksCount,
          completedThisWeek,
          productivityPercentage
        },
        todaysTasks,
        upcomingDeadlines,
        recentActivity
      });

      // Fetch attendance data (copied from employee profile)
      try {
        const attendanceRes = await axios.get(`${API}/employees/${user.id}/attendance`);
        const attendanceData = attendanceRes.data || [];
        
        // Check today's attendance
        const today = new Date().toISOString().split('T')[0];
        const todayRecord = attendanceData.find(record => 
          record.date === today || new Date(record.date).toISOString().split('T')[0] === today
        );
        setTodayAttendance(todayRecord);
      } catch (error) {
        console.warn("Failed to fetch attendance:", error);
        setTodayAttendance(null);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Attendance handler (copied from employee profile)
  const handleMarkAttendance = async (type) => {
    try {
      const response = await axios.post(`${API}/employees/${user.id}/attendance`, {
        type: type, // 'check_in' or 'check_out'
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      toast.success(`${type === 'check_in' ? 'Checked in' : 'Checked out'} successfully`);
      fetchDashboardData(); // Refresh to get updated attendance
    } catch (error) {
      console.error("Error marking attendance:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
      case 'Resolved':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'To Do':
      case 'Open':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const navigateToTask = (task) => {
    if (task.type === 'Support') {
      window.location.href = `/support/tickets/${task.ticket_id}`;
    } else if (task.type === 'Project') {
      window.location.href = `/projects/${task.project_id}/tasks/${task.task_index}`;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Welcome back, {user?.name}!</h1>
        <p className="text-slate-600">Here's what's happening with your work today</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
            <CheckCircle className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.metrics.totalTasks}</div>
            <p className="text-xs text-slate-600">Assigned to you</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{dashboardData.metrics.completedTasks}</div>
            <p className="text-xs text-slate-600">Tasks finished</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{dashboardData.metrics.inProgressTasks}</div>
            <p className="text-xs text-slate-600">Currently working</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Productivity</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{dashboardData.metrics.productivityPercentage}%</div>
            <p className="text-xs text-slate-600">Completion rate</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Tasks */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              Today's Priority Tasks
            </CardTitle>
            <CardDescription>Tasks you should focus on today</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData.todaysTasks.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.todaysTasks.map((task, index) => (
                  <div key={task.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                       onClick={() => navigateToTask(task)}>
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">{task.task_name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        <span className="text-xs text-slate-500">{task.project_name || task.type}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">All caught up!</h3>
                <p className="text-slate-500">No urgent tasks for today. Great work!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions & Stats */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={() => window.location.href = '/my-tasks'}
                className="w-full justify-start"
                variant="outline"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                View All My Tasks
              </Button>
              <Button 
                onClick={() => window.location.href = '/my-projects'}
                className="w-full justify-start"
                variant="outline"
              >
                <Building2 className="h-4 w-4 mr-2" />
                My Projects ({dashboardData.projects.length})
              </Button>
              <Button 
                onClick={() => window.location.href = '/my-profile'}
                className="w-full justify-start"
                variant="outline"
              >
                <User className="h-4 w-4 mr-2" />
                My Profile
              </Button>
            </CardContent>
          </Card>

          {/* Attendance Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Mark Attendance
              </CardTitle>
              <CardDescription>Quick check-in and check-out</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Check In/Out Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    onClick={() => handleMarkAttendance('check_in')}
                    className="bg-green-600 hover:bg-green-700 text-white w-full"
                    size="sm"
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Check In
                  </Button>
                  <Button 
                    onClick={() => handleMarkAttendance('check_out')}
                    className="bg-red-600 hover:bg-red-700 text-white w-full"
                    size="sm"
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Check Out
                  </Button>
                </div>
                
                {/* Today's Status */}
                {todayAttendance && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-2 text-sm">Today's Status</h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Check-in:</span>
                        <span className="font-medium">
                          {todayAttendance.check_in_time ? new Date(todayAttendance.check_in_time).toLocaleTimeString() : 'Not marked'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Check-out:</span>
                        <span className="font-medium">
                          {todayAttendance.check_out_time ? new Date(todayAttendance.check_out_time).toLocaleTimeString() : 'Not marked'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Link to full attendance */}
                <Button 
                  onClick={() => window.location.href = `/employee-profile/${user.id}?tab=attendance`}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  View Full Attendance
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Weekly Progress */}
          <Card>
            <CardHeader>
              <CardTitle>This Week's Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Tasks Completed</span>
                  <span className="font-bold text-green-600">{dashboardData.metrics.completedThisWeek}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Overall Progress</span>
                  <span className="font-bold">{dashboardData.metrics.productivityPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${dashboardData.metrics.productivityPercentage}%` }}
                  ></div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {dashboardData.metrics.productivityPercentage >= 80 ? '🎉 Excellent work!' :
                   dashboardData.metrics.productivityPercentage >= 60 ? '👍 Good progress!' :
                   '💪 Keep it up!'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Activity */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Recent Activity
          </CardTitle>
          <CardDescription>Your latest task updates and completions</CardDescription>
        </CardHeader>
        <CardContent>
          {dashboardData.recentActivity.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.recentActivity.map((task, index) => (
                <div key={task.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                     onClick={() => navigateToTask(task)}>
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{task.task_name}</p>
                    <p className="text-xs text-slate-500">
                      {task.project_name || task.type} • {new Date(task.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Activity className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-slate-500">No recent activity to show</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// CLIENT COMPONENTS

// Client Tasks Component - Similar to UnifiedTasks but filtered for client
const ClientTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const itemsPerPage = 50;

  const [user, setUser] = useState(null);

  // Helper function to truncate text with hover tooltip
  const TruncatedText = ({ text, maxLength = 15, className = "" }) => {
    if (!text) return <span className={className}>-</span>;
    
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? text.substring(0, maxLength) + '...' : text;
    
    if (shouldTruncate) {
      return (
        <span className={className} title={text}>
          {displayText}
        </span>
      );
    }
    
    return <span className={className}>{displayText}</span>;
  };

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchClientTasks();
    }
  }, [user, currentPage, statusFilter, typeFilter]);

  const fetchClientTasks = async () => {
    try {
      setLoading(true);
      
      // Get unified tasks and filter for this client
      const response = await axios.get(`${API}/tasks/unified`);
      let allTasks = response.data || [];
      
      // Filter tasks for this client
      const clientTasks = allTasks.filter(task => {
        // Match by client name or client ID
        return task.client_name === user.company_name ||
               task.client_id === user.id ||
               (user.contacts && user.contacts.some(contact => 
                 task.client_name === contact.name || 
                 task.client_email === contact.email
               ));
      });
      
      // Apply status filter
      let filteredTasks = clientTasks;
      if (statusFilter !== 'all') {
        filteredTasks = filteredTasks.filter(task => task.status === statusFilter);
      }
      
      // Apply type filter
      if (typeFilter !== 'all') {
        filteredTasks = filteredTasks.filter(task => task.type === typeFilter);
      }
      
      setTotalTasks(filteredTasks.length);
      
      // Apply pagination
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginatedTasks = filteredTasks.slice(startIndex, startIndex + itemsPerPage);
      
      setTasks(paginatedTasks);
      
    } catch (error) {
      console.error('Error fetching client tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'To Do':
        return <div className="w-2 h-2 bg-red-500 rounded-full"></div>;
      case 'Open':
        return <div className="w-2 h-2 bg-red-500 rounded-full"></div>;
      case 'In Progress':
        return <div className="w-2 h-2 bg-blue-500 rounded-full"></div>;
      case 'In testing':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>;
      case 'Resolved':
        return <div className="w-2 h-2 bg-green-500 rounded-full"></div>;
      case 'Published (Prod)':
        return <div className="w-2 h-2 bg-purple-500 rounded-full"></div>;
      case 'Closed':
        return <div className="w-2 h-2 bg-gray-500 rounded-full"></div>;
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full"></div>;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'To Do':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Open':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTaskClick = (task) => {
    if (task.type === 'Support') {
      navigate(`/client-support/${task.ticket_id}`);
    } else if (task.type === 'Project') {
      navigate(`/client-projects/${task.project_id}/tasks/${task.task_index}`);
    }
  };

  const filteredTasks = tasks.filter(task =>
    task.task_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.team_name && task.team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.assigned_employee_name && task.assigned_employee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.project_name && task.project_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(totalTasks / itemsPerPage);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Tasks</h1>
          <p className="text-gray-600 mt-1">All project tasks and support tickets related to your projects</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search tasks..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="To Do">To Do</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="In testing">In testing</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Published (Prod)">Published (Prod)</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Project">Project Tasks</SelectItem>
                <SelectItem value="Support">Support Tickets</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button 
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setTypeFilter('all');
                setCurrentPage(1);
              }}
              variant="outline"
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Tasks</h2>
            <span className="text-sm text-gray-500">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalTasks)} to {Math.min(currentPage * itemsPerPage, totalTasks)} of {totalTasks} tasks
            </span>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading tasks...</span>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500">No tasks match your current search and filter criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project/Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task, index) => (
                  <tr key={`${task.type}-${task.id}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        task.type === 'Project' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {task.type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {task.type === 'Project' ? `T${task.task_index + 1}` : `#${task.ticket_id?.slice(-6) || 'N/A'}`}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <TruncatedText 
                        text={task.task_name} 
                        maxLength={30}
                        className="text-gray-900 font-medium"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.project_name || task.team_name} 
                        maxLength={15}
                        className="text-gray-900"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {task.assigned_employee_name ? (
                          <>
                            <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {task.assigned_employee_name.charAt(0)}
                            </div>
                            <TruncatedText 
                              text={task.assigned_employee_name} 
                              maxLength={12}
                              className="text-gray-900"
                            />
                          </>
                        ) : (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(task.status)}`}>
                        {getStatusIcon(task.status)}
                        <span>{task.status}</span>
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTaskClick(task)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Client Dashboard Component
const ClientDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    projects: [],
    tasks: [],
    invoices: [],
    tickets: [],
    metrics: {
      totalProjects: 0,
      activeTasks: 0,
      pendingInvoices: 0,
      openTickets: 0
    }
  });

  useEffect(() => {
    if (user?.id) {
      fetchClientDashboardData();
    }
  }, [user]);

  const fetchClientDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch client's projects - use existing projects endpoint with improved filtering
      let clientProjects = [];
      try {
        const projectsResponse = await axios.get(`${API}/projects`);
        const allProjects = projectsResponse.data || [];
        
        // Get client contact information for proper filtering
        const clientEmail = user.contacts?.[0]?.email || user.email;
        const clientName = user.contacts?.[0]?.name || user.name;
        const companyName = user.company_name;
        
        // Filter projects based on client contact details
        clientProjects = allProjects.filter(project => 
          project.client_id === user.id || 
          project.client_name === companyName ||
          project.client_name === clientName ||
          project.client_email === clientEmail ||
          // Check if project client matches any contact information
          (user.contacts && user.contacts.some(contact => 
            project.client_name === contact.name || 
            project.client_email === contact.email
          ))
        );
      } catch (error) {
        console.warn('Failed to fetch projects:', error);
      }
      
      // Fetch client's tasks from unified endpoint
      let clientTasks = [];
      try {
        const tasksResponse = await axios.get(`${API}/tasks/unified`);
        const allTasks = tasksResponse.data || [];
        clientTasks = allTasks.filter(task => 
          task.client_name === user.company_name ||
          task.client_id === user.id
        );
      } catch (error) {
        console.warn('Failed to fetch tasks:', error);
      }
      
      // Fetch client's invoices - use existing invoices endpoint
      let clientInvoices = [];
      try {
        const invoicesResponse = await axios.get(`${API}/invoices`);
        const allInvoices = invoicesResponse.data || [];
        clientInvoices = allInvoices.filter(invoice => 
          invoice.client_id === user.id ||
          invoice.client_name === user.company_name
        );
      } catch (error) {
        console.warn('Failed to fetch invoices:', error);
      }
      
      // Fetch client's support tickets - use existing support tickets endpoint  
      let clientTickets = [];
      try {
        const ticketsResponse = await axios.get(`${API}/support-tickets`);
        const allTickets = ticketsResponse.data || [];
        clientTickets = allTickets.filter(ticket => 
          ticket.client_id === user.id ||
          ticket.client_name === user.company_name
        );
      } catch (error) {
        console.warn('Failed to fetch support tickets:', error);
      }

      // Calculate metrics
      const totalProjects = clientProjects.length;
      const activeTasks = clientTasks.filter(task => 
        task.status === 'In Progress' || task.status === 'To Do' || task.status === 'Open'
      ).length;
      const pendingInvoices = clientInvoices.filter(invoice => 
        invoice.status === 'Pending' || invoice.status === 'Sent'
      ).length;
      const openTickets = clientTickets.filter(ticket => 
        ticket.current_status === 'Open' || ticket.current_status === 'In Progress'
      ).length;

      setDashboardData({
        projects: clientProjects.slice(0, 5), // Latest 5 projects
        tasks: clientTasks.slice(0, 5), // Latest 5 tasks
        invoices: clientInvoices.slice(0, 3), // Latest 3 invoices
        tickets: clientTickets.slice(0, 3), // Latest 3 tickets
        metrics: {
          totalProjects,
          activeTasks,
          pendingInvoices,
          openTickets
        }
      });

    } catch (error) {
      console.error('Error fetching client dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Welcome, {user?.company_name}!</h1>
        <p className="text-slate-600">Here's an overview of your projects and activities</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <Building2 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.metrics.totalProjects}</div>
            <p className="text-xs text-slate-600">Total projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tasks</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{dashboardData.metrics.activeTasks}</div>
            <p className="text-xs text-slate-600">In progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Invoices</CardTitle>
            <Receipt className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{dashboardData.metrics.pendingInvoices}</div>
            <p className="text-xs text-slate-600">Awaiting payment</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Support Tickets</CardTitle>
            <HelpCircle className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{dashboardData.metrics.openTickets}</div>
            <p className="text-xs text-slate-600">Open tickets</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Projects</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = '/client-projects'}
              >
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.projects.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.projects.map((project) => (
                  <div key={project.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <div>
                      <h4 className="font-medium text-slate-900">{project.name}</h4>
                      <p className="text-sm text-slate-600">{project.status}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.location.href = `/client-projects/${project.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No projects yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Tasks</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = '/client-tasks'}
              >
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.tasks.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.tasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <div>
                      <h4 className="font-medium text-slate-900">{task.task_name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">{task.status}</Badge>
                        <span className="text-xs text-slate-500">{task.project_name}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (task.type === 'Support') {
                          window.location.href = `/client-support/${task.ticket_id}`;
                        } else {
                          window.location.href = `/client-projects/${task.project_id}/tasks/${task.task_index}`;
                        }
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No active tasks</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              onClick={() => window.location.href = '/client-projects'}
              className="h-20 flex-col"
              variant="outline"
            >
              <Building2 className="h-6 w-6 mb-2" />
              View Projects
            </Button>
            <Button 
              onClick={() => window.location.href = '/client-tasks'}
              className="h-20 flex-col"
              variant="outline"
            >
              <CheckCircle className="h-6 w-6 mb-2" />
              View Tasks
            </Button>
            <Button 
              onClick={() => window.location.href = '/client-invoices'}
              className="h-20 flex-col"
              variant="outline"
            >
              <Receipt className="h-6 w-6 mb-2" />
              View Invoices
            </Button>
            <Button 
              onClick={() => window.location.href = '/client-support'}
              className="h-20 flex-col"
              variant="outline"
            >
              <HelpCircle className="h-6 w-6 mb-2" />
              Create Ticket
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Client Projects Component
const ClientProjects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchClientProjects();
    }
  }, [user]);

  const fetchClientProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      const allProjects = response.data || [];
      // Filter projects for this client - improved matching based on contact details
      const clientProjects = allProjects.filter(project => {
        // Get client contact email for matching
        const clientEmail = user.contacts?.[0]?.email || user.email;
        const clientName = user.contacts?.[0]?.name || user.name;
        const companyName = user.company_name;
        
        // Match by client_id, client_name, or contact email
        return (
          project.client_id === user.id ||
          project.client_name === companyName ||
          project.client_name === clientName ||
          project.client_email === clientEmail ||
          // Check if project client matches any of the user's contact information
          (user.contacts && user.contacts.some(contact => 
            project.client_name === contact.name || 
            project.client_email === contact.email ||
            project.client_name === companyName
          ))
        );
      });
      
      console.log('Client user data:', {
        id: user.id,
        company_name: user.company_name,
        email: user.email,
        contacts: user.contacts
      });
      console.log('All projects found:', allProjects.length);
      console.log('Client-filtered projects:', clientProjects.length, clientProjects.map(p => ({
        name: p.name,
        client_id: p.client_id,
        client_name: p.client_name,
        client_email: p.client_email
      })));
      setProjects(clientProjects);
    } catch (error) {
      console.error('Error fetching client projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">My Projects</h1>
        <p className="text-slate-600">View all your current projects</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.length > 0 ? projects.map((project) => (
          <Card key={project.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{project.name}</CardTitle>
              <CardDescription>{project.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Status:</span>
                  <Badge variant="secondary">{project.status}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Budget:</span>
                  <span>${project.budget}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Type:</span>
                  <span>{project.billing_type}</span>
                </div>
              </div>
              <Button 
                onClick={() => window.location.href = `/client-projects/${project.id}`}
                className="w-full mt-4"
                variant="outline"
              >
                View Project Details
              </Button>
            </CardContent>
          </Card>
        )) : (
          <div className="col-span-full text-center py-12">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Projects Yet</h3>
            <p className="text-slate-500">Your projects will appear here once they are created.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Client Project Detail Component
const ClientProjectDetail = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      fetchProjectDetail();
    }
  }, [id]);

  const fetchProjectDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${id}`);
      const projectData = response.data;
      
      // Verify this project belongs to the client
      const clientEmail = user.contacts?.[0]?.email || user.email;
      const clientName = user.contacts?.[0]?.name || user.name;
      const companyName = user.company_name;
      
      const hasAccess = (
        projectData.client_id === user.id ||
        projectData.client_name === companyName ||
        projectData.client_name === clientName ||
        projectData.client_email === clientEmail ||
        (user.contacts && user.contacts.some(contact => 
          projectData.client_name === contact.name || 
          projectData.client_email === contact.email
        ))
      );
      
      if (!hasAccess) {
        toast.error('You do not have access to this project');
        window.location.href = '/client-projects';
        return;
      }
      
      setProject(projectData);
    } catch (error) {
      console.error('Error fetching project details:', error);
      toast.error('Failed to load project details');
      window.location.href = '/client-projects';
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">Loading project details...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Project Not Found</h3>
          <p className="text-gray-500">The requested project could not be found.</p>
          <Button onClick={() => window.location.href = '/client-projects'} className="mt-4">
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  // Filter task assignments to only show tasks related to this client
  const clientTasks = (project.task_assignments || []).filter(assignment => {
    // Show all tasks since this is the client's project
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/client-projects'}
            className="flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
        </div>
        <div className="mt-4">
          <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          <p className="text-gray-600 mt-1">{project.description}</p>
        </div>
      </div>

      {/* Project Status Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
                {project.status}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Budget</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">${project.budget}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Billing Type</dt>
            <dd className="mt-1 text-gray-900">{project.billing_type}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Tasks</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">{clientTasks.length}</dd>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({clientTasks.length})</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Project Name</dt>
                  <dd className="mt-1 text-gray-900">{project.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-gray-900">{project.description || 'No description provided'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created Date</dt>
                  <dd className="mt-1 text-gray-900">
                    {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                  <dd className="mt-1 text-gray-900">
                    {project.start_date ? new Date(project.start_date + 'T00:00:00').toLocaleDateString() : 'Not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">End Date</dt>
                  <dd className="mt-1 text-gray-900">
                    {project.end_date ? new Date(project.end_date + 'T00:00:00').toLocaleDateString() : 'Not set'}
                  </dd>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Project Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm">
                      <span>Completed Tasks</span>
                      <span>{clientTasks.filter(t => t.status === 'Completed').length} / {clientTasks.length}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ 
                          width: `${clientTasks.length > 0 ? (clientTasks.filter(t => t.status === 'Completed').length / clientTasks.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {clientTasks.filter(t => t.status === 'Completed').length}
                      </div>
                      <div className="text-xs text-gray-500">Completed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {clientTasks.filter(t => t.status === 'In Progress').length}
                      </div>
                      <div className="text-xs text-gray-500">In Progress</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-600">
                        {clientTasks.filter(t => t.status === 'To Do').length}
                      </div>
                      <div className="text-xs text-gray-500">To Do</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle>Project Tasks</CardTitle>
              <CardDescription>All tasks associated with this project</CardDescription>
            </CardHeader>
            <CardContent>
              {clientTasks.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Task Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned To
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {clientTasks.map((task, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{task.task_name}</div>
                            {task.description && (
                              <div className="text-sm text-gray-500">{task.description}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-gray-900">{task.employee_name || 'Unassigned'}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Badge variant={
                              task.status === 'Completed' ? 'default' :
                              task.status === 'In Progress' ? 'secondary' :
                              'outline'
                            }>
                              {task.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-gray-500">
                            {task.start_date ? new Date(task.start_date).toLocaleDateString() : 'Not set'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.location.href = `/client-projects/${project.id}/tasks/${task.task_index}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Yet</h3>
                  <p className="text-gray-500">Tasks will appear here once they are assigned to this project.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Project Documents</CardTitle>
              <CardDescription>Files and documents related to this project</CardDescription>
            </CardHeader>
            <CardContent>
              {project.documents && project.documents.length > 0 ? (
                <div className="space-y-3">
                  {project.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-900">{doc.name || `Document ${index + 1}`}</span>
                      </div>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents</h3>
                  <p className="text-gray-500">Project documents will appear here when they are uploaded.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Client Settings Component
const ClientSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contact_person: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      // Get the primary contact from user data
      const primaryContact = user.contacts?.find(c => c.is_primary) || user.contacts?.[0];
      setFormData({
        contact_person: primaryContact?.name || user.contact_person || '',
        email: primaryContact?.email || user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate password change if provided
      if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
        toast.error("New passwords don't match");
        return;
      }

      const updateData = {
        contact_person: formData.contact_person,
        email: formData.email
      };

      // Only include password data if changing password
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      // Handle password change separately if provided
      if (formData.newPassword) {
        await axios.post(`${API}/auth/client-change-password`, {
          client_id: user.id,
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        });
      }
      
      // Update profile information
      const response = await axios.put(`${API}/clients/${user.id}`, {
        contact_person: formData.contact_person,
        email: formData.email
      });
      
      if (response.data) {
        // Update user data in localStorage
        const updatedUser = { ...user, contact_person: formData.contact_person, email: formData.email };
        localStorage.setItem('bizmanager_user', JSON.stringify(updatedUser));
        
        toast.success("Profile updated successfully");
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Profile Settings</CardTitle>
          <CardDescription>Update your personal information and password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Person
                </label>
                <input
                  type="text"
                  id="contact_person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Password Change */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
              <p className="text-sm text-gray-600">Leave blank if you don't want to change your password</p>
              
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Manager Dashboard Component  
const ManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expiringTasks, setExpiringTasks] = useState([]);
  const [teamProductivity, setTeamProductivity] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingProductivity, setLoadingProductivity] = useState(true);

  useEffect(() => {
    if (user?.id && user?.isManager) {
      fetchManagerDashboardData();
      fetchExpiringTasks();
      fetchTeamProductivity();
    }
  }, [user]);

  const fetchManagerDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/managers/${user.id}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching manager dashboard data:', error);
      // Fallback to empty data
      setDashboardData({
        team_members: 0,
        active_projects: 0,
        pending_tasks: 0,
        completed_tasks: 0,
        open_tickets: 0,
        team_member_details: [],
        recent_projects: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiringTasks = async () => {
    try {
      setLoadingTasks(true);
      // Get all tasks first
      const response = await axios.get(`${API}/tasks/unified`);
      let allTasks = response.data;
      
      // Get manager's projects to filter relevant tasks
      const projectsResponse = await axios.get(`${API}/projects`);
      const projects = projectsResponse.data;
      
      // Get project IDs where manager is involved (project manager OR assigned employee)
      const managerProjects = projects.filter(project => {
        return project.manager_id === user.id || 
               (project.employee_ids && project.employee_ids.includes(user.id)) ||
               (project.task_assignments && project.task_assignments.some(assignment => 
                 assignment.employee_id === user.id
               ));
      });
      
      const managerProjectIds = managerProjects.map(p => p.id);
      
      // Filter tasks for manager's projects and get expiring ones
      const managerTasks = allTasks.filter(task => {
        if (task.type === 'Project') {
          return managerProjectIds.includes(task.project_id);
        }
        return false; // Only project tasks for now
      });
      
      // Filter for expiring tasks (within next 7 days)
      const now = new Date();
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(now.getDate() + 7);
      
      const expiring = managerTasks.filter(task => {
        if (task.end_date) {
          const endDate = new Date(task.end_date + 'T00:00:00');
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Set to start of today
          
          // Only include tasks that are due in the future (not expired) and within 7 days
          return endDate >= today && endDate <= sevenDaysFromNow && 
                 ['To Do', 'In Progress', 'In testing'].includes(task.status);
        }
        return false;
      }).sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
      
      setExpiringTasks(expiring.slice(0, 5)); // Show top 5 expiring tasks
    } catch (error) {
      console.error('Error fetching expiring tasks:', error);
      setExpiringTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchTeamProductivity = async () => {
    try {
      setLoadingProductivity(true);
      
      // Get team members
      if (!dashboardData?.team_member_details) {
        // Wait for dashboard data if not available yet
        setTimeout(fetchTeamProductivity, 1000);
        return;
      }
      
      // Get all tasks for productivity calculation
      const response = await axios.get(`${API}/tasks/unified`);
      const allTasks = response.data;
      
      // Calculate productivity for each team member
      const productivity = dashboardData.team_member_details.map(member => {
        const memberTasks = allTasks.filter(task => 
          task.assigned_employee_id === member.id || task.person === member.name
        );
        
        const totalTasks = memberTasks.length;
        const completedTasks = memberTasks.filter(task => 
          ['Resolved', 'Published (Prod)', 'Closed'].includes(task.status)
        ).length;
        const inProgressTasks = memberTasks.filter(task => 
          ['In Progress', 'In testing'].includes(task.status)
        ).length;
        const overdueTasks = memberTasks.filter(task => {
          if (task.end_date) {
            const endDate = new Date(task.end_date + 'T00:00:00');
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Set to start of today
            return endDate < today && !['Resolved', 'Published (Prod)', 'Closed'].includes(task.status);
          }
          return false;
        }).length;
        
        const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
        
        return {
          ...member,
          totalTasks,
          completedTasks,
          inProgressTasks,
          overdueTasks,
          completionRate
        };
      });
      
      setTeamProductivity(productivity);
    } catch (error) {
      console.error('Error fetching team productivity:', error);
      setTeamProductivity([]);
    } finally {
      setLoadingProductivity(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manager Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.name}!</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/manager-team')}
            className="text-emerald-600 hover:text-emerald-700"
          >
            <Users className="h-4 w-4 mr-2" />
            View Team
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData?.team_members || 0}</div>
            <p className="text-xs text-muted-foreground">Active team members</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData?.active_projects || 0}</div>
            <p className="text-xs text-muted-foreground">Currently active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Tasks</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{dashboardData?.pending_tasks || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting completion</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Tasks</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{dashboardData?.completed_tasks || 0}</div>
            <p className="text-xs text-muted-foreground">This period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{dashboardData?.open_tickets || 0}</div>
            <p className="text-xs text-muted-foreground">Support tickets</p>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Tasks and Team Productivity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Expiring Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Expiring Tasks
            </CardTitle>
            <p className="text-sm text-gray-600">Tasks due within the next 7 days</p>
          </CardHeader>
          <CardContent>
            {loadingTasks ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
              </div>
            ) : expiringTasks.length > 0 ? (
              <div className="space-y-3">
                {expiringTasks.map((task) => {
                  const daysUntilDue = Math.ceil((new Date(task.end_date + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={task.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          daysUntilDue <= 1 ? 'bg-red-500' : 
                          daysUntilDue <= 3 ? 'bg-orange-500' : 'bg-yellow-500'
                        }`}></div>
                        <div>
                          <p className="font-medium text-sm">{task.task_name}</p>
                          <p className="text-xs text-gray-600">{task.project_name || 'Project'} • {task.person}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs font-medium ${
                          daysUntilDue <= 1 ? 'text-red-600' : 
                          daysUntilDue <= 3 ? 'text-orange-600' : 'text-yellow-600'
                        }`}>
                          {daysUntilDue === 0 ? 'Due Today' : 
                           daysUntilDue === 1 ? 'Due Tomorrow' : 
                           `${daysUntilDue} days left`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(task.end_date + 'T00:00:00').toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/manager-tasks')}
                  className="w-full mt-3 text-orange-600 hover:text-orange-700"
                >
                  View All Tasks
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No tasks expiring soon!</p>
                <p className="text-sm">Great job keeping up with deadlines.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Productivity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Team Productivity
            </CardTitle>
            <p className="text-sm text-gray-600">Performance overview of your team members</p>
          </CardHeader>
          <CardContent>
            {loadingProductivity ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : teamProductivity.length > 0 ? (
              <div className="space-y-4">
                {teamProductivity.slice(0, 5).map((member) => (
                  <div key={member.id} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{member.name}</p>
                          <p className="text-xs text-gray-600">{member.designation}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          member.completionRate >= 80 ? 'bg-green-100 text-green-800' :
                          member.completionRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {member.completionRate}% Complete
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div className="text-center">
                        <p className="font-medium text-gray-900">{member.totalTasks}</p>
                        <p className="text-gray-500">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-green-600">{member.completedTasks}</p>
                        <p className="text-gray-500">Done</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-blue-600">{member.inProgressTasks}</p>
                        <p className="text-gray-500">Active</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-red-600">{member.overdueTasks}</p>
                        <p className="text-gray-500">Overdue</p>
                      </div>
                    </div>
                  </div>
                ))}
                {teamProductivity.length > 5 && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/manager-team')}
                    className="w-full mt-3 text-blue-600 hover:text-blue-700"
                  >
                    View Full Team Details
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No productivity data available</p>
                <p className="text-sm">Data will appear once team members are assigned tasks.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Members Overview */}
        <Card>
          <CardHeader>
            <CardTitle>My Team ({dashboardData?.team_members || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.team_member_details && dashboardData.team_member_details.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.team_member_details.slice(0, 5).map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{member.name}</p>
                        <p className="text-xs text-gray-500">{member.designation}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      member.status === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {member.status}
                    </span>
                  </div>
                ))}
                {dashboardData.team_member_details.length > 5 && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/manager-team')}
                    className="w-full mt-3"
                  >
                    View All Team Members ({dashboardData.team_member_details.length})
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No team members assigned yet.</p>
                <p className="text-sm">Ask your admin to assign employees to your team.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Projects */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Team Projects</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_projects && dashboardData.recent_projects.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_projects.map((project) => (
                  <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{project.name}</p>
                        <p className="text-xs text-gray-500">{project.client_name}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/manager-projects')}
                  className="w-full mt-3"
                >
                  View All Projects
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No projects assigned to your team yet.</p>
                <p className="text-sm">Projects will appear here once your team is assigned work.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Manager Team Component
const ManagerTeam = () => {
  const { user } = useAuth();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id && user?.isManager) {
      fetchTeamData();
    }
  }, [user]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/managers/${user.id}/team`);
      setTeamData(response.data);
    } catch (error) {
      console.error('Error fetching team data:', error);
      setTeamData({
        team_members: [],
        team_count: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading team data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Team</h1>
          <p className="text-gray-600 mt-1">Manage your team members and their assignments</p>
        </div>
        <div className="text-sm text-gray-500">
          Total Team Members: <span className="font-semibold text-emerald-600">{teamData?.team_count || 0}</span>
        </div>
      </div>

      {teamData?.team_members && teamData.team_members.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Team Members ({teamData.team_count})</CardTitle>
            <CardDescription>
              Overview of all team members reporting to you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Name</th>
                    <th className="text-left p-3 font-semibold">Designation</th>
                    <th className="text-left p-3 font-semibold">Team</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    <th className="text-right p-3 font-semibold">Experience</th>
                    <th className="text-left p-3 font-semibold">Email</th>
                    <th className="text-center p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamData.team_members.map((member, index) => (
                    <tr key={member.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                      <td className="p-3 font-medium">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div>
                            <div className="font-semibold">{member.name}</div>
                            <div className="text-sm text-slate-500">ID: {member.id.substring(0, 8)}...</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">{member.designation || 'N/A'}</td>
                      <td className="p-3">
                        {member.team_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {member.team_name}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">No Team</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (member.status || 'Active') === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {member.status || 'Active'}
                        </span>
                      </td>
                      <td className="p-3 text-right font-semibold text-purple-600">
                        {member.total_experience ? `${member.total_experience.toFixed(1)} yrs` : 'N/A'}
                      </td>
                      <td className="p-3 text-sm">{member.email || 'N/A'}</td>
                      <td className="p-3 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = `/employees/${member.id}`}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          View Profile
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Team Members Yet</h3>
              <p className="text-gray-600 mb-4">
                You don't have any team members assigned to you yet.
              </p>
              <p className="text-sm text-gray-500">
                Contact your administrator to assign employees to your management.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Manager Projects Component
const ManagerProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user context for role-based restrictions
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]); // For dropdown filtering
  const [allClients, setAllClients] = useState([]); // For name lookup
  const [loading, setLoading] = useState(false);
  
  // Check if current user is a manager
  const isManager = user?.isManager || false;
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const projectsPerPage = 50;

  // Filter states
  const [filters, setFilters] = useState({
    status: "all",
    date_from: null,
    date_to: null
  });

  useEffect(() => {
    const loadData = async () => {
      await fetchProjects();
      await fetchEmployees();
      await fetchClients();
    };
    loadData();
  }, []);

  // Filter projects whenever projects or filters change
  useEffect(() => {
    applyFilters();
  }, [projects, filters]);

  // Refetch clients when projects change (for manager filtering)
  useEffect(() => {
    if (projects.length > 0) {
      fetchClients();
    }
  }, [projects]);

  const applyFilters = () => {
    let filtered = [...projects];

    // Filter by status
    if (filters.status && filters.status !== "all") {
      filtered = filtered.filter(project => project.status === filters.status);
    }

    // Filter by date range
    if (filters.date_from || filters.date_to) {
      filtered = filtered.filter(project => {
        // Check if project has dates and falls within the filter range
        const projectStartDate = project.start_date ? new Date(project.start_date) : null;
        const projectEndDate = project.end_date ? new Date(project.end_date) : null;
        
        let matchesDateRange = true;

        if (filters.date_from) {
          const filterFromDate = new Date(filters.date_from);
          // Project should start on or after the filter start date
          if (projectStartDate && projectStartDate < filterFromDate) {
            matchesDateRange = false;
          }
          // If no project start date but has end date, check if end date is after filter start
          if (!projectStartDate && projectEndDate && projectEndDate < filterFromDate) {
            matchesDateRange = false;
          }
        }

        if (filters.date_to) {
          const filterToDate = new Date(filters.date_to);
          // Project should end on or before the filter end date
          if (projectEndDate && projectEndDate > filterToDate) {
            matchesDateRange = false;
          }
          // If no project end date but has start date, check if start date is before filter end
          if (!projectEndDate && projectStartDate && projectStartDate > filterToDate) {
            matchesDateRange = false;
          }
        }

        return matchesDateRange;
      });
    }

    setFilteredProjects(filtered);
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      date_from: null,
      date_to: null
    });
  };

  const fetchProjects = async (page = 1) => {
    try {
      setLoading(true);
      
      // For managers, filter projects where manager is project manager OR assigned employee
      if (isManager && user?.id) {
        // Fetch all projects and filter by manager_id AND employee_ids
        const projectsResponse = await axios.get(`${API}/projects/paginated`, {
          params: { page, limit: projectsPerPage }
        });
        
        if (projectsResponse.data) {
          let projectsData = projectsResponse.data.projects;
          
          // Filter to show projects where the manager is the project manager OR in assigned employees
          projectsData = projectsData.filter(project => {
            return project.manager_id === user.id || 
                   (project.employee_ids && project.employee_ids.includes(user.id));
          });
          
          setProjects(projectsData);
          setCurrentPage(projectsResponse.data.pagination.current_page);
          setTotalPages(projectsResponse.data.pagination.total_pages);
          setTotalProjects(projectsData.length); // Use filtered count for managers
        }
      } else {
        // For non-managers (this shouldn't happen in ManagerProjects, but fallback)
        const projectsResponse = await axios.get(`${API}/projects/paginated`, {
          params: { page, limit: projectsPerPage }
        });
        
        if (projectsResponse.data) {
          const projectsData = projectsResponse.data.projects;
          
          setProjects(projectsData);
          setCurrentPage(projectsResponse.data.pagination.current_page);
          setTotalPages(projectsResponse.data.pagination.total_pages);
          setTotalProjects(projectsResponse.data.pagination.total_projects);
        }
      }
      
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      const allClientsData = response.data;
      
      // Store all clients for name lookup
      setAllClients(allClientsData);
      
      // For managers, only show clients in dropdown whose projects the manager has access to
      if (isManager && user?.id) {
        // Get manager's team members
        const teamResponse = await axios.get(`${API}/managers/${user.id}/team`);
        const teamMemberIds = teamResponse.data.team_members ? teamResponse.data.team_members.map(member => member.id) : [];
        const managerAndTeamIds = [user.id, ...teamMemberIds];
        
        // Filter clients based on accessible projects
        const accessibleClientIds = new Set();
        projects.forEach(project => {
          const employeeIds = project.employee_ids || [];
          const taskAssignments = project.task_assignments || [];
          const assignedEmployeeIds = taskAssignments.map(task => task.employee_id).filter(id => id);
          
          // If manager or team has access to this project, include the client
          if (employeeIds.some(id => managerAndTeamIds.includes(id)) || 
              assignedEmployeeIds.some(id => managerAndTeamIds.includes(id))) {
            accessibleClientIds.add(project.client_id);
          }
        });
        
        // Filter clients for dropdown to only those the manager has access to
        const accessibleClients = allClientsData.filter(client => accessibleClientIds.has(client.id));
        setClients(accessibleClients);
      } else {
        // For non-managers, show all clients in dropdown
        setClients(allClientsData);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleViewProject = (project) => {
    navigate(`/projects/${project.id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-100 text-emerald-800";
      case "completed": return "bg-blue-100 text-blue-800";
      case "cancelled": return "bg-red-100 text-red-800";
      case "planning": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getClientName = (clientId) => {
    const client = allClients.find(c => c.id === clientId);
    return client ? client.company_name : "Unknown Client";
  };

  // Handle page changes
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProjects(page);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Team Projects</h1>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Filter Projects</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="text-gray-600 hover:text-gray-700"
            >
              Clear Filters
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <Label htmlFor="filter-status">Filter by Status</Label>
              <Select 
                value={filters.status} 
                onValueChange={(value) => setFilters({...filters, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date From Filter */}
            <div>
              <Label htmlFor="filter-date-from">Start Date From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date_from ? format(filters.date_from, "MMM dd, yyyy") : <span>Select date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.date_from}
                    onSelect={(date) => setFilters({...filters, date_from: date})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To Filter */}
            <div>
              <Label htmlFor="filter-date-to">End Date To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date_to ? format(filters.date_to, "MMM dd, yyyy") : <span>Select date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.date_to}
                    onSelect={(date) => setFilters({...filters, date_to: date})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Filter Status Display */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {filteredProjects.length} of {projects.length} projects
            </span>
            {(filters.status !== "all" || filters.client_id !== "all" || filters.date_from || filters.date_to) && (
              <div className="flex items-center space-x-2">
                <span>Active filters:</span>
                {filters.status !== "all" && (
                  <Badge variant="secondary" className="text-xs">
                    Status: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
                  </Badge>
                )}
                {filters.client_id !== "all" && (
                  <Badge variant="secondary" className="text-xs">
                    Client: {getClientName(filters.client_id)}
                  </Badge>
                )}
                {filters.date_from && (
                  <Badge variant="secondary" className="text-xs">
                    From: {format(filters.date_from, "MMM dd, yyyy")}
                  </Badge>
                )}
                {filters.date_to && (
                  <Badge variant="secondary" className="text-xs">
                    To: {format(filters.date_to, "MMM dd, yyyy")}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projects Table - Compact View */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[30%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Name
                </th>
                <th className="w-[20%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                {/* Manager restrictions: Hide Budget, Paid, Unpaid Balance columns */}
                <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-[14%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={project.name}>
                      {project.name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 truncate max-w-xs" title={getClientName(project.client_id)}>
                      {getClientName(project.client_id)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {project.start_date ? format(new Date(project.start_date + 'T00:00:00'), "MMM dd, yyyy") : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {project.end_date ? format(new Date(project.end_date + 'T00:00:00'), "MMM dd, yyyy") : '-'}
                  </td>
                  {/* Manager restrictions: No Budget, Paid, Unpaid Balance columns shown */}
                  <td className="px-4 py-3">
                    <Badge className={`${getStatusColor(project.status)} text-xs whitespace-nowrap`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewProject(project)}
                        className="bg-blue-600 text-white hover:bg-blue-700 px-3 py-1 text-xs whitespace-nowrap"
                      >
                        View
                      </Button>
                      {/* Manager restrictions: Hide Edit and Delete actions */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredProjects.length === 0 && (
          <div className="text-center py-8">
            {projects.length === 0 ? (
              <p className="text-gray-500 text-sm">No projects found.</p>
            ) : (
              <p className="text-gray-500 text-sm">No projects match the current filters. Try adjusting your search criteria.</p>
            )}
          </div>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * projectsPerPage) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * projectsPerPage, totalProjects)}
                    </span>{' '}
                    of <span className="font-medium">{totalProjects}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      Previous
                    </Button>
                    
                    {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                      const page = index + 1;
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </Button>
                      );
                    })}
                    
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      Next
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
// Manager Tasks Component - Exact replica of Admin Support > All Tasks
const ManagerTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teams, setTeams] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [itemsPerPage] = useState(50);
  
  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(null);

  // Helper function to truncate text with hover tooltip
  const TruncatedText = ({ text, maxLength = 15, className = "" }) => {
    if (!text) return <span className={className}>-</span>;
    
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text;
    
    return (
      <span 
        className={`${className} ${shouldTruncate ? 'cursor-help' : ''}`}
        title={shouldTruncate ? text : undefined}
      >
        {displayText}
      </span>
    );
  };

  useEffect(() => {
    // Get current user from localStorage
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchTasks();
      fetchTeams();
    }
  }, [typeFilter, statusFilter, teamFilter, currentUser, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [typeFilter, statusFilter, teamFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('task_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (teamFilter) params.append('team_id', teamFilter);
      
      // Add pagination parameters
      const skip = (currentPage - 1) * itemsPerPage;
      params.append('skip', skip.toString());
      params.append('limit', itemsPerPage.toString());
      
      // Fetch all tasks first
      const response = await axios.get(`${API}/tasks/unified?${params.toString()}`);
      let allTasks = response.data;
      
      // For managers, filter project tasks to only show those where manager is part of the project
      if (currentUser && currentUser.id) {
        // Get all projects to check manager involvement
        const projectsResponse = await axios.get(`${API}/projects`);
        const projects = projectsResponse.data;
        
        // Get project IDs where manager is involved (project manager OR assigned employee OR assigned to tasks)
        const managerProjects = projects.filter(project => {
          // Check if manager is the project manager
          if (project.manager_id === currentUser.id) {
            return true;
          }
          
          // Check if manager is in assigned employees list
          if (project.employee_ids && project.employee_ids.includes(currentUser.id)) {
            return true;
          }
          
          // Check if manager is assigned to any project tasks
          if (project.task_assignments && project.task_assignments.length > 0) {
            return project.task_assignments.some(assignment => 
              assignment.employee_id === currentUser.id
            );
          }
          
          return false;
        });
        
        const managerProjectIds = managerProjects.map(p => p.id);
        
        // Filter tasks: keep all support tasks, but only project tasks from manager's projects
        const filteredTasks = allTasks.filter(task => {
          if (task.type === 'Support') {
            // Keep all support tasks
            return true;
          } else if (task.type === 'Project') {
            // Only keep project tasks from projects where manager is involved
            return managerProjectIds.includes(task.project_id);
          }
          return false;
        });
        
        setTasks(filteredTasks);
      } else {
        setTasks(allTasks);
      }
      
      // Get total count for pagination (need to apply same filtering)
      const totalResponse = await axios.get(`${API}/tasks/unified?${new URLSearchParams({
        ...(typeFilter && { task_type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(teamFilter && { team_id: teamFilter }),
        limit: '1000' // Get a large number to count total
      }).toString()}`);
      
      let totalFilteredTasks = totalResponse.data;
      
      // Apply same filtering logic for total count
      if (currentUser && currentUser.id) {
        const projectsResponse = await axios.get(`${API}/projects`);
        const projects = projectsResponse.data;
        
        const managerProjects = projects.filter(project => {
          if (project.manager_id === currentUser.id) {
            return true;
          }
          // Check if manager is in assigned employees list
          if (project.employee_ids && project.employee_ids.includes(currentUser.id)) {
            return true;
          }
          if (project.task_assignments && project.task_assignments.length > 0) {
            return project.task_assignments.some(assignment => 
              assignment.employee_id === currentUser.id
            );
          }
          return false;
        });
        
        const managerProjectIds = managerProjects.map(p => p.id);
        
        totalFilteredTasks = totalResponse.data.filter(task => {
          if (task.type === 'Support') {
            return true;
          } else if (task.type === 'Project') {
            return managerProjectIds.includes(task.project_id);
          }
          return false;
        });
      }
      
      setTotalTasks(totalFilteredTasks.length);
    } catch (error) {
      console.error('Error fetching unified tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/team-types`);
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'Project':
        return <Building2 className="h-4 w-4 text-blue-500" />;
      case 'Support':
        return <Ticket className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTaskTypeBadgeClass = (type) => {
    switch (type) {
      case 'Project':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Support':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'In testing':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Published (Prod)':
        return <CheckCircle className="h-4 w-4 text-purple-500" />;
      case 'Closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTaskView = (task) => {
    if (task.type === 'Support') {
      navigate(`/support/tickets/${task.ticket_id}`);
    } else if (task.type === 'Project') {
      navigate(`/projects/${task.project_id}/tasks/${task.task_index}`);
    }
  };

  const filteredTasks = tasks.filter(task =>
    task.task_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.team_name && task.team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.assigned_employee_name && task.assigned_employee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.project_name && task.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.client_name && task.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Tasks</h1>
          <p className="text-gray-600 mt-1">Unified view of project tasks and support tickets</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="Project">Project</option>
              <option value="Support">Support</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="To Do">To Do</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="In testing">In testing</option>
              <option value="Resolved">Resolved</option>
              <option value="Published (Prod)">Published (Prod)</option>
              <option value="Closed">Closed</option>
            </select>

            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tasks Table - Exact replica of Admin Support > All Tasks */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500">
              {searchTerm || typeFilter || statusFilter || teamFilter
                ? "No tasks match your current filters."
                : "No tasks available at the moment."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    {/* Type Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getTaskTypeIcon(task.type)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTaskTypeBadgeClass(task.type)}`}>
                          {task.type}
                        </span>
                      </div>
                    </td>
                    
                    {/* ID Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-blue-600">
                        {task.type === 'Support' ? 
                          `#${task.ticket_id?.slice(-8) || task.id.slice(-8)}` : 
                          `T${task.task_index + 1}`
                        }
                      </div>
                    </td>
                    
                    {/* Task Name Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.task_name} 
                        maxLength={20}
                        className="font-medium text-gray-900"
                      />
                    </td>
                    
                    {/* Client Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.client_name} 
                        maxLength={12}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Team Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.team_name} 
                        maxLength={10}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Person Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {task.assigned_employee_name ? (
                          <>
                            <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {task.assigned_employee_name.charAt(0)}
                            </div>
                            <TruncatedText 
                              text={task.assigned_employee_name} 
                              maxLength={10}
                              className="text-gray-900"
                            />
                          </>
                        ) : (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </div>
                    </td>
                    
                    {/* Status Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(task.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeClass(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    </td>
                    
                    {/* Created Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(task.created_at).toLocaleDateString()}
                    </td>
                    
                    {/* Action Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleTaskView(task)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filteredTasks.length > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTasks)} of {totalTasks} tasks
          </div>
          
          {Math.ceil(totalTasks / itemsPerPage) > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalTasks / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalTasks / itemsPerPage);
                  let pageNumber;
                  
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === pageNumber
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalTasks / itemsPerPage)))}
                disabled={currentPage === Math.ceil(totalTasks / itemsPerPage)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Client Invoices Component
const ClientInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchClientInvoices();
    }
  }, [user]);

  const fetchClientInvoices = async () => {
    try {
      setLoading(true);
      // Use the downloads endpoint instead of invoices endpoint as it has correct data
      const response = await axios.get(`${API}/downloads`);
      const allDownloads = response.data || [];
      // Filter downloads for this client
      const clientInvoices = allDownloads.filter(download => 
        download.client_id === user.id ||
        download.client_name === user.company_name
      );
      setInvoices(clientInvoices);
    } catch (error) {
      console.error('Error fetching client invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (downloadId, invoiceNumber) => {
    try {
      // Use the view downloaded invoice endpoint since this is from downloads
      const response = await axios.get(`${API}/downloads/${downloadId}/view`);
      
      if (response.data.pdf_content) {
        // Create PDF blob from base64 content (matching admin panel logic)
        const binaryString = atob(response.data.pdf_content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoiceNumber || 'invoice'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Invoice downloaded successfully');
      } else {
        toast.error('Invoice content not available');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">Loading invoices...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Invoices</h1>
          <p className="text-gray-600 mt-1">View and download your invoices</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {invoices.length === 0 ? (
          <div className="p-8 text-center">
            <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
            <p className="text-gray-500">Your invoices will appear here once they are generated.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Downloaded Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="font-medium text-blue-600">#{invoice.invoice_number || invoice.id?.slice(-8) || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.download_date ? new Date(invoice.download_date).toLocaleDateString() : 'Not Downloaded'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(invoice.payment_status)}`}>
                        {invoice.payment_status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadInvoice(invoice.id, invoice.invoice_number)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Client Support Component - Matches admin Add Support Ticket form
const ClientSupport = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTicket, setNewTicket] = useState({
    issue_name: '',
    issue_type: 'Bug',
    issue_description: '',
    issue_link: '',
    issue_device: '',
    priority: 'Medium',
    issue_steps: [''],
    attachments: []
  });

  const handleAddStep = () => {
    setNewTicket({
      ...newTicket,
      issue_steps: [...newTicket.issue_steps, '']
    });
  };

  const handleStepChange = (index, value) => {
    const updatedSteps = [...newTicket.issue_steps];
    updatedSteps[index] = value;
    setNewTicket({
      ...newTicket,
      issue_steps: updatedSteps
    });
  };

  const handleRemoveStep = (index) => {
    if (newTicket.issue_steps.length > 1) {
      const updatedSteps = newTicket.issue_steps.filter((_, i) => i !== index);
      setNewTicket({
        ...newTicket,
        issue_steps: updatedSteps
      });
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedFiles.push({
          name: file.name,
          path: response.data.file_path,
          size: file.size
        });
      }
      
      setNewTicket({
        ...newTicket,
        attachments: [...newTicket.attachments, ...uploadedFiles]
      });
      
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = newTicket.attachments.filter((_, i) => i !== index);
    setNewTicket({
      ...newTicket,
      attachments: updatedFiles
    });
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const ticketData = {
        ...newTicket,
        client_id: user.id,
        client_name: user.company_name || user.name,
        issue_steps: newTicket.issue_steps.filter(step => step.trim() !== '')
      };
      
      await axios.post(`${API}/support-tickets`, ticketData);
      toast.success('Support ticket created successfully');
      
      // Reset form
      setNewTicket({
        issue_name: '',
        issue_type: 'Bug',
        issue_description: '',
        issue_link: '',
        issue_device: '',
        priority: 'Medium',
        issue_steps: [''],
        attachments: []
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Add Support Ticket</h1>
        <p className="text-gray-600 mt-1">Create a new support ticket</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Support Ticket Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateTicket} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Issue Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTicket.issue_name}
                  onChange={(e) => setNewTicket({...newTicket, issue_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter issue name"
                  required
                />
              </div>
              
              {/* Client Name (Pre-selected and disabled) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={user?.company_name || user?.name || 'Current Client'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  disabled
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Issue Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Issue Type</label>
                <select
                  value={newTicket.issue_type}
                  onChange={(e) => setNewTicket({...newTicket, issue_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Bug">Bug</option>
                  <option value="Feature Request">Feature Request</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Account Issue">Account Issue</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>
            
            {/* Issue Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={newTicket.issue_description}
                onChange={(e) => setNewTicket({...newTicket, issue_description: e.target.value})}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the issue in detail..."
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Issue Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Issue Link</label>
                <input
                  type="url"
                  value={newTicket.issue_link}
                  onChange={(e) => setNewTicket({...newTicket, issue_link: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>
              
              {/* Issue Device */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Issue Device</label>
                <input
                  type="text"
                  value={newTicket.issue_device}
                  onChange={(e) => setNewTicket({...newTicket, issue_device: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., iPhone 14, Windows 11, Chrome Browser"
                />
              </div>
            </div>

            {/* Documents */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Documents</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        {uploading ? 'Uploading...' : 'Click to upload files or drag and drop'}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        PDF, DOC, DOCX, TXT, PNG, JPG, GIF up to 10MB each
                      </span>
                    </div>
                  </label>
                </div>
                
                {newTicket.attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {newTicket.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Steps to Replicate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Steps to Replicate <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {newTicket.issue_steps.map((step, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500 w-8">{index + 1}.</span>
                    <input
                      type="text"
                      value={step}
                      onChange={(e) => handleStepChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Step ${index + 1}`}
                      required={index === 0}
                    />
                    {newTicket.issue_steps.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveStep(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddStep}
                  className="text-blue-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add More Steps
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline">
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating Ticket...' : 'Create Ticket'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Employee My Projects Component
const EmployeeMyProjects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyProjects();
  }, []);

  const fetchMyProjects = async () => {
    try {
      setLoading(true);
      // Use dedicated employee projects endpoint
      const response = await axios.get(`${API}/employees/${user?.id}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching employee projects:", error);
      toast.error("Failed to load projects. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading your projects...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Projects</h1>
          <p className="text-slate-600">Projects you are assigned to</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.length > 0 ? projects.map((project) => (
          <Card key={project.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{project.name}</CardTitle>
              <CardDescription>{project.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Status:</span>
                  <Badge variant="secondary">{project.status}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Client:</span>
                  <span>{project.client_name}</span>
                </div>
              </div>
              <Button 
                onClick={() => window.location.href = `/projects/${project.id}`}
                className="w-full mt-4"
                variant="outline"
              >
                View Project Details
              </Button>
            </CardContent>
          </Card>
        )) : (
          <div className="col-span-full text-center py-12">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Projects Assigned</h3>
            <p className="text-slate-500">You haven't been assigned to any projects yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Manager Support Component - REMOVED AS REQUESTED

// Manager Settings Component
const ManagerSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const updateData = {
        name: formData.name,
        email: formData.email
      };

      const response = await axios.put(`${API}/auth/update-profile`, updateData);
      
      if (response.data) {
        // Update localStorage with new user data
        const updatedUser = { ...user, ...updateData };
        localStorage.setItem('bizmanager_user', JSON.stringify(updatedUser));
        
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      
      const passwordData = {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      };

      await axios.put(`${API}/auth/change-password`, passwordData);
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      toast.success('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Manager Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>
              Update your personal information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                  required
                  minLength={6}
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                  minLength={6}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Account Information */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>
            Your account details and role information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Role</Label>
              <p className="text-sm text-gray-900 mt-1">Manager</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">User ID</Label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{user?.id}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Account Status</Label>
              <p className="text-sm text-emerald-600 mt-1 font-medium">Active</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Login Type</Label>
              <p className="text-sm text-gray-900 mt-1">Manager Account</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Projects = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user context for role-based restrictions
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]); // For name lookup
  const [managers, setManagers] = useState([]); // Add managers state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Check if current user is an employee or client
  const isEmployee = user?.isEmployee || false;
  const isClient = user?.isClient || false;
  const [reportsSummary, setReportsSummary] = useState({
    total_value: 0,
    total_paid: 0,
    total_due: 0,
    total_projects: 0
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const projectsPerPage = 50;
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_id: null,
    budget: "",
    billing_type: "one_time",
    status: "planning",
    start_date: null,
    end_date: null,
    employee_ids: [],
    tasks: [""],
    manager_id: null // Use null instead of empty string
  });

  // Filter states
  const [filters, setFilters] = useState({
    status: "all",
    client_id: "all",
    date_from: null,
    date_to: null
  });

  useEffect(() => {
    const loadData = async () => {
      await fetchProjects();
      await fetchEmployees();
      await fetchClients();
      await fetchManagers();
    };
    loadData();
  }, []);

  // Filter projects whenever projects or filters change
  useEffect(() => {
    applyFilters();
  }, [projects, filters]);

  // Refetch clients when projects change (for manager filtering)
  useEffect(() => {
    if (projects.length > 0) {
      fetchClients();
    }
  }, [projects]);

  const applyFilters = () => {
    let filtered = [...projects];

    // Filter by status
    if (filters.status && filters.status !== "all") {
      filtered = filtered.filter(project => project.status === filters.status);
    }
    // Filter by client
    if (filters.client_id && filters.client_id !== "all") {
      filtered = filtered.filter(project => project.client_id === filters.client_id);
    }

    // Filter by date range
    if (filters.date_from || filters.date_to) {
      filtered = filtered.filter(project => {
        // Check if project has dates and falls within the filter range
        const projectStartDate = project.start_date ? new Date(project.start_date) : null;
        const projectEndDate = project.end_date ? new Date(project.end_date) : null;
        
        let matchesDateRange = true;

        if (filters.date_from) {
          const filterFromDate = new Date(filters.date_from);
          // Project should start on or after the filter start date
          if (projectStartDate && projectStartDate < filterFromDate) {
            matchesDateRange = false;
          }
          // If no project start date but has end date, check if end date is after filter start
          if (!projectStartDate && projectEndDate && projectEndDate < filterFromDate) {
            matchesDateRange = false;
          }
        }

        if (filters.date_to) {
          const filterToDate = new Date(filters.date_to);
          // Project should end on or before the filter end date
          if (projectEndDate && projectEndDate > filterToDate) {
            matchesDateRange = false;
          }
          // If no project end date but has start date, check if start date is before filter end
          if (!projectEndDate && projectStartDate && projectStartDate > filterToDate) {
            matchesDateRange = false;
          }
        }

        return matchesDateRange;
      });
    }

    setFilteredProjects(filtered);
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      client_id: "all",
      date_from: null,
      date_to: null
    });
  };

  const fetchProjects = async (page = 1) => {
    try {
      setLoading(true);
      
      // Fetch paginated projects
      const projectsResponse = await axios.get(`${API}/projects/paginated`, {
        params: { page, limit: projectsPerPage }
      });
      
      if (projectsResponse.data) {
        let projectsData = projectsResponse.data.projects;
        
        // Filter projects for employees - only show assigned projects
        if (isEmployee && user?.id) {
          projectsData = projectsData.filter(project => 
            project.employee_ids && project.employee_ids.includes(user.id)
          );
        }
        
        setProjects(projectsData);
        setCurrentPage(projectsResponse.data.pagination.current_page);
        setTotalPages(projectsResponse.data.pagination.total_pages);
        setTotalProjects(isEmployee ? projectsData.length : projectsResponse.data.pagination.total_projects);
      }
      
      // Only fetch reports summary for admin users, not employees
      if (!isEmployee) {
        const reportsResponse = await axios.get(`${API}/projects/reports/summary`);
        if (reportsResponse.data) {
          setReportsSummary(reportsResponse.data);
        }
      }
      
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      setAllClients(response.data); // Store all clients for name lookup
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/employees/managers`);
      setManagers(response.data);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        budget: parseFloat(formData.budget),
        start_date: formData.start_date ? `${formData.start_date.getFullYear()}-${String(formData.start_date.getMonth() + 1).padStart(2, '0')}-${String(formData.start_date.getDate()).padStart(2, '0')}` : null,
        end_date: formData.end_date ? `${formData.end_date.getFullYear()}-${String(formData.end_date.getMonth() + 1).padStart(2, '0')}-${String(formData.end_date.getDate()).padStart(2, '0')}` : null,
        tasks: formData.tasks.filter(task => task.trim() !== ""),
        manager_id: formData.manager_id === "no_manager" ? null : formData.manager_id
      };

      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, data);
        toast.success("Project updated successfully");
      } else {
        await axios.post(`${API}/projects`, data);
        toast.success("Project created successfully");
      }
      
      setIsDialogOpen(false);
      setEditingProject(null);
      setFormData({
        name: "",
        description: "",
        client_id: null,
        budget: "",
        billing_type: "one_time",
        status: "planning",
        start_date: null,
        end_date: null,
        employee_ids: [],
        tasks: [""],
        manager_id: null
      });
      fetchProjects();
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error(error.response?.data?.detail || "Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || "",
      client_id: project.client_id || null,
      budget: project.budget.toString(),
      billing_type: project.billing_type,
      status: project.status,
      start_date: project.start_date ? new Date(project.start_date + 'T00:00:00') : null,
      end_date: project.end_date ? new Date(project.end_date + 'T00:00:00') : null,
      employee_ids: project.employee_ids || [],
      tasks: project.tasks && project.tasks.length > 0 ? project.tasks : [""],
      manager_id: project.manager_id || null
    });
    setIsDialogOpen(true);
  };

  const handleViewProject = (project) => {
    // Navigate to project detail page using React Router
    navigate(`/projects/${project.id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await axios.delete(`${API}/projects/${id}`);
        toast.success("Project deleted successfully");
        fetchProjects();
      } catch (error) {
        console.error("Error deleting project:", error);
        toast.error("Failed to delete project");
      }
    }
  };

  const handleAssignEmployees = async (projectId, employeeIds) => {
    try {
      await axios.put(`${API}/projects/${projectId}/assign-employees`, {
        project_id: projectId,
        employee_ids: employeeIds
      });
      toast.success("Employees assigned successfully");
      setIsAssignDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error("Error assigning employees:", error);
      toast.error("Failed to assign employees");
    }
  };

  const openAssignDialog = (project) => {
    setSelectedProject(project);
    setIsAssignDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "planning": return "bg-yellow-100 text-yellow-800";
      case "active": return "bg-green-100 text-green-800";
      case "completed": return "bg-blue-100 text-blue-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getBillingTypeColor = (type) => {
    switch (type) {
      case "one_time": return "bg-blue-100 text-blue-800";
      case "monthly_retainer": return "bg-green-100 text-green-800";
      case "annual_fee": return "bg-purple-100 text-purple-800";
      case "custom_development": return "bg-orange-100 text-orange-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getClientName = (clientId) => {
    const client = allClients.find(c => c.id === clientId);
    return client ? client.company_name : "Unknown Client";
  };

  // Add useEffect to load initial data
  useEffect(() => {
    fetchProjects();
    fetchEmployees();
    fetchClients();
    fetchManagers();
  }, []);

  // Handle page changes
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProjects(page);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Reports Section - Only show for admin users */}
      {!isEmployee && !isClient && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">
                ${reportsSummary.total_value?.toLocaleString() || '0'}
              </div>
              <p className="text-xs text-slate-500 mt-1">All projects budget</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                ${reportsSummary.total_paid?.toLocaleString() || '0'}
              </div>
              <p className="text-xs text-slate-500 mt-1">Total collected</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Due</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                ${reportsSummary.total_due?.toLocaleString() || '0'}
              </div>
              <p className="text-xs text-slate-500 mt-1">Pending from clients</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Project Management</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProject ? "Edit Project" : "Add New Project"}
              </DialogTitle>
              <DialogDescription>
                Enter the project details below.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Top Row - Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Project Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="client_id">Client</Label>
                  <Select value={formData.client_id || ""} onValueChange={(value) => setFormData({...formData, client_id: value || null})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Second Row - Manager and Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="manager_id">Project Manager</Label>
                  <Select value={formData.manager_id || ""} onValueChange={(value) => setFormData({...formData, manager_id: value || null})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_manager">No Manager Assigned</SelectItem>
                      {managers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="min-h-[80px]"
                  />
                </div>
              </div>
              
              {/* Third Row - Tasks Section */}
              <div>
                <Label htmlFor="tasks">Project Tasks</Label>
                <div className="space-y-2">
                  {formData.tasks.map((task, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        value={task}
                        onChange={(e) => {
                          const newTasks = [...formData.tasks];
                          newTasks[index] = e.target.value;
                          setFormData({...formData, tasks: newTasks});
                        }}
                        placeholder={`Task ${index + 1} (e.g., Setup project structure, Design UI mockups, etc.)`}
                        className="flex-1"
                      />
                      {formData.tasks.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newTasks = formData.tasks.filter((_, i) => i !== index);
                            setFormData({...formData, tasks: newTasks});
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData({...formData, tasks: [...formData.tasks, ""]});
                    }}
                    className="w-full"
                  >
                    + Add Another Task
                  </Button>
                </div>
              </div>
              
              {/* Fourth Row - Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? format(formData.start_date, "PPP") : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData({...formData, start_date: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.end_date ? format(formData.end_date, "PPP") : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => setFormData({...formData, end_date: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              {/* Fifth Row - Budget and Billing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="budget">Budget ($)</Label>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="billing_type">Billing Type</Label>
                  <Select value={formData.billing_type} onValueChange={(value) => setFormData({...formData, billing_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">One Time</SelectItem>
                      <SelectItem value="monthly_retainer">Monthly Retainer</SelectItem>
                      <SelectItem value="annual_fee">Annual Fee</SelectItem>
                      <SelectItem value="custom_development">Custom Development</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planning">Planning</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sixth Row - Employee Assignment */}
              <div>
                <Label>Assign Employees</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2 max-h-48 overflow-y-auto border rounded-lg p-4">
                  {employees.map((employee) => (
                    <div key={employee.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={employee.id}
                        checked={formData.employee_ids.includes(employee.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              employee_ids: [...formData.employee_ids, employee.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              employee_ids: formData.employee_ids.filter(id => id !== employee.id)
                            });
                          }
                        }}
                      />
                      <Label htmlFor={employee.id} className="text-sm">
                        {employee.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                {editingProject ? "Update Project" : "Create Project"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Employee Assignment Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Employees</DialogTitle>
            <DialogDescription>
              Select employees for {selectedProject?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
              {employees.map((employee) => (
                <div key={employee.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`assign-${employee.id}`}
                    checked={selectedProject?.employee_ids?.includes(employee.id) || false}
                    onCheckedChange={(checked) => {
                      if (selectedProject) {
                        const newEmployeeIds = checked
                          ? [...(selectedProject.employee_ids || []), employee.id]
                          : (selectedProject.employee_ids || []).filter(id => id !== employee.id);
                        setSelectedProject({
                          ...selectedProject,
                          employee_ids: newEmployeeIds
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`assign-${employee.id}`} className="text-sm">
                    {employee.name} (${employee.selling_per_hour}/hr)
                  </Label>
                </div>
              ))}
            </div>
            <Button
              onClick={() => handleAssignEmployees(selectedProject?.id, selectedProject?.employee_ids || [])}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Update Assignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Filter Projects</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="text-gray-600 hover:text-gray-700"
            >
              Clear Filters
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <Label htmlFor="filter-status">Filter by Status</Label>
              <Select 
                value={filters.status} 
                onValueChange={(value) => setFilters({...filters, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Client Filter */}
            <div>
              <Label htmlFor="filter-client">Filter by Client</Label>
              <Select 
                value={filters.client_id} 
                onValueChange={(value) => setFilters({...filters, client_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Date From Filter */}
            <div>
              <Label htmlFor="filter-date-from">Start Date From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date_from ? format(filters.date_from, "MMM dd, yyyy") : <span>Select date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.date_from}
                    onSelect={(date) => setFilters({...filters, date_from: date})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To Filter */}
            <div>
              <Label htmlFor="filter-date-to">End Date To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date_to ? format(filters.date_to, "MMM dd, yyyy") : <span>Select date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.date_to}
                    onSelect={(date) => setFilters({...filters, date_to: date})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Filter Status Display */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {filteredProjects.length} of {projects.length} projects
            </span>
            {(filters.status !== "all" || filters.client_id !== "all" || filters.date_from || filters.date_to) && (
              <div className="flex items-center space-x-2">
                <span>Active filters:</span>
                {filters.status !== "all" && (
                  <Badge variant="secondary" className="text-xs">
                    Status: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
                  </Badge>
                )}
                {filters.client_id !== "all" && (
                  <Badge variant="secondary" className="text-xs">
                    Client: {getClientName(filters.client_id)}
                  </Badge>
                )}
                {filters.date_from && (
                  <Badge variant="secondary" className="text-xs">
                    From: {format(filters.date_from, "MMM dd, yyyy")}
                  </Badge>
                )}
                {filters.date_to && (
                  <Badge variant="secondary" className="text-xs">
                    To: {format(filters.date_to, "MMM dd, yyyy")}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projects Table - Compact View */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-1/6 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Name
                </th>
                <th className="w-1/8 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="w-1/12 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="w-1/12 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                {!isEmployee && !isClient && (
                  <>
                    <th className="w-1/12 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="w-1/12 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid
                    </th>
                    <th className="w-1/12 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unpaid Balance
                    </th>
                  </>
                )}
                <th className="w-1/12 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-1/6 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 truncate">
                    <div className="text-sm font-medium text-gray-900 truncate" title={project.name}>
                      {project.name}
                    </div>
                  </td>
                  <td className="px-3 py-2 truncate">
                    <div className="text-sm text-gray-900 truncate" title={getClientName(project.client_id)}>
                      {getClientName(project.client_id)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {project.start_date ? format(new Date(project.start_date + 'T00:00:00'), "MMM dd, yyyy") : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {project.end_date ? format(new Date(project.end_date + 'T00:00:00'), "MMM dd, yyyy") : '-'}
                  </td>
                  {!isEmployee && !isClient && (
                    <>
                      <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                        ${project.budget?.toLocaleString() || '0'}
                      </td>
                      <td className="px-3 py-2 text-sm text-emerald-600 font-medium">
                        ${project.total_paid?.toLocaleString() || '0'}
                      </td>
                      <td className="px-3 py-2 text-sm text-red-600 font-medium">
                        ${project.remaining_balance?.toLocaleString() || '0'}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2">
                    <Badge className={`${getStatusColor(project.status)} text-xs`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-sm font-medium">
                    <div className="flex space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewProject(project)}
                        className="bg-blue-600 text-white hover:bg-blue-700 px-2 py-1 text-xs"
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(project)}
                        className="text-gray-600 hover:text-gray-700 px-2 py-1"
                        title="Edit Project"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(project.id)}
                        className="text-red-600 hover:text-red-700 px-2 py-1"
                        title="Delete Project"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredProjects.length === 0 && (
          <div className="text-center py-8">
            {projects.length === 0 ? (
              <p className="text-gray-500 text-sm">No projects found. Create your first project to get started.</p>
            ) : (
              <p className="text-gray-500 text-sm">No projects match the current filters. Try adjusting your search criteria.</p>
            )}
          </div>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span> ({totalProjects} total projects)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          onClick={() => handlePageChange(pageNum)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Categories Management Component
const Categories = () => {
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("expense");
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: "",
    value: "",
    type: "expense"
  });
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);

  useEffect(() => {
    initializeCategories();
  }, []);

  const initializeCategories = async () => {
    try {
      // Initialize default categories if needed
      await axios.post(`${API}/initialize-default-categories`);
      
      // Fetch categories
      await fetchCategories();
    } catch (error) {
      console.error("Error initializing categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const [expenseCategoriesRes, revenueCategoriesRes] = await Promise.all([
        axios.get(`${API}/expense-categories`),
        axios.get(`${API}/revenue-categories`)
      ]);
      setExpenseCategories(expenseCategoriesRes.data);
      setRevenueCategories(revenueCategoriesRes.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const endpoint = newCategoryForm.type === "expense" ? "expense-categories" : "revenue-categories";
      await axios.post(`${API}/${endpoint}`, {
        name: newCategoryForm.name,
        value: newCategoryForm.value.toLowerCase().replace(/\s+/g, '_')
      });
      
      await fetchCategories();
      setNewCategoryForm({ name: "", value: "", type: newCategoryForm.type });
      setIsCategoryDialogOpen(false);
      toast.success("Category created successfully");
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(error.response?.data?.detail || "Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId, type) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    
    try {
      const endpoint = type === "expense" ? "expense-categories" : "revenue-categories";
      await axios.delete(`${API}/${endpoint}/${categoryId}`);
      await fetchCategories();
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(error.response?.data?.detail || "Failed to delete category");
    }
  };

  const handleSetDefaultCategory = async (categoryId, type) => {
    try {
      await axios.post(`${API}/set-default-category?category_type=${type}&category_id=${categoryId}`);
      await fetchCategories();
      toast.success("Default category updated successfully");
    } catch (error) {
      console.error("Error setting default category:", error);
      toast.error(error.response?.data?.detail || "Failed to set default category");
    }
  };

  const openAddCategoryDialog = (type) => {
    setNewCategoryForm({
      name: "",
      value: "",
      type: type
    });
    setIsCategoryDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-lg text-slate-600">Loading categories...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-slate-900 mb-8">Category Management</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="expense">Expense Categories</TabsTrigger>
          <TabsTrigger value="revenue">Revenue Categories</TabsTrigger>
        </TabsList>

        {/* Expense Categories Tab */}
        <TabsContent value="expense">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Expense Categories</CardTitle>
                  <CardDescription>
                    Manage expense categories and set default category
                  </CardDescription>
                </div>
                <Button onClick={() => openAddCategoryDialog("expense")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expense Category
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {expenseCategories.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h3 className="font-medium">{category.name}</h3>
                        <p className="text-sm text-slate-600">Value: {category.value}</p>
                      </div>
                      {category.is_default && (
                        <Badge variant="default" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {!category.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefaultCategory(category.id, "expense")}
                        >
                          Set as Default
                        </Button>
                      )}
                      {!category.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id, "expense")}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {expenseCategories.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No expense categories found. Add your first category to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Categories Tab */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Revenue Categories</CardTitle>
                  <CardDescription>
                    Manage revenue categories and set default category
                  </CardDescription>
                </div>
                <Button onClick={() => openAddCategoryDialog("revenue")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Revenue Category
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {revenueCategories.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h3 className="font-medium">{category.name}</h3>
                        <p className="text-sm text-slate-600">Value: {category.value}</p>
                      </div>
                      {category.is_default && (
                        <Badge variant="default" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {!category.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefaultCategory(category.id, "revenue")}
                        >
                          Set as Default
                        </Button>
                      )}
                      {!category.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id, "revenue")}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {revenueCategories.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No revenue categories found. Add your first category to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Category Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add New {newCategoryForm.type === "expense" ? "Expense" : "Revenue"} Category
            </DialogTitle>
            <DialogDescription>
              Create a new category for {newCategoryForm.type === "expense" ? "expenses" : "revenue"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div>
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                value={newCategoryForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewCategoryForm({
                    ...newCategoryForm,
                    name,
                    value: name.toLowerCase().replace(/\s+/g, '_')
                  });
                }}
                placeholder="e.g., Office Supplies"
                required
              />
            </div>
            <div>
              <Label htmlFor="category-value">Internal Value</Label>
              <Input
                id="category-value"
                value={newCategoryForm.value}
                onChange={(e) => setNewCategoryForm({...newCategoryForm, value: e.target.value})}
                placeholder="e.g., office_supplies"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                This will be used internally for data storage
              </p>
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                Create Category
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsCategoryDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Finance Management Component (Simplified)
const Finances = () => {
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState("expenses");
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isRevenueDialogOpen, setIsRevenueDialogOpen] = useState(false);
  const [isCSVDialogOpen, setIsCSVDialogOpen] = useState(false);
  const [csvFile, setCSVFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    date: new Date(),
    description: "",
    amount: "",
    category: "miscellaneous",
    project_id: "",
    note: ""
  });
  const [revenueForm, setRevenueForm] = useState({
    date: new Date(),
    description: "",
    amount: "",
    category: "subscription",
    project_id: "",
    employee_id: "",
    hours_worked: "",
    note: ""
  });
  
  // Dynamic Categories State
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);

  // Balance Management State
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [balanceHistory, setBalanceHistory] = useState(null);
  const [isBalanceManagementOpen, setIsBalanceManagementOpen] = useState(false);
  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    amount: "",
    description: "Opening Balance",
    date_set: new Date()
  });

  // Transaction Log State
  const [transactionLog, setTransactionLog] = useState(null);
  const [loadingTransactionLog, setLoadingTransactionLog] = useState(false);

  // Pagination and Filtering State
  const [expensePage, setExpensePage] = useState(1);
  const [revenuePage, setRevenuePage] = useState(1);
  const [expenseTotalPages, setExpenseTotalPages] = useState(1);
  const [revenueTotalPages, setRevenueTotalPages] = useState(1);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [expenseFilters, setExpenseFilters] = useState({
    description: "",
    dateFrom: "",
    dateTo: "", 
    amountMin: "",
    amountMax: ""
  });
  const [revenueFilters, setRevenueFilters] = useState({
    description: "",
    dateFrom: "",
    dateTo: "",
    amountMin: "",
    amountMax: ""
  });
  const itemsPerPage = 50;

  useEffect(() => {
    fetchFinanceData();
    initializeCategories();
    fetchBalanceSummary();
  }, [expensePage, revenuePage, expenseFilters, revenueFilters]);

  // Fetch transaction log when switching to transaction-log tab
  useEffect(() => {
    if (activeTab === 'transaction-log') {
      fetchTransactionLog();
    }
  }, [activeTab]);

  const fetchBalanceSummary = async () => {
    try {
      const response = await axios.get(`${API}/balance-summary`);
      setBalanceSummary(response.data);
    } catch (error) {
      // Balance not set yet - not an error
      setBalanceSummary(null);
    }
  };

  const fetchBalanceHistory = async () => {
    try {
      const response = await axios.get(`${API}/balance-history`);
      setBalanceHistory(response.data);
    } catch (error) {
      console.error("Error fetching balance history:", error);
    }
  };

  const handleSetOpeningBalance = async (e) => {
    e.preventDefault();
    try {
      const data = {
        amount: parseFloat(openingBalanceForm.amount),
        description: openingBalanceForm.description,
        date_set: format(openingBalanceForm.date_set, "yyyy-MM-dd")
      };
      
      await axios.post(`${API}/opening-balance`, data);
      toast.success("Opening balance set successfully");
      
      // Generate monthly balances after setting opening balance
      await axios.post(`${API}/generate-monthly-balances`);
      
      await fetchBalanceSummary();
      await fetchBalanceHistory();
      setIsBalanceManagementOpen(false);
      
      // Reset form
      setOpeningBalanceForm({
        amount: "",
        description: "Opening Balance", 
        date_set: new Date()
      });
    } catch (error) {
      console.error("Error setting opening balance:", error);
      toast.error(error.response?.data?.detail || "Failed to set opening balance");
    }
  };

  const handleGenerateMonthlyBalances = async () => {
    try {
      const response = await axios.post(`${API}/generate-monthly-balances`);
      toast.success(`Generated monthly balances: ${response.data.records_created} new records`);
      await fetchBalanceHistory();
    } catch (error) {
      console.error("Error generating monthly balances:", error);
      toast.error("Failed to generate monthly balances");
    }
  };

  const fetchTransactionLog = async () => {
    try {
      setLoadingTransactionLog(true);
      const response = await axios.get(`${API}/transaction-log`);
      setTransactionLog(response.data);
    } catch (error) {
      console.error("Error fetching transaction log:", error);
      toast.error("Failed to load transaction log");
    } finally {
      setLoadingTransactionLog(false);
    }
  };

  const downloadTransactionLogCSV = async () => {
    try {
      const response = await axios.get(`${API}/transaction-log/download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'transaction_log.csv';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=([^;]+)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Transaction log downloaded successfully");
    } catch (error) {
      console.error("Error downloading transaction log:", error);
      toast.error("Failed to download transaction log");
    }
  };

  const initializeCategories = async () => {
    try {
      // Initialize default categories if needed
      await axios.post(`${API}/initialize-default-categories`);
      
      // Fetch categories
      await fetchCategories();
    } catch (error) {
      console.error("Error initializing categories:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const [expenseCategoriesRes, revenueCategoriesRes] = await Promise.all([
        axios.get(`${API}/expense-categories`),
        axios.get(`${API}/revenue-categories`)
      ]);
      setExpenseCategories(expenseCategoriesRes.data);
      setRevenueCategories(revenueCategoriesRes.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      date: new Date(),
      description: "",
      amount: "",
      category: expenseCategories.length > 0 ? expenseCategories[0].value : "miscellaneous",
      project_id: "",
      note: ""
    });
  };

  const resetRevenueForm = () => {
    setRevenueForm({
      date: new Date(),
      description: "",
      amount: "",
      category: revenueCategories.length > 0 ? revenueCategories[0].value : "subscription",
      project_id: "",
      employee_id: "",
      hours_worked: "",
      note: ""
    });
  };

  // Filter change handlers
  const handleExpenseFilterChange = (filterKey, value) => {
    setExpenseFilters({...expenseFilters, [filterKey]: value});
    setExpensePage(1); // Reset to first page when filters change
  };

  const handleRevenueFilterChange = (filterKey, value) => {
    setRevenueFilters({...revenueFilters, [filterKey]: value});
    setRevenuePage(1); // Reset to first page when filters change
  };

  const fetchFinanceData = async () => {
    try {
      // Check if we need client-side filtering for expenses
      const needsExpenseFiltering = expenseFilters.description || expenseFilters.dateFrom || expenseFilters.dateTo || expenseFilters.amountMin || expenseFilters.amountMax;
      
      // Check if we need client-side filtering for revenue
      const needsRevenueFiltering = revenueFilters.description || revenueFilters.dateFrom || revenueFilters.dateTo || revenueFilters.amountMin || revenueFilters.amountMax;
      
      let expensesPromise, revenuesPromise;
      
      // Handle expenses with or without filtering
      if (needsExpenseFiltering) {
        // Fetch all expenses for client-side filtering
        expensesPromise = axios.get(`${API}/expenses`);
      } else {
        // Use normal pagination when no filters
        expensesPromise = axios.get(`${API}/expenses/paginated?page=${expensePage}&limit=${itemsPerPage}`);
      }
      
      // Handle revenue with or without filtering  
      if (needsRevenueFiltering) {
        // Fetch all revenue for client-side filtering
        revenuesPromise = axios.get(`${API}/revenue`);
      } else {
        // Use normal pagination when no filters
        revenuesPromise = axios.get(`${API}/revenue/paginated?page=${revenuePage}&limit=${itemsPerPage}`);
      }
      
      const [expensesRes, revenuesRes, projectsRes, employeesRes] = await Promise.all([
        expensesPromise,
        revenuesPromise,
        axios.get(`${API}/projects`),
        axios.get(`${API}/employees`)
      ]);
      
      // Handle expenses response
      if (needsExpenseFiltering) {
        // Apply client-side filtering for expenses
        let allExpenses = expensesRes.data || [];
        let filteredExpenses = allExpenses;
        
        if (expenseFilters.description) {
          filteredExpenses = filteredExpenses.filter(expense => 
            (expense.description || "").toLowerCase().includes(expenseFilters.description.toLowerCase())
          );
        }
        
        if (expenseFilters.dateFrom) {
          filteredExpenses = filteredExpenses.filter(expense => 
            expense.date >= expenseFilters.dateFrom
          );
        }
        
        if (expenseFilters.dateTo) {
          filteredExpenses = filteredExpenses.filter(expense => 
            expense.date <= expenseFilters.dateTo
          );
        }
        
        if (expenseFilters.amountMin) {
          const minAmount = parseFloat(expenseFilters.amountMin);
          filteredExpenses = filteredExpenses.filter(expense => 
            (expense.amount || 0) >= minAmount
          );
        }
        
        if (expenseFilters.amountMax) {
          const maxAmount = parseFloat(expenseFilters.amountMax);
          filteredExpenses = filteredExpenses.filter(expense => 
            (expense.amount || 0) <= maxAmount
          );
        }
        
        // Apply pagination manually
        const totalFiltered = filteredExpenses.length;
        const startIndex = (expensePage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedExpenses = filteredExpenses.slice(startIndex, endIndex);
        
        setExpenses(paginatedExpenses);
        setExpenseTotalPages(Math.ceil(totalFiltered / itemsPerPage));
        setTotalExpenses(totalFiltered);
        
      } else {
        // Handle normal paginated expenses response
        if (expensesRes.data.expenses) {
          setExpenses(expensesRes.data.expenses);
          setExpenseTotalPages(expensesRes.data.pagination.total_pages);
          setTotalExpenses(expensesRes.data.pagination.total_expenses);
        } else {
          setExpenses(expensesRes.data);
        }
      }
      
      // Handle revenue response
      if (needsRevenueFiltering) {
        // Apply client-side filtering for revenue
        let allRevenue = revenuesRes.data || [];
        let filteredRevenue = allRevenue;
        
        if (revenueFilters.description) {
          filteredRevenue = filteredRevenue.filter(revenue => 
            (revenue.description || "").toLowerCase().includes(revenueFilters.description.toLowerCase())
          );
        }
        
        if (revenueFilters.dateFrom) {
          filteredRevenue = filteredRevenue.filter(revenue => 
            revenue.date >= revenueFilters.dateFrom
          );
        }
        
        if (revenueFilters.dateTo) {
          filteredRevenue = filteredRevenue.filter(revenue => 
            revenue.date <= revenueFilters.dateTo
          );
        }
        
        if (revenueFilters.amountMin) {
          const minAmount = parseFloat(revenueFilters.amountMin);
          filteredRevenue = filteredRevenue.filter(revenue => 
            (revenue.amount || 0) >= minAmount
          );
        }
        
        if (revenueFilters.amountMax) {
          const maxAmount = parseFloat(revenueFilters.amountMax);
          filteredRevenue = filteredRevenue.filter(revenue => 
            (revenue.amount || 0) <= maxAmount
          );
        }
        
        // Apply pagination manually
        const totalFiltered = filteredRevenue.length;
        const startIndex = (revenuePage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedRevenue = filteredRevenue.slice(startIndex, endIndex);
        
        setRevenues(paginatedRevenue);
        setRevenueTotalPages(Math.ceil(totalFiltered / itemsPerPage));
        setTotalRevenue(totalFiltered);
        
      } else {
        // Handle normal paginated revenue response
        if (revenuesRes.data.revenue) {
          setRevenues(revenuesRes.data.revenue);
          setRevenueTotalPages(revenuesRes.data.pagination.total_pages);
          setTotalRevenue(revenuesRes.data.pagination.total_revenue);
        } else {
          setRevenues(revenuesRes.data);
        }
      }
      
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error("Error fetching finance data:", error);
      toast.error("Failed to load finance data");
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...expenseForm,
        date: expenseForm.date instanceof Date ? 
          `${expenseForm.date.getFullYear()}-${String(expenseForm.date.getMonth() + 1).padStart(2, '0')}-${String(expenseForm.date.getDate()).padStart(2, '0')}` : 
          expenseForm.date,
        amount: parseFloat(expenseForm.amount),
        project_id: expenseForm.project_id || null
      };
      await axios.post(`${API}/expenses`, data);
      toast.success("Expense added successfully");
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
      fetchFinanceData();
      fetchBalanceSummary(); // Refresh balance after adding expense
      if (activeTab === 'transaction-log') {
        fetchTransactionLog(); // Refresh transaction log if currently viewing it
      }
    } catch (error) {
      console.error("Error adding expense:", error);
      toast.error("Failed to add expense");
    }
  };

  const handleRevenueSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...revenueForm,
        date: revenueForm.date instanceof Date ? 
          `${revenueForm.date.getFullYear()}-${String(revenueForm.date.getMonth() + 1).padStart(2, '0')}-${String(revenueForm.date.getDate()).padStart(2, '0')}` : 
          revenueForm.date,
        amount: parseFloat(revenueForm.amount),
        project_id: revenueForm.project_id || null,
        employee_id: revenueForm.employee_id || null,
        hours_worked: revenueForm.hours_worked ? parseFloat(revenueForm.hours_worked) : null
      };
      await axios.post(`${API}/revenue`, data);
      toast.success("Revenue added successfully");
      setIsRevenueDialogOpen(false);
      resetRevenueForm();
      fetchFinanceData();
      fetchBalanceSummary(); // Refresh balance after adding revenue
      if (activeTab === 'transaction-log') {
        fetchTransactionLog(); // Refresh transaction log if currently viewing it
      }
    } catch (error) {
      console.error("Error adding revenue:", error);
      toast.error("Failed to add revenue");
    }
  };

  const deleteExpense = async (id) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      try {
        await axios.delete(`${API}/expenses/${id}`);
        toast.success("Expense deleted successfully");
        fetchFinanceData();
        fetchBalanceSummary(); // Refresh balance after deleting expense
        if (activeTab === 'transaction-log') {
          fetchTransactionLog(); // Refresh transaction log if currently viewing it
        }
      } catch (error) {
        console.error("Error deleting expense:", error);
        toast.error("Failed to delete expense");
      }
    }
  };

  const deleteRevenue = async (id) => {
    if (window.confirm("Are you sure you want to delete this revenue?")) {
      try {
        await axios.delete(`${API}/revenue/${id}`);
        toast.success("Revenue deleted successfully");
        fetchFinanceData();
        fetchBalanceSummary(); // Refresh balance after deleting revenue
        if (activeTab === 'transaction-log') {
          fetchTransactionLog(); // Refresh transaction log if currently viewing it
        }
      } catch (error) {
        console.error("Error deleting revenue:", error);
        toast.error("Failed to delete revenue");
      }
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(`${API}/upload-bank-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      toast.success(`Successfully processed ${response.data.processed_rows} transactions`);
      setIsCSVDialogOpen(false);
      fetchFinanceData(); // Refresh the data
    } catch (error) {
      console.error("Error uploading CSV:", error);
      toast.error("Failed to upload CSV file");
    } finally {
      setIsUploading(false);
    }
  };

  // Filtering and Pagination Functions
  const filterExpenses = (expenses) => {
    return expenses.filter(expense => {
      const descMatch = expense.description.toLowerCase().includes(expenseFilters.description.toLowerCase());
      const dateMatch = (!expenseFilters.dateFrom || expense.date >= expenseFilters.dateFrom) && 
                       (!expenseFilters.dateTo || expense.date <= expenseFilters.dateTo);
      const amountMatch = (!expenseFilters.amountMin || expense.amount >= parseFloat(expenseFilters.amountMin)) &&
                         (!expenseFilters.amountMax || expense.amount <= parseFloat(expenseFilters.amountMax));
      return descMatch && dateMatch && amountMatch;
    });
  };

  const filterRevenues = (revenues) => {
    return revenues.filter(revenue => {
      const descMatch = revenue.description.toLowerCase().includes(revenueFilters.description.toLowerCase());
      const dateMatch = (!revenueFilters.dateFrom || revenue.date >= revenueFilters.dateFrom) && 
                       (!revenueFilters.dateTo || revenue.date <= revenueFilters.dateTo);
      const amountMatch = (!revenueFilters.amountMin || revenue.amount >= parseFloat(revenueFilters.amountMin)) &&
                         (!revenueFilters.amountMax || revenue.amount <= parseFloat(revenueFilters.amountMax));
      return descMatch && dateMatch && amountMatch;
    });
  };

  const paginateData = (data, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (dataLength) => Math.ceil(dataLength / itemsPerPage);

  const handleDeleteAllExpenses = async () => {
    if (window.confirm("Are you sure you want to delete ALL expenses? This cannot be undone.")) {
      try {
        for (const expense of expenses) {
          await axios.delete(`${API}/expenses/${expense.id}`);
        }
        toast.success("All expenses deleted successfully");
        await fetchFinanceData();
        await fetchBalanceSummary(); // Refresh balance after deleting all expenses
        if (activeTab === 'transaction-log') {
          await fetchTransactionLog(); // Refresh transaction log if currently viewing it
        }
        setExpensePage(1);
      } catch (error) {
        console.error("Error deleting all expenses:", error);
        toast.error("Failed to delete all expenses");
      }
    }
  };

  const handleDeleteAllRevenues = async () => {
    if (window.confirm("Are you sure you want to delete ALL revenues? This cannot be undone.")) {
      try {
        for (const revenue of revenues) {
          await axios.delete(`${API}/revenue/${revenue.id}`);
        }
        toast.success("All revenues deleted successfully");
        await fetchFinanceData();
        await fetchBalanceSummary(); // Refresh balance after deleting all revenues
        if (activeTab === 'transaction-log') {
          await fetchTransactionLog(); // Refresh transaction log if currently viewing it
        }
        setRevenuePage(1);
      } catch (error) {
        console.error("Error deleting all revenues:", error);
        toast.error("Failed to delete all revenues");
      }
    }
  };

  const resetExpenseFilters = () => {
    setExpenseFilters({
      description: "",
      dateFrom: "",
      dateTo: "",
      amountMin: "",
      amountMax: ""
    });
    setExpensePage(1);
  };

  const handleExpensePageChange = (page) => {
    setExpensePage(page);
  };

  const handleRevenuePageChange = (page) => {
    setRevenuePage(page);
  };

  const resetRevenueFilters = () => {
    setRevenueFilters({
      description: "",
      dateFrom: "",
      dateTo: "",
      amountMin: "",
      amountMax: ""
    });
    setRevenuePage(1);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-slate-900 mb-8">Financial Management</h1>
      
      {/* Balance Management Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5" />
            <span>Balance Tracking</span>
          </CardTitle>
          <CardDescription>
            Set opening balance and track your business financial position over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {balanceSummary ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full mb-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Current Balance</h3>
                  <p className={`text-2xl font-bold ${balanceSummary.current_balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    ${balanceSummary.current_balance.toFixed(2)}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-slate-800">Opening Balance</h3>
                  <p className="text-2xl font-bold text-slate-600">
                    ${balanceSummary.opening_balance.toFixed(2)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800">Total Revenue</h3>
                  <p className="text-2xl font-bold text-green-600">
                    ${balanceSummary.total_revenue.toFixed(2)}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-red-800">Total Expenses</h3>
                  <p className="text-2xl font-bold text-red-600">
                    ${balanceSummary.total_expenses.toFixed(2)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 p-4 rounded-lg w-full mb-4">
                <p className="text-yellow-800">No opening balance set. Set your opening balance to start tracking.</p>
              </div>
            )}
            
            <Dialog open={isBalanceManagementOpen} onOpenChange={setIsBalanceManagementOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="bg-blue-50 hover:bg-blue-100 border-blue-200">
                  <DollarSign className="h-4 w-4 mr-2" />
                  {balanceSummary ? 'Update Opening Balance' : 'Set Opening Balance'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Set Opening Balance</DialogTitle>
                  <DialogDescription>
                    Set the starting balance for your business to enable balance tracking
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSetOpeningBalance} className="space-y-4">
                  <div>
                    <Label htmlFor="amount">Opening Balance Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={openingBalanceForm.amount}
                      onChange={(e) => setOpeningBalanceForm({...openingBalanceForm, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={openingBalanceForm.description}
                      onChange={(e) => setOpeningBalanceForm({...openingBalanceForm, description: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Date Set</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {openingBalanceForm.date_set ? format(openingBalanceForm.date_set, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={openingBalanceForm.date_set}
                          onSelect={(date) => setOpeningBalanceForm({...openingBalanceForm, date_set: date || new Date()})}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    Set Opening Balance
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {balanceSummary && (
              <Button 
                onClick={handleGenerateMonthlyBalances}
                variant="outline" 
                className="bg-green-50 hover:bg-green-100 border-green-200"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Generate Monthly Balances
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* CSV Upload Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Bank Record Import</span>
          </CardTitle>
          <CardDescription>
            Upload a CSV file with bank transactions to automatically create expense and revenue records
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={isCSVDialogOpen} onOpenChange={setIsCSVDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Upload Bank CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload Bank CSV File</DialogTitle>
                <DialogDescription>
                  Select a CSV file with columns: Date, Description, Amount, Status, Bank Description, Note
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="csvFile">CSV File</Label>
                  <Input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCSVFile(e.target.files[0])}
                  />
                </div>
                
                {uploadResult && (
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    <h4 className="font-medium">Upload Results:</h4>
                    <div className="text-sm space-y-1">
                      <div>Total Rows: {uploadResult.total_rows}</div>
                      <div>Processed: {uploadResult.processed_rows}</div>
                      <div>Skipped: {uploadResult.skipped_rows}</div>
                      <div className="text-green-600">Expenses Created: {uploadResult.expenses_created}</div>
                      <div className="text-green-600">Revenues Created: {uploadResult.revenues_created}</div>
                      {uploadResult.errors.length > 0 && (
                        <div className="text-red-600">
                          <div>Errors:</div>
                          {uploadResult.errors.slice(0, 3).map((error, idx) => (
                            <div key={idx} className="text-xs">{error}</div>
                          ))}
                          {uploadResult.errors.length > 3 && (
                            <div className="text-xs">...and {uploadResult.errors.length - 3} more</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="text-sm text-slate-600">
                  <strong>CSV Format:</strong>
                  <ul className="list-disc ml-4 mt-1">
                    <li>Positive amounts = Revenue</li>
                    <li>Negative amounts = Expense</li>
                    <li>Failed transactions are skipped</li>
                    <li>Default categories applied</li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleCSVUpload}
                  disabled={!csvFile || isUploading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {isUploading ? "Processing..." : "Upload and Process"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="transaction-log">Transaction Log</TabsTrigger>
        </TabsList>

        <TabsContent value="expenses">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Expense Tracking</h2>
            <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleExpenseSubmit} className="space-y-4">
                  <div>
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(expenseForm.date, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={expenseForm.date}
                          onSelect={(date) => setExpenseForm({...expenseForm, date: date || new Date()})}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({...expenseForm, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {expenseCategories.map((category) => (
                          <SelectItem key={category.id} value={category.value}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="project">Project (Optional)</Label>
                    <Select value={expenseForm.project_id} onValueChange={(value) => setExpenseForm({...expenseForm, project_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="note">Note (Optional)</Label>
                    <Textarea
                      id="note"
                      placeholder="Additional notes about this expense..."
                      value={expenseForm.note}
                      onChange={(e) => setExpenseForm({...expenseForm, note: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
                    Add Expense
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Expense Filters */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="description-filter">Description</Label>
                  <Input
                    id="description-filter"
                    placeholder="Filter by description..."
                    value={expenseFilters.description}
                    onChange={(e) => handleExpenseFilterChange('description', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="date-from">Date From</Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={expenseFilters.dateFrom}
                    onChange={(e) => handleExpenseFilterChange('dateFrom', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="date-to">Date To</Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={expenseFilters.dateTo}
                    onChange={(e) => handleExpenseFilterChange('dateTo', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="amount-min">Min Amount</Label>
                  <Input
                    id="amount-min"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={expenseFilters.amountMin}
                    onChange={(e) => handleExpenseFilterChange('amountMin', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="amount-max">Max Amount</Label>
                  <Input
                    id="amount-max"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={expenseFilters.amountMax}
                    onChange={(e) => handleExpenseFilterChange('amountMax', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={resetExpenseFilters} variant="outline" size="sm">
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
                <Button onClick={handleDeleteAllExpenses} variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete All Expenses
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Expense Table */}
          {(() => {
            const filteredExpenses = filterExpenses(expenses);
            const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

            return (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="flex items-center gap-2">
                        <TrendingDown className="h-5 w-5 text-red-600" />
                        Expenses ({totalExpenses} total)
                      </CardTitle>
                      <div className="text-lg font-bold text-red-600">
                        Total: -${totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-semibold">Date</th>
                            <th className="text-left p-3 font-semibold">Description</th>
                            <th className="text-left p-3 font-semibold">Category</th>
                            <th className="text-left p-3 font-semibold">Project</th>
                            <th className="text-left p-3 font-semibold">Notes</th>
                            <th className="text-right p-3 font-semibold">Amount</th>
                            <th className="text-center p-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenses.length > 0 ? expenses.map((expense, index) => (
                            <tr key={expense.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                              <td className="p-3">{expense.date}</td>
                              <td className="p-3 font-medium">{expense.description}</td>
                              <td className="p-3">
                                <Badge variant="outline">
                                  {expense.category.replace('_', ' ').toUpperCase()}
                                </Badge>
                              </td>
                              <td className="p-3">
                                {expense.project_id && projects.find(p => p.id === expense.project_id)?.name || '-'}
                              </td>
                              <td className="p-3 max-w-xs truncate" title={expense.note}>
                                {expense.note || '-'}
                              </td>
                              <td className="p-3 text-right font-bold text-red-600">
                                -${expense.amount.toFixed(2)}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteExpense(expense.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="7" className="p-8 text-center text-slate-500">
                                No expenses found matching your filters
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Pagination */}
                <Pagination
                  currentPage={expensePage}
                  totalPages={expenseTotalPages}
                  onPageChange={handleExpensePageChange}
                  totalItems={totalExpenses}
                  itemsPerPage={itemsPerPage}
                  itemName="expenses"
                />
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="revenue">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Revenue Tracking</h2>
            <Dialog open={isRevenueDialogOpen} onOpenChange={setIsRevenueDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Revenue
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Revenue</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRevenueSubmit} className="space-y-4">
                  <div>
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(revenueForm.date, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={revenueForm.date}
                          onSelect={(date) => setRevenueForm({...revenueForm, date: date || new Date()})}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={revenueForm.description}
                      onChange={(e) => setRevenueForm({...revenueForm, description: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={revenueForm.amount}
                      onChange={(e) => setRevenueForm({...revenueForm, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="revenue_category">Revenue Category</Label>
                    <Select value={revenueForm.category} onValueChange={(value) => setRevenueForm({...revenueForm, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {revenueCategories.map((category) => (
                          <SelectItem key={category.id} value={category.value}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="project">Project (Optional)</Label>
                    <Select value={revenueForm.project_id} onValueChange={(value) => setRevenueForm({...revenueForm, project_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="employee">Employee (Optional)</Label>
                    <Select value={revenueForm.employee_id} onValueChange={(value) => setRevenueForm({...revenueForm, employee_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="hours">Hours Worked (Optional)</Label>
                    <Input
                      id="hours"
                      type="number"
                      step="0.1"
                      value={revenueForm.hours_worked}
                      onChange={(e) => setRevenueForm({...revenueForm, hours_worked: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="revenue_note">Note (Optional)</Label>
                    <Textarea
                      id="revenue_note"
                      placeholder="Additional notes about this revenue..."
                      value={revenueForm.note}
                      onChange={(e) => setRevenueForm({...revenueForm, note: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    Add Revenue
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Revenue Filters */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="revenue-description-filter">Description</Label>
                  <Input
                    id="revenue-description-filter"
                    placeholder="Filter by description..."
                    value={revenueFilters.description}
                    onChange={(e) => handleRevenueFilterChange('description', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="revenue-date-from">Date From</Label>
                  <Input
                    id="revenue-date-from"
                    type="date"
                    value={revenueFilters.dateFrom}
                    onChange={(e) => handleRevenueFilterChange('dateFrom', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="revenue-date-to">Date To</Label>
                  <Input
                    id="revenue-date-to"
                    type="date"
                    value={revenueFilters.dateTo}
                    onChange={(e) => handleRevenueFilterChange('dateTo', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="revenue-amount-min">Min Amount</Label>
                  <Input
                    id="revenue-amount-min"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={revenueFilters.amountMin}
                    onChange={(e) => handleRevenueFilterChange('amountMin', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="revenue-amount-max">Max Amount</Label>
                  <Input
                    id="revenue-amount-max"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={revenueFilters.amountMax}
                    onChange={(e) => handleRevenueFilterChange('amountMax', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={resetRevenueFilters} variant="outline" size="sm">
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
                <Button onClick={handleDeleteAllRevenues} variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete All Revenues
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Table */}
          {(() => {
            const filteredRevenues = filterRevenues(revenues);
            const totalAmount = filteredRevenues.reduce((sum, revenue) => sum + revenue.amount, 0);

            return (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-green-600" />
                        Revenues ({totalRevenue} total)
                      </CardTitle>
                      <div className="text-lg font-bold text-green-600">
                        Total: +${totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-semibold">Date</th>
                            <th className="text-left p-3 font-semibold">Description</th>
                            <th className="text-left p-3 font-semibold">Category</th>
                            <th className="text-left p-3 font-semibold">Project</th>
                            <th className="text-left p-3 font-semibold">Employee</th>
                            <th className="text-left p-3 font-semibold">Hours</th>
                            <th className="text-left p-3 font-semibold">Notes</th>
                            <th className="text-right p-3 font-semibold">Amount</th>
                            <th className="text-center p-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenues.length > 0 ? revenues.map((revenue, index) => (
                            <tr key={revenue.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                              <td className="p-3">{revenue.date}</td>
                              <td className="p-3 font-medium">{revenue.description}</td>
                              <td className="p-3">
                                <Badge variant="outline">
                                  {revenue.category.replace('_', ' ').toUpperCase()}
                                </Badge>
                              </td>
                              <td className="p-3">
                                {revenue.project_id && projects.find(p => p.id === revenue.project_id)?.name || '-'}
                              </td>
                              <td className="p-3">
                                {revenue.employee_id && employees.find(e => e.id === revenue.employee_id)?.name || '-'}
                              </td>
                              <td className="p-3">
                                {revenue.hours_worked ? `${revenue.hours_worked}h` : '-'}
                              </td>
                              <td className="p-3 max-w-xs truncate" title={revenue.note}>
                                {revenue.note || '-'}
                              </td>
                              <td className="p-3 text-right font-bold text-green-600">
                                +${revenue.amount.toFixed(2)}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteRevenue(revenue.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="9" className="p-8 text-center text-slate-500">
                                No revenues found matching your filters
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Pagination */}
                <Pagination
                  currentPage={revenuePage}
                  totalPages={revenueTotalPages}
                  onPageChange={handleRevenuePageChange}
                  totalItems={totalRevenue}
                  itemsPerPage={itemsPerPage}
                  itemName="revenue"
                />
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="transaction-log">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Transaction Log</h2>
            <div className="flex gap-2">
              <Button 
                onClick={fetchTransactionLog}
                variant="outline"
                disabled={loadingTransactionLog}
              >
                {loadingTransactionLog ? "Loading..." : "Refresh Log"}
              </Button>
              <Button 
                onClick={downloadTransactionLogCSV}
                variant="default"
                disabled={loadingTransactionLog || !transactionLog}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
            </div>
          </div>

          {loadingTransactionLog ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-slate-500">Loading transaction log...</p>
              </CardContent>
            </Card>
          ) : transactionLog ? (
            <Card>
              <CardHeader>
                <CardTitle>Financial Transaction History</CardTitle>
                <CardDescription>
                  Shows how each revenue and expense entry affected your balance by date
                </CardDescription>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-blue-600">Opening Balance</div>
                    <div className="text-2xl font-bold text-blue-900">
                      ${transactionLog.opening_balance.toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-green-600">Current Balance</div>
                    <div className="text-2xl font-bold text-green-900">
                      ${transactionLog.current_balance.toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-slate-600">Total Transactions</div>
                    <div className="text-2xl font-bold text-slate-900">
                      {transactionLog.total_entries}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-slate-700">Date</th>
                        <th className="text-left p-3 font-medium text-slate-700">Type</th>
                        <th className="text-left p-3 font-medium text-slate-700">Description</th>
                        <th className="text-left p-3 font-medium text-slate-700">Category</th>
                        <th className="text-right p-3 font-medium text-slate-700">Amount</th>
                        <th className="text-right p-3 font-medium text-slate-700">Balance Impact</th>
                        <th className="text-right p-3 font-medium text-slate-700">Running Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionLog.transactions.map((transaction, index) => (
                        <tr key={transaction.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                          <td className="p-3 text-sm">
                            {transaction.date}
                          </td>
                          <td className="p-3">
                            <Badge 
                              variant={transaction.type === 'revenue' ? 'default' : 'destructive'}
                              className={transaction.type === 'revenue' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                            >
                              {transaction.type === 'revenue' ? 'Revenue' : 'Expense'}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm max-w-xs">
                            <div className="font-medium">{transaction.description}</div>
                            {transaction.project_name && (
                              <div className="text-xs text-slate-500">Project: {transaction.project_name}</div>
                            )}
                            {transaction.employee_name && (
                              <div className="text-xs text-slate-500">Employee: {transaction.employee_name}</div>
                            )}
                          </td>
                          <td className="p-3 text-sm">
                            {transaction.category || '-'}
                          </td>
                          <td className="p-3 text-right font-medium">
                            ${transaction.amount.toFixed(2)}
                          </td>
                          <td className="p-3 text-right font-bold">
                            <span className={transaction.balance_impact >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {transaction.balance_impact >= 0 ? '+' : ''}${transaction.balance_impact.toFixed(2)}
                            </span>
                          </td>
                          <td className="p-3 text-right font-bold text-slate-900">
                            ${transaction.running_balance.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {transactionLog.transactions.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No transactions found. Add some revenue or expenses to see them here.
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-slate-500">No transaction data available</p>
                <Button onClick={fetchTransactionLog} className="mt-4">
                  Load Transaction Log
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Online Payments Component
const OnlinePayments = () => {
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [activeTab, setActiveTab] = useState("wallets");
  const [loading, setLoading] = useState(false);
  
  // Wallet Management State
  const [isWalletDialogOpen, setIsWalletDialogOpen] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [walletForm, setWalletForm] = useState({
    name: "",
    wallet_type: "",
    opening_balance: "",
    description: ""
  });

  // Transaction Management State  
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionForm, setTransactionForm] = useState({
    type: "income",
    description: "",
    amount: "",
    category: "",
    date: new Date().toISOString().split('T')[0]
  });

  // Transaction Log State
  const [walletTransactionLog, setWalletTransactionLog] = useState(null);
  const [loadingTransactionLog, setLoadingTransactionLog] = useState(false);

  const walletTypes = [
    { value: "stripe", label: "Stripe" },
    { value: "paypal", label: "PayPal" },
    { value: "razorpay", label: "Razorpay" },
    { value: "cashapp", label: "Cash App" },
    { value: "venmo", label: "Venmo" },
    { value: "wise", label: "Wise" },
    { value: "revolut", label: "Revolut" },
    { value: "other", label: "Other" }
  ];

  useEffect(() => {
    fetchWallets();
  }, []);

  useEffect(() => {
    if (selectedWallet && activeTab === "transactions") {
      fetchWalletTransactions(selectedWallet.id);
    }
  }, [selectedWallet, activeTab]);

  useEffect(() => {
    if (selectedWallet && activeTab === "transaction-log") {
      fetchWalletTransactionLog(selectedWallet.id);
    }
  }, [selectedWallet, activeTab]);

  const fetchWallets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/online-wallets`);
      setWallets(response.data);
    } catch (error) {
      console.error("Error fetching wallets:", error);
      toast.error("Failed to fetch wallets");
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletTransactions = async (walletId) => {
    try {
      const response = await axios.get(`${API}/online-wallets/${walletId}/transactions`);
      setWalletTransactions(response.data);
    } catch (error) {
      console.error("Error fetching wallet transactions:", error);
      toast.error("Failed to fetch transactions");
    }
  };

  const fetchWalletTransactionLog = async (walletId) => {
    try {
      setLoadingTransactionLog(true);
      const response = await axios.get(`${API}/online-wallets/${walletId}/transaction-log`);
      setWalletTransactionLog(response.data);
    } catch (error) {
      console.error("Error fetching wallet transaction log:", error);
      toast.error("Failed to load transaction log");
    } finally {
      setLoadingTransactionLog(false);
    }
  };

  const downloadWalletTransactionLogCSV = async (walletId) => {
    try {
      const response = await axios.get(`${API}/online-wallets/${walletId}/transaction-log/download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'wallet_transactions.csv';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=([^;]+)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Transaction log downloaded successfully");
    } catch (error) {
      console.error("Error downloading transaction log:", error);
      toast.error("Failed to download transaction log");
    }
  };

  const handleWalletSubmit = async (e) => {
    e.preventDefault();
    try {
      const walletData = {
        name: walletForm.name,
        wallet_type: walletForm.wallet_type,
        opening_balance: parseFloat(walletForm.opening_balance),
        description: walletForm.description || undefined
      };

      if (editingWallet) {
        await axios.put(`${API}/online-wallets/${editingWallet.id}`, walletData);
        toast.success("Wallet updated successfully");
      } else {
        await axios.post(`${API}/online-wallets`, walletData);
        toast.success("Wallet created successfully");
      }

      setIsWalletDialogOpen(false);
      resetWalletForm();
      fetchWallets();
    } catch (error) {
      console.error("Error saving wallet:", error);
      toast.error("Failed to save wallet");
    }
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedWallet) return;

    try {
      const transactionData = {
        wallet_id: selectedWallet.id,
        type: transactionForm.type,
        description: transactionForm.description,
        amount: parseFloat(transactionForm.amount),
        category: transactionForm.category || undefined,
        date: transactionForm.date
      };

      if (editingTransaction) {
        await axios.put(`${API}/online-wallets/${selectedWallet.id}/transactions/${editingTransaction.id}`, transactionData);
        toast.success("Transaction updated successfully");
      } else {
        await axios.post(`${API}/online-wallets/${selectedWallet.id}/transactions`, transactionData);
        toast.success("Transaction added successfully");
      }

      setIsTransactionDialogOpen(false);
      resetTransactionForm();
      fetchWalletTransactions(selectedWallet.id);
      fetchWallets(); // Refresh wallet balances
      
      // Refresh transaction log if currently viewing it
      if (activeTab === "transaction-log") {
        fetchWalletTransactionLog(selectedWallet.id);
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      toast.error("Failed to save transaction");
    }
  };

  const deleteWallet = async (walletId) => {
    if (!window.confirm("Are you sure you want to delete this wallet and all its transactions?")) {
      return;
    }

    try {
      await axios.delete(`${API}/online-wallets/${walletId}`);
      toast.success("Wallet deleted successfully");
      fetchWallets();
      if (selectedWallet && selectedWallet.id === walletId) {
        setSelectedWallet(null);
        setActiveTab("wallets");
      }
    } catch (error) {
      console.error("Error deleting wallet:", error);
      toast.error("Failed to delete wallet");
    }
  };

  const deleteTransaction = async (transactionId) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) {
      return;
    }

    try {
      await axios.delete(`${API}/online-wallets/${selectedWallet.id}/transactions/${transactionId}`);
      toast.success("Transaction deleted successfully");
      fetchWalletTransactions(selectedWallet.id);
      fetchWallets(); // Refresh wallet balances
      
      // Refresh transaction log if currently viewing it
      if (activeTab === "transaction-log") {
        fetchWalletTransactionLog(selectedWallet.id);
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error("Failed to delete transaction");
    }
  };

  const resetWalletForm = () => {
    setWalletForm({
      name: "",
      wallet_type: "",
      opening_balance: "",
      description: ""
    });
    setEditingWallet(null);
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      type: "income",
      description: "",
      amount: "",
      category: "",
      date: new Date().toISOString().split('T')[0]
    });
    setEditingTransaction(null);
  };

  const handleEditWallet = (wallet) => {
    setEditingWallet(wallet);
    setWalletForm({
      name: wallet.name,
      wallet_type: wallet.wallet_type,
      opening_balance: wallet.opening_balance.toString(),
      description: wallet.description || ""
    });
    setIsWalletDialogOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      type: transaction.type,
      description: transaction.description,
      amount: transaction.amount.toString(),
      category: transaction.category || "",
      date: transaction.date
    });
    setIsTransactionDialogOpen(true);
  };

  const getWalletTypeIcon = (type) => {
    const icons = {
      stripe: "💳",
      paypal: "🅿️",
      razorpay: "💰",
      cashapp: "💵",
      venmo: "📱",
      wise: "🌍",
      revolut: "🏦",
      other: "💼"
    };
    return icons[type] || "💼";
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Online Payments</h1>
        <Button 
          onClick={() => setIsWalletDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Wallet
        </Button>
      </div>

      {/* Wallet Selection and Tabs */}
      <div className="mb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="wallets">Wallets Overview</TabsTrigger>
            <TabsTrigger value="transactions" disabled={!selectedWallet}>
              Transactions {selectedWallet && `(${selectedWallet.name})`}
            </TabsTrigger>
            <TabsTrigger value="transaction-log" disabled={!selectedWallet}>
              Transaction Log {selectedWallet && `(${selectedWallet.name})`}
            </TabsTrigger>
          </TabsList>

          {/* Wallets Overview Tab */}
          <TabsContent value="wallets">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                <div className="col-span-full text-center py-8">
                  <p className="text-slate-500">Loading wallets...</p>
                </div>
              ) : wallets.length > 0 ? (
                wallets.map((wallet) => (
                  <Card key={wallet.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">{getWalletTypeIcon(wallet.wallet_type)}</span>
                          <div>
                            <CardTitle className="text-lg">{wallet.name}</CardTitle>
                            <p className="text-sm text-slate-500 capitalize">{wallet.wallet_type}</p>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleEditWallet(wallet)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => deleteWallet(wallet.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Current Balance:</span>
                          <span className={`font-bold ${wallet.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${wallet.current_balance.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Total Income:</span>
                          <span className="text-green-600">${wallet.total_income.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Total Expenses:</span>
                          <span className="text-red-600">${wallet.total_expenses.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Transactions:</span>
                          <span className="text-slate-900">{wallet.transaction_count}</span>
                        </div>
                      </div>
                      <Button 
                        className="w-full mt-4" 
                        variant="outline"
                        onClick={() => {
                          setSelectedWallet(wallet);
                          setActiveTab("transactions");
                        }}
                      >
                        Manage Transactions
                      </Button>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="text-6xl mb-4">💳</div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Wallets Yet</h3>
                  <p className="text-slate-500 mb-4">
                    Create your first online wallet to start tracking payments and transactions
                  </p>
                  <Button onClick={() => setIsWalletDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Wallet
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            {selectedWallet && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Transactions - {selectedWallet.name}</CardTitle>
                      <CardDescription>
                        Manage income and expenses for this wallet
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setIsTransactionDialogOpen(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Transaction
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-medium text-slate-700">Date</th>
                          <th className="text-left p-3 font-medium text-slate-700">Type</th>
                          <th className="text-left p-3 font-medium text-slate-700">Description</th>
                          <th className="text-left p-3 font-medium text-slate-700">Category</th>
                          <th className="text-right p-3 font-medium text-slate-700">Amount</th>
                          <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {walletTransactions.map((transaction, index) => (
                          <tr key={transaction.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                            <td className="p-3 text-sm">
                              {transaction.date}
                            </td>
                            <td className="p-3">
                              <Badge 
                                variant={transaction.type === 'income' ? 'default' : 'destructive'}
                                className={transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                              >
                                {transaction.type === 'income' ? 'Income' : 'Expense'}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm max-w-xs truncate" title={transaction.description}>
                              {transaction.description}
                            </td>
                            <td className="p-3 text-sm">
                              {transaction.category || '-'}
                            </td>
                            <td className="p-3 text-right font-medium">
                              <span className={transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                                {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toFixed(2)}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center space-x-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleEditTransaction(transaction)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => deleteTransaction(transaction.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {walletTransactions.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        No transactions found. Add some income or expenses to get started.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Transaction Log Tab */}
          <TabsContent value="transaction-log">
            {selectedWallet && (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Transaction Log - {selectedWallet.name}</h2>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => fetchWalletTransactionLog(selectedWallet.id)}
                      variant="outline"
                      disabled={loadingTransactionLog}
                    >
                      {loadingTransactionLog ? "Loading..." : "Refresh Log"}
                    </Button>
                    <Button 
                      onClick={() => downloadWalletTransactionLogCSV(selectedWallet.id)}
                      variant="default"
                      disabled={loadingTransactionLog || !walletTransactionLog}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV
                    </Button>
                  </div>
                </div>

                {loadingTransactionLog ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">Loading transaction log...</p>
                    </CardContent>
                  </Card>
                ) : walletTransactionLog ? (
                  <Card>
                    <CardHeader>
                      <CardTitle>Financial Transaction History</CardTitle>
                      <CardDescription>
                        Shows how each income and expense entry affected your wallet balance by date
                      </CardDescription>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="text-sm font-medium text-blue-600">Opening Balance</div>
                          <div className="text-2xl font-bold text-blue-900">
                            ${walletTransactionLog.opening_balance.toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className="text-sm font-medium text-green-600">Current Balance</div>
                          <div className="text-2xl font-bold text-green-900">
                            ${walletTransactionLog.current_balance.toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="text-sm font-medium text-slate-600">Total Transactions</div>
                          <div className="text-2xl font-bold text-slate-900">
                            {walletTransactionLog.total_entries}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full table-auto">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3 font-medium text-slate-700">Date</th>
                              <th className="text-left p-3 font-medium text-slate-700">Type</th>
                              <th className="text-left p-3 font-medium text-slate-700">Description</th>
                              <th className="text-left p-3 font-medium text-slate-700">Category</th>
                              <th className="text-right p-3 font-medium text-slate-700">Amount</th>
                              <th className="text-right p-3 font-medium text-slate-700">Balance Impact</th>
                              <th className="text-right p-3 font-medium text-slate-700">Running Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {walletTransactionLog.transactions.map((transaction, index) => (
                              <tr key={transaction.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                <td className="p-3 text-sm">
                                  {transaction.date}
                                </td>
                                <td className="p-3">
                                  <Badge 
                                    variant={transaction.type === 'income' ? 'default' : 'destructive'}
                                    className={transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                                  >
                                    {transaction.type === 'income' ? 'Income' : 'Expense'}
                                  </Badge>
                                </td>
                                <td className="p-3 text-sm max-w-xs">
                                  <div className="font-medium">{transaction.description}</div>
                                </td>
                                <td className="p-3 text-sm">
                                  {transaction.category || '-'}
                                </td>
                                <td className="p-3 text-right font-medium">
                                  ${transaction.amount.toFixed(2)}
                                </td>
                                <td className="p-3 text-right font-bold">
                                  <span className={transaction.balance_impact >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {transaction.balance_impact >= 0 ? '+' : ''}${transaction.balance_impact.toFixed(2)}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-bold text-slate-900">
                                  ${transaction.running_balance.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {walletTransactionLog.transactions.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          No transactions found. Add some income or expenses to see them here.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">No transaction data available</p>
                      <Button onClick={() => fetchWalletTransactionLog(selectedWallet.id)} className="mt-4">
                        Load Transaction Log
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Wallet Dialog */}
      <Dialog open={isWalletDialogOpen} onOpenChange={setIsWalletDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingWallet ? "Edit Wallet" : "Add New Wallet"}</DialogTitle>
            <DialogDescription>
              {editingWallet ? "Update wallet information" : "Create a new online payment wallet"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleWalletSubmit} className="space-y-4">
            <div>
              <Label htmlFor="wallet-name">Wallet Name</Label>
              <Input
                id="wallet-name"
                value={walletForm.name}
                onChange={(e) => setWalletForm({...walletForm, name: e.target.value})}
                placeholder="e.g., My Stripe Account"
                required
              />
            </div>
            <div>
              <Label htmlFor="wallet-type">Wallet Type</Label>
              <Select 
                value={walletForm.wallet_type} 
                onValueChange={(value) => setWalletForm({...walletForm, wallet_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select wallet type" />
                </SelectTrigger>
                <SelectContent>
                  {walletTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {getWalletTypeIcon(type.value)} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="opening-balance">Opening Balance</Label>
              <Input
                id="opening-balance"
                type="number"
                step="0.01"
                value={walletForm.opening_balance}
                onChange={(e) => setWalletForm({...walletForm, opening_balance: e.target.value})}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label htmlFor="wallet-description">Description (Optional)</Label>
              <Input
                id="wallet-description"
                value={walletForm.description}
                onChange={(e) => setWalletForm({...walletForm, description: e.target.value})}
                placeholder="Additional notes about this wallet"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsWalletDialogOpen(false);
                resetWalletForm();
              }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingWallet ? "Update Wallet" : "Create Wallet"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={isTransactionDialogOpen} onOpenChange={setIsTransactionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTransaction ? "Edit Transaction" : "Add New Transaction"}
            </DialogTitle>
            <DialogDescription>
              {editingTransaction ? "Update transaction details" : `Add a new transaction to ${selectedWallet?.name}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTransactionSubmit} className="space-y-4">
            <div>
              <Label htmlFor="transaction-type">Type</Label>
              <Select 
                value={transactionForm.type} 
                onValueChange={(value) => setTransactionForm({...transactionForm, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="transaction-description">Description</Label>
              <Input
                id="transaction-description"
                value={transactionForm.description}
                onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                placeholder="Transaction description"
                required
              />
            </div>
            <div>
              <Label htmlFor="transaction-amount">Amount</Label>
              <Input
                id="transaction-amount"
                type="number"
                step="0.01"
                value={transactionForm.amount}
                onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label htmlFor="transaction-category">Category (Optional)</Label>
              <Input
                id="transaction-category"
                value={transactionForm.category}
                onChange={(e) => setTransactionForm({...transactionForm, category: e.target.value})}
                placeholder="e.g., Payment processing, Subscription"
              />
            </div>
            <div>
              <Label htmlFor="transaction-date">Date</Label>
              <Input
                id="transaction-date"
                type="date"
                value={transactionForm.date}
                onChange={(e) => setTransactionForm({...transactionForm, date: e.target.value})}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsTransactionDialogOpen(false);
                resetTransactionForm();
              }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingTransaction ? "Update Transaction" : "Add Transaction"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Client Management Component
const Clients = () => {
  const [clients, setClients] = useState([]);
  const [clientsRevenue, setClientsRevenue] = useState([]);
  const [clientsDue, setClientsDue] = useState({});
  const [countries, setCountries] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [clientsPerPage] = useState(50);
  
  // Filter state
  const [filters, setFilters] = useState({
    country: "",
    revenue_min: "",
    revenue_max: "",
    client_name: "",
    status: ""
  });
  
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    country: "",
    address: "",
    status: "Active",
    contacts: [
      {
        id: "",
        name: "",
        email: "",
        phone: "",
        position: "",
        is_primary: true
      }
    ]
  });

  useEffect(() => {
    fetchClientsAndRevenue();
    fetchCountries();
  }, [currentPage, filters]);

  const fetchClientsDueAmounts = async () => {
    try {
      const dueAmounts = {};
      for (const client of clients) {
        const response = await axios.get(`${API}/clients/${client.id}/due-amount`);
        dueAmounts[client.id] = response.data.due_amount;
      }
      setClientsDue(dueAmounts);
    } catch (error) {
      console.error("Error fetching client due amounts:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to load countries");
    }
  };

  const fetchClientsAndRevenue = async () => {
    setLoading(true);
    try {
      // Build query parameters with filters
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: clientsPerPage.toString()
      });
      
      // Add filter parameters if they have values
      if (filters.country && filters.country.trim()) {
        queryParams.append('country', filters.country.trim());
      }
      if (filters.revenue_min && filters.revenue_min.trim()) {
        queryParams.append('revenue_min', filters.revenue_min.trim());
      }
      if (filters.revenue_max && filters.revenue_max.trim()) {
        queryParams.append('revenue_max', filters.revenue_max.trim());
      }
      if (filters.client_name && filters.client_name.trim()) {
        queryParams.append('client_name', filters.client_name.trim());
      }
      if (filters.status && filters.status.trim()) {
        queryParams.append('status', filters.status.trim());
      }
      
      // Fetch both clients and revenue analytics
      const [clientsResponse, revenueResponse] = await Promise.all([
        axios.get(`${API}/clients/paginated?${queryParams.toString()}`),
        axios.get(`${API}/clients/revenue-analytics`)
      ]);
      
      // Handle paginated clients response
      if (clientsResponse.data.clients) {
        setClients(clientsResponse.data.clients);
        setTotalPages(clientsResponse.data.pagination.total_pages);
        setTotalClients(clientsResponse.data.pagination.total_clients);
      } else {
        setClients(clientsResponse.data); // Fallback for non-paginated response
      }
      
      setClientsRevenue(revenueResponse.data.clients);
      
      // Fetch due amounts for current page clients
      const currentClients = clientsResponse.data.clients || clientsResponse.data;
      if (currentClients.length > 0) {
        const dueAmounts = {};
        await Promise.all(
          currentClients.map(async (client) => {
            try {
              const response = await axios.get(`${API}/clients/${client.id}/due-amount`);
              dueAmounts[client.id] = response.data.due_amount;
            } catch (error) {
              console.error(`Error fetching due amount for client ${client.id}:`, error);
              dueAmounts[client.id] = 0;
            }
          })
        );
        setClientsDue(dueAmounts);
      }
    } catch (error) {
      console.error("Error fetching clients and revenue:", error);
      toast.error("Failed to load clients data");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    // Updated to use the combined function
    await fetchClientsAndRevenue();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data for submission
      const submitData = {
        company_name: formData.company_name,
        country: formData.country,
        address: formData.address,
        status: formData.status,
        contacts: formData.contacts.filter(contact => contact.name && contact.email).map(contact => ({
          name: contact.name,
          email: contact.email,
          phone: contact.phone || null,
          position: contact.position || null,
          is_primary: contact.is_primary
        }))
      };

      // Validation: Ensure at least one contact has name and email
      if (submitData.contacts.length === 0) {
        toast.error("Please add at least one contact with name and email");
        return;
      }

      // Ensure at least one contact is marked as primary
      const hasPrimary = submitData.contacts.some(contact => contact.is_primary);
      if (!hasPrimary) {
        submitData.contacts[0].is_primary = true;
      }

      // For backward compatibility, also include legacy fields based on primary contact
      const primaryContact = submitData.contacts.find(contact => contact.is_primary) || submitData.contacts[0];
      submitData.contact_person = primaryContact.name;
      submitData.email = primaryContact.email;
      submitData.phone = primaryContact.phone;

      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, submitData);
        toast.success("Client updated successfully");
      } else {
        await axios.post(`${API}/clients`, submitData);
        toast.success("Client created successfully");
      }
      
      setIsDialogOpen(false);
      setEditingClient(null);
      setFormData({
        company_name: "",
        contact_person: "",
        email: "",
        phone: "",
        country: "",
        address: "",
        status: "Active",
        contacts: [
          {
            id: "",
            name: "",
            email: "",
            phone: "",
            position: "",
            is_primary: true
          }
        ]
      });
      fetchClientsAndRevenue();
    } catch (error) {
      console.error("Error submitting client:", error);
      const errorMessage = error.response?.data?.detail || "Failed to save client";
      toast.error(errorMessage);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    
    // Convert client data to form format, handling both old and new contact structures
    let contacts = [];
    
    if (client.contacts && client.contacts.length > 0) {
      // New format: client has contacts array
      contacts = client.contacts.map(contact => ({
        id: contact.id || Date.now().toString(),
        name: contact.name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        position: contact.position || "",
        is_primary: contact.is_primary || false
      }));
    } else if (client.contact_person && client.email) {
      // Legacy format: convert single contact to contacts array
      contacts = [{
        id: Date.now().toString(),
        name: client.contact_person,
        email: client.email,
        phone: client.phone || "",
        position: "",
        is_primary: true
      }];
    } else {
      // Fallback: create empty contact
      contacts = [{
        id: Date.now().toString(),
        name: "",
        email: "",
        phone: "",
        position: "",
        is_primary: true
      }];
    }
    
    setFormData({
      company_name: client.company_name,
      contact_person: client.contact_person || "", // Keep for legacy compatibility
      email: client.email || "", // Keep for legacy compatibility
      phone: client.phone || "", // Keep for legacy compatibility
      country: client.country,
      address: client.address || "",
      status: client.status || "Active",
      contacts: contacts
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      try {
        await axios.delete(`${API}/clients/${id}`);
        toast.success("Client deleted successfully");
        fetchClients();
      } catch (error) {
        console.error("Error deleting client:", error);
        if (error.response?.status === 400) {
          toast.error("Cannot delete client with associated projects");
        } else {
          toast.error("Failed to delete client");
        }
      }
    }
  };

  const getClientRevenue = (clientId) => {
    const clientRevenue = clientsRevenue.find(c => c.client_id === clientId);
    return clientRevenue || {
      total_revenue: 0,
      project_revenue: 0,
      sales_revenue: 0,
      total_transactions: 0,
      project_count: 0,
      sales_count: 0
    };
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      country: "",
      revenue_min: "",
      revenue_max: "",
      client_name: "",
      status: ""
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(filter => filter && filter.toString().trim());

  // Contact management functions
  const addContact = () => {
    const newContact = {
      id: Date.now().toString(), // Temporary ID for new contacts
      name: "",
      email: "",
      phone: "",
      position: "",
      is_primary: formData.contacts.length === 0 // First contact is primary by default
    };
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, newContact]
    }));
  };

  const removeContact = (contactIndex) => {
    if (formData.contacts.length <= 1) {
      toast.error("Client must have at least one contact");
      return;
    }
    
    const updatedContacts = formData.contacts.filter((_, index) => index !== contactIndex);
    
    // If we removed the primary contact, make the first remaining contact primary
    const hadPrimary = formData.contacts[contactIndex].is_primary;
    if (hadPrimary && updatedContacts.length > 0) {
      updatedContacts[0].is_primary = true;
    }
    
    setFormData(prev => ({
      ...prev,
      contacts: updatedContacts
    }));
  };

  const updateContact = (contactIndex, field, value) => {
    setFormData(prev => {
      const updatedContacts = [...prev.contacts];
      
      // If setting as primary, unset other primary contacts
      if (field === 'is_primary' && value) {
        updatedContacts.forEach((contact, index) => {
          contact.is_primary = index === contactIndex;
        });
      } else {
        updatedContacts[contactIndex] = {
          ...updatedContacts[contactIndex],
          [field]: value
        };
      }
      
      return {
        ...prev,
        contacts: updatedContacts
      };
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Client Management</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingClient ? "Edit Client" : "Add New Client"}
              </DialogTitle>
              <DialogDescription>
                Enter the client details below. Use the compact form to add multiple contacts efficiently.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  required
                />
              </div>
              
              {/* Multiple Contacts Section - Compact Layout */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Company Contacts</Label>
                  <Button
                    type="button"
                    onClick={addContact}
                    className="text-sm bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Contact
                  </Button>
                </div>
                
                {formData.contacts.map((contact, index) => (
                  <Card key={contact.id || index} className="p-3 border-l-4 border-l-blue-500">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Label className="font-medium text-sm">
                          Contact {index + 1}
                          {contact.is_primary && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-semibold">
                              Primary
                            </span>
                          )}
                        </Label>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          onClick={() => updateContact(index, 'is_primary', true)}
                          disabled={contact.is_primary}
                          className="text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 h-6 px-2"
                          size="sm"
                        >
                          Set Primary
                        </Button>
                        {formData.contacts.length > 1 && (
                          <Button
                            type="button"
                            onClick={() => removeContact(index)}
                            className="text-xs bg-red-600 hover:bg-red-700 h-6 px-2"
                            size="sm"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Compact 4-column grid layout */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      <div>
                        <Label htmlFor={`contact-name-${index}`} className="text-xs">Name *</Label>
                        <Input
                          id={`contact-name-${index}`}
                          value={contact.name}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                          placeholder="Contact name"
                          className="h-8 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor={`contact-email-${index}`} className="text-xs">Email *</Label>
                        <Input
                          id={`contact-email-${index}`}
                          type="email"
                          value={contact.email}
                          onChange={(e) => updateContact(index, 'email', e.target.value)}
                          placeholder="contact@company.com"
                          className="h-8 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor={`contact-phone-${index}`} className="text-xs">Phone</Label>
                        <Input
                          id={`contact-phone-${index}`}
                          value={contact.phone}
                          onChange={(e) => updateContact(index, 'phone', e.target.value)}
                          placeholder="+1 555-123-4567"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`contact-position-${index}`} className="text-xs">Position</Label>
                        <Input
                          id={`contact-position-${index}`}
                          value={contact.position}
                          onChange={(e) => updateContact(index, 'position', e.target.value)}
                          placeholder="Manager, Director"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {/* Company Details - Compact 2-column layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="country">Country</Label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="status">Status *</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="min-h-[60px]"
                  placeholder="Company address"
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                {editingClient ? "Update Client" : "Create Client"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Revenue Summary Cards */}
      {clientsRevenue.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Clients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {clientsRevenue.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Revenue Generated</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                ${clients.reduce((sum, c) => sum + (c.total_revenue || 0), 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Active Revenue Clients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {clientsRevenue.filter(c => c.total_revenue > 0).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Average Revenue per Client</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                ${clientsRevenue.length > 0 ? (clientsRevenue.reduce((sum, c) => sum + c.total_revenue, 0) / clientsRevenue.length).toFixed(2) : '0.00'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comprehensive Client Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            Client Filters
            {hasActiveFilters && (
              <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {Object.values(filters).filter(f => f && f.toString().trim()).length} active
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Filter clients by multiple criteria using AND logic
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Client Name Filter */}
            <div>
              <Label htmlFor="filter-client-name">Client Name</Label>
              <Input
                id="filter-client-name"
                type="text"
                placeholder="Search by company name or contact person..."
                value={filters.client_name}
                onChange={(e) => handleFilterChange('client_name', e.target.value)}
              />
            </div>
            
            {/* Country Filter */}
            <div>
              <Label htmlFor="filter-country">Country</Label>
              <select
                id="filter-country"
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">All Countries</option>
                {countries.map(country => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Status Filter */}
            <div>
              <Label htmlFor="filter-status">Status</Label>
              <select
                id="filter-status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            
            {/* Revenue Min Filter */}
            <div>
              <Label htmlFor="filter-revenue-min">Min Revenue ($)</Label>
              <Input
                id="filter-revenue-min"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={filters.revenue_min}
                onChange={(e) => handleFilterChange('revenue_min', e.target.value)}
              />
            </div>
            
            {/* Revenue Max Filter */}
            <div>
              <Label htmlFor="filter-revenue-max">Max Revenue ($)</Label>
              <Input
                id="filter-revenue-max"
                type="number"
                step="0.01"
                placeholder="10000.00"
                value={filters.revenue_max}
                onChange={(e) => handleFilterChange('revenue_max', e.target.value)}
              />
            </div>
            
            {/* Clear Filters Button */}
            <div className="flex items-end">
              <Button 
                onClick={clearFilters} 
                variant="outline" 
                className="w-full"
                disabled={!hasActiveFilters}
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
          
          {hasActiveFilters && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-700">
                <strong>Active Filters:</strong>
                {filters.client_name && <span className="ml-2 bg-white px-2 py-1 rounded">Name: "{filters.client_name}"</span>}
                {filters.country && <span className="ml-2 bg-white px-2 py-1 rounded">Country: {filters.country}</span>}
                {filters.status && <span className="ml-2 bg-white px-2 py-1 rounded">Status: {filters.status}</span>}
                {filters.revenue_min && <span className="ml-2 bg-white px-2 py-1 rounded">Min Revenue: ${filters.revenue_min}</span>}
                {filters.revenue_max && <span className="ml-2 bg-white px-2 py-1 rounded">Max Revenue: ${filters.revenue_max}</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Client Management</span>
          </CardTitle>
          <CardDescription>
            View all clients with basic information and revenue overview
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-medium text-slate-700">Client Name</th>
                  <th className="text-left p-3 font-medium text-slate-700">Contact Person</th>
                  <th className="text-left p-3 font-medium text-slate-700">Country</th>
                  <th className="text-center p-3 font-medium text-slate-700">Status</th>
                  <th className="text-right p-3 font-medium text-slate-700">Total Revenue</th>
                  <th className="text-right p-3 font-medium text-slate-700">Total Paid</th>
                  <th className="text-right p-3 font-medium text-slate-700">Due</th>
                  <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-slate-500">
                      Loading clients and revenue data...
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-slate-500">
                      No clients found. Create your first client to get started.
                    </td>
                  </tr>
                ) : (
                  clients.map((client, index) => {
                    // Use the total_revenue and total_paid from the updated aggregation pipeline
                    const totalRevenue = client.total_revenue || 0;
                    const totalPaid = client.total_paid || 0;
                    const dueAmount = client.due_amount || (clientsDue[client.id] || 0);
                    return (
                      <tr key={client.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100 transition-colors`}>
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{client.company_name}</div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center">
                            <UserCheck className="h-4 w-4 mr-2 text-slate-400" />
                            <div>
                              <div className="text-slate-700 font-medium">
                                {client.contact_person || client.contacts?.[0]?.name || 'No contact'}
                              </div>
                              {client?.contacts && client.contacts.length > 1 && (
                                <div className="text-xs text-slate-500">
                                  +{client.contacts.length - 1} more contact{client.contacts.length > 2 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center">
                            <Globe className="h-4 w-4 mr-2 text-slate-400" />
                            <span className="text-slate-600">{client.country}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            (client.status || 'Active') === 'Active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {client.status || 'Active'}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className={`font-bold text-lg ${totalRevenue > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                            ${totalRevenue.toFixed(2)}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className={`font-bold text-lg ${totalPaid > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                            ${totalPaid.toFixed(2)}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className={`font-medium ${dueAmount > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                            ${dueAmount.toFixed(2)}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.location.href = `/clients/${client.id}`}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(client)}
                              title="Edit Client"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(client.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Client"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => setCurrentPage(page)}
        totalItems={totalClients}
        itemsPerPage={clientsPerPage}
        itemName="clients"
      />
    </div>
  );
};

// Weekly Planner Component
const WeeklyPlanner = () => {
  const [weeklyTasks, setWeeklyTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);

  useEffect(() => {
    fetchWeeklyData();
  }, []);

  const fetchWeeklyData = async () => {
    try {
      setLoading(true);
      
      // Get current week start (Monday)
      const now = new Date();
      const dayOfWeek = now.getDay();
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
      const weekStart = new Date(now.setDate(diff));
      weekStart.setHours(0, 0, 0, 0);
      setCurrentWeekStart(weekStart);

      // Fetch all data
      const [employeesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/projects`)
      ]);

      setEmployees(employeesRes.data);
      setProjects(projectsRes.data);

      // Process task assignments for the week
      const weeklyTaskData = [];
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      for (const project of projectsRes.data) {
        const taskAssignments = project.task_assignments || [];
        
        for (const assignment of taskAssignments) {
          const startDate = new Date(assignment.start_date);
          const endDate = new Date(assignment.end_date);
          
          // Check if task overlaps with this week
          if (endDate >= weekStart && startDate <= weekEnd) {
            weeklyTaskData.push({
              ...assignment,
              project_name: project.name,
              project_id: project.id,
              start_date: startDate,
              end_date: endDate
            });
          }
        }
      }

      setWeeklyTasks(weeklyTaskData);
    } catch (error) {
      console.error("Error fetching weekly data:", error);
      toast.error("Failed to load weekly planner data");
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    if (!currentWeekStart) return [];
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getTasksForDay = (date) => {
    return weeklyTasks.filter(task => {
      const taskStartDate = new Date(task.start_date);
      const taskEndDate = new Date(task.end_date);
      
      // Check if the date falls within the task's date range
      return date >= taskStartDate && date <= taskEndDate;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      "To Do": "bg-gray-100 text-gray-800 border-gray-300",
      "In Progress": "bg-blue-100 text-blue-800 border-blue-300", 
      "Review": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "Completed": "bg-green-100 text-green-800 border-green-300"
    };
    return colors[status] || colors["To Do"];
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newWeekStart);
    
    // Refetch data for new week
    fetchWeeklyData();
  };

  const goToCurrentWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const weekStart = new Date(now.setDate(diff));
    weekStart.setHours(0, 0, 0, 0);
    setCurrentWeekStart(weekStart);
    fetchWeeklyData();
  };

  // Employee statistics helper function
  const getEmployeeStatistics = () => {
    const employeeStats = {};
    
    // Initialize stats for all employees who have tasks this week
    weeklyTasks.forEach(task => {
      if (!employeeStats[task.employee_id]) {
        const employee = employees.find(emp => emp.id === task.employee_id);
        employeeStats[task.employee_id] = {
          id: task.employee_id,
          name: employee?.name || 'Unknown Employee',
          email: employee?.email || '',
          taskCount: 0,
          projects: new Set()
        };
      }
      
      employeeStats[task.employee_id].taskCount += 1;
      employeeStats[task.employee_id].projects.add(task.project_id);
    });

    // Convert to array and add project count
    return Object.values(employeeStats).map(stat => ({
      ...stat,
      projectCount: stat.projects.size,
      projects: undefined // Remove the Set object for cleaner data
    })).sort((a, b) => b.taskCount - a.taskCount); // Sort by task count descending
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading weekly planner...</div>
        </div>
      </div>
    );
  }

  const weekDays = getWeekDays();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Main content container with max width */}
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Weekly Planner</h1>
            <p className="text-gray-600 mt-1">
              {currentWeekStart && format(currentWeekStart, "MMMM dd")} - {currentWeekStart && format(new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000), "MMMM dd, yyyy")}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous Week
            </Button>
            <Button variant="outline" onClick={goToCurrentWeek}>
              Current Week
            </Button>
            <Button variant="outline" onClick={() => navigateWeek(1)}>
              Next Week
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Weekly Calendar */}
        <Card>
          <CardHeader>
            <CardTitle>Employee Task Schedule</CardTitle>
            <CardDescription>Tasks scheduled for each day of the week by employee</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-4">
              {weekDays.map((day, index) => {
                const dayTasks = getTasksForDay(day);
                const isToday = day.toDateString() === new Date().toDateString();
                
                return (
                  <div key={index} className={`min-h-96 p-4 rounded-lg border ${isToday ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                    {/* Day Header */}
                    <div className="text-center mb-4">
                      <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                        {format(day, "EEEE")}
                      </div>
                      <div className={`text-lg font-bold ${isToday ? 'text-blue-800' : 'text-gray-900'}`}>
                        {format(day, "dd")}
                      </div>
                      {dayTasks.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    {/* Tasks for the day */}
                    <div className="space-y-2">
                      {dayTasks.length > 0 ? (
                        dayTasks.map((task, taskIndex) => {
                          const employee = employees.find(emp => emp.id === task.employee_id);
                          
                          return (
                            <div key={taskIndex} className={`p-3 rounded-lg border text-xs ${getStatusColor(task.status)}`}>
                              {/* Employee info */}
                              <div className="flex items-center space-x-2 mb-2">
                                <div className="w-5 h-5 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                  {employee?.name?.charAt(0) || 'U'}
                                </div>
                                <span className="font-medium truncate">{employee?.name || 'Unknown'}</span>
                              </div>
                              
                              {/* Task info */}
                              <div className="space-y-1">
                                <div className="font-medium text-gray-900 leading-tight">
                                  {task.task_name}
                                </div>
                                <div className="text-gray-600 text-xs">
                                  Project: {task.project_name}
                                </div>
                                <div className="text-gray-600 text-xs">
                                  Duration: {format(new Date(task.start_date), "MMM dd")} - {format(new Date(task.end_date), "MMM dd")}
                                </div>
                                <div className="flex items-center justify-between">
                                  <Badge className={`text-xs ${getStatusColor(task.status)}`}>
                                    {task.status || 'To Do'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8 text-gray-400 text-sm">
                          No tasks scheduled
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Summary Section */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Total Tasks This Week</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{weeklyTasks.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Active Employees</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {new Set(weeklyTasks.map(task => task.employee_id)).size}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Projects Involved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {new Set(weeklyTasks.map(task => task.project_id)).size}
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Employee Statistics Table - Same Width as Calendar */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Employee Workload Summary</CardTitle>
            <CardDescription>Task and project distribution by employee for this week</CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const employeeStats = getEmployeeStatistics();
              
              if (employeeStats.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    No employee assignments for this week
                  </div>
                );
              }

              return (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tasks This Week
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Projects Involved
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Workload Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {employeeStats.map((employee, index) => {
                        // Determine workload status based on task count
                        const getWorkloadStatus = (taskCount) => {
                          if (taskCount >= 5) return { label: 'High', color: 'bg-red-100 text-red-700' };
                          if (taskCount >= 3) return { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' };
                          if (taskCount >= 1) return { label: 'Light', color: 'bg-green-100 text-green-700' };
                          return { label: 'None', color: 'bg-gray-100 text-gray-700' };
                        };

                        const workloadStatus = getWorkloadStatus(employee.taskCount);

                        return (
                          <tr key={employee.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">
                                  {employee.name.charAt(0)}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {employee.name}
                                  </div>
                                  {employee.email && (
                                    <div className="text-sm text-gray-500">
                                      {employee.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex flex-col items-center">
                                <span className="text-2xl font-bold text-gray-900">
                                  {employee.taskCount}
                                </span>
                                <span className="text-xs text-gray-500">
                                  task{employee.taskCount !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex flex-col items-center">
                                <span className="text-xl font-semibold text-gray-900">
                                  {employee.projectCount}
                                </span>
                                <span className="text-xs text-gray-500">
                                  project{employee.projectCount !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <Badge className={`${workloadStatus.color} text-xs font-medium px-2 py-1`}>
                                {workloadStatus.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const UserRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Admin'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    appName: 'BizManager',
    logo: null
  });
  
  const navigate = useNavigate();
  const { register } = useAuth();

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/general`);
      setSettings({
        appName: response.data.appName || 'BizManager',
        logo: response.data.logo ? `${API}/settings/logo` : null
      });
    } catch (error) {
      console.error("Error fetching app settings:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (formData.password.length < 6) {
      setMessage('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const result = await register(formData);
      
      if (result.success) {
        setSuccess(true);
        setMessage('User registered successfully! You can now login.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage(result.error);
      }
    } catch (error) {
      setMessage('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              {settings.logo ? (
                <img
                  src={settings.logo}
                  alt="Logo"
                  className="w-16 h-16 object-contain mx-auto"
                />
              ) : (
                <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">{settings.appName}</CardTitle>
            <CardDescription className="text-slate-600">
              Create New User Account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="p-4 bg-green-100 border border-green-300 text-green-700 rounded">
                  {message}
                </div>
                <p className="text-sm text-gray-600">
                  Redirecting to login page...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password (min 6 characters)"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => handleInputChange('role', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {message && (
                  <div className={`p-3 rounded text-sm ${
                    success 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {message}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                    onClick={() => navigate('/login')}
                  >
                    Already have an account? Login
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    appName: 'BizManager',
    logo: null
  });
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/general`);
      setSettings({
        appName: response.data.appName || 'BizManager',
        logo: response.data.logo ? `${API}/settings/logo` : null
      });
    } catch (error) {
      console.error("Error fetching app settings:", error);
    }
  };

  const getTokenFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get('token');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long');
      return;
    }

    const token = getTokenFromURL();
    if (!token) {
      setMessage('Invalid reset link');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      setSuccess(true);
      setMessage('Your password has been reset successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              {settings.logo ? (
                <img
                  src={settings.logo}
                  alt="Logo"
                  className="w-16 h-16 object-contain mx-auto"
                />
              ) : (
                <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">{settings.appName}</CardTitle>
            <CardDescription className="text-slate-600">
              Create Your New Password
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="p-4 bg-green-100 border border-green-300 text-green-700 rounded">
                  {message}
                </div>
                <p className="text-sm text-gray-600">
                  Redirecting to login page in 3 seconds...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                {message && (
                  <div className={`p-3 rounded text-sm ${
                    success 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {message}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                    onClick={() => navigate('/login')}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-slate-50">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<UserRegistration />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <Navigation />
                <div className="ml-64">
                  <div className="min-h-screen">
                    <Routes>
                      <Route path="/" element={<DashboardRouter />} />
                      <Route path="/leads/list" element={<LeadsList />} />
                      <Route path="/leads/status" element={<LeadStatusManagement />} />
                      <Route path="/leads/:id" element={<LeadDetail />} />
                      <Route path="/leads" element={<LeadsList />} />
                      <Route path="/clients" element={<Clients />} />
                      <Route path="/clients/:id" element={<ClientProfile />} />
                      <Route path="/employees" element={<Employees />} />
                      <Route path="/employees/:id" element={<EmployeeProfile />} />
                      <Route path="/employee-profile/:id" element={
                        <EmployeeRoute>
                          <EmployeeProfileRestricted />
                        </EmployeeRoute>
                      } />
                      <Route path="/my-profile" element={
                        <EmployeeRoute>
                          <EmployeeProfileRestricted />
                        </EmployeeRoute>
                      } />
                      <Route path="/my-projects" element={
                        <EmployeeRoute>
                          <EmployeeMyProjects />
                        </EmployeeRoute>
                      } />
                      <Route path="/my-tasks" element={
                        <EmployeeRoute>
                          <EmployeeMyTasks />
                        </EmployeeRoute>
                      } />
                      <Route path="/my-settings" element={
                        <EmployeeRoute>
                          <EmployeeSettings />
                        </EmployeeRoute>
                      } />
                      
                      {/* Manager Routes */}
                      <Route path="/manager-dashboard" element={
                        <ManagerRoute>
                          <ManagerDashboard />
                        </ManagerRoute>
                      } />
                      <Route path="/manager-team" element={
                        <ManagerRoute>
                          <ManagerTeam />
                        </ManagerRoute>
                      } />
                      <Route path="/manager-projects" element={
                        <ManagerRoute>
                          <ManagerProjects />
                        </ManagerRoute>
                      } />
                      <Route path="/manager-tasks" element={
                        <ManagerRoute>
                          <ManagerTasks />
                        </ManagerRoute>
                      } />
                      <Route path="/manager-settings" element={
                        <ManagerRoute>
                          <ManagerSettings />
                        </ManagerRoute>
                      } />
                      
                      {/* Client Routes */}
                      <Route path="/client-dashboard" element={
                        <ClientRoute>
                          <ClientDashboard />
                        </ClientRoute>
                      } />
                      <Route path="/client-projects" element={
                        <ClientRoute>
                          <ClientProjects />
                        </ClientRoute>
                      } />
                      <Route path="/client-projects/:id" element={
                        <ClientRoute>
                          <ProjectDetail />
                        </ClientRoute>
                      } />
                      <Route path="/client-projects/:projectId/tasks/:taskIndex" element={
                        <ClientRoute>
                          <ProjectTaskViewEnhanced />
                        </ClientRoute>
                      } />
                      <Route path="/client-tasks" element={
                        <ClientRoute>
                          <ClientTasks />
                        </ClientRoute>
                      } />
                      <Route path="/client-invoices" element={
                        <ClientRoute>
                          <ClientInvoices />
                        </ClientRoute>
                      } />
                      <Route path="/client-support" element={
                        <ClientRoute>
                          <ClientSupport />
                        </ClientRoute>
                      } />
                      <Route path="/client-support/:ticketId" element={
                        <ClientRoute>
                          <SupportTicketDetailEnhanced />
                        </ClientRoute>
                      } />
                      <Route path="/client-settings" element={
                        <ClientRoute>
                          <ClientSettings />
                        </ClientRoute>
                      } />
                      
                      <Route path="/dashboard" element={
                        <EmployeeRoute>
                          <EmployeeDashboard />
                        </EmployeeRoute>
                      } />
                      <Route path="/projects" element={<Projects />} />
                      <Route path="/projects/weekly-planner" element={<WeeklyPlanner />} />
                      <Route path="/projects/:id" element={<ProjectDetail />} />
                      <Route path="/finances/management" element={<Finances />} />
                      <Route path="/finances/categories" element={<Categories />} />
                      <Route path="/finances/online-payments" element={<OnlinePayments />} />
                      <Route path="/finances" element={<Finances />} />
                      <Route path="/sales/products" element={<SalesProducts />} />
                      <Route path="/sales/sales" element={<SalesTransactions />} />
                      <Route path="/sales/expenses" element={<SalesExpenses />} />
                      <Route path="/sales/invoices" element={<InvoiceManagement />} />
                      <Route path="/sales" element={<Sales />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/communications/mails" element={<CommunicationsMails />} />
                      <Route path="/communications/templates" element={<CommunicationsTemplates />} />
                      <Route path="/communications/marketing" element={<CommunicationsMarketing />} />
                      <Route path="/communications/chat" element={<CommunicationsChat />} />
                      <Route path="/communications" element={<CommunicationsTemplates />} />
                      <Route path="/settings/general" element={<GeneralSettings />} />
                      <Route path="/settings/team-types" element={<TeamTypes />} />
          <Route path="/support/tickets" element={
            <AdminManagerRoute>
              <SupportTickets />
            </AdminManagerRoute>
          } />
          <Route path="/support/tickets/add" element={
            <AdminManagerRoute>
              <AddSupportTicket />
            </AdminManagerRoute>
          } />
          <Route path="/support/tickets/:ticketId" element={
            <AdminManagerRoute>
              <SupportTicketDetailEnhanced />
            </AdminManagerRoute>
          } />
          <Route path="/support/tasks" element={
            <AdminManagerRoute>
              <UnifiedTasks />
            </AdminManagerRoute>
          } />
          <Route path="/projects/:projectId/tasks/:taskIndex" element={<ProjectTaskViewEnhanced />} />
                      <Route path="/settings/roles" element={<RoleManagement />} />
                      <Route path="/settings/language" element={<LanguageManagement />} />
                      <Route path="/settings/admin" element={<AdminSettings />} />
                      <Route path="/settings/reset-db" element={<ResetDB />} />
                      <Route path="/settings" element={<GeneralSettings />} />
                    </Routes>
                  </div>
                </div>
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Client Profile Component
const ClientProfile = () => {
  const [clientProfile, setClientProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [whatsappData, setWhatsappData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [clientDueData, setClientDueData] = useState(null);
  const { id: clientId } = useParams();
  
  // Documents and Bank Info states
  const [documents, setDocuments] = useState([]);
  const [bankInfo, setBankInfo] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [bankForm, setBankForm] = useState({
    bank_name: "",
    additional_info: ""
  });

  // Fetch all necessary data when component mounts
  useEffect(() => {
    if (clientId) {
      fetchClientProfile();
      fetchProducts();
      fetchWhatsAppData();
      fetchClientDocuments();
      fetchClientBankInfo();
      fetchPaymentHistory();
      fetchClientDueData();
    }
  }, [clientId]);

  const fetchClientProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/${clientId}/revenue-details`);
      setClientProfile(response.data);
    } catch (error) {
      console.error("Error fetching client profile:", error);
      toast.error("Failed to load client profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsAppData = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/whatsapp-number`);
      setWhatsappData(response.data);
    } catch (error) {
      console.error("Error fetching WhatsApp data:", error);
      // Don't show error toast as WhatsApp might not be available for all clients
    }
  };

  // Fetch comprehensive payment history
  const fetchPaymentHistory = async () => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/clients/${clientId}/payment-history`);
      setPaymentHistory(response.data.payment_history || []);
      setPaymentSummary(response.data.summary || null);
    } catch (error) {
      console.error("Error fetching payment history:", error);
      setPaymentHistory([]);
      setPaymentSummary(null);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Fetch client due amount data
  const fetchClientDueData = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/due-amount`);
      setClientDueData(response.data);
    } catch (error) {
      console.error("Error fetching client due data:", error);
      setClientDueData(null);
    }
  };

  const handleWhatsAppClick = () => {
    if (whatsappData && whatsappData.whatsapp_url) {
      window.open(whatsappData.whatsapp_url, '_blank');
    }
  };

  // Individual contact WhatsApp functionality using GSMA/ITU standards
  const handleContactWhatsAppClick = (phone, countryCode) => {
    try {
      // Create full phone number with country code
      let fullPhoneNumber;
      
      // Handle different input formats
      if (phone.startsWith('+')) {
        // Phone already includes country code
        fullPhoneNumber = phone;
      } else if (phone.startsWith('0')) {
        // Remove leading zero and add country code
        const cleanPhone = phone.substring(1);
        fullPhoneNumber = `${countryCode}${cleanPhone}`;
      } else {
        // Add country code directly
        fullPhoneNumber = `${countryCode}${phone}`;
      }
      
      // Ensure country code starts with '+'
      if (!fullPhoneNumber.startsWith('+')) {
        fullPhoneNumber = `+${fullPhoneNumber}`;
      }
      
      // Parse and validate using GSMA/ITU standards
      const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
      
      if (!phoneNumber) {
        throw new Error('Invalid phone number format');
      }
      
      // Validate the phone number
      if (!isValidPhoneNumber(fullPhoneNumber)) {
        throw new Error('Phone number is not valid according to GSMA/ITU standards');
      }
      
      // Get the properly formatted E.164 number
      const e164Number = phoneNumber.format('E.164');
      
      // Create WhatsApp URL (remove the + from E.164 for wa.me format)
      const whatsappUrl = `https://wa.me/${e164Number.replace('+', '')}`;
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
      
      console.log(`✅ WhatsApp opened for validated number: ${e164Number}`);
      console.log(`Country: ${phoneNumber.country}, Type: ${phoneNumber.getType()}`);
      
    } catch (error) {
      console.error('❌ WhatsApp error:', error.message);
      
      // Fallback: Try simple concatenation if GSMA/ITU validation fails
      try {
        console.log('🔄 Attempting fallback WhatsApp method...');
        const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
        const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
        const fallbackNumber = `${formattedCountryCode}${cleanPhone}`;
        const fallbackUrl = `https://wa.me/${fallbackNumber.replace('+', '')}`;
        
        window.open(fallbackUrl, '_blank');
        console.log(`⚠️ WhatsApp opened with fallback method: ${fallbackNumber}`);
        
        toast.warning(`WhatsApp opened with basic formatting. For best results, please verify the phone number format.`);
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError.message);
        toast.error(`Failed to open WhatsApp: ${error.message}`);
      }
    }
  };

  // Format phone number for display using GSMA/ITU standards
  const formatPhoneForDisplay = (phone, countryCode) => {
    try {
      // Create full phone number
      let fullPhoneNumber;
      
      if (phone.startsWith('+')) {
        fullPhoneNumber = phone;
      } else if (phone.startsWith('0')) {
        const cleanPhone = phone.substring(1);
        fullPhoneNumber = `${countryCode}${cleanPhone}`;
      } else {
        fullPhoneNumber = `${countryCode}${phone}`;
      }
      
      if (!fullPhoneNumber.startsWith('+')) {
        fullPhoneNumber = `+${fullPhoneNumber}`;
      }
      
      // Parse and format using GSMA/ITU standards
      const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
      
      if (phoneNumber && isValidPhoneNumber(fullPhoneNumber)) {
        // Return international format for display
        return phoneNumber.formatInternational();
      }
    } catch (error) {
      console.log('Phone formatting error:', error.message);
    }
    
    // Fallback to simple format
    const formattedCountryCode = countryCode?.startsWith('+') ? countryCode : `+${countryCode}`;
    return `${formattedCountryCode} ${phone}`;
  };

  // Fetch client documents
  const fetchClientDocuments = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      // Don't show error toast as documents might not exist
    }
  };

  // Fetch client bank info
  const fetchClientBankInfo = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/bank-info`);
      setBankInfo(response.data);
      setBankForm({
        bank_name: response.data.bank_name || "",
        additional_info: response.data.additional_info || ""
      });
    } catch (error) {
      console.error("Error fetching bank info:", error);
      // Don't show error toast as bank info might not exist
    }
  };

  // Handle document upload
  const handleDocumentUpload = async () => {
    if (!selectedFile || !documentTitle.trim()) {
      toast.error("Please select a file and provide a title");
      return;
    }

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("title", documentTitle);

      await axios.post(`${API}/clients/${clientId}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Document uploaded successfully");
      setSelectedFile(null);
      setDocumentTitle("");
      fetchClientDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingDocument(false);
    }
  };

  // Handle document delete
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await axios.delete(`${API}/clients/${clientId}/documents/${documentId}`);
      toast.success("Document deleted successfully");
      fetchClientDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  // Handle bank info save
  const handleSaveBankInfo = async () => {
    if (!bankForm.bank_name.trim()) {
      toast.error("Please provide a bank name");
      return;
    }

    try {
      await axios.post(`${API}/clients/${clientId}/bank-info`, bankForm);
      toast.success("Bank information saved successfully");
      fetchClientBankInfo();
    } catch (error) {
      console.error("Error saving bank info:", error);
      toast.error("Failed to save bank information");
    }
  };

  // Notes functionality
  const [newNote, setNewNote] = useState("");

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      await axios.post(`${API}/clients/${clientId}/notes`, {
        note: newNote.trim()
      });
      
      toast.success("Note added successfully");
      setNewNote("");
      // Refresh client profile to get updated notes
      await fetchClientProfile();
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : "Unknown Product";
  };

  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-lg text-slate-600">Loading client profile...</div>
        </div>
      </div>
    );
  }

  if (!clientProfile) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-lg text-red-600">Client not found</div>
          <Button 
            onClick={() => window.location.href = '/clients'} 
            className="mt-4"
          >
            Back to Clients
          </Button>
        </div>
      </div>
    );
  }

  const { client, projects, project_revenues, sales, summary } = clientProfile || {};

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{client.company_name}</h1>
          <p className="text-slate-600 mt-1">Client Profile & Revenue Details</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/clients'}
          className="flex items-center space-x-2"
        >
          <Building2 className="h-4 w-4" />
          <span>Back to Clients</span>
        </Button>
      </div>

      {/* Client Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Total Revenue Generated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              ${(summary?.total_revenue || 0).toFixed(2)}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Project Budget + Sales
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {summary?.project_count || 0}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              ${(summary?.project_revenue_entries || 0).toFixed(2)} project revenue entries
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Sales Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {summary?.sales_transactions || 0}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              ${(summary?.sales_revenue || 0).toFixed(2)} sales revenue
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="overview">Client Info</TabsTrigger>
          <TabsTrigger value="projects">Projects ({projects?.length || 0})</TabsTrigger>
          <TabsTrigger value="sales">Sales ({sales?.length || 0})</TabsTrigger>
          <TabsTrigger value="revenue">Revenue History</TabsTrigger>
          <TabsTrigger value="notes">Notes ({client?.client_notes ? client.client_notes.length : 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="bankinfo">Bank Info</TabsTrigger>
        </TabsList>

        {/* Client Info Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Company Contacts
                  {client?.contacts && client.contacts.length > 1 && (
                    <span className="text-sm font-normal bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {client.contacts.length} contacts
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Handle both new contacts array and legacy single contact */}
                  {client.contacts && client.contacts.length > 0 ? (
                    // New format: multiple contacts
                    client.contacts.map((contact, index) => (
                      <div key={contact.id || index} className={`${index > 0 ? 'border-t pt-6' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <UserCheck className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-slate-900">{contact.name}</span>
                            {contact.is_primary && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                                Primary
                              </span>
                            )}
                          </div>
                          {contact.position && (
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                              {contact.position}
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-3 ml-6">
                          {/* Email */}
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-3 text-slate-400" />
                            <a 
                              href={`mailto:${contact.email}`}
                              className="text-slate-700 hover:text-blue-600 transition-colors"
                            >
                              {contact.email}
                            </a>
                          </div>
                          
                          {/* Phone with WhatsApp */}
                          {contact.phone && (
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-3 text-slate-400" />
                              <span className="text-slate-700">{formatPhoneForDisplay(contact.phone, client.country_code)}</span>
                              <button
                                onClick={() => handleContactWhatsAppClick(contact.phone, client.country_code)}
                                className="ml-2 p-1 rounded-full hover:bg-green-100 transition-colors"
                                title={`Open WhatsApp: ${formatPhoneForDisplay(contact.phone, client.country_code)}`}
                              >
                                <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    // Legacy format: single contact fallback
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <UserCheck className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-slate-900">{client.contact_person}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                          Primary
                        </span>
                      </div>
                      
                      <div className="space-y-3 ml-6">
                        {/* Email */}
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-3 text-slate-400" />
                          <a 
                            href={`mailto:${client.email}`}
                            className="text-slate-700 hover:text-blue-600 transition-colors"
                          >
                            {client.email}
                          </a>
                        </div>
                        
                        {/* Phone with WhatsApp */}
                        {client.phone && (
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-3 text-slate-400" />
                            <span className="text-slate-700">{formatPhoneForDisplay(client.phone, client.country_code)}</span>
                            <button
                              onClick={() => handleContactWhatsAppClick(client.phone, client.country_code)}
                              className="ml-2 p-1 rounded-full hover:bg-green-100 transition-colors"
                              title={`Open WhatsApp: ${formatPhoneForDisplay(client.phone, client.country_code)}`}
                            >
                              <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Company Name</label>
                    <div className="text-lg font-semibold text-slate-900">{client.company_name}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Status</label>
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        (client.status || 'Active') === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {client.status || 'Active'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Country</label>
                    <div className="text-slate-900 flex items-center">
                      <Globe className="h-4 w-4 mr-2" />
                      {client.country}
                    </div>
                  </div>
                  {client.address && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">Address</label>
                      <div className="text-slate-900 flex items-start">
                        <MapPin className="h-4 w-4 mr-2 mt-0.5" />
                        <span>{client.address}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-emerald-800">Total Revenue</div>
                      <div className="text-2xl font-bold text-emerald-600">${(summary?.total_revenue || 0).toFixed(2)}</div>
                    </div>
                    <div className="text-emerald-600">
                      <DollarSign className="h-8 w-8" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Project Revenue:</span>
                      <span className="font-medium text-blue-600">${(summary?.project_revenue_entries || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Sales Revenue:</span>
                      <span className="font-medium text-purple-600">${(summary?.sales_revenue || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Total Transactions:</span>
                      <span className="font-medium">{summary.revenue_transactions + summary.sales_transactions}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <CardTitle>Associated Projects</CardTitle>
              <CardDescription>
                All projects associated with {client.company_name} and their revenue contribution
              </CardDescription>
            </CardHeader>
            <CardContent>
              {projects.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium text-slate-700">Project Name</th>
                        <th className="text-left p-3 font-medium text-slate-700">Budget</th>
                        <th className="text-left p-3 font-medium text-slate-700">Billing Type</th>
                        <th className="text-left p-3 font-medium text-slate-700">Status</th>
                        <th className="text-right p-3 font-medium text-slate-700">Revenue Generated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project, index) => {
                        const projectRevenue = project_revenues
                          .filter(rev => rev.project_id === project.id)
                          .reduce((sum, rev) => sum + rev.amount, 0);
                        
                        return (
                          <tr key={project.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                            <td className="p-3">
                              <div>
                                <div className="font-medium text-slate-900">{project.name}</div>
                                {project.description && (
                                  <div className="text-sm text-slate-500">{project.description}</div>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="font-medium">${(project.budget || 0).toFixed(2)}</div>
                            </td>
                            <td className="p-3">
                              <Badge variant="outline">
                                {project.billing_type.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
                                {project.status.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="p-3 text-right">
                              <div className="font-bold text-blue-600">${(projectRevenue || 0).toFixed(2)}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No projects associated with this client yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales">
          <Card>
            <CardHeader>
              <CardTitle>Sales Transactions</CardTitle>
              <CardDescription>
                Direct sales to {client.company_name} including subscription revenues
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sales.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium text-slate-700">Date</th>
                        <th className="text-left p-3 font-medium text-slate-700">Product</th>
                        <th className="text-left p-3 font-medium text-slate-700">Type</th>
                        <th className="text-left p-3 font-medium text-slate-700">Next Billing</th>
                        <th className="text-right p-3 font-medium text-slate-700">Amount</th>
                        <th className="text-left p-3 font-medium text-slate-700">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map((sale, index) => (
                        <tr key={sale.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                          <td className="p-3">{sale.date}</td>
                          <td className="p-3 font-medium">{getProductName(sale.product_id)}</td>
                          <td className="p-3">
                            <Badge variant={sale.pricing_type === 'subscription' ? 'default' : 'secondary'}>
                              {sale.pricing_type.toUpperCase()}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {sale.next_billing_date ? (
                              <div>
                                <div className="text-sm font-medium">{sale.next_billing_date}</div>
                                {sale.pricing_type === 'subscription' && (
                                  <div className="text-xs text-slate-500">
                                    Recurring Revenue
                                  </div>
                                )}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-purple-600">${(sale.sale_price || 0).toFixed(2)}</div>
                          </td>
                          <td className="p-3 max-w-xs truncate" title={sale.notes}>
                            {sale.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No sales transactions found for this client.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader>
              <CardTitle>Payment History & Revenue Breakdown</CardTitle>
              <CardDescription>
                Comprehensive payment history from projects and sales transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Revenue Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm font-medium text-slate-600">Total Revenue</div>
                    <div className="text-2xl font-bold text-emerald-600">
                      ${(clientDueData?.total_revenue || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">Project Budgets + Sales</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm font-medium text-slate-600">Total Paid</div>
                    <div className="text-2xl font-bold text-blue-600">
                      ${(clientDueData?.total_paid || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">Project Payments + Sales</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm font-medium text-slate-600">Due</div>
                    <div className={`text-2xl font-bold ${(clientDueData?.due_amount || 0) > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                      ${(clientDueData?.due_amount || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(clientDueData?.payment_percentage || 0).toFixed(1)}% paid
                    </div>
                  </CardContent>
                </Card>
              </div>

              {loadingPayments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-slate-600">Loading payment history...</p>
                </div>
              ) : paymentHistory && paymentHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium text-slate-700">Date</th>
                        <th className="text-left p-3 font-medium text-slate-700">Source</th>
                        <th className="text-left p-3 font-medium text-slate-700">Description</th>
                        <th className="text-right p-3 font-medium text-slate-700">Amount</th>
                        <th className="text-left p-3 font-medium text-slate-700">Method</th>
                        <th className="text-left p-3 font-medium text-slate-700">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment, index) => (
                        <tr key={payment.id || index} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-slate-700">
                            {payment.payment_date ? 
                              new Date(payment.payment_date).toLocaleDateString() : 
                              new Date(payment.created_at).toLocaleDateString()
                            }
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              payment.source === 'Project' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {payment.source}
                            </span>
                          </td>
                          <td className="p-3 text-slate-700">
                            {payment.type === 'project_payment' ? (
                              <div>
                                <div className="font-medium">{payment.project_name}</div>
                                <div className="text-xs text-slate-500">{payment.description}</div>
                              </div>
                            ) : (
                              <div>
                                <div className="font-medium">Sales Transaction</div>
                                <div className="text-xs text-slate-500">{payment.description}</div>
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right font-bold">
                            <span className={payment.type === 'project_payment' ? 'text-blue-600' : 'text-green-600'}>
                              ${(payment.amount || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="p-3 text-slate-700">{payment.payment_method}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              payment.type === 'project_payment' 
                                ? 'bg-blue-50 text-blue-700' 
                                : 'bg-green-50 text-green-700'
                            }`}>
                              {payment.type === 'project_payment' ? 'Payment' : 'Revenue'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">No Payment History</p>
                  <p>No payments or sales transactions found for this client.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <CardTitle>Client Notes</CardTitle>
              <CardDescription>
                Add and manage notes about {client.company_name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Add Note Section */}
              <div className="mb-6">
                <Label htmlFor="new-note">Add New Note</Label>
                <div className="flex space-x-2 mt-2">
                  <Textarea
                    id="new-note"
                    placeholder="Enter your note about this client..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={3}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    className="self-start"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Note
                  </Button>
                </div>
              </div>

              {/* Notes List */}
              <div className="space-y-4">
                {client.client_notes && client.client_notes.length > 0 ? (
                  client.client_notes
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((note, index) => (
                      <Card key={note.id || index} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-blue-500" />
                              <span className="text-sm font-medium text-slate-600">
                                Note by {note.created_by}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1 text-xs text-slate-500">
                              <Clock className="h-3 w-3" />
                              <span>{formatDateTime(note.created_at)}</span>
                            </div>
                          </div>
                          <p className="text-slate-900 whitespace-pre-wrap">{note.note}</p>
                        </CardContent>
                      </Card>
                    ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No notes found for this client.</p>
                    <p className="text-sm">Add your first note to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
              <CardDescription>
                Upload and manage documents for {client.company_name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Upload Document Section */}
              <div className="mb-6 p-4 border rounded-lg bg-slate-50">
                <Label className="text-base font-medium mb-4 block">Upload New Document</Label>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="document-title">Document Title</Label>
                    <Input
                      id="document-title"
                      placeholder="Enter document title..."
                      value={documentTitle}
                      onChange={(e) => setDocumentTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="document-file">Select File</Label>
                    <Input
                      id="document-file"
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.xls"
                    />
                  </div>
                  <Button
                    onClick={handleDocumentUpload}
                    disabled={uploadingDocument || !selectedFile || !documentTitle.trim()}
                    className="w-full"
                  >
                    {uploadingDocument ? (
                      <>
                        <Upload className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Documents List */}
              <div className="space-y-4">
                {documents && documents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-medium text-slate-700">Title</th>
                          <th className="text-left p-3 font-medium text-slate-700">File Name</th>
                          <th className="text-left p-3 font-medium text-slate-700">Upload Date</th>
                          <th className="text-left p-3 font-medium text-slate-700">Size</th>
                          <th className="text-right p-3 font-medium text-slate-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc, index) => (
                          <tr key={doc.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                            <td className="p-3 font-medium">{doc.title}</td>
                            <td className="p-3">{doc.filename}</td>
                            <td className="p-3">{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                            <td className="p-3">{((doc.file_size || 0) / 1024).toFixed(1)} KB</td>
                            <td className="p-3 text-right">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Upload className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No documents found for this client.</p>
                    <p className="text-sm">Upload your first document to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Info Tab */}
        <TabsContent value="bankinfo">
          <Card>
            <CardHeader>
              <CardTitle>Bank Information</CardTitle>
              <CardDescription>
                Manage bank details for {client.company_name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label htmlFor="bank-name">Bank Name</Label>
                  <Input
                    id="bank-name"
                    placeholder="Enter bank name..."
                    value={bankForm.bank_name}
                    onChange={(e) => setBankForm(prev => ({ ...prev, bank_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="additional-info">Additional Information</Label>
                  <Textarea
                    id="additional-info"
                    placeholder="Enter account numbers, routing numbers, SWIFT codes, or any other bank information..."
                    rows={6}
                    value={bankForm.additional_info}
                    onChange={(e) => setBankForm(prev => ({ ...prev, additional_info: e.target.value }))}
                  />
                </div>
                <Button onClick={handleSaveBankInfo} disabled={!bankForm.bank_name.trim()}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Bank Information
                </Button>
              </div>

              {/* Display Saved Bank Info in Table Format */}
              {bankInfo && (
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-slate-900">Saved Bank Information</h3>
                    <div className="text-sm text-slate-500">
                      Last updated: {new Date(bankInfo.updated_at).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Bank Name</th>
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Bank Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-4 py-3 font-medium">{bankInfo.bank_name}</td>
                          <td className="border border-slate-300 px-4 py-3 whitespace-pre-wrap">
                            {bankInfo.additional_info || 'No additional information provided'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-800">
                        Only one bank account can be saved per client. Use the form above to update this information.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Employee Profile Component
// Employee Attendance History Component for Admin View
const EmployeeAttendanceHistory = ({ employeeId }) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeAttendance();
  }, [employeeId]);

  const fetchEmployeeAttendance = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/employees/${employeeId}/attendance`);
      setAttendance(response.data || []);
    } catch (error) {
      console.error("Error fetching employee attendance:", error);
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAttendance = async (attendanceId) => {
    try {
      await axios.put(`${API}/employees/${employeeId}/attendance/${attendanceId}/approve`);
      toast.success("Attendance approved successfully");
      // Refresh the attendance data
      fetchEmployeeAttendance();
    } catch (error) {
      console.error("Error approving attendance:", error);
      toast.error("Failed to approve attendance");
    }
  };

  const handleDeleteAttendance = async (attendanceId) => {
    if (!window.confirm("Are you sure you want to delete this attendance record?")) {
      return;
    }

    try {
      await axios.delete(`${API}/employees/${employeeId}/attendance/${attendanceId}`);
      toast.success("Attendance record deleted successfully");
      // Refresh the attendance data
      fetchEmployeeAttendance();
    } catch (error) {
      console.error("Error deleting attendance:", error);
      toast.error("Failed to delete attendance record");
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-8">Loading attendance history...</div>;
  }

  return (
    <div>
      {attendance.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-slate-300">
            <thead>
              <tr className="bg-slate-50">
                <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Date</th>
                <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Check In</th>
                <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Check Out</th>
                <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Total Hours</th>
                <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">IP Address</th>
                <th className="border border-slate-300 px-4 py-3 text-center font-medium text-slate-700">Status</th>
                <th className="border border-slate-300 px-4 py-3 text-center font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map((record, index) => (
                <tr key={record.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="border border-slate-300 px-4 py-3 font-medium">
                    {new Date(record.date).toLocaleDateString()}
                  </td>
                  <td className="border border-slate-300 px-4 py-3">
                    {record.check_in_time ? new Date(record.check_in_time).toLocaleTimeString() : '-'}
                  </td>
                  <td className="border border-slate-300 px-4 py-3">
                    {record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString() : '-'}
                  </td>
                  <td className="border border-slate-300 px-4 py-3">
                    {record.total_hours ? `${(record.total_hours || 0).toFixed(2)}h` : '-'}
                  </td>
                  <td className="border border-slate-300 px-4 py-3 text-sm">
                    {record.ip_address || '-'}
                  </td>
                  <td className="border border-slate-300 px-4 py-3 text-center">
                    <div className="flex flex-col items-center space-y-1">
                      <Badge 
                        variant={
                          record.approved ? "default" :
                          record.check_in_time && record.check_out_time ? "secondary" :
                          record.check_in_time ? "secondary" : "destructive"
                        }
                        className={
                          record.approved ? "bg-green-600" :
                          record.check_in_time && record.check_out_time ? "bg-blue-600" :
                          record.check_in_time ? "bg-yellow-600" : "bg-red-600"
                        }
                      >
                        {record.approved ? "Approved" :
                         record.check_in_time && record.check_out_time ? "Complete" :
                         record.check_in_time ? "Incomplete" : "Absent"}
                      </Badge>
                    </div>
                  </td>
                  <td className="border border-slate-300 px-4 py-3 text-center">
                    <div className="flex justify-center space-x-2">
                      {!record.approved && (
                        <Button
                          size="sm"
                          onClick={() => handleApproveAttendance(record.id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs"
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Approve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteAttendance(record.id)}
                        className="px-3 py-1 text-xs"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Attendance Records</h3>
          <p className="text-slate-500">Employee hasn't used the check-in/check-out system yet.</p>
        </div>
      )}
    </div>
  );
};

const EmployeeProfile = () => {
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [attendanceData, setAttendanceData] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  
  // Get current user to check if they're a manager
  const { user } = useAuth();
  const isManager = user?.isManager || false;
  const [newAttendance, setNewAttendance] = useState({
    date: new Date(),
    check_in_time: "",
    check_out_time: "",
    status: "present",
    notes: ""
  });

  const [skills, setSkills] = useState([]);
  const [notes, setNotes] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamTypes, setTeamTypes] = useState([]);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [selectedTeamType, setSelectedTeamType] = useState("");
  const [newSkill, setNewSkill] = useState({
    skill_name: "",
    skill_level: "Intermediate"
  });
  const [newNote, setNewNote] = useState("");
  const [newShift, setNewShift] = useState({
    shift_name: "",
    start_time: "09:00",
    end_time: "17:00",
    timezone: "UTC",
    valid_from: format(new Date(), "yyyy-MM-dd"),
    valid_to: "",
    days_of_week: []
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [yearLeaveBalance, setYearLeaveBalance] = useState(null);
  const [yearLeaveRequests, setYearLeaveRequests] = useState([]);
  
  // Documents and Bank Info states
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [employeeBankInfo, setEmployeeBankInfo] = useState(null);
  const [uploadingEmployeeDocument, setUploadingEmployeeDocument] = useState(false);
  const [employeeDocumentTitle, setEmployeeDocumentTitle] = useState("");
  const [selectedEmployeeFile, setSelectedEmployeeFile] = useState(null);
  const [employeeBankForm, setEmployeeBankForm] = useState({
    bank_name: "",
    additional_info: ""
  });

  // Get employee ID from URL
  const employeeId = window.location.pathname.split('/employees/')[1];

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeProfile();
      fetchAvailableYears();
      fetchEmployeeDocuments();
      fetchEmployeeBankInfo();
      fetchTeamTypes();
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId && selectedYear) {
      fetchLeaveDataForYear(selectedYear);
    }
  }, [selectedYear]);

  const fetchEmployeeProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/profile`);
      setEmployeeProfile(response.data);
      setLeaveBalance(response.data.leave_balance);
      setAttendanceData(response.data.attendance_records);
      setSkills(response.data.skills || []);
      setNotes(response.data.notes || []);
      setShifts(response.data.shifts || []);
      setProjects(response.data.projects || []); // Extract projects from response
    } catch (error) {
      console.error("Error fetching employee profile:", error);
      toast.error("Failed to load employee profile");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee documents
  const fetchEmployeeDocuments = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/documents`);
      setEmployeeDocuments(response.data);
    } catch (error) {
      console.error("Error fetching employee documents:", error);
      // Don't show error toast as documents might not exist
    }
  };

  // Fetch team types
  const fetchTeamTypes = async () => {
    try {
      const response = await axios.get(`${API}/team-types`);
      setTeamTypes(response.data);
    } catch (error) {
      console.error("Error fetching team types:", error);
    }
  };

  // Update employee team
  const handleTeamUpdate = async () => {
    try {
      await axios.put(`${API}/employees/${employeeId}`, {
        team_type_id: selectedTeamType === "none" ? undefined : selectedTeamType
      });
      toast.success("Employee team updated successfully");
      setIsTeamDialogOpen(false);
      fetchEmployeeProfile(); // Refresh employee data
    } catch (error) {
      console.error("Error updating employee team:", error);
      toast.error("Failed to update employee team");
    }
  };

  const openTeamDialog = () => {
    setSelectedTeamType(employeeProfile?.team_type_id || "none");
    setIsTeamDialogOpen(true);
  };

  // Fetch employee bank info
  const fetchEmployeeBankInfo = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/bank-info`);
      setEmployeeBankInfo(response.data);
      setEmployeeBankForm({
        bank_name: response.data.bank_name || "",
        additional_info: response.data.additional_info || ""
      });
    } catch (error) {
      console.error("Error fetching employee bank info:", error);
      // Don't show error toast as bank info might not exist
    }
  };

  // Handle employee document upload
  const handleEmployeeDocumentUpload = async () => {
    if (!selectedEmployeeFile || !employeeDocumentTitle.trim()) {
      toast.error("Please select a file and provide a title");
      return;
    }

    setUploadingEmployeeDocument(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedEmployeeFile);
      formData.append("title", employeeDocumentTitle);

      await axios.post(`${API}/employees/${employeeId}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Document uploaded successfully");
      setSelectedEmployeeFile(null);
      setEmployeeDocumentTitle("");
      fetchEmployeeDocuments();
    } catch (error) {
      console.error("Error uploading employee document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingEmployeeDocument(false);
    }
  };

  // Handle employee document delete
  const handleDeleteEmployeeDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await axios.delete(`${API}/employees/${employeeId}/documents/${documentId}`);
      toast.success("Document deleted successfully");
      fetchEmployeeDocuments();
    } catch (error) {
      console.error("Error deleting employee document:", error);
      toast.error("Failed to delete document");
    }
  };

  // Handle leave request approval
  const handleApproveLeaveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/leave-requests/${requestId}/approve?approved_by=admin`);
      toast.success("Leave request approved successfully");
      
      // Refresh leave requests and balance
      fetchEmployeeProfile();
      fetchLeaveDataForYear(selectedYear);
    } catch (error) {
      console.error("Error approving leave request:", error);
      toast.error("Failed to approve leave request");
    }
  };

  // Handle leave request rejection
  const handleRejectLeaveRequest = async (requestId) => {
    const reason = window.prompt("Please provide a reason for rejection (optional):");
    if (reason === null) return; // User cancelled
    
    try {
      const rejectionReason = reason.trim() || "No reason provided";
      await axios.put(`${API}/leave-requests/${requestId}/reject?approved_by=admin&rejection_reason=${encodeURIComponent(rejectionReason)}`);
      toast.success("Leave request rejected");
      
      // Refresh leave requests
      fetchEmployeeProfile();
      fetchLeaveDataForYear(selectedYear);
    } catch (error) {
      console.error("Error rejecting leave request:", error);
      toast.error("Failed to reject leave request");
    }
  };

  // Handle employee bank info save
  const handleSaveEmployeeBankInfo = async () => {
    if (!employeeBankForm.bank_name.trim()) {
      toast.error("Please provide a bank name");
      return;
    }

    try {
      await axios.post(`${API}/employees/${employeeId}/bank-info`, employeeBankForm);
      toast.success("Bank information saved successfully");
      fetchEmployeeBankInfo();
    } catch (error) {
      console.error("Error saving employee bank info:", error);
      toast.error("Failed to save bank information");
    }
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        employee_id: employeeId,
        date: format(newAttendance.date, "yyyy-MM-dd"),
        check_in_time: newAttendance.check_in_time ? `${format(newAttendance.date, "yyyy-MM-dd")}T${newAttendance.check_in_time}:00` : null,
        check_out_time: newAttendance.check_out_time ? `${format(newAttendance.date, "yyyy-MM-dd")}T${newAttendance.check_out_time}:00` : null,
        status: newAttendance.status,
        notes: newAttendance.notes
      };
      
      await axios.post(`${API}/employees/${employeeId}/attendance`, data);
      toast.success("Attendance recorded successfully");
      fetchEmployeeProfile();
      setNewAttendance({
        date: new Date(),
        check_in_time: "",
        check_out_time: "",
        status: "present",
        notes: ""
      });
    } catch (error) {
      console.error("Error recording attendance:", error);
      toast.error("Failed to record attendance");
    }
  };



  const fetchAvailableYears = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/leave-years`);
      setAvailableYears(response.data.years);
      setSelectedYear(response.data.current_year);
    } catch (error) {
      console.error("Error fetching available years:", error);
      setAvailableYears([new Date().getFullYear()]);
    }
  };

  const fetchLeaveDataForYear = async (year) => {
    try {
      // Fetch leave balance for year
      const balanceResponse = await axios.get(`${API}/employees/${employeeId}/leave-balance/${year}`);
      setYearLeaveBalance(balanceResponse.data.leave_balance);
      
      // Fetch leave requests for year
      const requestsResponse = await axios.get(`${API}/employees/${employeeId}/leave-requests/${year}`);
      setYearLeaveRequests(requestsResponse.data.leave_requests);
    } catch (error) {
      console.error("Error fetching leave data for year:", error);
      setYearLeaveBalance(null);
      setYearLeaveRequests([]);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    fetchLeaveDataForYear(year);
  };

  const handleLeaveAllocation = async (e) => {
    e.preventDefault();
    try {
      const totalLeaves = parseInt(e.target.total_leaves.value);
      await axios.post(`${API}/employees/${employeeId}/leave-balance/${selectedYear}?total_leaves=${totalLeaves}`);
      toast.success(`Leave balance set for ${selectedYear}`);
      fetchLeaveDataForYear(selectedYear);
    } catch (error) {
      console.error("Error setting leave allocation:", error);
      toast.error("Failed to set leave allocation");
    }
  };

  const handleAddPayment = async (amount, paymentType, notes, periodStart, periodEnd) => {
    try {
      const data = {
        employee_id: employeeId,
        payment_date: format(new Date(), "yyyy-MM-dd"),
        amount: parseFloat(amount),
        payment_type: paymentType,
        notes: notes,
        period_start: periodStart || null,
        period_end: periodEnd || null
      };
      
      await axios.post(`${API}/employees/${employeeId}/payment`, data);
      toast.success("Payment recorded successfully");
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error("Failed to record payment");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!confirm("Are you sure you want to delete this payment? This action cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/employees/${employeeId}/payment/${paymentId}`);
      toast.success("Payment deleted successfully");
      fetchEmployeeProfile(); // Refresh the payment history
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    }
  };

  // Helper function to format dates for display
  const formatDateReadable = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Handle date string format "2025-08-01"
      const date = new Date(dateStr + 'T00:00:00'); // Add time to ensure proper parsing
      if (isNaN(date.getTime())) {
        return dateStr; // Return original if can't parse
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr; // Return original if error
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/payment/${paymentId}/receipt`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payment_receipt_${paymentId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Receipt downloaded successfully");
    } catch (error) {
      console.error("Error downloading receipt:", error);
      toast.error("Failed to download receipt");
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    if (!newSkill.skill_name.trim()) {
      toast.error("Please enter a skill name");
      return;
    }
    
    try {
      await axios.post(`${API}/employees/${employeeId}/skills`, newSkill);
      toast.success("Skill added successfully");
      setNewSkill({ skill_name: "", skill_level: "Intermediate" });
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error adding skill:", error);
      if (error.response?.data?.detail?.includes("already exists")) {
        toast.error("This skill already exists for this employee");
      } else {
        toast.error("Failed to add skill");
      }
    }
  };

  const handleDeleteSkill = async (skillId) => {
    if (!confirm("Are you sure you want to delete this skill?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/employees/${employeeId}/skills/${skillId}`);
      toast.success("Skill deleted successfully");
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error deleting skill:", error);
      toast.error("Failed to delete skill");
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    try {
      await axios.post(`${API}/employees/${employeeId}/notes`, { note: newNote });
      toast.success("Note added successfully");
      setNewNote("");
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm("Are you sure you want to delete this note?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/employees/${employeeId}/notes/${noteId}`);
      toast.success("Note deleted successfully");
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleAddShift = async (e) => {
    e.preventDefault();
    if (!newShift.shift_name.trim()) {
      toast.error("Please enter a shift name");
      return;
    }

    try {
      const shiftData = {
        ...newShift,
        days_of_week: newShift.days_of_week,
        valid_to: newShift.valid_to || null
      };
      
      await axios.post(`${API}/employees/${employeeId}/shifts`, shiftData);
      toast.success("Shift added successfully");
      setNewShift({
        shift_name: "",
        start_time: "09:00",
        end_time: "17:00",
        timezone: "UTC",
        valid_from: format(new Date(), "yyyy-MM-dd"),
        valid_to: "",
        days_of_week: []
      });
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error adding shift:", error);
      toast.error("Failed to add shift");
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!confirm("Are you sure you want to delete this shift?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/employees/${employeeId}/shifts/${shiftId}`);
      toast.success("Shift deleted successfully");
      fetchEmployeeProfile();
    } catch (error) {
      console.error("Error deleting shift:", error);
      toast.error("Failed to delete shift");
    }
  };

  const handleDayToggle = (dayIndex) => {
    setNewShift(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayIndex)
        ? prev.days_of_week.filter(d => d !== dayIndex)
        : [...prev.days_of_week, dayIndex].sort()
    }));
  };

  const formatDaysOfWeek = (days) => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (!days || days.length === 0) return 'All days';
    return days.map(d => dayNames[d]).join(', ');
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading employee profile...</div>;
  }

  if (!employeeProfile) {
    return <div className="max-w-7xl mx-auto p-6">Employee not found</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={() => window.history.back()}>
          ← Back to Employees
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{employeeProfile.employee.name}</h1>
          <p className="text-slate-600">{employeeProfile.employee.designation || 'Employee'}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-10">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="shifts">Shifts</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves">Leave Management</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="payments">Financial History</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="bankinfo">Bank Info</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Employee Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Name</Label>
                    <p className="font-medium">{employeeProfile.employee.name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Designation</Label>
                    <p className="font-medium">{employeeProfile.employee.designation || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Country</Label>
                    <p className="font-medium">{employeeProfile.employee.country || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Date of Joining</Label>
                    <p className="font-medium">{employeeProfile.employee.date_of_joining || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Reports To</Label>
                    <p className="font-medium">
                      {employeeProfile.employee.manager_name ? (
                        <span className="text-emerald-600 font-semibold">
                          {employeeProfile.employee.manager_name}
                        </span>
                      ) : (
                        <span className="text-slate-400">Direct Report</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Role</Label>
                    <p className="font-medium">
                      {employeeProfile.employee.isManager ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                          Manager
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          Employee
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Total Experience</Label>
                    <p className="font-bold text-purple-600 text-lg">
                      {employeeProfile.employee.total_experience ? `${employeeProfile.employee.total_experience.toFixed(1)} years` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Email</Label>
                    <p className="font-medium">{employeeProfile.employee.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Phone</Label>
                    <p className="font-medium">{employeeProfile.employee.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Team</Label>
                    <div className="flex items-center justify-between">
                      <div>
                        {employeeProfile.employee.team_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {employeeProfile.employee.team_name}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">No Team</span>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={openTeamDialog}
                        className="ml-2"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Financial Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Monthly Salary</Label>
                    <p className="font-bold text-green-600 text-lg">
                      ${employeeProfile.employee.monthly_salary ? employeeProfile.employee.monthly_salary.toFixed(2) : 'N/A'}
                    </p>
                  </div>
                  {!isManager && (
                    <>
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Cost per Hour</Label>
                        <p className="font-bold text-orange-600 text-lg">${employeeProfile.employee.cost_per_hour.toFixed(2)}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Selling Rate per Hour</Label>
                        <p className="font-bold text-blue-600 text-lg">${employeeProfile.employee.selling_per_hour.toFixed(2)}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Custom Rate</Label>
                        <p className="font-bold text-purple-600 text-lg">
                          {employeeProfile.employee.custom_rate ? `$${employeeProfile.employee.custom_rate.toFixed(2)}` : 'N/A'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {employeeProfile.employee.contract_file_name && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Contract</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 p-4 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{employeeProfile.employee.contract_file_name}</p>
                      <p className="text-sm text-slate-600">Contract document</p>
                    </div>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Skills Tab */}
        <TabsContent value="skills">
          <div className="space-y-6">
            {/* Add Skill Form */}
            <Card>
              <CardHeader>
                <CardTitle>Add New Skill</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddSkill} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="skill_name">Skill Name</Label>
                    <Input
                      id="skill_name"
                      type="text"
                      placeholder="e.g., React, Python, Project Management"
                      value={newSkill.skill_name}
                      onChange={(e) => setNewSkill({...newSkill, skill_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="skill_level">Skill Level</Label>
                    <select
                      id="skill_level"
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newSkill.skill_level}
                      onChange={(e) => setNewSkill({...newSkill, skill_level: e.target.value})}
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Expert">Expert</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="w-full">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Skill
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Skills List */}
            <Card>
              <CardHeader>
                <CardTitle>Current Skills ({skills.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {skills.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {skills.map((skill, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-blue-900">{skill.skill_name}</h4>
                            <p className="text-sm text-blue-700">{skill.skill_level}</p>
                            <p className="text-xs text-blue-600 mt-1">
                              Added: {new Date(skill.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleDeleteSkill(skill.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-500">
                      <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No skills added yet</p>
                      <p className="text-sm">Add the first skill to get started</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <div className="space-y-6">
            {/* Add Note Form */}
            <Card>
              <CardHeader>
                <CardTitle>Add New Note</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddNote} className="space-y-4">
                  <div>
                    <Label htmlFor="note">Note</Label>
                    <textarea
                      id="note"
                      className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add a note about this employee..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Note
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Notes List */}
            <Card>
              <CardHeader>
                <CardTitle>Notes History ({notes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {notes.length > 0 ? (
                  <div className="space-y-4">
                    {notes.map((note, index) => (
                      <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-slate-800">{note.note}</p>
                            <p className="text-sm text-slate-500 mt-2">
                              {new Date(note.created_at).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleDeleteNote(note.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No notes added yet</p>
                      <p className="text-sm">Add the first note to get started</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Shifts Tab */}
        <TabsContent value="shifts">
          <div className="space-y-6">
            {/* Add Shift Form - Hidden for Managers */}
            {!isManager && (
              <Card>
                <CardHeader>
                  <CardTitle>Add New Shift</CardTitle>
                  <CardDescription>
                    Define work schedules with time ranges, time zones, and validity periods
                  </CardDescription>
                </CardHeader>
                <CardContent>
                <form onSubmit={handleAddShift} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="shift_name">Shift Name</Label>
                      <Input
                        id="shift_name"
                        value={newShift.shift_name}
                        onChange={(e) => setNewShift({...newShift, shift_name: e.target.value})}
                        placeholder="e.g., Morning Shift, Night Shift"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="timezone">Time Zone</Label>
                      <select
                        id="timezone"
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={newShift.timezone}
                        onChange={(e) => setNewShift({...newShift, timezone: e.target.value})}
                      >
                        {/* UTC */}
                        <option value="UTC">UTC (Coordinated Universal Time)</option>
                        
                        {/* Africa */}
                        <option value="Africa/Cairo">Africa/Cairo (Egypt)</option>
                        <option value="Africa/Johannesburg">Africa/Johannesburg (South Africa)</option>
                        <option value="Africa/Lagos">Africa/Lagos (Nigeria)</option>
                        <option value="Africa/Nairobi">Africa/Nairobi (Kenya)</option>
                        <option value="Africa/Casablanca">Africa/Casablanca (Morocco)</option>
                        
                        {/* Americas - North America */}
                        <option value="America/New_York">America/New_York (Eastern Time US)</option>
                        <option value="America/Chicago">America/Chicago (Central Time US)</option>
                        <option value="America/Denver">America/Denver (Mountain Time US)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (Pacific Time US)</option>
                        <option value="America/Anchorage">America/Anchorage (Alaska)</option>
                        <option value="Pacific/Honolulu">Pacific/Honolulu (Hawaii)</option>
                        <option value="America/Toronto">America/Toronto (Canada Eastern)</option>
                        <option value="America/Vancouver">America/Vancouver (Canada Pacific)</option>
                        <option value="America/Mexico_City">America/Mexico_City (Mexico)</option>
                        
                        {/* Americas - Central & South America */}
                        <option value="America/Guatemala">America/Guatemala (Guatemala)</option>
                        <option value="America/Costa_Rica">America/Costa_Rica (Costa Rica)</option>
                        <option value="America/Panama">America/Panama (Panama)</option>
                        <option value="America/Bogota">America/Bogota (Colombia)</option>
                        <option value="America/Lima">America/Lima (Peru)</option>
                        <option value="America/Sao_Paulo">America/Sao_Paulo (Brazil)</option>
                        <option value="America/Argentina/Buenos_Aires">America/Argentina/Buenos_Aires (Argentina)</option>
                        <option value="America/Santiago">America/Santiago (Chile)</option>
                        <option value="America/Caracas">America/Caracas (Venezuela)</option>
                        
                        {/* Europe */}
                        <option value="Europe/London">Europe/London (United Kingdom)</option>
                        <option value="Europe/Dublin">Europe/Dublin (Ireland)</option>
                        <option value="Europe/Paris">Europe/Paris (France)</option>
                        <option value="Europe/Berlin">Europe/Berlin (Germany)</option>
                        <option value="Europe/Rome">Europe/Rome (Italy)</option>
                        <option value="Europe/Madrid">Europe/Madrid (Spain)</option>
                        <option value="Europe/Amsterdam">Europe/Amsterdam (Netherlands)</option>
                        <option value="Europe/Brussels">Europe/Brussels (Belgium)</option>
                        <option value="Europe/Vienna">Europe/Vienna (Austria)</option>
                        <option value="Europe/Zurich">Europe/Zurich (Switzerland)</option>
                        <option value="Europe/Prague">Europe/Prague (Czech Republic)</option>
                        <option value="Europe/Warsaw">Europe/Warsaw (Poland)</option>
                        <option value="Europe/Stockholm">Europe/Stockholm (Sweden)</option>
                        <option value="Europe/Oslo">Europe/Oslo (Norway)</option>
                        <option value="Europe/Helsinki">Europe/Helsinki (Finland)</option>
                        <option value="Europe/Copenhagen">Europe/Copenhagen (Denmark)</option>
                        <option value="Europe/Athens">Europe/Athens (Greece)</option>
                        <option value="Europe/Istanbul">Europe/Istanbul (Turkey)</option>
                        <option value="Europe/Moscow">Europe/Moscow (Russia)</option>
                        <option value="Europe/Kiev">Europe/Kiev (Ukraine)</option>
                        
                        {/* Asia */}
                        <option value="Asia/Dubai">Asia/Dubai (UAE)</option>
                        <option value="Asia/Qatar">Asia/Qatar (Qatar)</option>
                        <option value="Asia/Riyadh">Asia/Riyadh (Saudi Arabia)</option>
                        <option value="Asia/Tehran">Asia/Tehran (Iran)</option>
                        <option value="Asia/Karachi">Asia/Karachi (Pakistan)</option>
                        <option value="Asia/Kolkata">Asia/Kolkata (India)</option>
                        <option value="Asia/Dhaka">Asia/Dhaka (Bangladesh)</option>
                        <option value="Asia/Kathmandu">Asia/Kathmandu (Nepal)</option>
                        <option value="Asia/Colombo">Asia/Colombo (Sri Lanka)</option>
                        <option value="Asia/Bangkok">Asia/Bangkok (Thailand)</option>
                        <option value="Asia/Singapore">Asia/Singapore (Singapore)</option>
                        <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur (Malaysia)</option>
                        <option value="Asia/Jakarta">Asia/Jakarta (Indonesia)</option>
                        <option value="Asia/Manila">Asia/Manila (Philippines)</option>
                        <option value="Asia/Hong_Kong">Asia/Hong_Kong (Hong Kong)</option>
                        <option value="Asia/Taipei">Asia/Taipei (Taiwan)</option>
                        <option value="Asia/Shanghai">Asia/Shanghai (China)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (Japan)</option>
                        <option value="Asia/Seoul">Asia/Seoul (South Korea)</option>
                        <option value="Asia/Pyongyang">Asia/Pyongyang (North Korea)</option>
                        <option value="Asia/Vladivostok">Asia/Vladivostok (Russia Far East)</option>
                        <option value="Asia/Yekaterinburg">Asia/Yekaterinburg (Russia Ural)</option>
                        <option value="Asia/Novosibirsk">Asia/Novosibirsk (Russia Siberia)</option>
                        
                        {/* Australia & Oceania */}
                        <option value="Australia/Sydney">Australia/Sydney (Australia Eastern)</option>
                        <option value="Australia/Melbourne">Australia/Melbourne (Australia Eastern)</option>
                        <option value="Australia/Brisbane">Australia/Brisbane (Australia Eastern)</option>
                        <option value="Australia/Adelaide">Australia/Adelaide (Australia Central)</option>
                        <option value="Australia/Darwin">Australia/Darwin (Australia Central)</option>
                        <option value="Australia/Perth">Australia/Perth (Australia Western)</option>
                        <option value="Pacific/Auckland">Pacific/Auckland (New Zealand)</option>
                        <option value="Pacific/Fiji">Pacific/Fiji (Fiji)</option>
                        <option value="Pacific/Guam">Pacific/Guam (Guam)</option>
                        
                        {/* Atlantic */}
                        <option value="Atlantic/Azores">Atlantic/Azores (Azores)</option>
                        <option value="Atlantic/Cape_Verde">Atlantic/Cape_Verde (Cape Verde)</option>
                        <option value="Atlantic/Reykjavik">Atlantic/Reykjavik (Iceland)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_time">Start Time</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={newShift.start_time}
                        onChange={(e) => setNewShift({...newShift, start_time: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_time">End Time</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={newShift.end_time}
                        onChange={(e) => setNewShift({...newShift, end_time: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="valid_from">Valid From</Label>
                      <Input
                        id="valid_from"
                        type="date"
                        value={newShift.valid_from}
                        onChange={(e) => setNewShift({...newShift, valid_from: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="valid_to">Valid To (Optional)</Label>
                      <Input
                        id="valid_to"
                        type="date"
                        value={newShift.valid_to}
                        onChange={(e) => setNewShift({...newShift, valid_to: e.target.value})}
                      />
                      <p className="text-sm text-slate-500 mt-1">Leave empty for ongoing shift</p>
                    </div>
                  </div>

                  <div>
                    <Label>Days of Week (Optional)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                        <Button
                          key={day}
                          type="button"
                          variant={newShift.days_of_week.includes(index) ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleDayToggle(index)}
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Leave empty to apply to all days</p>
                  </div>

                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Shift
                  </Button>
                </form>
              </CardContent>
            </Card>
            )}

            {/* Shifts List */}
            <Card>
              <CardHeader>
                <CardTitle>Work Shifts ({shifts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {shifts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shifts.map((shift, index) => (
                      <div key={index} className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-indigo-900 text-lg">{shift.shift_name}</h4>
                            <div className="text-sm text-indigo-700 space-y-1 mt-2">
                              <div className="flex items-center space-x-2">
                                <Clock className="h-4 w-4" />
                                <span>{shift.start_time} - {shift.end_time}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Globe className="h-4 w-4" />
                                <span>{shift.timezone}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Calendar className="h-4 w-4" />
                                <span>
                                  {shift.valid_from} {shift.valid_to ? `to ${shift.valid_to}` : '(ongoing)'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Users className="h-4 w-4" />
                                <span>{formatDaysOfWeek(shift.days_of_week)}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className={`w-2 h-2 rounded-full ${shift.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                                <span>{shift.is_active ? 'Active' : 'Inactive'}</span>
                              </div>
                            </div>
                          </div>
                          {!isManager && (
                            <Button
                              onClick={() => handleDeleteShift(shift.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-500">
                      <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No shifts defined yet</p>
                      <p className="text-sm">Add the first shift to get started</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <div className="space-y-6">
            {/* Current Shift Information */}
            {shifts.length > 0 && (
              <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-indigo-800">
                    <Clock className="h-5 w-5" />
                    <span>Current Shift Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const today = new Date();
                    const currentWeekday = today.getDay() === 0 ? 6 : today.getDay() - 1; // Convert Sunday=0 to Monday=0 format
                    const currentDate = today.toISOString().split('T')[0];
                    
                    const activeShift = shifts.find(shift => {
                      const validFrom = shift.valid_from <= currentDate;
                      const validTo = !shift.valid_to || shift.valid_to >= currentDate;
                      const validDay = !shift.days_of_week.length || shift.days_of_week.includes(currentWeekday);
                      return shift.is_active && validFrom && validTo && validDay;
                    });

                    if (activeShift) {
                      return (
                        <div className="bg-white rounded-lg p-4 border border-indigo-100">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <Label className="text-indigo-600 font-medium">Shift Name</Label>
                              <p className="font-semibold">{activeShift.shift_name}</p>
                            </div>
                            <div>
                              <Label className="text-indigo-600 font-medium">Work Hours</Label>
                              <p className="font-semibold">{activeShift.start_time} - {activeShift.end_time}</p>
                            </div>
                            <div>
                              <Label className="text-indigo-600 font-medium">Time Zone</Label>
                              <p className="font-semibold">{activeShift.timezone}</p>
                            </div>
                            <div>
                              <Label className="text-indigo-600 font-medium">Days</Label>
                              <p className="font-semibold">{formatDaysOfWeek(activeShift.days_of_week)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-center text-indigo-700">
                          <p>No active shift for today. <a href="#" onClick={() => setActiveTab('shifts')} className="underline">Define a shift</a> to track attendance better.</p>
                        </div>
                      );
                    }
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Employee Check-In/Check-Out History */}
            <Card>
              <CardHeader>
                <CardTitle>Employee Attendance History</CardTitle>
                <CardDescription>Real-time check-in/check-out records from employee portal</CardDescription>
              </CardHeader>
              <CardContent>
                <EmployeeAttendanceHistory employeeId={employeeId} />
              </CardContent>
            </Card>

            {/* Add Attendance Form - Hidden for Managers */}
            {!isManager && (
              <Card>
                <CardHeader>
                  <CardTitle>Manual Attendance Record</CardTitle>
                </CardHeader>
                <CardContent>
                <form onSubmit={handleAttendanceSubmit} className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(newAttendance.date, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newAttendance.date}
                          onSelect={(date) => setNewAttendance({...newAttendance, date: date || new Date()})}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Check In</Label>
                    <Input
                      type="time"
                      value={newAttendance.check_in_time}
                      onChange={(e) => setNewAttendance({...newAttendance, check_in_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Check Out</Label>
                    <Input
                      type="time"
                      value={newAttendance.check_out_time}
                      onChange={(e) => setNewAttendance({...newAttendance, check_out_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={newAttendance.status} onValueChange={(value) => setNewAttendance({...newAttendance, status: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="present">Present</SelectItem>
                        <SelectItem value="absent">Absent</SelectItem>
                        <SelectItem value="half_day">Half Day</SelectItem>
                        <SelectItem value="late">Late</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
            )}
          </div>
        </TabsContent>

        {/* Leave Management Tab */}
        <TabsContent value="leaves">
          <div className="space-y-6">
            {/* Year Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Leave Management</span>
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="year-select">Year:</Label>
                    <select
                      id="year-select"
                      className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={selectedYear}
                      onChange={(e) => handleYearChange(parseInt(e.target.value))}
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </CardTitle>
              </CardHeader>
            </Card>

            {/* Leave Balance Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Leave Balance ({selectedYear})</CardTitle>
              </CardHeader>
              <CardContent>
                {yearLeaveBalance ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{yearLeaveBalance.total_leaves}</div>
                      <div className="text-sm text-slate-600">Total Allocated</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{yearLeaveBalance.used_leaves}</div>
                      <div className="text-sm text-slate-600">Used</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{yearLeaveBalance.remaining_leaves}</div>
                      <div className="text-sm text-slate-600">Remaining</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-500">
                      <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No leave balance allocated for {selectedYear}</p>
                      <p className="text-sm">Set up annual leave allocation below</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Set Leave Balance - Hidden for Managers */}
            {!isManager && (
              <Card>
                <CardHeader>
                  <CardTitle>Allocate Annual Leaves</CardTitle>
                  <CardDescription>
                    Set the total annual leave allocation for {selectedYear}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                <form onSubmit={handleLeaveAllocation} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label htmlFor="total_leaves">Total Leaves for {selectedYear}</Label>
                    <Input
                      id="total_leaves"
                      name="total_leaves"
                      type="number"
                      defaultValue={yearLeaveBalance?.total_leaves || 24}
                      min="0"
                      max="365"
                      required
                    />
                  </div>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    Set Leave Balance
                  </Button>
                </form>
              </CardContent>
            </Card>
            )}

            {/* Leave Requests */}
            <Card>
              <CardHeader>
                <CardTitle>Leave Requests ({selectedYear})</CardTitle>
              </CardHeader>
              <CardContent>
                {yearLeaveRequests.length > 0 ? (
                  <div className="space-y-3">
                    {yearLeaveRequests.map((request) => (
                      <div key={request.id} className="flex justify-between items-center p-4 border rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">{request.leave_type} - {request.days_requested} days</div>
                          <div className="text-sm text-slate-600">
                            {request.start_date} to {request.end_date}
                          </div>
                          <div className="text-sm text-slate-500">{request.reason}</div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge variant={request.status === 'approved' ? 'default' : request.status === 'rejected' ? 'destructive' : 'secondary'}>
                            {request.status}
                          </Badge>
                          {request.status === 'pending' && (
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveLeaveRequest(request.id)}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleRejectLeaveRequest(request.id)}
                                className="px-3 py-1 text-xs"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No leave requests found for {selectedYear}</p>
                      <p className="text-sm">Leave requests will appear here when submitted</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <CardTitle>Assigned Projects</CardTitle>
            </CardHeader>
            <CardContent>
              {projects && projects.length > 0 ? (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div key={project.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{project.name}</h3>
                          <p className="text-sm text-slate-600 mt-1">{project.description}</p>
                          <div className="flex gap-4 mt-2 text-sm">
                            {!isManager && <span>Budget: ${project.budget}</span>}
                            <span>Type: {project.billing_type}</span>
                            <span>Status: {project.status}</span>
                          </div>
                        </div>
                        <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
                          {project.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">
                  {loading ? "Loading projects..." : "No projects assigned"}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <div className="space-y-6">
            {/* Add Payment Form - Hidden for Managers */}
            {!isManager && (
              <Card>
                <CardHeader>
                  <CardTitle>Add New Payment</CardTitle>
                  <CardDescription>
                    Record salary, bonus, commission, or other payments to the employee
                  </CardDescription>
                </CardHeader>
                <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const amount = parseFloat(formData.get('amount'));
                  const paymentType = formData.get('payment_type');
                  const notes = formData.get('notes');
                  const periodStart = formData.get('period_start');
                  const periodEnd = formData.get('period_end');
                  handleAddPayment(amount, paymentType, notes, periodStart, periodEnd);
                  e.target.reset();
                }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="payment_amount">Amount ($)</Label>
                    <Input
                      id="payment_amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      placeholder="5000.00"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_type">Payment Type</Label>
                    <select
                      id="payment_type"
                      name="payment_type"
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="salary">Monthly Salary</option>
                      <option value="bonus">Bonus</option>
                      <option value="commission">Commission</option>
                      <option value="overtime">Overtime</option>
                      <option value="reimbursement">Reimbursement</option>
                      <option value="allowance">Allowance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="period_start">Period Start</Label>
                    <Input
                      id="period_start"
                      name="period_start"
                      type="date"
                      placeholder="Payment period start date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="period_end">Period End</Label>
                    <Input
                      id="period_end"
                      name="period_end"
                      type="date"
                      placeholder="Payment period end date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_notes">Notes (Optional)</Label>
                    <Input
                      id="payment_notes"
                      name="notes"
                      placeholder="e.g., August 2025 salary"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Payment
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
            )}

            {/* Payment History */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Payment History ({employeeProfile.payment_history.length})</CardTitle>
                  <div className="text-lg font-bold text-green-600">
                    Total: ${employeeProfile.total_payments.toFixed(2)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
              {employeeProfile.payment_history.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-left p-3 font-semibold">Type</th>
                        <th className="text-right p-3 font-semibold">Amount</th>
                        <th className="text-left p-3 font-semibold">Period</th>
                        <th className="text-left p-3 font-semibold">Notes</th>
                        <th className="text-center p-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeProfile.payment_history.map((payment, index) => (
                        <tr key={payment.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                          <td className="p-3">{payment.payment_date}</td>
                          <td className="p-3">
                            <Badge variant="outline">{payment.payment_type}</Badge>
                          </td>
                          <td className="p-3 text-right font-bold text-green-600">${payment.amount.toFixed(2)}</td>
                          <td className="p-3">
                            {payment.period_start && payment.period_end 
                              ? `${formatDateReadable(payment.period_start)} to ${formatDateReadable(payment.period_end)}`
                              : payment.period_start 
                                ? formatDateReadable(payment.period_start)
                                : '-'
                            }
                          </td>
                          <td className="p-3">{payment.notes || '-'}</td>
                          <td className="p-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadReceipt(payment.id)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Receipt
                              </Button>
                              {!isManager && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeletePayment(payment.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No payment history found</p>
              )}
            </CardContent>
          </Card>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
              <CardDescription>
                Upload and manage documents for {employeeProfile.employee.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Upload Document Section */}
              <div className="mb-6 p-4 border rounded-lg bg-slate-50">
                <Label className="text-base font-medium mb-4 block">Upload New Document</Label>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="employee-document-title">Document Title</Label>
                    <Input
                      id="employee-document-title"
                      placeholder="Enter document title..."
                      value={employeeDocumentTitle}
                      onChange={(e) => setEmployeeDocumentTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="employee-document-file">Select File</Label>
                    <Input
                      id="employee-document-file"
                      type="file"
                      onChange={(e) => setSelectedEmployeeFile(e.target.files[0])}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.xls"
                    />
                  </div>
                  <Button
                    onClick={handleEmployeeDocumentUpload}
                    disabled={uploadingEmployeeDocument || !selectedEmployeeFile || !employeeDocumentTitle.trim()}
                    className="w-full"
                  >
                    {uploadingEmployeeDocument ? (
                      <>
                        <Upload className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Documents List */}
              <div className="space-y-4">
                {employeeDocuments && employeeDocuments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-medium text-slate-700">Title</th>
                          <th className="text-left p-3 font-medium text-slate-700">File Name</th>
                          <th className="text-left p-3 font-medium text-slate-700">Upload Date</th>
                          <th className="text-left p-3 font-medium text-slate-700">Size</th>
                          <th className="text-right p-3 font-medium text-slate-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeDocuments.map((doc, index) => (
                          <tr key={doc.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                            <td className="p-3 font-medium">{doc.title}</td>
                            <td className="p-3">{doc.filename}</td>
                            <td className="p-3">{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                            <td className="p-3">{((doc.file_size || 0) / 1024).toFixed(1)} KB</td>
                            <td className="p-3 text-right">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteEmployeeDocument(doc.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Upload className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No documents found for this employee.</p>
                    <p className="text-sm">Upload your first document to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Info Tab */}
        <TabsContent value="bankinfo">
          <Card>
            <CardHeader>
              <CardTitle>Bank Information</CardTitle>
              <CardDescription>
                Manage bank details for {employeeProfile.employee.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label htmlFor="employee-bank-name">Bank Name</Label>
                  <Input
                    id="employee-bank-name"
                    placeholder="Enter bank name..."
                    value={employeeBankForm.bank_name}
                    onChange={(e) => setEmployeeBankForm(prev => ({ ...prev, bank_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="employee-additional-info">Additional Information</Label>
                  <Textarea
                    id="employee-additional-info"
                    placeholder="Enter account numbers, routing numbers, SWIFT codes, or any other bank information..."
                    rows={6}
                    value={employeeBankForm.additional_info}
                    onChange={(e) => setEmployeeBankForm(prev => ({ ...prev, additional_info: e.target.value }))}
                  />
                </div>
                <Button onClick={handleSaveEmployeeBankInfo} disabled={!employeeBankForm.bank_name.trim()}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Bank Information
                </Button>
              </div>

              {/* Display Saved Bank Info in Table Format */}
              {employeeBankInfo && (
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-slate-900">Saved Bank Information</h3>
                    <div className="text-sm text-slate-500">
                      Last updated: {new Date(employeeBankInfo.updated_at).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Bank Name</th>
                          <th className="border border-slate-300 px-4 py-3 text-left font-medium text-slate-700">Bank Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-4 py-3 font-medium">{employeeBankInfo.bank_name}</td>
                          <td className="border border-slate-300 px-4 py-3 whitespace-pre-wrap">
                            {employeeBankInfo.additional_info || 'No additional information provided'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-800">
                        Only one bank account can be saved per employee. Use the form above to update this information.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Team Update Dialog */}
      <Dialog open={isTeamDialogOpen} onOpenChange={setIsTeamDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Employee Team</DialogTitle>
            <DialogDescription>
              Assign {employeeProfile?.employee?.name} to a team type
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="team-selection">Team Type</Label>
              <Select value={selectedTeamType} onValueChange={setSelectedTeamType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Team</SelectItem>
                  {teamTypes.map((teamType) => (
                    <SelectItem key={teamType.id} value={teamType.id}>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: teamType.color || "#3b82f6" }}
                        ></div>
                        <span>{teamType.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsTeamDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleTeamUpdate}>
                Update Team
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sales Products Component
const SalesProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [productFormData, setProductFormData] = useState({
    name: "",
    description: "",
    pricing_type: "one_time",
    price: ""
  });
  const [editingProduct, setEditingProduct] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsPerPage] = useState(50);

  // Filtering state
  const [productFilters, setProductFilters] = useState({
    name: "",
    pricing_type: ""
  });

  useEffect(() => {
    fetchProducts();
  }, [currentPage, productFilters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Check if we need client-side filtering
      const needsClientSideFiltering = productFilters.name || (productFilters.pricing_type && productFilters.pricing_type !== "all");
      
      if (needsClientSideFiltering) {
        // Fetch all products for client-side filtering
        const response = await axios.get(`${API}/products`);
        let allProducts = response.data || [];
        
        // Apply client-side filters
        let filteredProducts = allProducts;
        
        if (productFilters.name) {
          filteredProducts = filteredProducts.filter(product => 
            (product.name || "").toLowerCase().includes(productFilters.name.toLowerCase())
          );
        }
        
        if (productFilters.pricing_type && productFilters.pricing_type !== "all") {
          filteredProducts = filteredProducts.filter(product => 
            product.pricing_type === productFilters.pricing_type
          );
        }
        
        // Apply pagination manually
        const totalFiltered = filteredProducts.length;
        const startIndex = (currentPage - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
        const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
        
        setProducts(paginatedProducts);
        setTotalPages(Math.ceil(totalFiltered / productsPerPage));
        setTotalProducts(totalFiltered);
        
      } else {
        // Use normal pagination when no filters are applied
        const response = await axios.get(`${API}/products/paginated?page=${currentPage}&limit=${productsPerPage}`);
        
        // Handle paginated products response
        if (response.data.products) {
          setProducts(response.data.products);
          setTotalPages(response.data.pagination.total_pages);
          setTotalProducts(response.data.pagination.total_products);
        } else {
          setProducts(response.data); // Fallback for non-paginated response
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (filterKey, value) => {
    setProductFilters({...productFilters, [filterKey]: value});
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...productFormData,
        price: parseFloat(productFormData.price)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success("Product created successfully");
      }

      await fetchProducts();
      resetProductForm();
      setIsProductDialogOpen(false);
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      name: product.name,
      description: product.description || "",
      pricing_type: product.pricing_type,
      price: product.price.toString()
    });
    setIsProductDialogOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted successfully");
      await fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const resetProductForm = () => {
    setProductFormData({
      name: "",
      description: "",
      pricing_type: "one_time", 
      price: ""
    });
    setEditingProduct(null);
  };

  // Products are already filtered in fetchProducts function
  const filteredProducts = products;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Products Management</h1>
        <Button onClick={() => setIsProductDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Product Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filter Products</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="product-name-filter">Product Name</Label>
              <Input
                id="product-name-filter"
                placeholder="Search by name..."
                value={productFilters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="pricing-type-filter">Pricing Type</Label>
              <Select value={productFilters.pricing_type || "all"} onValueChange={(value) => handleFilterChange('pricing_type', value === "all" ? "" : value)}>
                <SelectTrigger id="pricing-type-filter">
                  <SelectValue placeholder="All pricing types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="one_time">One Time</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle>Products ({totalProducts} total)</CardTitle>
          <CardDescription>
            Manage your products and services catalog
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading products...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-700">Product Name</th>
                    <th className="text-left p-3 font-medium text-slate-700">Description</th>
                    <th className="text-left p-3 font-medium text-slate-700">Type</th>
                    <th className="text-right p-3 font-medium text-slate-700">Price</th>
                    <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? products.map((product, index) => (
                    <tr key={product.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                      <td className="p-3 font-medium">{product.name}</td>
                      <td className="p-3 max-w-xs truncate" title={product.description}>
                        {product.description || '-'}
                      </td>
                      <td className="p-3">
                        <Badge variant={product.pricing_type === 'subscription' ? 'default' : 'secondary'}>
                          {product.pricing_type === 'one_time' ? 'One Time' : 'Subscription'}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-bold text-emerald-600">
                        ${product.price ? product.price.toFixed(2) : '0.00'}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-slate-500">
                        No products found. Create your first product to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalProducts}
        itemsPerPage={productsPerPage}
        itemName="products"
      />

      {/* Add/Edit Product Dialog */}
      <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? "Update the product details" : "Create a new product in your catalog"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProductSubmit} className="space-y-4">
            <div>
              <Label htmlFor="product-name">Product Name</Label>
              <Input
                id="product-name"
                value={productFormData.name}
                onChange={(e) => setProductFormData({...productFormData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="product-description">Description</Label>
              <Textarea
                id="product-description"
                value={productFormData.description}
                onChange={(e) => setProductFormData({...productFormData, description: e.target.value})}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="pricing-type">Pricing Type</Label>
              <Select value={productFormData.pricing_type} onValueChange={(value) => setProductFormData({...productFormData, pricing_type: value})}>
                <SelectTrigger id="pricing-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One Time Payment</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="product-price">Price ($)</Label>
              <Input
                id="product-price"
                type="number"
                step="0.01"
                value={productFormData.price}
                onChange={(e) => setProductFormData({...productFormData, price: e.target.value})}
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                {editingProduct ? "Update Product" : "Create Product"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsProductDialogOpen(false);
                  resetProductForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SalesTransactions = () => {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]); // For name lookup
  const [loading, setLoading] = useState(true);
  const [isSalesDialogOpen, setIsSalesDialogOpen] = useState(false);
  const [salesFormData, setSalesFormData] = useState({
    date: new Date(),
    product_id: "",
    client_id: "",
    sale_price: "",
    pricing_type: "one_time",
    notes: ""
  });
  const [editingSale, setEditingSale] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const [salesPerPage] = useState(50);

  // Filtering state
  const [salesFilters, setSalesFilters] = useState({
    client: "",
    product: "",
    dateFrom: "",
    dateTo: ""
  });

  useEffect(() => {
    fetchSalesData();
  }, [currentPage]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const [salesResponse, productsResponse, clientsResponse] = await Promise.all([
        axios.get(`${API}/sales/paginated?page=${currentPage}&limit=${salesPerPage}`),
        axios.get(`${API}/products`),
        axios.get(`${API}/clients`)
      ]);
      
      // Handle paginated sales response
      if (salesResponse.data.sales) {
        setSales(salesResponse.data.sales);
        setTotalPages(salesResponse.data.pagination.total_pages);
        setTotalSales(salesResponse.data.pagination.total_sales);
      } else {
        setSales(salesResponse.data); // Fallback for non-paginated response
      }
      
      setProducts(productsResponse.data);
      setClients(clientsResponse.data);
      setAllClients(clientsResponse.data); // Store all clients for name lookup
    } catch (error) {
      console.error("Error fetching sales data:", error);
      toast.error("Failed to load sales data");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSalesSubmit = async (e) => {
    e.preventDefault();
    try {
      const salesData = {
        ...salesFormData,
        date: format(salesFormData.date, "yyyy-MM-dd"),
        sale_price: parseFloat(salesFormData.sale_price)
      };

      if (editingSale) {
        await axios.put(`${API}/sales/${editingSale.id}`, salesData);
        toast.success("Sale updated successfully");
      } else {
        await axios.post(`${API}/sales`, salesData);
        toast.success("Sale recorded successfully");
      }

      await fetchSalesData();
      resetSalesForm();
      setIsSalesDialogOpen(false);
    } catch (error) {
      console.error("Error saving sale:", error);
      
      // Handle different types of errors
      let errorMessage = "Failed to save sale";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle validation errors array
            errorMessage = error.response.data.detail
              .map(err => typeof err === 'string' ? err : err.msg || err.message || 'Validation error')
              .join(', ');
          } else {
            errorMessage = "Validation error occurred";
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setSalesFormData({
      date: new Date(sale.date),
      product_id: sale.product_id,
      client_id: sale.client_id,
      sale_price: sale.sale_price.toString(),
      pricing_type: sale.pricing_type || "one_time",
      notes: sale.notes || ""
    });
    setIsSalesDialogOpen(true);
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm("Are you sure you want to delete this sale?")) return;
    
    try {
      await axios.delete(`${API}/sales/${saleId}`);
      toast.success("Sale deleted successfully");
      await fetchSalesData();
    } catch (error) {
      console.error("Error deleting sale:", error);
      toast.error("Failed to delete sale");
    }
  };

  const resetSalesForm = () => {
    setSalesFormData({
      date: new Date(),
      product_id: "",
      client_id: "",
      sale_price: "",
      pricing_type: "one_time",
      notes: ""
    });
    setEditingSale(null);
  };

  const getClientName = (clientId) => {
    const client = allClients.find(c => c.id === clientId);
    return client ? client.company_name : "Unknown Client";
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : "Unknown Product";
  };

  // Filter sales based on current filters
  const filteredSales = sales.filter(sale => {
    const client = clients.find(c => c.id === sale.client_id);
    const product = products.find(p => p.id === sale.product_id);
    const clientName = client ? (client.company_name || "").toLowerCase() : "";
    const productName = product ? (product.name || "").toLowerCase() : "";
    
    return (
      clientName.includes(salesFilters.client.toLowerCase()) &&
      productName.includes(salesFilters.product.toLowerCase()) &&
      (salesFilters.dateFrom === "" || sale.date >= salesFilters.dateFrom) &&
      (salesFilters.dateTo === "" || sale.date <= salesFilters.dateTo)
    );
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Sales Transactions</h1>
        <Button onClick={() => setIsSalesDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Record Sale
        </Button>
      </div>

      {/* Sales Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filter Sales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="client-filter">Client</Label>
              <Input
                id="client-filter"
                placeholder="Search by client..."
                value={salesFilters.client}
                onChange={(e) => setSalesFilters({...salesFilters, client: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="product-filter">Product</Label>
              <Input
                id="product-filter"
                placeholder="Search by product..."
                value={salesFilters.product}
                onChange={(e) => setSalesFilters({...salesFilters, product: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="date-from">From Date</Label>
              <Input
                id="date-from"
                type="date"
                value={salesFilters.dateFrom}
                onChange={(e) => setSalesFilters({...salesFilters, dateFrom: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="date-to">To Date</Label>
              <Input
                id="date-to"
                type="date"
                value={salesFilters.dateTo}
                onChange={(e) => setSalesFilters({...salesFilters, dateTo: e.target.value})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sales ({totalSales} total)</CardTitle>
          <CardDescription>
            View and manage all sales transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading sales...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-700">Date</th>
                    <th className="text-left p-3 font-medium text-slate-700">Client</th>
                    <th className="text-left p-3 font-medium text-slate-700">Product</th>
                    <th className="text-left p-3 font-medium text-slate-700">Sales Type</th>
                    <th className="text-right p-3 font-medium text-slate-700">Sale Price</th>
                    <th className="text-left p-3 font-medium text-slate-700">Notes</th>
                    <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.length > 0 ? sales.map((sale, index) => (
                    <tr key={sale.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                      <td className="p-3">{sale.date}</td>
                      <td className="p-3 font-medium">{getClientName(sale.client_id)}</td>
                      <td className="p-3">{getProductName(sale.product_id)}</td>
                      <td className="p-3">
                        <Badge variant={sale.pricing_type === 'subscription' ? 'default' : 'secondary'}>
                          {sale.pricing_type === 'one_time' ? 'One Time' : 
                           sale.pricing_type === 'subscription' ? 'Subscription' : 
                           'One Time'}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-bold text-emerald-600">
                        ${sale.sale_price ? sale.sale_price.toFixed(2) : '0.00'}
                      </td>
                      <td className="p-3 max-w-xs truncate" title={sale.notes}>
                        {sale.notes || '-'}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditSale(sale)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteSale(sale.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-slate-500">
                        No sales found. Record your first sale to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalSales}
        itemsPerPage={salesPerPage}
        itemName="sales"
      />

      {/* Add/Edit Sale Dialog */}
      <Dialog open={isSalesDialogOpen} onOpenChange={setIsSalesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSale ? "Edit Sale" : "Record New Sale"}
            </DialogTitle>
            <DialogDescription>
              {editingSale ? "Update the sale details" : "Record a new sale transaction"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSalesSubmit} className="space-y-4">
            <div>
              <Label htmlFor="sale-date">Sale Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {salesFormData.date ? format(salesFormData.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={salesFormData.date}
                    onSelect={(date) => setSalesFormData({...salesFormData, date})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="sale-client">Client</Label>
              <Select value={salesFormData.client_id} onValueChange={(value) => setSalesFormData({...salesFormData, client_id: value})}>
                <SelectTrigger id="sale-client">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sale-product">Product</Label>
              <Select value={salesFormData.product_id} onValueChange={(value) => setSalesFormData({...salesFormData, product_id: value})}>
                <SelectTrigger id="sale-product">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (${product.price})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sale-price">Sale Price ($)</Label>
              <Input
                id="sale-price"
                type="number"
                step="0.01"
                value={salesFormData.sale_price}
                onChange={(e) => setSalesFormData({...salesFormData, sale_price: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="pricing-type">Pricing Type</Label>
              <Select value={salesFormData.pricing_type} onValueChange={(value) => setSalesFormData({...salesFormData, pricing_type: value})}>
                <SelectTrigger id="pricing-type">
                  <SelectValue placeholder="Select pricing type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One Time</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sale-notes">Notes</Label>
              <Textarea
                id="sale-notes"
                value={salesFormData.notes}
                onChange={(e) => setSalesFormData({...salesFormData, notes: e.target.value})}
                rows={3}
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                {editingSale ? "Update Sale" : "Record Sale"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsSalesDialogOpen(false);
                  resetSalesForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SalesExpenses = () => {
  const [salesExpenses, setSalesExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSalesExpenseDialogOpen, setIsSalesExpenseDialogOpen] = useState(false);
  const [salesExpenseFormData, setSalesExpenseFormData] = useState({
    date: new Date(),
    expense_type: "tool",
    name: "",
    amount: "",
    notes: ""
  });
  const [editingSalesExpense, setEditingSalesExpense] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSalesExpenses, setTotalSalesExpenses] = useState(0);
  const [salesExpensesPerPage] = useState(50);

  // Filtering state
  const [salesExpenseFilters, setSalesExpenseFilters] = useState({
    description: "",
    type: "",
    dateFrom: "",
    dateTo: ""
  });

  // Analytics state
  const [isAnalyticsExpanded, setIsAnalyticsExpanded] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({
    monthlySpending: [],
    categoryBreakdown: [],
    employeeSpending: [],
    toolSpending: [],
    totalSpending: 0,
    avgMonthlySpending: 0
  });

  useEffect(() => {
    fetchSalesExpensesData();
  }, [currentPage]);

  const fetchSalesExpensesData = async () => {
    try {
      setLoading(true);
      const [salesExpensesResponse, employeesResponse] = await Promise.all([
        axios.get(`${API}/sales-expenses/paginated?page=${currentPage}&limit=${salesExpensesPerPage}`),
        axios.get(`${API}/employees`)
      ]);
      
      // Handle paginated sales expenses response
      if (salesExpensesResponse.data.sales_expenses) {
        setSalesExpenses(salesExpensesResponse.data.sales_expenses);
        setTotalPages(salesExpensesResponse.data.pagination.total_pages);
        setTotalSalesExpenses(salesExpensesResponse.data.pagination.total_sales_expenses);
      } else {
        setSalesExpenses(salesExpensesResponse.data); // Fallback for non-paginated response
      }
      
      setEmployees(employeesResponse.data);

      // Fetch all sales expenses for analytics (not paginated)
      try {
        const allExpensesResponse = await axios.get(`${API}/sales-expenses`);
        processAnalyticsData(allExpensesResponse.data || []);
      } catch (analyticsError) {
        console.warn("Could not fetch analytics data:", analyticsError);
        // Don't show error to user, just use empty analytics
        processAnalyticsData([]);
      }
    } catch (error) {
      console.error("Error fetching sales expenses data:", error);
      toast.error("Failed to load sales expenses data");
    } finally {
      setLoading(false);
    }
  };

  // Process expense data for analytics
  const processAnalyticsData = (expenses) => {
    if (!expenses || expenses.length === 0) {
      setAnalyticsData({
        monthlySpending: [],
        categoryBreakdown: [],
        employeeSpending: [],
        toolSpending: [],
        totalSpending: 0,
        avgMonthlySpending: 0
      });
      return;
    }

    // Calculate total spending
    const totalSpending = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

    // Monthly spending breakdown
    const monthlyMap = {};
    const categoryMap = {};
    const employeeMap = {};
    const toolMap = {};

    expenses.forEach(expense => {
      const date = new Date(expense.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      
      // Monthly totals
      if (!monthlyMap[monthKey]) {
        monthlyMap[monthKey] = { month: monthName, total: 0, categories: {} };
      }
      monthlyMap[monthKey].total += expense.amount || 0;
      
      // Category breakdown within months
      const category = expense.expense_type || 'other';
      if (!monthlyMap[monthKey].categories[category]) {
        monthlyMap[monthKey].categories[category] = 0;
      }
      monthlyMap[monthKey].categories[category] += expense.amount || 0;

      // Overall category breakdown
      if (!categoryMap[category]) {
        categoryMap[category] = { name: category, total: 0, percentage: 0 };
      }
      categoryMap[category].total += expense.amount || 0;

      // Employee spending breakdown
      if (category === 'employee' || category === 'employee_payment') {
        const employeeName = expense.employee_name || expense.name || 'Unknown Employee';
        if (!employeeMap[employeeName]) {
          employeeMap[employeeName] = { name: employeeName, total: 0, transactions: 0 };
        }
        employeeMap[employeeName].total += expense.amount || 0;
        employeeMap[employeeName].transactions += 1;
      }

      // Tool spending breakdown
      if (category === 'tool' || category === 'software' || category === 'subscription') {
        const toolName = expense.name || expense.description || 'Unknown Tool';
        if (!toolMap[toolName]) {
          toolMap[toolName] = { name: toolName, total: 0, transactions: 0, type: category };
        }
        toolMap[toolName].total += expense.amount || 0;
        toolMap[toolName].transactions += 1;
      }
    });

    // Calculate percentages for categories
    Object.values(categoryMap).forEach(cat => {
      cat.percentage = totalSpending > 0 ? ((cat.total / totalSpending) * 100).toFixed(1) : 0;
    });

    // Convert maps to sorted arrays
    const monthlySpending = Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));
    const categoryBreakdown = Object.values(categoryMap).sort((a, b) => b.total - a.total);
    const employeeSpending = Object.values(employeeMap).sort((a, b) => b.total - a.total);
    const toolSpending = Object.values(toolMap).sort((a, b) => b.total - a.total);

    const avgMonthlySpending = monthlySpending.length > 0 ? totalSpending / monthlySpending.length : 0;

    setAnalyticsData({
      monthlySpending,
      categoryBreakdown,
      employeeSpending,
      toolSpending,
      totalSpending,
      avgMonthlySpending
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSalesExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const salesExpenseData = {
        ...salesExpenseFormData,
        date: format(salesExpenseFormData.date, "yyyy-MM-dd"),
        amount: parseFloat(salesExpenseFormData.amount)
      };

      if (editingSalesExpense) {
        await axios.put(`${API}/sales-expenses/${editingSalesExpense.id}`, salesExpenseData);
        toast.success("Sales expense updated successfully");
      } else {
        await axios.post(`${API}/sales-expenses`, salesExpenseData);
        toast.success("Sales expense recorded successfully");
      }

      await fetchSalesExpensesData();
      resetSalesExpenseForm();
      setIsSalesExpenseDialogOpen(false);
    } catch (error) {
      console.error("Error saving sales expense:", error);
      
      // Handle different types of errors
      let errorMessage = "Failed to save sales expense";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle validation errors array
            errorMessage = error.response.data.detail
              .map(err => typeof err === 'string' ? err : err.msg || err.message || 'Validation error')
              .join(', ');
          } else {
            errorMessage = "Validation error occurred";
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEditSalesExpense = (expense) => {
    setEditingSalesExpense(expense);
    setSalesExpenseFormData({
      date: new Date(expense.date),
      expense_type: expense.expense_type || "tool",
      name: expense.name || "",
      amount: expense.amount.toString(),
      notes: expense.notes || ""
    });
    setIsSalesExpenseDialogOpen(true);
  };

  const handleDeleteSalesExpense = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this sales expense?")) return;
    
    try {
      await axios.delete(`${API}/sales-expenses/${expenseId}`);
      toast.success("Sales expense deleted successfully");
      await fetchSalesExpensesData();
    } catch (error) {
      console.error("Error deleting sales expense:", error);
      toast.error("Failed to delete sales expense");
    }
  };

  const resetSalesExpenseForm = () => {
    setSalesExpenseFormData({
      date: new Date(),
      expense_type: "tool",
      name: "",
      amount: "",
      notes: ""
    });
    setEditingSalesExpense(null);
  };

  // Filter sales expenses based on current filters
  const filteredSalesExpenses = salesExpenses.filter(expense => {
    return (
      (expense.description || "").toLowerCase().includes(salesExpenseFilters.description.toLowerCase()) &&
      (salesExpenseFilters.type === "" || salesExpenseFilters.type === "all" || expense.expense_type === salesExpenseFilters.type) &&
      (salesExpenseFilters.dateFrom === "" || expense.date >= salesExpenseFilters.dateFrom) &&
      (salesExpenseFilters.dateTo === "" || expense.date <= salesExpenseFilters.dateTo)
    );
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Sales Expenses</h1>
        <Button onClick={() => setIsSalesExpenseDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Sales Expense
        </Button>
      </div>

      {/* Expense Analytics Section */}
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsAnalyticsExpanded(!isAnalyticsExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              Sales Expense Analytics
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isAnalyticsExpanded ? 'rotate-180' : ''}`} />
          </CardTitle>
          <CardDescription>Detailed breakdown of your sales expenses by category, employee, and time period</CardDescription>
        </CardHeader>
        {isAnalyticsExpanded && (
          <CardContent>
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-600">Total Spending</h3>
                  <p className="text-2xl font-bold text-blue-900">${analyticsData.totalSpending.toFixed(2)}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-600">Monthly Average</h3>
                  <p className="text-2xl font-bold text-green-900">${analyticsData.avgMonthlySpending.toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-600">Top Category</h3>
                  <p className="text-lg font-bold text-purple-900">
                    {analyticsData.categoryBreakdown[0]?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-purple-600">
                    {analyticsData.categoryBreakdown[0]?.percentage || 0}% of total
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-orange-600">Categories</h3>
                  <p className="text-2xl font-bold text-orange-900">{analyticsData.categoryBreakdown.length}</p>
                </div>
              </div>

              {/* Monthly Spending Chart */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Monthly Spending Overview
                </h3>
                {analyticsData.monthlySpending.length > 0 ? (
                  <div className="space-y-3">
                    {analyticsData.monthlySpending.map((month, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{month.month}</span>
                            <span className="font-bold text-lg">${month.total.toFixed(2)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full" 
                              style={{ width: `${(month.total / Math.max(...analyticsData.monthlySpending.map(m => m.total))) * 100}%` }}
                            ></div>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(month.categories).map(([cat, amount]) => (
                              <span key={cat} className="text-xs bg-white px-2 py-1 rounded text-gray-600">
                                {cat}: ${amount.toFixed(2)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No monthly data available</p>
                )}
              </div>

              {/* Category Breakdown */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Spending by Category
                </h3>
                {analyticsData.categoryBreakdown.length > 0 ? (
                  <div className="space-y-2">
                    {analyticsData.categoryBreakdown.map((category, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded ${
                            index === 0 ? 'bg-blue-500' :
                            index === 1 ? 'bg-green-500' :
                            index === 2 ? 'bg-purple-500' :
                            index === 3 ? 'bg-orange-500' : 'bg-gray-500'
                          }`}></div>
                          <span className="font-medium capitalize">{category.name.replace('_', ' ')}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">${category.total.toFixed(2)}</div>
                          <div className="text-sm text-gray-500">{category.percentage}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No category data available</p>
                )}
              </div>

              {/* Employee Spending Details */}
              {analyticsData.employeeSpending.length > 0 && (
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Employee Payment Breakdown
                  </h3>
                  <div className="space-y-2">
                    {analyticsData.employeeSpending.slice(0, 10).map((employee, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {employee.name.charAt(0)}
                          </div>
                          <span className="font-medium">{employee.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">${employee.total.toFixed(2)}</div>
                          <div className="text-sm text-gray-500">{employee.transactions} payments</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tool/Software Spending Details */}
              {analyticsData.toolSpending.length > 0 && (
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Tool & Software Spending
                  </h3>
                  <div className="space-y-2">
                    {analyticsData.toolSpending.slice(0, 10).map((tool, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            tool.type === 'software' ? 'bg-blue-400' :
                            tool.type === 'subscription' ? 'bg-green-400' : 'bg-orange-400'
                          }`}></div>
                          <span className="font-medium">{tool.name}</span>
                          <span className="text-xs text-gray-500 capitalize">({tool.type})</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">${tool.total.toFixed(2)}</div>
                          <div className="text-sm text-gray-500">{tool.transactions} transactions</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sales Expense Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filter Sales Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="expense-description-filter">Description</Label>
              <Input
                id="expense-description-filter"
                placeholder="Search by description..."
                value={salesExpenseFilters.description}
                onChange={(e) => setSalesExpenseFilters({...salesExpenseFilters, description: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="expense-type-filter">Expense Type</Label>
              <Select value={salesExpenseFilters.type || "all"} onValueChange={(value) => setSalesExpenseFilters({...salesExpenseFilters, type: value === "all" ? "" : value})}>
                <SelectTrigger id="expense-type-filter">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="tool">Tool</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="expense-date-from">From Date</Label>
              <Input
                id="expense-date-from"
                type="date"
                value={salesExpenseFilters.dateFrom}
                onChange={(e) => setSalesExpenseFilters({...salesExpenseFilters, dateFrom: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="expense-date-to">To Date</Label>
              <Input
                id="expense-date-to"
                type="date"
                value={salesExpenseFilters.dateTo}
                onChange={(e) => setSalesExpenseFilters({...salesExpenseFilters, dateTo: e.target.value})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Expenses ({totalSalesExpenses} total)</CardTitle>
          <CardDescription>
            Track expenses related to sales activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading sales expenses...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-700">Date</th>
                    <th className="text-left p-3 font-medium text-slate-700">Description</th>
                    <th className="text-left p-3 font-medium text-slate-700">Type</th>
                    <th className="text-left p-3 font-medium text-slate-700">Emp Name</th>
                    <th className="text-right p-3 font-medium text-slate-700">Amount</th>
                    <th className="text-left p-3 font-medium text-slate-700">Notes</th>
                    <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salesExpenses.length > 0 ? salesExpenses.map((expense, index) => (
                    <tr key={expense.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                      <td className="p-3">{expense.date}</td>
                      <td className="p-3 font-medium">{expense.name || expense.description || '-'}</td>
                      <td className="p-3">
                        <Badge variant={expense.expense_type === 'employee' || expense.expense_type === 'employee_payment' ? 'default' : 'secondary'}>
                          {expense.expense_type === 'employee' ? 'Employee' :
                           expense.expense_type === 'employee_payment' ? 'Employee Payment' :
                           expense.expense_type === 'tool' ? 'Tool' : 
                           expense.expense_type === 'marketing' ? 'Marketing' :
                           expense.expense_type === 'travel' ? 'Travel' : 
                           expense.expense_type === 'training' ? 'Training' :
                           expense.expense_type || 'Other'}
                        </Badge>
                      </td>
                      <td className="p-3 font-medium">
                        {expense.employee_name || '-'}
                      </td>
                      <td className="p-3 text-right font-bold text-red-600">
                        ${expense.amount ? expense.amount.toFixed(2) : '0.00'}
                      </td>
                      <td className="p-3">{expense.notes || '-'}</td>
                      <td className="p-3">
                        <div className="flex justify-center space-x-2">
                          {expense.source !== 'employee_payment' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSalesExpense(expense)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteSalesExpense(expense.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {expense.source === 'employee_payment' && (
                            <span className="text-sm text-slate-500 italic">Employee Payment</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-slate-500">
                        No sales expenses found. Add your first sales expense to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalSalesExpenses}
        itemsPerPage={salesExpensesPerPage}
        itemName="sales expenses"
      />

      {/* Add/Edit Sales Expense Dialog */}
      <Dialog open={isSalesExpenseDialogOpen} onOpenChange={setIsSalesExpenseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSalesExpense ? "Edit Sales Expense" : "Add Sales Expense"}
            </DialogTitle>
            <DialogDescription>
              {editingSalesExpense ? "Update the sales expense details" : "Record a new sales-related expense"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSalesExpenseSubmit} className="space-y-4">
            <div>
              <Label htmlFor="expense-date">Expense Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {salesExpenseFormData.date ? format(salesExpenseFormData.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={salesExpenseFormData.date}
                    onSelect={(date) => setSalesExpenseFormData({...salesExpenseFormData, date})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="expense-description">Name/Description</Label>
              <Input
                id="expense-description"
                value={salesExpenseFormData.name}
                onChange={(e) => setSalesExpenseFormData({...salesExpenseFormData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="expense-type">Expense Type</Label>
              <Select value={salesExpenseFormData.expense_type} onValueChange={(value) => setSalesExpenseFormData({...salesExpenseFormData, expense_type: value})}>
                <SelectTrigger id="expense-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tool">Tool/Software</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {salesExpenseFormData.expense_type === "employee" && (
              <div>
                <Label htmlFor="employee-name">Employee Name</Label>
                <Select value={salesExpenseFormData.employee_name} onValueChange={(value) => setSalesExpenseFormData({...salesExpenseFormData, employee_name: value})}>
                  <SelectTrigger id="employee-name">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(employee => (
                      <SelectItem key={employee.id} value={employee.name}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="expense-amount">Amount ($)</Label>
              <Input
                id="expense-amount"
                type="number"
                step="0.01"
                value={salesExpenseFormData.amount}
                onChange={(e) => setSalesExpenseFormData({...salesExpenseFormData, amount: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="expense-notes">Notes (Optional)</Label>
              <Input
                id="expense-notes"
                value={salesExpenseFormData.notes}
                onChange={(e) => setSalesExpenseFormData({...salesExpenseFormData, notes: e.target.value})}
                placeholder="Additional details about this expense..."
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                {editingSalesExpense ? "Update Expense" : "Add Expense"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsSalesExpenseDialogOpen(false);
                  resetSalesExpenseForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Authentication Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const savedUser = localStorage.getItem('bizmanager_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('bizmanager_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('bizmanager_user', JSON.stringify(userData));
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed. Please try again.' 
      };
    }
  };

  const employeeLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/employee-login`, {
        email,
        password
      });
      
      const employeeData = response.data.employee;
      // Set employee as user with special role
      const userWithRole = {
        ...employeeData,
        role: 'Employee',
        isEmployee: true
      };
      setUser(userWithRole);
      localStorage.setItem('bizmanager_user', JSON.stringify(userWithRole));
      return { success: true };
    } catch (error) {
      console.error('Employee login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Employee login failed. Please try again.' 
      };
    }
  };

  const managerLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/employee-login`, {
        email,
        password
      });
      
      const employeeData = response.data.employee;
      
      // Check if employee is a manager
      if (!employeeData.isManager) {
        return {
          success: false,
          error: 'This account does not have manager privileges. Please contact your administrator.'
        };
      }
      
      // Set manager as user with special role
      const userWithRole = {
        ...employeeData,
        role: 'Manager',
        isManager: true,
        isEmployee: true // Managers are also employees
      };
      setUser(userWithRole);
      localStorage.setItem('bizmanager_user', JSON.stringify(userWithRole));
      return { success: true };
    } catch (error) {
      console.error('Manager login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Manager login failed. Please try again.' 
      };
    }
  };

  const clientLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/client-login`, {
        email,
        password
      });
      
      const clientData = response.data.client;
      // Set client as user with special role
      const userWithRole = {
        ...clientData,
        role: 'Client',
        isClient: true
      };
      setUser(userWithRole);
      localStorage.setItem('bizmanager_user', JSON.stringify(userWithRole));
      return { success: true };
    } catch (error) {
      console.error('Client login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Client login failed. Please try again.' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      return { 
        success: true, 
        user: response.data.user,
        message: response.data.message 
      };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed. Please try again.' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('bizmanager_user');
  };

  // Create admin user if no users exist
  const createAdminUser = async () => {
    try {
      const response = await axios.post(`${API}/auth/create-admin`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to create admin user' 
      };
    }
  };

  const updateProfile = async (updatedData) => {
    try {
      // Make API call to update profile in database
      const response = await axios.put(`${API}/auth/update-profile`, {
        name: updatedData.name,
        email: updatedData.email
      });
      
      if (response.status === 200) {
        // Update local state only if API call succeeds
        const newUserData = { ...user, ...updatedData };
        setUser(newUserData);
        localStorage.setItem('bizmanager_user', JSON.stringify(newUserData));
        return { success: true };
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update profile' 
      };
    }
  };

  const value = {
    user,
    login,
    employeeLogin,
    managerLogin,
    clientLogin,
    register,
    logout,
    createAdminUser,
    updateProfile,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'employee'
  const [settings, setSettings] = useState({
    appName: 'BizManager',
    logo: null
  });
  const { login, employeeLogin, managerLogin, clientLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/general`);
      setSettings({
        appName: response.data.appName || 'BizManager',
        logo: response.data.logo ? `${API}/settings/logo` : null
      });
    } catch (error) {
      console.error("Error fetching app settings:", error);
      // Use defaults if settings not available
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (loginType === 'admin') {
        result = await login(email, password);
      } else if (loginType === 'employee') {
        result = await employeeLogin(email, password);
      } else if (loginType === 'manager') {
        result = await managerLogin(email, password);
      } else if (loginType === 'client') {
        result = await clientLogin(email, password);
      }
      
      if (result.success) {
        // Login successful - redirect based on login type
        if (loginType === 'employee') {
          // For employee login, get user data from localStorage and redirect to their profile
          const userData = JSON.parse(localStorage.getItem('bizmanager_user'));
          if (userData && userData.id) {
            navigate(`/employee-profile/${userData.id}`);
          }
        } else if (loginType === 'manager') {
          // For manager login, redirect to manager dashboard
          navigate('/manager-dashboard');
        } else if (loginType === 'client') {
          // For client login, redirect to client dashboard
          navigate('/client-dashboard');
        } else {
          // For admin login, redirect to dashboard
          navigate('/');
        }
      } else {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotMessage('Password reset link sent to your email!');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotMessage('');
        setForgotEmail('');
      }, 3000);
    } catch (error) {
      setForgotMessage('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-8">
          <Card className="shadow-2xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4">
                {settings.logo ? (
                  <img
                    src={settings.logo}
                    alt="Logo"
                    className="w-16 h-16 object-contain mx-auto"
                  />
                ) : (
                  <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto">
                    <Building2 className="h-8 w-8 text-white" />
                  </div>
                )}
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">{settings.appName}</CardTitle>
              <CardDescription className="text-slate-600">
                Reset Your Password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgotEmail">Email Address</Label>
                  <Input
                    id="forgotEmail"
                    type="email"
                    placeholder="Enter your email address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    disabled={forgotLoading}
                  />
                </div>

                {forgotMessage && (
                  <div className={`p-3 rounded text-sm ${
                    forgotMessage.includes('sent') 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {forgotMessage}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              {settings.logo ? (
                <img
                  src={settings.logo}
                  alt="Logo"
                  className="w-16 h-16 object-contain mx-auto"
                />
              ) : (
                <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">{settings.appName}</CardTitle>
            <CardDescription className="text-slate-600">
              Welcome back! Please sign in to your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="loginType">Login As</Label>
                <select
                  id="loginType"
                  value={loginType}
                  onChange={(e) => setLoginType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="admin">Admin Login</option>
                  <option value="employee">Employee Login</option>
                  <option value="manager">Manager Login</option>
                  <option value="client">Client Login</option>
                </select>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="benison.thomas@shoppias.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="demo1234"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-slate-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot your password?
                </button>
                {/* Registration link removed as requested */}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Role Management Component
const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchUsers();
    fetchAvailablePermissions();
    initializeDefaultRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to fetch roles");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/employees`); // Using employees endpoint for user list
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions/modules`);
      setAvailablePermissions(response.data.modules);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultRoles = async () => {
    try {
      await axios.post(`${API}/roles/initialize-default`);
    } catch (error) {
      console.error("Error initializing default roles:", error);
    }
  };

  const handleCreateRole = async () => {
    try {
      if (!roleForm.name.trim()) {
        toast.error("Role name is required");
        return;
      }

      const roleData = {
        name: roleForm.name.trim(),
        description: roleForm.description.trim(),
        permissions: roleForm.permissions
      };

      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, roleData);
        toast.success("Role updated successfully");
      } else {
        await axios.post(`${API}/roles`, roleData);
        toast.success("Role created successfully");
      }

      fetchRoles();
      setIsRoleDialogOpen(false);
      resetRoleForm();
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error(error.response?.data?.detail || "Failed to save role");
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;

    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success("Role deleted successfully");
      fetchRoles();
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setIsRoleDialogOpen(true);
  };

  const resetRoleForm = () => {
    setEditingRole(null);
    setRoleForm({
      name: '',
      description: '',
      permissions: []
    });
  };

  const handleAddPermission = () => {
    const newPermission = {
      module: 'projects',
      resource: 'list',
      field: null,
      action: 'view'
    };
    setRoleForm(prev => ({
      ...prev,
      permissions: [...prev.permissions, newPermission]
    }));
  };

  const handleUpdatePermission = (index, field, value) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.map((perm, i) =>
        i === index ? { ...perm, [field]: value } : perm
      )
    }));
  };

  const handleRemovePermission = (index) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.filter((_, i) => i !== index)
    }));
  };

  const assignRoleToUser = async (userId, roleId) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role_id: roleId });
      toast.success("Role assigned successfully");
      fetchUsers();
    } catch (error) {
      console.error("Error assigning role:", error);
      toast.error(error.response?.data?.detail || "Failed to assign role");
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading roles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Role Management</h1>
          <p className="text-gray-600 mt-1">Create and manage user roles with custom permissions</p>
        </div>
        <Button 
          onClick={() => {
            resetRoleForm();
            setIsRoleDialogOpen(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Roles List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Roles ({roles.length})</span>
            </CardTitle>
            <CardDescription>
              Manage system roles and their permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {roles.map((role) => (
                <div key={role.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-gray-900">{role.name}</h3>
                      {role.is_system_role && (
                        <Badge variant="outline" className="text-xs">System</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{role.description || 'No description'}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {role.permissions?.length || 0} permissions
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditRole(role)}
                      disabled={role.is_system_role}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRole(role.id)}
                      disabled={role.is_system_role}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {roles.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No roles found</p>
                  <p className="text-sm">Create your first role to get started</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Role Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>User Role Assignment</span>
            </CardTitle>
            <CardDescription>
              Assign roles to your team members
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{user.name}</h3>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <p className="text-xs text-gray-400">
                      Current role: {user.role_name || user.role || 'No role assigned'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      className="text-sm border rounded px-2 py-1"
                      value={user.role_id || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          assignRoleToUser(user.id, e.target.value);
                        }
                      }}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              
              {users.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No users found</p>
                  <p className="text-sm">Add team members to assign roles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Creation/Edit Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="max-w-4xl max-h-screen overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </DialogTitle>
            <DialogDescription>
              {editingRole ? 'Modify role permissions and settings' : 'Define role permissions and access levels'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Role Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="roleName">Role Name</Label>
                <Input
                  id="roleName"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Project Manager"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="roleDescription">Description</Label>
                <Input
                  id="roleDescription"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this role"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Permissions */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-medium">Permissions</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddPermission}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Permission
                </Button>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {roleForm.permissions.map((permission, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {/* Module */}
                    <div className="flex-1">
                      <Label className="text-xs text-gray-600">Module</Label>
                      <select
                        className="w-full text-sm border rounded px-2 py-1 mt-1"
                        value={permission.module}
                        onChange={(e) => handleUpdatePermission(index, 'module', e.target.value)}
                      >
                        {Object.keys(availablePermissions).map((module) => (
                          <option key={module} value={module}>
                            {module.charAt(0).toUpperCase() + module.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Resource */}
                    <div className="flex-1">
                      <Label className="text-xs text-gray-600">Resource</Label>
                      <select
                        className="w-full text-sm border rounded px-2 py-1 mt-1"
                        value={permission.resource}
                        onChange={(e) => handleUpdatePermission(index, 'resource', e.target.value)}
                      >
                        {(availablePermissions[permission.module]?.resources || []).map((resource) => (
                          <option key={resource} value={resource}>
                            {resource.replace('_', ' ').charAt(0).toUpperCase() + resource.replace('_', ' ').slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Field (Optional) */}
                    <div className="flex-1">
                      <Label className="text-xs text-gray-600">Field (Optional)</Label>
                      <select
                        className="w-full text-sm border rounded px-2 py-1 mt-1"
                        value={permission.field || ''}
                        onChange={(e) => handleUpdatePermission(index, 'field', e.target.value || null)}
                      >
                        <option value="">All fields</option>
                        {(availablePermissions[permission.module]?.fields || []).map((field) => (
                          <option key={field} value={field}>
                            {field.replace('_', ' ').charAt(0).toUpperCase() + field.replace('_', ' ').slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Action */}
                    <div className="flex-1">
                      <Label className="text-xs text-gray-600">Action</Label>
                      <select
                        className="w-full text-sm border rounded px-2 py-1 mt-1"
                        value={permission.action}
                        onChange={(e) => handleUpdatePermission(index, 'action', e.target.value)}
                      >
                        {(availablePermissions[permission.module]?.actions || []).map((action) => (
                          <option key={action} value={action}>
                            {action.charAt(0).toUpperCase() + action.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Remove Button */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePermission(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                {roleForm.permissions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No permissions added</p>
                    <p className="text-sm">Add permissions to define role capabilities</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsRoleDialogOpen(false);
                resetRoleForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateRole}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Invoice Management Component
const InvoiceManagement = () => {
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoicesPerPage] = useState(50);
  
  // Dialog states
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  
  // Preview states
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Form states
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Template editor states
  const [templateEditorType, setTemplateEditorType] = useState('form'); // 'form' or 'html'
  
  // Download filters
  const [downloadFilters, setDownloadFilters] = useState({
    client_id: '',
    payment_status: '',
    start_date: '',
    end_date: ''
  });
  const [htmlTemplateContent, setHtmlTemplateContent] = useState('');
  const [cssTemplateContent, setCssTemplateContent] = useState('');
  const [availablePlaceholders, setAvailablePlaceholders] = useState([]);
  const [showPlaceholderHelper, setShowPlaceholderHelper] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    template_id: '',
    client_id: '',
    issue_date: new Date().toISOString().split('T')[0], // Safe date format
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Safe date format
    frequency: 'one_time',  // Frequency field for download-based system
    is_recurring: false,      // Recurring flag
    product_type: 'SERVICE',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    internal_notes: ''
  });
  
  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    template_type: 'form_based',
    html_content: '',
    css_content: '',
    company_name: 'Your Company Name',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_logo_url: '',
    currency: 'USD',
    currency_symbol: '$',
    tax_rate: 0,
    discount_rate: 0,
    payment_terms: 'Net 30',
    notes: '',
    footer_text: 'Thank you for your business!',
    primary_color: '#1f2937',
    accent_color: '#3b82f6',
    is_default: false
  });
  


  useEffect(() => {
    fetchAllData();
  }, []);



  const fetchDefaultHtmlTemplate = async () => {
    try {
      const response = await axios.get(`${API}/invoice-templates/default-html`);
      setHtmlTemplateContent(response.data.html_template);
      setCssTemplateContent(response.data.css_template);
      setAvailablePlaceholders(response.data.available_placeholders);
    } catch (error) {
      console.error('Error fetching default template:', error);
      toast.error('Failed to load default template');
    }
  };

  const handleLogoUpload = async (templateId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/invoice-templates/${templateId}/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.logo_url;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('html-editor');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = htmlTemplateContent;
      const newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end);
      setHtmlTemplateContent(newValue);
      
      // Reset cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      if (!invoiceForm.template_id || !invoiceForm.client_id) {
        toast.error('Please select template and client');
        return;
      }

      // Validate automatic send date if enabled
      if (invoiceForm.auto_send_enabled && !invoiceForm.auto_send_date) {
        toast.error('Please select an automatic send date');
        return;
      }

      // Validate that auto send date is not in the past
      if (invoiceForm.auto_send_enabled && invoiceForm.auto_send_date) {
        const sendDate = new Date(invoiceForm.auto_send_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (sendDate < today) {
          toast.error('Automatic send date cannot be in the past');
          return;
        }
      }

      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, invoiceForm);
        toast.success('Invoice updated successfully');
      } else {
        await axios.post(`${API}/invoices`, invoiceForm);
        toast.success('Invoice created successfully');
      }

      await fetchAllData();
      setIsInvoiceDialogOpen(false);
      resetInvoiceForm();
    } catch (error) {
      console.error('Error saving invoice:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to save invoice';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle Pydantic validation errors
            const messages = errorData.detail.map(err => {
              if (typeof err === 'object' && err.msg) {
                return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
              }
              return String(err);
            });
            errorMessage = messages.join(', ');
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      if (!templateForm.name.trim()) {
        toast.error('Template name is required');
        return;
      }

      let templateData = { ...templateForm };

      // Handle HTML template content
      if (templateForm.template_type === 'html_based') {
        templateData.html_content = htmlTemplateContent;
        templateData.css_content = cssTemplateContent;
      }

      let templateId;
      if (editingTemplate) {
        await axios.put(`${API}/invoice-templates/${editingTemplate.id}`, templateData);
        templateId = editingTemplate.id;
        toast.success('Template updated successfully');
      } else {
        const response = await axios.post(`${API}/invoice-templates`, templateData);
        templateId = response.data.id;
        toast.success('Template created successfully');
      }

      // Handle logo upload if there's a new logo file
      if (logoFile && templateId) {
        try {
          const logoUrl = await handleLogoUpload(templateId, logoFile);
          console.log('Logo uploaded:', logoUrl);
        } catch (error) {
          toast.error('Template saved but logo upload failed');
        }
      }

      fetchAllData();
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
    } catch (error) {
      console.error('Error saving template:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to save template';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle Pydantic validation errors
            const messages = errorData.detail.map(err => {
              if (typeof err === 'object' && err.msg) {
                return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
              }
              return String(err);
            });
            errorMessage = messages.join(', ');
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`);
      toast.success('Invoice sent successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleViewInvoiceOnline = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/view-online`);
      const htmlContent = response.data.html_content;
      
      // Open in new window
      const newWindow = window.open('', '_blank');
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } catch (error) {
      console.error('Error viewing invoice online:', error);
      toast.error('Failed to view invoice online');
    }
  };

  const handleViewSentEmail = (invoice) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const invoiceUrl = `${backendUrl}/api/invoices/${invoice.id}/view-online`;
    
    const emailContent = `
Subject: Invoice #${invoice.invoice_number} from ${invoice.template?.company_name || 'Company'}

Dear ${invoice.client_name},

Your invoice #${invoice.invoice_number} is ready for review.

Invoice Details:
- Amount: $${invoice.total_amount?.toFixed(2)}
- Due Date: ${invoice.due_date}
- Issue Date: ${invoice.issue_date}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📄 VIEW YOUR INVOICE ONLINE

Click the link below to view your invoice:
👉 ${invoiceUrl}

This link will open your invoice in your browser with full details and professional formatting.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Thank you for your business!

Best regards,
${invoice.template?.company_name || 'Company'}

---
This email was sent on: ${invoice.sent_at ? new Date(invoice.sent_at).toLocaleString() : 'N/A'}
To: ${invoice.client_email || 'Client Email'}
    `;

    // Show email content in a modal or new window
    const newWindow = window.open('', '_blank', 'width=700,height=500');
    newWindow.document.write(`
      <html>
        <head><title>Sent Email View - Invoice #${invoice.invoice_number}</title></head>
        <body style="font-family: Arial, sans-serif; padding: 20px; background-color: #f8fafc;">
          <div style="background-color: white; padding: 30px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.05);">
            <h2 style="color: #2563eb; margin-bottom: 20px; font-size: 20px;">📧 Email View - Invoice #${invoice.invoice_number}</h2>
            <div style="background-color: #f8fafc; padding: 20px; border-radius: 6px; border-left: 4px solid #2563eb;">
              <pre style="white-space: pre-wrap; font-family: 'Courier New', monospace; line-height: 1.6; margin: 0; font-size: 13px;">${emailContent}</pre>
            </div>
            <div style="margin-top: 20px; padding: 15px; background-color: #dbeafe; border-radius: 6px;">
              <p style="margin: 0; font-size: 12px; color: #1e40af;"><strong>💡 Note:</strong> This email contains an online invoice link instead of a PDF attachment for better deliverability and faster loading.</p>
            </div>
          </div>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success('Invoice deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API}/invoice-templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };







  // Download invoice handler with frequency-based logic
  const handleDownloadInvoice = async (invoice) => {
    try {
      const response = await axios.post(`${API}/invoices/download/${invoice.id}`, {
        frequency: invoice.frequency
      });
      
      if (response.data.success) {
        const downloadData = response.data;
        
        // Add to downloads list
        const newDownload = {
          id: downloadData.download_id,
          invoice_number: downloadData.invoice_number,
          client_name: invoice.client_name,
          issue_date: downloadData.issue_date,
          due_date: downloadData.due_date,
          download_date: new Date().toISOString()
        };
        
        setDownloads(prev => [newDownload, ...prev]);
        
        // Create PDF blob and download (proper binary handling)
        const binaryString = atob(downloadData.pdf_content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${downloadData.invoice_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`Invoice ${downloadData.invoice_number} downloaded successfully!`);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to download invoice';
      toast.error(errorMessage);
    }
  };

  const handleViewDownloadedInvoice = async (downloadId) => {
    try {
      const response = await axios.get(`${API}/downloads/${downloadId}/view`);
      
      if (response.data.pdf_content) {
        // Create PDF blob and open in new tab
        // Fix: Properly convert base64 to Uint8Array to avoid UTF-8 corruption
        const binaryString = atob(response.data.pdf_content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error viewing downloaded invoice:', error);
      toast.error('Failed to view downloaded invoice');
    }
  };

  const handlePreviewInvoice = async (invoiceId, invoiceNumber) => {
    try {
      setPreviewLoading(true);
      setPreviewTitle(`Invoice Preview - ${invoiceNumber}`);
      
      const response = await axios.get(`${API}/invoices/${invoiceId}/preview`);
      setPreviewHtml(response.data.html);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error generating invoice preview:', error);
      toast.error('Failed to generate invoice preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewTemplate = async (templateId, templateName) => {
    try {
      setPreviewLoading(true);
      setPreviewTitle(`Template Preview - ${templateName}`);
      
      const response = await axios.get(`${API}/invoice-templates/${templateId}/preview`);
      setPreviewHtml(response.data.html);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error generating template preview:', error);
      toast.error('Failed to generate template preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Payment status update handler
  const handleUpdatePaymentStatus = async (downloadId, newStatus) => {
    try {
      await axios.put(`${API}/downloads/${downloadId}/payment-status`, {
        payment_status: newStatus
      });
      
      // Update the downloads list
      setDownloads(prev => prev.map(download => 
        download.id === downloadId 
          ? { ...download, payment_status: newStatus }
          : download
      ));
      
      toast.success(`Payment status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  // Filter handler
  const handleFilterChange = (filterName, value) => {
    // Convert "all" to empty string for API filtering
    const filterValue = value === "all" ? "" : value;
    setDownloadFilters(prev => ({
      ...prev,
      [filterName]: filterValue
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setDownloadFilters({
      client_id: '',
      payment_status: '',
      start_date: '',
      end_date: ''
    });
  };

  const resetInvoiceForm = () => {
    setEditingInvoice(null);
    setInvoiceForm({
      template_id: '',
      client_id: '',
      issue_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      frequency: 'one_time',      // Frequency field for download-based system
      is_recurring: false,        // Recurring flag
      product_type: 'SERVICE',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      internal_notes: ''
    });
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      description: '',
      template_type: 'form_based',
      html_content: '',
      css_content: '',
      company_name: 'Your Company Name',
      company_address: '',
      company_phone: '',
      company_email: '',
      company_logo_url: '',
      currency: 'USD',
      currency_symbol: '$',
      tax_rate: 0,
      discount_rate: 0,
      payment_terms: 'Net 30',
      notes: '',
      footer_text: 'Thank you for your business!',
      primary_color: '#1f2937',
      accent_color: '#3b82f6',
      is_default: false
    });
    setHtmlTemplateContent('');
    setCssTemplateContent('');
    setLogoFile(null);
    setLogoPreview(null);
    setTemplateEditorType('form');
  };

  // Data fetching
  useEffect(() => {
    fetchAllData();
  }, [currentPage]);

  // Refetch downloads when filters change
  useEffect(() => {
    fetchDownloads();
  }, [downloadFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [templatesRes, invoicesRes, clientsRes, productsRes] = await Promise.all([
        axios.get(`${API}/invoice-templates`),
        axios.get(`${API}/invoices/paginated?page=${currentPage}&limit=${invoicesPerPage}`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/products`)
      ]);
      
      setTemplates(templatesRes.data);
      
      // Handle paginated invoices response
      if (invoicesRes.data.invoices) {
        setInvoices(invoicesRes.data.invoices);
        setTotalPages(invoicesRes.data.pagination.total_pages);
        setTotalInvoices(invoicesRes.data.pagination.total_invoices);
      } else {
        setInvoices(invoicesRes.data); // Fallback for non-paginated response
      }
      
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      
      // Fetch downloads with current filters
      await fetchDownloads();
      
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDownloads = async () => {
    try {
      // Build query parameters for filtering
      const params = new URLSearchParams();
      
      if (downloadFilters.client_id) {
        params.append('client_id', downloadFilters.client_id);
      }
      if (downloadFilters.payment_status) {
        params.append('payment_status', downloadFilters.payment_status);
      }
      if (downloadFilters.start_date) {
        params.append('start_date', downloadFilters.start_date);
      }
      if (downloadFilters.end_date) {
        params.append('end_date', downloadFilters.end_date);
      }
      
      const url = `${API}/downloads${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setDownloads(response.data || []);
      
    } catch (error) {
      console.error('Error fetching downloads:', error);
      toast.error('Failed to load downloads');
    }
  };

  const addInvoiceItem = () => {
    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeInvoiceItem = (index) => {
    if (invoiceForm.items.length > 1) {
      setInvoiceForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateInvoiceItem = (index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateInvoiceTotal = (items) => {
    return items.reduce((total, item) => total + (item.quantity * item.unit_price), 0);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading invoices...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoice Management</h1>
          <p className="text-gray-600 mt-1">Create templates, manage invoices, and schedule automated billing</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'templates', label: 'Templates', icon: Copy, count: templates.length },
            { id: 'invoices', label: 'Invoices', icon: FileText, count: invoices.filter(inv => inv.status !== 'sent').length },
            { id: 'downloads', label: 'Downloads', icon: Download }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
              <Badge variant="secondary" className="ml-1">
                {tab.count}
              </Badge>
            </button>
          ))}
        </nav>
      </div>

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Invoices</h2>
            <Button
              onClick={() => {
                resetInvoiceForm();
                setIsInvoiceDialogOpen(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Frequency Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.client_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${invoice.total_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.is_recurring ? (
                            <div className="flex flex-col">
                              <Badge variant="default" className="bg-purple-100 text-purple-800 mb-1">
                                {invoice.frequency?.charAt(0).toUpperCase() + invoice.frequency?.slice(1)}
                              </Badge>
                              <div className="text-xs text-gray-500">
                                {invoice.frequency_start_date && `${invoice.frequency_start_date}`}
                                {invoice.frequency_end_date && ` - ${invoice.frequency_end_date}`}
                              </div>
                            </div>
                          ) : (
                            <Badge variant="outline">
                              One Time
                            </Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingInvoice(invoice);
                                setInvoiceForm({
                                  ...invoice,
                                  issue_date: invoice.issue_date || format(new Date(), 'yyyy-MM-dd'),
                                  due_date: invoice.due_date || format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
                                });
                                setIsInvoiceDialogOpen(true);
                              }}
                              className="text-green-600 hover:text-green-700"
                              title="Edit Invoice"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreviewInvoice(invoice.id, invoice.invoice_number)}
                              className="text-purple-600 hover:text-purple-700"
                              disabled={previewLoading}
                              title="Preview Invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadInvoice(invoice)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Download Invoice"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Invoice"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {invoices.length === 0 && (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 mb-2">No invoices found</p>
                    <p className="text-sm text-gray-400">Create your first invoice to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Pagination for Invoices */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalInvoices}
            itemsPerPage={invoicesPerPage}
            itemName="invoices"
          />
        </div>
      )}



      {/* Downloads Tab */}
      {activeTab === 'downloads' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Downloaded Invoices</h2>
            <div className="text-sm text-gray-600">
              Track all invoices downloaded from the Invoices tab
            </div>
          </div>

          {/* Filter Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="filter-client">Client</Label>
                  <Select value={downloadFilters.client_id || "all"} onValueChange={(value) => handleFilterChange('client_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="filter-status">Payment Status</Label>
                  <Select value={downloadFilters.payment_status || "all"} onValueChange={(value) => handleFilterChange('payment_status', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Unpaid">Unpaid</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="filter-start-date">Start Date</Label>
                  <Input
                    type="date"
                    value={downloadFilters.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor="filter-end-date">End Date</Label>
                  <Input
                    type="date"
                    value={downloadFilters.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Issue Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Download Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {downloads.map((download) => (
                      <tr key={download.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          {download.invoice_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {download.client_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(download.issue_date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(download.due_date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(download.download_date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center space-x-2">
                            <Badge 
                              variant={
                                (download.payment_status || 'Pending') === 'Paid' ? 'default' : 
                                (download.payment_status || 'Pending') === 'Unpaid' ? 'destructive' : 
                                'secondary'
                              }
                              className={
                                (download.payment_status || 'Pending') === 'Paid' ? 'bg-green-100 text-green-800' :
                                (download.payment_status || 'Pending') === 'Unpaid' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }
                            >
                              {download.payment_status || 'Pending'}
                            </Badge>
                            <Select 
                              value={download.payment_status || 'Pending'} 
                              onValueChange={(value) => handleUpdatePaymentStatus(download.id, value)}
                            >
                              <SelectTrigger className="w-24 h-6 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Paid">Paid</SelectItem>
                                <SelectItem value="Unpaid">Unpaid</SelectItem>
                                <SelectItem value="Pending">Pending</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDownloadedInvoice(download.id)}
                            className="text-blue-600 hover:text-blue-700"
                            title="View Downloaded Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {downloads.length === 0 && (
                  <div className="text-center py-12">
                    <Download className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 mb-2">No downloads yet</p>
                    <p className="text-sm text-gray-400">Downloaded invoices will appear here when you use the Download button in the Invoices tab</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Invoice Templates</h2>
            <Button
              onClick={() => {
                resetTemplateForm();
                setIsTemplateDialogOpen(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {template.is_default && (
                      <Badge variant="default">Default</Badge>
                    )}
                  </div>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p><strong>Company:</strong> {template.company_name}</p>
                    <p><strong>Currency:</strong> {template.currency_symbol} {template.currency}</p>
                    <p><strong>Tax Rate:</strong> {template.tax_rate}%</p>
                    <p><strong>Payment Terms:</strong> {template.payment_terms}</p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewTemplate(template.id, template.name)}
                        className="text-purple-600 hover:text-purple-700"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingTemplate(template);
                          setTemplateForm({
                            ...template,
                            template_type: template.template_type || 'form_based'
                          });
                          if (template.template_type === 'html_based') {
                            setHtmlTemplateContent(template.html_content || '');
                            setCssTemplateContent(template.css_content || '');
                          }
                          if (template.company_logo_url) {
                            setLogoPreview(template.company_logo_url);
                          }
                          setIsTemplateDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {templates.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Copy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-2">No templates found</p>
                <p className="text-sm text-gray-400">Create your first template to get started</p>
              </div>
            )}
          </div>
        </div>
      )}



      {/* Create/Edit Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="max-w-4xl max-h-screen overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}
            </DialogTitle>
            <DialogDescription>
              Fill in the invoice details below
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template_id">Template *</Label>
                <select
                  id="template_id"
                  className="w-full mt-1 border rounded px-3 py-2"
                  value={invoiceForm.template_id}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, template_id: e.target.value }))}
                >
                  <option value="">Select Template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="client_id">Client *</Label>
                <select
                  id="client_id"
                  className="w-full mt-1 border rounded px-3 py-2"
                  value={invoiceForm.client_id}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, client_id: e.target.value }))}
                >
                  <option value="">Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name} - {client.contact_person}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="issue_date">Issue Date</Label>
                <Input
                  id="issue_date"
                  type="date"
                  value={invoiceForm.issue_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, issue_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="product_type">Product Type</Label>
                <Input
                  id="product_type"
                  value={invoiceForm.product_type}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, product_type: e.target.value }))}
                  placeholder="e.g., SERVICE, PRODUCT, CONSULTING"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Frequency Settings Section */}
            <div className="border rounded-lg p-4 bg-purple-50">
              <h3 className="text-lg font-medium mb-3 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-purple-600" />
                Invoice Frequency
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="frequency">Frequency *</Label>
                  <select
                    id="frequency"
                    className="w-full mt-1 border rounded px-3 py-2"
                    value={invoiceForm.frequency}
                    onChange={(e) => {
                      const isRecurring = e.target.value !== 'one_time';
                      setInvoiceForm(prev => ({ 
                        ...prev, 
                        frequency: e.target.value,
                        is_recurring: isRecurring
                      }));
                    }}
                  >
                    <option value="one_time">One Time</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                

              </div>
              
              {invoiceForm.is_recurring && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Recurring Invoice:</strong> This invoice will automatically update its dates and invoice number based on the selected frequency. 
                    The issue date and due date will increment {invoiceForm.frequency === 'weekly' ? 'weekly' : 
                    invoiceForm.frequency === 'monthly' ? 'monthly' : 
                    invoiceForm.frequency === 'quarterly' ? 'quarterly' : 'annually'}.
                  </p>
                </div>
              )}
            </div>



            {/* Invoice Items */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-medium">Invoice Items</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addInvoiceItem}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
              
              <div className="space-y-3">
                {invoiceForm.items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateInvoiceItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-28">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateInvoiceItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-28 text-right font-medium">
                      ${(item.quantity * item.unit_price).toFixed(2)}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeInvoiceItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                <div className="flex justify-end">
                  <div className="w-28 text-right font-bold text-lg">
                    Total: ${calculateInvoiceTotal(invoiceForm.items).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="notes">Notes (visible to client)</Label>
                <textarea
                  id="notes"
                  rows={3}
                  className="w-full mt-1 border rounded px-3 py-2"
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes for the client"
                />
              </div>
              
              <div>
                <Label htmlFor="internal_notes">Internal Notes</Label>
                <textarea
                  id="internal_notes"
                  rows={3}
                  className="w-full mt-1 border rounded px-3 py-2"
                  value={invoiceForm.internal_notes}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, internal_notes: e.target.value }))}
                  placeholder="Internal notes (not visible to client)"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsInvoiceDialogOpen(false);
                resetInvoiceForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateInvoice}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="max-w-6xl max-h-screen overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              Create form-based templates or custom HTML templates with your own design
            </DialogDescription>
          </DialogHeader>
          
          {/* Available Placeholders Section - Always Visible */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-900 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Available Placeholders
              </h3>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {availablePlaceholders.length} Total
              </Badge>
            </div>
            
            <p className="text-blue-700 mb-4 text-sm">
              Use these placeholders in your HTML templates. They will be automatically replaced with actual data when generating invoices.
            </p>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {[
                { placeholder: '{{company_name}}', desc: 'Your company name', category: 'Company' },
                { placeholder: '{{company_address}}', desc: 'Company address (with line breaks)', category: 'Company' },
                { placeholder: '{{company_phone}}', desc: 'Company phone number', category: 'Company' },
                { placeholder: '{{company_email}}', desc: 'Company email address', category: 'Company' },
                { placeholder: '{{company_website}}', desc: 'Company website URL', category: 'Company' },
                { placeholder: '{{company_logo}}', desc: 'Company logo image (upload via settings)', category: 'Company' },
                { placeholder: '{{client_name}}', desc: 'Client/customer company name', category: 'Client' },
                { placeholder: '{{client_address}}', desc: 'Client address (with line breaks)', category: 'Client' },
                { placeholder: '{{client_email}}', desc: 'Client email address', category: 'Client' },
                { placeholder: '{{invoice_number}}', desc: 'Auto-generated invoice number', category: 'Invoice' },
                { placeholder: '{{issue_date}}', desc: 'Invoice issue date (formatted)', category: 'Invoice' },
                { placeholder: '{{due_date}}', desc: 'Invoice due date (formatted)', category: 'Invoice' },
                { placeholder: '{{payment_terms}}', desc: 'Payment terms (e.g., Net 30)', category: 'Invoice' },
                { placeholder: '{{status}}', desc: 'Invoice status (DRAFT, SENT, PAID)', category: 'Invoice' },
                { placeholder: '{{currency_symbol}}', desc: 'Currency symbol ($, €, £)', category: 'Financial' },
                { placeholder: '{{items_table}}', desc: 'Complete HTML table of line items', category: 'Financial' },
                { placeholder: '{{subtotal}}', desc: 'Subtotal amount with currency', category: 'Financial' },
                { placeholder: '{{tax_rate}}', desc: 'Tax rate percentage', category: 'Financial' },
                { placeholder: '{{tax_amount}}', desc: 'Tax amount with currency', category: 'Financial' },
                { placeholder: '{{discount_amount}}', desc: 'Discount amount with currency', category: 'Financial' },
                { placeholder: '{{total_amount}}', desc: 'Final total with currency', category: 'Financial' },
                { placeholder: '{{notes}}', desc: 'Invoice notes (with line breaks)', category: 'Content' },
                { placeholder: '{{footer_text}}', desc: 'Footer text for invoice', category: 'Content' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="bg-white p-3 rounded-lg border border-blue-100 hover:border-blue-300 cursor-pointer transition-all group"
                  onClick={() => {
                    if (templateForm.template_type === 'html_based') {
                      insertPlaceholder(item.placeholder);
                    }
                  }}
                  title={`${item.desc} - Click to insert (HTML templates only)`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <code className="text-xs font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded group-hover:bg-blue-200">
                      {item.placeholder}
                    </code>
                    <Badge variant="outline" className="text-xs">
                      {item.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600 leading-tight">{item.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <div className="flex items-start space-x-2">
                <FileText className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Usage Tips:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>For <strong>HTML templates</strong>: Click any placeholder above to insert it at cursor position</li>
                    <li>For <strong>Form templates</strong>: Placeholders are automatically applied to the professional template</li>
                    <li><strong>Special placeholders</strong>: {`{{items_table}}`} generates a complete HTML table, {`{{company_logo}}`} shows uploaded logo</li>
                    <li><strong>Line breaks</strong>: Address and notes placeholders support HTML line breaks (&lt;br&gt;)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template_name">Template Name *</Label>
                <Input
                  id="template_name"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Standard Invoice"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="template_description">Description</Label>
                <Input
                  id="template_description"
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Template Type Selection */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="text-base font-medium mb-3 block">Template Type</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    templateForm.template_type === 'form_based' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setTemplateForm(prev => ({ ...prev, template_type: 'form_based' }))}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <input
                      type="radio"
                      checked={templateForm.template_type === 'form_based'}
                      onChange={() => setTemplateForm(prev => ({ ...prev, template_type: 'form_based' }))}
                    />
                    <h3 className="font-semibold">Form-Based Template</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Easy-to-use form with predefined fields for company info, colors, and settings. 
                    Perfect for users who want a professional template without coding.
                  </p>
                </div>
                
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    templateForm.template_type === 'html_based' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setTemplateForm(prev => ({ ...prev, template_type: 'html_based' }));
                    if (!htmlTemplateContent) {
                      fetchDefaultHtmlTemplate();
                    }
                  }}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <input
                      type="radio"
                      checked={templateForm.template_type === 'html_based'}
                      onChange={() => {
                        setTemplateForm(prev => ({ ...prev, template_type: 'html_based' }));
                        if (!htmlTemplateContent) {
                          fetchDefaultHtmlTemplate();
                        }
                      }}
                    />
                    <h3 className="font-semibold">Custom HTML Template</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Full control over design with HTML and CSS. Use placeholders like {`{{company_name}}`}, 
                    {`{{invoice_number}}`} for dynamic content. Perfect for custom branding.
                  </p>
                </div>
              </div>
            </div>

            {/* HTML Template Editor (only show for HTML-based templates) */}
            {templateForm.template_type === 'html_based' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">HTML Template Editor</h3>
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={fetchDefaultHtmlTemplate}
                    >
                      Load Default Template
                    </Button>
                  </div>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <div className="flex items-start space-x-2">
                    <FileText className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Template Editor Instructions:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Click any placeholder above to insert it at your cursor position in the HTML editor</li>
                        <li>Use standard HTML tags and CSS for styling and layout</li>
                        <li>The {`{{items_table}}`} placeholder will generate a complete HTML table automatically</li>
                        <li>Preview your template anytime using the "Preview" button after saving</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* HTML Editor */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="html-editor">HTML Content</Label>
                    <textarea
                      id="html-editor"
                      rows={20}
                      className="w-full mt-1 border rounded px-3 py-2 font-mono text-sm"
                      value={htmlTemplateContent}
                      onChange={(e) => setHtmlTemplateContent(e.target.value)}
                      placeholder="Enter your HTML template here... Click placeholders above to insert them."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="css-editor">CSS Styles</Label>
                    <textarea
                      id="css-editor"
                      rows={20}
                      className="w-full mt-1 border rounded px-3 py-2 font-mono text-sm"
                      value={cssTemplateContent}
                      onChange={(e) => setCssTemplateContent(e.target.value)}
                      placeholder="Enter your CSS styles here..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Form-Based Template Settings (only show for form-based templates) */}
            {templateForm.template_type === 'form_based' && (
              <>
                {/* Company Information */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company_name">Company Name</Label>
                      <Input
                        id="company_name"
                        value={templateForm.company_name}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, company_name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="company_email">Company Email</Label>
                      <Input
                        id="company_email"
                        type="email"
                        value={templateForm.company_email}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, company_email: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="company_phone">Company Phone</Label>
                      <Input
                        id="company_phone"
                        value={templateForm.company_phone}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, company_phone: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="company_address">Company Address</Label>
                      <textarea
                        id="company_address"
                        rows={3}
                        className="w-full mt-1 border rounded px-3 py-2"
                        value={templateForm.company_address}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, company_address: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Colors and Branding */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Colors & Branding</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="primary_color">Primary Color</Label>
                      <div className="flex items-center space-x-2 mt-1">
                        <input
                          type="color"
                          id="primary_color"
                          value={templateForm.primary_color}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="w-12 h-10 rounded border"
                        />
                        <Input
                          value={templateForm.primary_color}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, primary_color: e.target.value }))}
                          placeholder="#1f2937"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="accent_color">Accent Color</Label>
                      <div className="flex items-center space-x-2 mt-1">
                        <input
                          type="color"
                          id="accent_color"
                          value={templateForm.accent_color}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, accent_color: e.target.value }))}
                          className="w-12 h-10 rounded border"
                        />
                        <Input
                          value={templateForm.accent_color}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, accent_color: e.target.value }))}
                          placeholder="#3b82f6"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Settings */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Financial Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <Input
                        id="currency"
                        value={templateForm.currency}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, currency: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="currency_symbol">Currency Symbol</Label>
                      <Input
                        id="currency_symbol"
                        value={templateForm.currency_symbol}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, currency_symbol: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tax_rate">Tax Rate (%)</Label>
                      <Input
                        id="tax_rate"
                        type="number"
                        step="0.01"
                        value={templateForm.tax_rate}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="discount_rate">Discount Rate (%)</Label>
                      <Input
                        id="discount_rate"
                        type="number"
                        step="0.01"
                        value={templateForm.discount_rate}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, discount_rate: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Logo Upload (for all template types) */}
            <div>
              <h3 className="text-lg font-medium mb-3">Company Logo</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="logo-upload">Upload Logo</Label>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setLogoFile(file);
                        const reader = new FileReader();
                        reader.onload = (e) => setLogoPreview(e.target.result);
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                </div>
                
                {(logoPreview || templateForm.company_logo_url) && (
                  <div>
                    <Label>Logo Preview</Label>
                    <div className="mt-1 p-4 border rounded-lg bg-gray-50">
                      <img
                        src={logoPreview || templateForm.company_logo_url}
                        alt="Logo preview"
                        className="max-h-20 max-w-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Input
                  id="payment_terms"
                  value={templateForm.payment_terms}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, payment_terms: e.target.value }))}
                  placeholder="e.g., Net 30"
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={templateForm.is_default}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, is_default: e.target.checked }))}
                />
                <Label htmlFor="is_default">Set as default template</Label>
              </div>
            </div>

            <div>
              <Label htmlFor="footer_text">Footer Text</Label>
              <textarea
                id="footer_text"
                rows={2}
                className="w-full mt-1 border rounded px-3 py-2"
                value={templateForm.footer_text}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, footer_text: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsTemplateDialogOpen(false);
                resetTemplateForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTemplate}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice/Template Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-6xl max-h-screen overflow-hidden">
          <DialogHeader>
            <DialogTitle>{previewTitle}</DialogTitle>
            <DialogDescription>
              Professional invoice preview - this is how your invoice will appear
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {previewLoading ? (
              <div className="flex justify-center items-center h-96">
                <div className="text-gray-500">Generating preview...</div>
              </div>
            ) : (
              <div className="h-[70vh] border rounded-lg overflow-auto bg-gray-50">
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0"
                  title="Invoice Preview"
                />
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-4">
            <div className="text-sm text-gray-500">
              This preview shows how your invoice will look when printed or emailed
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  const blob = new Blob([previewHtml], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${previewTitle.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                disabled={previewLoading || !previewHtml}
              >
                <Download className="h-4 w-4 mr-2" />
                Download HTML
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(previewHtml);
                  printWindow.document.close();
                  printWindow.print();
                }}
                disabled={previewLoading || !previewHtml}
              >
                <FileText className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsPreviewDialogOpen(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GeneralSettings = () => {
  const [settings, setSettings] = useState({
    appName: 'BizManager',
    logo: null,
    logoPreview: null,
    smtpHost: '',
    smtpPort: '587',
    smtpUsername: '',
    smtpPassword: '',
    smtpSecure: true,
    smtpFromEmail: '',
    smtpFromName: '',
    // Company Information
    companyName: '',
    companyAddress: '',
    companyLogo: null,
    companyLogoPreview: null,
    // World Clock Countries
    worldClockCountries: ['Indonesia', 'India', 'Argentina']
  });
  
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [companyLogoFile, setCompanyLogoFile] = useState(null);

  // Available countries for world clock
  const availableCountries = [
    { name: 'United States (New York)', value: 'United States (New York)', timezone: 'America/New_York', offset: 'GMT-5' },
    { name: 'United States (Los Angeles)', value: 'United States (Los Angeles)', timezone: 'America/Los_Angeles', offset: 'GMT-8' },
    { name: 'United Kingdom', value: 'United Kingdom', timezone: 'Europe/London', offset: 'GMT+0' },
    { name: 'Germany', value: 'Germany', timezone: 'Europe/Berlin', offset: 'GMT+1' },
    { name: 'France', value: 'France', timezone: 'Europe/Paris', offset: 'GMT+1' },
    { name: 'Japan', value: 'Japan', timezone: 'Asia/Tokyo', offset: 'GMT+9' },
    { name: 'China', value: 'China', timezone: 'Asia/Shanghai', offset: 'GMT+8' },
    { name: 'Australia (Sydney)', value: 'Australia (Sydney)', timezone: 'Australia/Sydney', offset: 'GMT+10' },
    { name: 'India', value: 'India', timezone: 'Asia/Kolkata', offset: 'GMT+5:30' },
    { name: 'Indonesia', value: 'Indonesia', timezone: 'Asia/Jakarta', offset: 'GMT+7' },
    { name: 'Singapore', value: 'Singapore', timezone: 'Asia/Singapore', offset: 'GMT+8' },
    { name: 'Philippines', value: 'Philippines', timezone: 'Asia/Manila', offset: 'GMT+8' },
    { name: 'South Korea', value: 'South Korea', timezone: 'Asia/Seoul', offset: 'GMT+9' },
    { name: 'Thailand', value: 'Thailand', timezone: 'Asia/Bangkok', offset: 'GMT+7' },
    { name: 'United Arab Emirates', value: 'United Arab Emirates', timezone: 'Asia/Dubai', offset: 'GMT+4' },
    { name: 'Brazil', value: 'Brazil', timezone: 'America/Sao_Paulo', offset: 'GMT-3' },
    { name: 'Argentina', value: 'Argentina', timezone: 'America/Argentina/Buenos_Aires', offset: 'GMT-3' },
    { name: 'Mexico', value: 'Mexico', timezone: 'America/Mexico_City', offset: 'GMT-6' },
    { name: 'Canada', value: 'Canada', timezone: 'America/Toronto', offset: 'GMT-5' },
    { name: 'Russia (Moscow)', value: 'Russia (Moscow)', timezone: 'Europe/Moscow', offset: 'GMT+3' },
    { name: 'South Africa', value: 'South Africa', timezone: 'Africa/Johannesburg', offset: 'GMT+2' },
    { name: 'Egypt', value: 'Egypt', timezone: 'Africa/Cairo', offset: 'GMT+2' },
    { name: 'Turkey', value: 'Turkey', timezone: 'Europe/Istanbul', offset: 'GMT+3' },
    { name: 'New Zealand', value: 'New Zealand', timezone: 'Pacific/Auckland', offset: 'GMT+12' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/general`);
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          logoPreview: response.data.logo ? `${API}/settings/logo` : null,
          companyLogoPreview: response.data.companyLogo ? `${API}/settings/company-logo` : null
        }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      // Don't show error for first time, use defaults
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({
          ...prev,
          logoPreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompanyLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setCompanyLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({
          ...prev,
          companyLogoPreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);

      // Save general settings
      const settingsData = {
        appName: settings.appName,
        smtpHost: settings.smtpHost,
        smtpPort: parseInt(settings.smtpPort),
        smtpUsername: settings.smtpUsername,
        smtpPassword: settings.smtpPassword,
        smtpSecure: settings.smtpSecure,
        smtpFromEmail: settings.smtpFromEmail,
        smtpFromName: settings.smtpFromName,
        // Company Information
        companyName: settings.companyName,
        companyAddress: settings.companyAddress,
        // World Clock Countries
        worldClockCountries: settings.worldClockCountries
      };

      await axios.put(`${API}/settings/general`, settingsData);

      // Upload app logo if changed
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        
        await axios.post(`${API}/settings/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
      }

      // Upload company logo if changed
      if (companyLogoFile) {
        const formData = new FormData();
        formData.append('logo', companyLogoFile);
        
        await axios.post(`${API}/settings/company-logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
      }

      toast.success('Settings saved successfully');
      
      // Refresh page to apply new app name and logo
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const testSmtpConnection = async () => {
    try {
      setLoading(true);
      
      const testData = {
        smtpHost: settings.smtpHost,
        smtpPort: parseInt(settings.smtpPort),
        smtpUsername: settings.smtpUsername,
        smtpPassword: settings.smtpPassword,
        smtpSecure: settings.smtpSecure,
        smtpFromEmail: settings.smtpFromEmail,
        smtpFromName: settings.smtpFromName
      };

      await axios.post(`${API}/settings/test-smtp`, testData);
      toast.success('SMTP connection test successful!');
    } catch (error) {
      console.error("SMTP test failed:", error);
      toast.error('SMTP connection test failed. Please check your settings.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">General Settings</h1>
        <Button 
          onClick={handleSaveSettings}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {loading ? (
            <>Loading...</>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Application Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <span>Application Branding</span>
            </CardTitle>
            <CardDescription>
              Customize your application name and logo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* App Name */}
            <div>
              <Label htmlFor="appName">Application Name</Label>
              <Input
                id="appName"
                value={settings.appName}
                onChange={(e) => handleInputChange('appName', e.target.value)}
                placeholder="BizManager"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                This name will appear in the sidebar and throughout the application
              </p>
            </div>

            {/* Logo Upload */}
            <div>
              <Label htmlFor="logo">Application Logo</Label>
              <div className="mt-2 space-y-4">
                {/* Logo Preview */}
                {settings.logoPreview && (
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 border border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={settings.logoPreview}
                        alt="Logo preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="text-sm text-gray-600">Current logo</div>
                  </div>
                )}
                
                {/* File Upload */}
                <div className="flex items-center space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose Logo
                  </Button>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <span className="text-xs text-gray-500">
                    PNG, JPG, or GIF up to 5MB
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMTP Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Mail className="h-5 w-5" />
              <span>SMTP Configuration</span>
            </CardTitle>
            <CardDescription>
              Configure email settings for notifications and password resets
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* SMTP Host */}
              <div>
                <Label htmlFor="smtpHost">SMTP Host</Label>
                <Input
                  id="smtpHost"
                  value={settings.smtpHost}
                  onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="mt-1"
                />
              </div>

              {/* SMTP Port */}
              <div>
                <Label htmlFor="smtpPort">SMTP Port</Label>
                <Input
                  id="smtpPort"
                  type="number"
                  value={settings.smtpPort}
                  onChange={(e) => handleInputChange('smtpPort', e.target.value)}
                  placeholder="587"
                  className="mt-1"
                />
              </div>

              {/* SMTP Username */}
              <div>
                <Label htmlFor="smtpUsername">Username</Label>
                <Input
                  id="smtpUsername"
                  value={settings.smtpUsername}
                  onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
                  placeholder="your-email@gmail.com"
                  className="mt-1"
                />
              </div>

              {/* SMTP Password */}
              <div>
                <Label htmlFor="smtpPassword">Password</Label>
                <Input
                  id="smtpPassword"
                  type="password"
                  value={settings.smtpPassword}
                  onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
                  placeholder="••••••••"
                  className="mt-1"
                />
              </div>

              {/* From Email */}
              <div>
                <Label htmlFor="smtpFromEmail">From Email</Label>
                <Input
                  id="smtpFromEmail"
                  type="email"
                  value={settings.smtpFromEmail}
                  onChange={(e) => handleInputChange('smtpFromEmail', e.target.value)}
                  placeholder="noreply@yourcompany.com"
                  className="mt-1"
                />
              </div>

              {/* From Name */}
              <div>
                <Label htmlFor="smtpFromName">From Name</Label>
                <Input
                  id="smtpFromName"
                  value={settings.smtpFromName}
                  onChange={(e) => handleInputChange('smtpFromName', e.target.value)}
                  placeholder="Your Company Name"
                  className="mt-1"
                />
              </div>
            </div>

            {/* SSL/TLS */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smtpSecure"
                checked={settings.smtpSecure}
                onChange={(e) => handleInputChange('smtpSecure', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="smtpSecure">Use SSL/TLS (Recommended)</Label>
            </div>

            {/* Test Connection Button */}
            <div className="pt-4">
              <Button
                onClick={testSmtpConnection}
                variant="outline"
                disabled={loading || !settings.smtpHost || !settings.smtpUsername}
              >
                {loading ? 'Testing...' : 'Test SMTP Connection'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Company Information</span>
            </CardTitle>
            <CardDescription>
              Configure your company details for invoicing and official documents
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Name */}
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={settings.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                placeholder="Your Company Name"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will appear on invoices and official documents
              </p>
            </div>

            {/* Company Address */}
            <div>
              <Label htmlFor="companyAddress">Company Address</Label>
              <Textarea
                id="companyAddress"
                value={settings.companyAddress}
                onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                placeholder="123 Business Street&#10;City, State 12345&#10;Country"
                rows={4}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Full company address including street, city, state, and country
              </p>
            </div>

            {/* Company Logo Upload */}
            <div>
              <Label htmlFor="companyLogo">Company Logo</Label>
              <div className="mt-2 space-y-4">
                {/* Logo Preview */}
                {settings.companyLogoPreview && (
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 border border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={settings.companyLogoPreview}
                        alt="Company logo preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="text-sm text-gray-600">Current company logo</div>
                  </div>
                )}
                
                {/* File Upload */}
                <div className="flex items-center space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('company-logo-upload').click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose Company Logo
                  </Button>
                  <input
                    id="company-logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleCompanyLogoChange}
                    className="hidden"
                  />
                  <span className="text-xs text-gray-500">
                    PNG, JPG, or GIF up to 5MB
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  This logo will be used on invoices and official company documents
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* World Clock Countries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5" />
              <span>World Clock Settings</span>
            </CardTitle>
            <CardDescription>
              Select up to 6 countries to display in the dashboard world clock
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Selected Countries ({settings.worldClockCountries.length}/6)</Label>
              <div className="mt-2 space-y-3">
                {settings.worldClockCountries.map((country, index) => (
                  <div key={country} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: index === 0 ? '#2563eb' : 
                                       index === 1 ? '#16a34a' : 
                                       index === 2 ? '#9333ea' :
                                       index === 3 ? '#dc2626' :
                                       index === 4 ? '#ea580c' : '#0891b2'
                      }}></div>
                      <span className="font-medium">{country}</span>
                      <span className="text-sm text-slate-500">
                        {availableCountries.find(c => c.name === country)?.offset || 'GMT+0'}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newCountries = settings.worldClockCountries.filter((_, i) => i !== index);
                        setSettings(prev => ({ ...prev, worldClockCountries: newCountries }));
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                {settings.worldClockCountries.length < 6 && (
                  <div>
                    <Label htmlFor="country-select">Add Country</Label>
                    <select
                      id="country-select"
                      className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => {
                        const selectedCountry = e.target.value;
                        if (selectedCountry && !settings.worldClockCountries.includes(selectedCountry)) {
                          setSettings(prev => ({
                            ...prev,
                            worldClockCountries: [...prev.worldClockCountries, selectedCountry]
                          }));
                        }
                        e.target.value = '';
                      }}
                    >
                      <option value="">Select a country to add...</option>
                      {availableCountries
                        .filter(country => !settings.worldClockCountries.includes(country.name))
                        .map(country => (
                          <option key={country.value} value={country.value}>
                            {country.name} ({country.offset})
                          </option>
                        ))
                      }
                    </select>
                  </div>
                )}
                
                {settings.worldClockCountries.length === 0 && (
                  <p className="text-sm text-slate-500 italic">
                    No countries selected. The world clock will not be displayed on the dashboard.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Team Types Component
const TeamTypes = () => {
  const [teamTypes, setTeamTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeamType, setEditingTeamType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#3b82f6" // Default blue color
  });

  useEffect(() => {
    fetchTeamTypes();
  }, []);

  const fetchTeamTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team-types`);
      setTeamTypes(response.data);
    } catch (error) {
      console.error("Error fetching team types:", error);
      toast.error("Failed to fetch team types");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const teamTypeData = {
        name: formData.name,
        description: formData.description || undefined,
        color: formData.color
      };

      if (editingTeamType) {
        await axios.put(`${API}/team-types/${editingTeamType.id}`, teamTypeData);
        toast.success("Team type updated successfully");
      } else {
        await axios.post(`${API}/team-types`, teamTypeData);
        toast.success("Team type created successfully");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchTeamTypes();
    } catch (error) {
      console.error("Error saving team type:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to save team type");
      }
    }
  };

  const handleEdit = (teamType) => {
    setEditingTeamType(teamType);
    setFormData({
      name: teamType.name,
      description: teamType.description || "",
      color: teamType.color || "#3b82f6"
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (teamTypeId) => {
    if (!window.confirm("Are you sure you want to delete this team type? This action cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/team-types/${teamTypeId}`);
      toast.success("Team type deleted successfully");
      fetchTeamTypes();
    } catch (error) {
      console.error("Error deleting team type:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to delete team type");
      }
    }
  };

  const handleSetDefault = async (teamTypeId) => {
    try {
      await axios.put(`${API}/team-types/${teamTypeId}/set-default`);
      toast.success("Default team type updated successfully");
      fetchTeamTypes();
    } catch (error) {
      console.error("Error setting default team type:", error);
      toast.error("Failed to set default team type");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      color: "#3b82f6"
    });
    setEditingTeamType(null);
  };

  const colorOptions = [
    { value: "#3b82f6", label: "Blue", class: "bg-blue-500" },
    { value: "#10b981", label: "Green", class: "bg-green-500" },
    { value: "#f59e0b", label: "Yellow", class: "bg-yellow-500" },
    { value: "#ef4444", label: "Red", class: "bg-red-500" },
    { value: "#8b5cf6", label: "Purple", class: "bg-purple-500" },
    { value: "#06b6d4", label: "Cyan", class: "bg-cyan-500" },
    { value: "#84cc16", label: "Lime", class: "bg-lime-500" },
    { value: "#f97316", label: "Orange", class: "bg-orange-500" }
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Team Types</h1>
        <Button 
          onClick={() => setIsDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Team Type
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">Loading team types...</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Team Types Management</CardTitle>
            <CardDescription>
              Manage different types of teams in your organization (e.g., Support, Development, AI)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teamTypes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-slate-700">Team Type</th>
                      <th className="text-left p-3 font-medium text-slate-700">Description</th>
                      <th className="text-center p-3 font-medium text-slate-700">Color</th>
                      <th className="text-center p-3 font-medium text-slate-700">Default</th>
                      <th className="text-right p-3 font-medium text-slate-700">Created</th>
                      <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamTypes.map((teamType, index) => (
                      <tr key={teamType.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                        <td className="p-3">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: teamType.color || "#3b82f6" }}
                            ></div>
                            <span className="font-medium">{teamType.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-slate-600 max-w-xs">
                          {teamType.description || '-'}
                        </td>
                        <td className="p-3 text-center">
                          <div 
                            className="w-6 h-6 rounded-full mx-auto"
                            style={{ backgroundColor: teamType.color || "#3b82f6" }}
                            title={teamType.color}
                          ></div>
                        </td>
                        <td className="p-3 text-center">
                          {teamType.is_default ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              <Check className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetDefault(teamType.id)}
                              className="text-xs"
                            >
                              Set Default
                            </Button>
                          )}
                        </td>
                        <td className="p-3 text-right text-sm text-slate-500">
                          {new Date(teamType.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center space-x-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleEdit(teamType)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDelete(teamType.id)}
                              className="text-red-600 hover:text-red-700"
                              disabled={teamType.is_default}
                              title={teamType.is_default ? "Cannot delete default team type" : "Delete team type"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Team Types Yet</h3>
                <p className="text-slate-500 mb-4">
                  Create team types to organize your employees into different departments or specializations
                </p>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Team Type
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Team Type Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTeamType ? "Edit Team Type" : "Add New Team Type"}
            </DialogTitle>
            <DialogDescription>
              {editingTeamType ? "Update team type information" : "Create a new team type for your organization"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="team-name">Team Type Name *</Label>
              <Input
                id="team-name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Support, Development, AI"
                required
              />
            </div>
            <div>
              <Label htmlFor="team-description">Description</Label>
              <Input
                id="team-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this team type"
              />
            </div>
            <div>
              <Label htmlFor="team-color">Color</Label>
              <div className="flex items-center space-x-2 mt-2">
                <input
                  type="color"
                  id="team-color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="w-8 h-8 rounded border"
                />
                <span className="text-sm text-slate-600">{formData.color}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({...formData, color: color.value})}
                    className={`w-8 h-8 rounded ${color.class} ${formData.color === color.value ? 'ring-2 ring-slate-400' : ''}`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsDialogOpen(false);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingTeamType ? "Update Team Type" : "Create Team Type"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Language Management Component
const LanguageManagement = () => {
  const [languages, setLanguages] = useState([]);
  const [systemTexts, setSystemTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState({ language_code: 'en', language_name: 'English' });
  
  // Dialog states
  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  const [editingText, setEditingText] = useState(null);
  
  // Form states
  const [textForm, setTextForm] = useState({
    text_key: '',
    english_text: '',
    spanish_text: '',
    french_text: '',
    portuguese_text: '',
    category: ''
  });
  
  const [languageForm, setLanguageForm] = useState({
    language_code: '',
    language_name: '',
    is_active: false
  });

  // Filter states
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    fetchLanguages();
    fetchSystemTexts();
    fetchActiveLanguage();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/admin/languages`);
      setLanguages(response.data);
    } catch (error) {
      console.error("Error fetching languages:", error);
      toast.error("Failed to load languages");
    }
  };

  const fetchSystemTexts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/system-texts`);
      setSystemTexts(response.data);
    } catch (error) {
      console.error("Error fetching system texts:", error);
      toast.error("Failed to load system texts");
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveLanguage = async () => {
    try {
      const response = await axios.get(`${API}/admin/active-language`);
      setActiveLanguage(response.data);
    } catch (error) {
      console.error("Error fetching active language:", error);
    }
  };

  const handleActivateLanguage = async (languageCode) => {
    try {
      await axios.put(`${API}/admin/languages/${languageCode}/activate`);
      toast.success("Language activated successfully");
      fetchLanguages();
      fetchActiveLanguage();
    } catch (error) {
      console.error("Error activating language:", error);
      toast.error("Failed to activate language");
    }
  };

  const handleCreateText = () => {
    setEditingText(null);
    setTextForm({
      text_key: '',
      english_text: '',
      spanish_text: '',
      french_text: '',
      portuguese_text: '',
      category: ''
    });
    setIsTextDialogOpen(true);
  };

  const handleEditText = (text) => {
    setEditingText(text);
    setTextForm({
      text_key: text.text_key,
      english_text: text.english_text,
      spanish_text: text.spanish_text || '',
      french_text: text.french_text || '',
      portuguese_text: text.portuguese_text || '',
      category: text.category || ''
    });
    setIsTextDialogOpen(true);
  };

  const handleSaveText = async () => {
    if (!textForm.text_key.trim() || !textForm.english_text.trim()) {
      toast.error("Text key and English text are required");
      return;
    }

    try {
      setLoading(true);
      
      if (editingText) {
        // Update existing text
        await axios.put(`${API}/admin/system-texts/${editingText.id}`, {
          english_text: textForm.english_text,
          spanish_text: textForm.spanish_text,
          french_text: textForm.french_text,
          portuguese_text: textForm.portuguese_text,
          category: textForm.category
        });
        toast.success("System text updated successfully");
      } else {
        // Create new text
        await axios.post(`${API}/admin/system-texts`, textForm);
        toast.success("System text created successfully");
      }

      setIsTextDialogOpen(false);
      fetchSystemTexts();
    } catch (error) {
      console.error("Error saving system text:", error);
      const errorMsg = error.response?.data?.detail || "Failed to save system text";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteText = async (textId) => {
    if (!confirm("Are you sure you want to delete this system text?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/system-texts/${textId}`);
      toast.success("System text deleted successfully");
      fetchSystemTexts();
    } catch (error) {
      console.error("Error deleting system text:", error);
      toast.error("Failed to delete system text");
    }
  };

  // Filter system texts based on search and category
  const filteredTexts = systemTexts.filter(text => {
    const matchesSearch = !searchFilter || 
      text.text_key.toLowerCase().includes(searchFilter.toLowerCase()) ||
      text.english_text.toLowerCase().includes(searchFilter.toLowerCase());
    
    const matchesCategory = !categoryFilter || text.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter
  const categories = [...new Set(systemTexts.map(text => text.category).filter(Boolean))];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Language Management</h1>
          <p className="text-slate-600 mt-2">Manage system languages and text translations</p>
        </div>
      </div>

      {/* Active Language & Language Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-blue-600" />
            Active Language
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {languages.map((language) => (
              <Button
                key={language.language_code}
                variant={language.is_active ? "default" : "outline"}
                size="sm"
                onClick={() => handleActivateLanguage(language.language_code)}
                className={language.is_active ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                <Globe2 className="h-4 w-4 mr-2" />
                {language.language_name}
                {language.is_active && <Check className="h-4 w-4 ml-2" />}
              </Button>
            ))}
          </div>
          <div className="mt-4 text-sm text-slate-600">
            Currently active: <span className="font-semibold text-blue-600">{activeLanguage.language_name}</span>
          </div>
        </CardContent>
      </Card>

      {/* System Texts Management */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Languages className="h-5 w-5 text-green-600" />
                System Text Translations
              </CardTitle>
              <CardDescription>
                Manage all system text labels and their translations
              </CardDescription>
            </div>
            <Button onClick={handleCreateText} className="bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Text
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <Label htmlFor="search">Search Texts</Label>
              <Input
                id="search"
                placeholder="Search by key or text..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>
            <div className="w-48">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* System Texts Table */}
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-slate-500">Loading system texts...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold">Text Key</th>
                    <th className="text-left p-3 font-semibold">English</th>
                    <th className="text-left p-3 font-semibold">Spanish</th>
                    <th className="text-left p-3 font-semibold">French</th>
                    <th className="text-left p-3 font-semibold">Portuguese</th>
                    <th className="text-left p-3 font-semibold">Category</th>
                    <th className="text-center p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTexts.length > 0 ? filteredTexts.map((text, index) => (
                    <tr key={text.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                      <td className="p-3">
                        <code className="text-sm bg-slate-200 px-2 py-1 rounded text-slate-800">
                          {text.text_key}
                        </code>
                      </td>
                      <td className="p-3 max-w-xs">
                        <div className="truncate" title={text.english_text}>
                          {text.english_text}
                        </div>
                      </td>
                      <td className="p-3 max-w-xs">
                        <div className={`truncate ${!text.spanish_text ? 'text-slate-400 italic' : ''}`} title={text.spanish_text || 'Not translated'}>
                          {text.spanish_text || 'Not translated'}
                        </div>
                      </td>
                      <td className="p-3 max-w-xs">
                        <div className={`truncate ${!text.french_text ? 'text-slate-400 italic' : ''}`} title={text.french_text || 'Not translated'}>
                          {text.french_text || 'Not translated'}
                        </div>
                      </td>
                      <td className="p-3 max-w-xs">
                        <div className={`truncate ${!text.portuguese_text ? 'text-slate-400 italic' : ''}`} title={text.portuguese_text || 'Not translated'}>
                          {text.portuguese_text || 'Not translated'}
                        </div>
                      </td>
                      <td className="p-3">
                        {text.category && (
                          <Badge variant="secondary" className="text-xs">
                            {text.category}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditText(text)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteText(text.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-slate-500">
                        {searchFilter || categoryFilter ? 'No texts match your filters' : 'No system texts found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Text Dialog */}
      <Dialog open={isTextDialogOpen} onOpenChange={setIsTextDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingText ? 'Edit System Text' : 'Add New System Text'}
            </DialogTitle>
            <DialogDescription>
              {editingText ? 'Update the text and its translations' : 'Create a new system text with translations'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="text_key">Text Key *</Label>
                <Input
                  id="text_key"
                  value={textForm.text_key}
                  onChange={(e) => setTextForm({...textForm, text_key: e.target.value})}
                  placeholder="e.g., dashboard.title"
                  disabled={!!editingText}
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={textForm.category}
                  onChange={(e) => setTextForm({...textForm, category: e.target.value})}
                  placeholder="e.g., dashboard, navigation"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="english_text">English Text *</Label>
              <Input
                id="english_text"
                value={textForm.english_text}
                onChange={(e) => setTextForm({...textForm, english_text: e.target.value})}
                placeholder="English text"
              />
            </div>
            
            <div>
              <Label htmlFor="spanish_text">Spanish Translation</Label>
              <Input
                id="spanish_text"
                value={textForm.spanish_text}
                onChange={(e) => setTextForm({...textForm, spanish_text: e.target.value})}
                placeholder="Texto en español"
              />
            </div>
            
            <div>
              <Label htmlFor="french_text">French Translation</Label>
              <Input
                id="french_text"
                value={textForm.french_text}
                onChange={(e) => setTextForm({...textForm, french_text: e.target.value})}
                placeholder="Texte français"
              />
            </div>
            
            <div>
              <Label htmlFor="portuguese_text">Portuguese Translation</Label>
              <Input
                id="portuguese_text"
                value={textForm.portuguese_text}
                onChange={(e) => setTextForm({...textForm, portuguese_text: e.target.value})}
                placeholder="Texto português"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsTextDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveText} disabled={loading}>
              {loading ? 'Saving...' : editingText ? 'Update Text' : 'Create Text'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reset DB Component
const ResetDB = () => {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleResetDB = async () => {
    if (confirmText !== 'RESET DATABASE') {
      toast.error('Please type "RESET DATABASE" to confirm');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/reset-database`);
      
      if (response.status === 200) {
        toast.success('Database reset successfully! All data except admin profile has been cleared.');
        setShowConfirmDialog(false);
        setConfirmText('');
      }
    } catch (error) {
      console.error('Error resetting database:', error);
      toast.error('Failed to reset database. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reset Database</h1>
          <p className="text-slate-600 mt-2">Permanently delete all data except admin profile</p>
        </div>
      </div>

      <Card className="border-red-200">
        <CardHeader className="bg-red-50">
          <CardTitle className="text-red-800 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Warning</h3>
              <p className="text-yellow-700 text-sm">
                This action will permanently delete ALL data from the database including:
              </p>
              <ul className="list-disc list-inside text-yellow-700 text-sm mt-2 space-y-1">
                <li>All employee records and profiles</li>
                <li>All client information and data</li>
                <li>All projects and project data</li>
                <li>All financial records (expenses, revenue, invoices)</li>
                <li>All attendance and leave records</li>
                <li>All documents and files</li>
                <li>All notes and communications</li>
              </ul>
              <p className="text-yellow-700 text-sm mt-2 font-semibold">
                Only the admin profile will be preserved.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                variant="destructive"
                onClick={() => setShowConfirmDialog(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                Reset Database
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-800">Confirm Database Reset</DialogTitle>
            <DialogDescription>
              This action cannot be undone. All data will be permanently deleted except the admin profile.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-red-800 text-sm font-medium">
                Type <span className="font-mono bg-red-100 px-1 rounded">RESET DATABASE</span> to confirm:
              </p>
            </div>
            
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESET DATABASE"
              className="font-mono"
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowConfirmDialog(false);
                setConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetDB}
              disabled={loading || confirmText !== 'RESET DATABASE'}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Resetting...' : 'Reset Database'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Communications Templates Component
const CommunicationsTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    name: '',
    subject: '',
    content: '',
    category: 'marketing',
    variables: []
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/communication-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateData({
      name: '',
      subject: '',
      content: '',
      category: 'marketing',
      variables: []
    });
    setIsDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateData({
      name: template.name,
      subject: template.subject,
      content: template.content,
      category: template.category,
      variables: template.variables || []
    });
    setIsDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateData.name.trim() || !templateData.subject.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      if (editingTemplate) {
        // Update existing template
        await axios.put(`${API}/communication-templates/${editingTemplate.id}`, templateData);
        toast.success("Template updated successfully");
      } else {
        // Create new template
        await axios.post(`${API}/communication-templates`, templateData);
        toast.success("Template created successfully");
      }
      
      setIsDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      await axios.delete(`${API}/communication-templates/${templateId}`);
      toast.success("Template deleted successfully");
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateData.content;
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newContent = before + variable + after;
      
      setTemplateData(prev => ({
        ...prev,
        content: newContent
      }));
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 10);
    }
  };

  const commonVariables = [
    '{{client_name}}',
    '{{company_name}}',
    '{{client_email}}',
    '{{client_company}}',
    '{{current_date}}',
    '{{invoice_number}}',
    '{{total_amount}}',
    '{{due_date}}'
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Communication Templates</h1>
          <p className="text-slate-600 mt-1">Create and manage email templates for marketing and communications</p>
        </div>
        <Button onClick={handleCreateTemplate} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Email Templates</CardTitle>
          <CardDescription>
            Manage your email templates for different communication purposes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading templates...</div>
          ) : templates.length > 0 ? (
            <div className="space-y-4">
              {templates.map((template) => (
                <div key={template.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{template.name}</h3>
                      <p className="text-sm text-slate-600 mt-1">{template.subject}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {template.category}
                        </span>
                        {template.variables && template.variables.length > 0 && (
                          <span className="text-xs text-slate-500">
                            {template.variables.length} variables
                          </span>
                        )}
                        <span className="text-xs text-slate-500">
                          Created: {new Date(template.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No templates found.</p>
              <p className="text-sm">Create your first email template to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Template Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update your email template' : 'Create a new email template for your communications'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-4">
              {/* Template Name */}
              <div>
                <Label htmlFor="template-name">Template Name *</Label>
                <Input
                  id="template-name"
                  value={templateData.name}
                  onChange={(e) => setTemplateData(prev => ({...prev, name: e.target.value}))}
                  placeholder="e.g., Monthly Newsletter"
                  className="mt-1"
                />
              </div>

              {/* Category */}
              <div>
                <Label htmlFor="template-category">Category</Label>
                <Select 
                  value={templateData.category} 
                  onValueChange={(value) => setTemplateData(prev => ({...prev, category: value}))}
                >
                  <SelectTrigger id="template-category" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="notification">Notification</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Subject Line */}
              <div>
                <Label htmlFor="template-subject">Subject Line *</Label>
                <Input
                  id="template-subject"
                  value={templateData.subject}
                  onChange={(e) => setTemplateData(prev => ({...prev, subject: e.target.value}))}
                  placeholder="e.g., Your monthly update from {{company_name}}"
                  className="mt-1"
                />
              </div>

              {/* Email Content */}
              <div>
                <Label htmlFor="template-content">Email Content (HTML)</Label>
                <Textarea
                  id="template-content"
                  value={templateData.content}
                  onChange={(e) => setTemplateData(prev => ({...prev, content: e.target.value}))}
                  placeholder="Enter your email template content. You can use HTML and variables like {{client_name}}."
                  rows={12}
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>

            {/* Variables Panel */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Available Variables</h3>
                <div className="space-y-2">
                  {commonVariables.map((variable) => (
                    <button
                      key={variable}
                      onClick={() => insertVariable(variable)}
                      className="block w-full text-left px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 transition-colors"
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Template Preview</h3>
                <div className="border border-slate-200 rounded p-3 bg-slate-50 text-xs">
                  <div className="font-semibold mb-2">Subject: {templateData.subject || 'Subject line'}</div>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: templateData.content || '<p>Content preview will appear here...</p>' 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Communications Marketing Component
const CommunicationsMarketing = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [campaignData, setCampaignData] = useState({
    name: '',
    template_id: '',
    recipients: [],
    scheduled_at: '',
    send_interval: 4
  });
  const [selectedRecipientType, setSelectedRecipientType] = useState('clients');

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchRecipients();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/marketing-campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setCampaigns([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/communication-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
    }
  };

  const fetchRecipients = async () => {
    try {
      const [clientsResponse, leadsResponse] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/leads`)
      ]);
      setClients(clientsResponse.data);
      setLeads(leadsResponse.data);
    } catch (error) {
      console.error("Error fetching recipients:", error);
      setClients([]);
      setLeads([]);
    }
  };

  const handleCreateCampaign = () => {
    setCampaignData({
      name: '',
      template_id: '',
      recipients: [],
      scheduled_at: '',
      send_interval: 4
    });
    setSelectedRecipientType('clients');
    setIsDialogOpen(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignData.name.trim() || !campaignData.template_id || campaignData.recipients.length === 0) {
      toast.error("Please fill in all required fields and select recipients");
      return;
    }

    try {
      setLoading(true);
      const campaignPayload = {
        ...campaignData,
        scheduled_at: campaignData.scheduled_at ? new Date(campaignData.scheduled_at).toISOString() : null
      };
      
      await axios.post(`${API}/marketing-campaigns`, campaignPayload);
      toast.success("Campaign created successfully");
      setIsDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast.error("Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const handleRecipientSelection = (recipientEmails) => {
    setCampaignData(prev => ({
      ...prev,
      recipients: recipientEmails
    }));
  };

  const selectAllRecipients = () => {
    const allEmails = selectedRecipientType === 'clients' 
      ? clients.map(client => client.email).filter(Boolean)
      : leads.map(lead => lead.email).filter(Boolean);
    
    handleRecipientSelection(allEmails);
  };

  const getActiveRecipients = () => {
    return selectedRecipientType === 'clients' ? clients : leads;
  };

  const getCampaignStats = () => {
    const totalCampaigns = campaigns.length;
    const activeCampaigns = campaigns.filter(c => c.status === 'running' || c.status === 'scheduled').length;
    const totalEmailsSent = campaigns.reduce((sum, c) => sum + (c.emails_sent || 0), 0);
    
    return { totalCampaigns, activeCampaigns, totalEmailsSent };
  };

  const stats = getCampaignStats();

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Marketing Campaigns</h1>
          <p className="text-slate-600 mt-1">Schedule and manage email marketing campaigns</p>
        </div>
        <Button onClick={handleCreateCampaign} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Total Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{stats.totalCampaigns}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Active Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{stats.activeCampaigns}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Emails Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.totalEmailsSent}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Campaign Management</CardTitle>
          <CardDescription>
            Create and schedule marketing campaigns using your templates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length > 0 ? (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Template: {templates.find(t => t.id === campaign.template_id)?.name || 'Unknown'}
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'running' ? 'bg-blue-100 text-blue-800' :
                          campaign.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.status}
                        </span>
                        <span className="text-xs text-slate-500">
                          {campaign.emails_sent || 0} / {campaign.total_recipients || 0} sent
                        </span>
                        <span className="text-xs text-slate-500">
                          Interval: {campaign.send_interval} min
                        </span>
                        {campaign.scheduled_at && (
                          <span className="text-xs text-slate-500">
                            Scheduled: {new Date(campaign.scheduled_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Send className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No marketing campaigns found.</p>
              <p className="text-sm">Create your first campaign to reach your clients and leads.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Marketing Campaign</DialogTitle>
            <DialogDescription>
              Set up a new email marketing campaign using your templates
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Campaign Name */}
            <div>
              <Label htmlFor="campaign-name">Campaign Name *</Label>
              <Input
                id="campaign-name"
                value={campaignData.name}
                onChange={(e) => setCampaignData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Q1 Product Launch"
                className="mt-1"
              />
            </div>

            {/* Template Selection */}
            <div>
              <Label htmlFor="campaign-template">Email Template *</Label>
              <Select 
                value={campaignData.template_id} 
                onValueChange={(value) => setCampaignData(prev => ({...prev, template_id: value}))}
              >
                <SelectTrigger id="campaign-template" className="mt-1">
                  <SelectValue placeholder="Select an email template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Send Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="send-interval">Send Interval (minutes)</Label>
                <Input
                  id="send-interval"
                  type="number"
                  min="1"
                  max="1440"
                  value={campaignData.send_interval}
                  onChange={(e) => setCampaignData(prev => ({...prev, send_interval: parseInt(e.target.value) || 4}))}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Minutes between each email (prevents spam)
                </p>
              </div>
              <div>
                <Label htmlFor="scheduled-at">Schedule Date (Optional)</Label>
                <Input
                  id="scheduled-at"
                  type="datetime-local"
                  value={campaignData.scheduled_at}
                  onChange={(e) => setCampaignData(prev => ({...prev, scheduled_at: e.target.value}))}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave empty to send immediately
                </p>
              </div>
            </div>

            {/* Recipients Selection */}
            <div>
              <Label>Recipients *</Label>
              <div className="mt-2">
                {/* Recipient Type Toggle */}
                <div className="flex space-x-4 mb-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="clients"
                      checked={selectedRecipientType === 'clients'}
                      onChange={(e) => setSelectedRecipientType(e.target.value)}
                      className="mr-2"
                    />
                    Clients ({clients.length})
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="leads"
                      checked={selectedRecipientType === 'leads'}
                      onChange={(e) => setSelectedRecipientType(e.target.value)}
                      className="mr-2"
                    />
                    Leads ({leads.length})
                  </label>
                </div>

                {/* Select All Button */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-600">
                    {campaignData.recipients.length} recipients selected
                  </span>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllRecipients}
                  >
                    Select All {selectedRecipientType}
                  </Button>
                </div>

                {/* Recipients List */}
                <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                  {getActiveRecipients().map((recipient) => (
                    <label key={recipient.id} className="flex items-center p-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={campaignData.recipients.includes(recipient.email)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCampaignData(prev => ({
                              ...prev,
                              recipients: [...prev.recipients, recipient.email]
                            }));
                          } else {
                            setCampaignData(prev => ({
                              ...prev,
                              recipients: prev.recipients.filter(email => email !== recipient.email)
                            }));
                          }
                        }}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {selectedRecipientType === 'clients' ? recipient.company_name : recipient.name}
                        </div>
                        <div className="text-xs text-slate-500">{recipient.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCampaign}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Communications Mails Component
const CommunicationsMails = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [emailStats, setEmailStats] = useState({
    total_emails: 0,
    unread_emails: 0,
    client_emails: 0,
    today_emails: 0
  });
  const [emailSettings, setEmailSettings] = useState({
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_secure: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_secure: true
  });

  useEffect(() => {
    fetchEmails();
    fetchEmailStats();
    fetchEmailSettings();
  }, []);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/emails?limit=50`);
      setEmails(response.data);
    } catch (error) {
      console.error("Error fetching emails:", error);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const response = await axios.get(`${API}/emails/stats`);
      setEmailStats(response.data);
    } catch (error) {
      console.error("Error fetching email stats:", error);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/email-settings`);
      setEmailSettings(response.data);
    } catch (error) {
      console.error("Error fetching email settings:", error);
      // Use default settings
    }
  };

  const handleSyncEmails = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/emails/sync`);
      
      if (response.data.success) {
        toast.success(`Successfully synced ${response.data.emails_synced} new emails`);
        await fetchEmails();
        await fetchEmailStats();
      } else {
        toast.error(`Email sync failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error("Error syncing emails:", error);
      toast.error("Failed to sync emails");
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/email-settings`, emailSettings);
      toast.success("Email settings saved successfully");
      setIsSettingsOpen(false);
    } catch (error) {
      console.error("Error saving email settings:", error);
      toast.error("Failed to save email settings");
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (type) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/emails/test-connection?connection_type=${type}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(`Error testing ${type} connection:`, error);
      toast.error(`${type.toUpperCase()} connection test failed`);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (emailId, isRead = true) => {
    try {
      await axios.put(`${API}/emails/${emailId}/read?is_read=${isRead}`);
      
      // Update local state
      setEmails(prev => prev.map(email => 
        email.id === emailId ? { ...email, is_read: isRead } : email
      ));
      
      // Update stats
      await fetchEmailStats();
      
      toast.success(`Email marked as ${isRead ? 'read' : 'unread'}`);
    } catch (error) {
      console.error("Error updating email status:", error);
      toast.error("Failed to update email status");
    }
  };

  const handleViewEmail = async (email) => {
    setSelectedEmail(email);
    setIsEmailOpen(true);
    
    // Mark as read if not already read
    if (!email.is_read) {
      await handleMarkAsRead(email.id, true);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays === 2) {
        return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays <= 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Email Management</h1>
          <p className="text-slate-600 mt-1">View and manage emails from your clients</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={handleSyncEmails}
            disabled={syncing}
          >
            <Download className="h-4 w-4 mr-2" />
            {syncing ? 'Syncing...' : 'Sync Emails'}
          </Button>
          <Button variant="outline" onClick={() => setIsSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Email Settings
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Total Emails</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{emailStats.total_emails}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Unread</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{emailStats.unread_emails}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Client Emails</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{emailStats.client_emails}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{emailStats.today_emails}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Email Inbox</CardTitle>
          <CardDescription>
            Emails from your registered clients and other contacts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading emails...</div>
          ) : emails.length > 0 ? (
            <div className="space-y-4">
              {emails.map((email) => (
                <div key={email.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 cursor-pointer" onClick={() => handleViewEmail(email)}>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${email.is_read ? 'bg-slate-300' : 'bg-blue-500'}`} />
                        <h3 className={`font-semibold ${email.is_read ? 'text-slate-700' : 'text-slate-900'}`}>
                          {email.subject || '(No Subject)'}
                        </h3>
                        {email.is_from_client && (
                          <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Client
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        From: {email.sender_name || email.sender_email}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatDate(email.received_date)}
                      </p>
                      {email.body_text && (
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                          {email.body_text.substring(0, 150)}...
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewEmail(email)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleMarkAsRead(email.id, !email.is_read)}
                      >
                        {email.is_read ? 'Mark Unread' : 'Mark Read'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No emails found.</p>
              <p className="text-sm">Configure your email settings and sync to start receiving emails.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Settings Dialog - keeping the same as before */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Configuration</DialogTitle>
            <DialogDescription>
              Configure your IMAP and SMTP settings to receive and send emails
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* IMAP Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">IMAP Settings (Incoming)</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testConnection('imap')}
                  disabled={loading}
                >
                  Test Connection
                </Button>
              </div>
              
              <div>
                <Label htmlFor="imap-host">IMAP Host *</Label>
                <Input
                  id="imap-host"
                  value={emailSettings.imap_host}
                  onChange={(e) => setEmailSettings(prev => ({...prev, imap_host: e.target.value}))}
                  placeholder="imap.gmail.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="imap-port">IMAP Port</Label>
                <Input
                  id="imap-port"
                  type="number"
                  value={emailSettings.imap_port}
                  onChange={(e) => setEmailSettings(prev => ({...prev, imap_port: parseInt(e.target.value) || 993}))}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="imap-username">Username/Email *</Label>
                <Input
                  id="imap-username"
                  type="email"
                  value={emailSettings.imap_username}
                  onChange={(e) => setEmailSettings(prev => ({...prev, imap_username: e.target.value}))}
                  placeholder="your-email@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="imap-password">Password/App Password *</Label>
                <Input
                  id="imap-password"
                  type="password"
                  value={emailSettings.imap_password}
                  onChange={(e) => setEmailSettings(prev => ({...prev, imap_password: e.target.value}))}
                  placeholder="Your email password"
                  className="mt-1"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="imap-secure"
                  type="checkbox"
                  checked={emailSettings.imap_secure}
                  onChange={(e) => setEmailSettings(prev => ({...prev, imap_secure: e.target.checked}))}
                />
                <Label htmlFor="imap-secure">Use SSL/TLS (Recommended)</Label>
              </div>
            </div>

            {/* SMTP Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">SMTP Settings (Outgoing)</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testConnection('smtp')}
                  disabled={loading}
                >
                  Test Connection
                </Button>
              </div>
              
              <div>
                <Label htmlFor="smtp-host">SMTP Host *</Label>
                <Input
                  id="smtp-host"
                  value={emailSettings.smtp_host}
                  onChange={(e) => setEmailSettings(prev => ({...prev, smtp_host: e.target.value}))}
                  placeholder="smtp.gmail.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="smtp-port">SMTP Port</Label>
                <Input
                  id="smtp-port"
                  type="number"
                  value={emailSettings.smtp_port}
                  onChange={(e) => setEmailSettings(prev => ({...prev, smtp_port: parseInt(e.target.value) || 587}))}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="smtp-username">Username/Email *</Label>
                <Input
                  id="smtp-username"
                  type="email"
                  value={emailSettings.smtp_username}
                  onChange={(e) => setEmailSettings(prev => ({...prev, smtp_username: e.target.value}))}
                  placeholder="your-email@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="smtp-password">Password/App Password *</Label>
                <Input
                  id="smtp-password"
                  type="password"
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings(prev => ({...prev, smtp_password: e.target.value}))}
                  placeholder="Your email password"
                  className="mt-1"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="smtp-secure"
                  type="checkbox"
                  checked={emailSettings.smtp_secure}
                  onChange={(e) => setEmailSettings(prev => ({...prev, smtp_secure: e.target.checked}))}
                />
                <Label htmlFor="smtp-secure">Use STARTTLS (Recommended)</Label>
              </div>
            </div>
          </div>

          {/* Common Email Provider Quick Setup */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-semibold text-slate-900 mb-2">Common Email Providers</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div>
                <strong>Gmail:</strong><br />
                IMAP: imap.gmail.com:993<br />
                SMTP: smtp.gmail.com:587<br />
                <em>Use App Password for 2FA</em>
              </div>
              <div>
                <strong>Outlook/Hotmail:</strong><br />
                IMAP: outlook.office365.com:993<br />
                SMTP: smtp-mail.outlook.com:587<br />
              </div>
              <div>
                <strong>Yahoo:</strong><br />
                IMAP: imap.mail.yahoo.com:993<br />
                SMTP: smtp.mail.yahoo.com:587<br />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSettings}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Viewer Dialog */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmail?.subject || '(No Subject)'}</DialogTitle>
            <DialogDescription>
              From: {selectedEmail?.sender_name || selectedEmail?.sender_email} • {selectedEmail && formatDate(selectedEmail.received_date)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                {selectedEmail.is_from_client && (
                  <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Client Email
                  </span>
                )}
                <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                  selectedEmail.is_read ? 'bg-slate-100 text-slate-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {selectedEmail.is_read ? 'Read' : 'Unread'}
                </span>
              </div>
              
              <div className="border border-slate-200 rounded-lg p-4">
                {selectedEmail.body_html ? (
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap text-sm text-slate-700">
                    {selectedEmail.body_text || 'No content available'}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Communications Chat Component
const CommunicationsChat = () => {
  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Chat</h1>
          <p className="text-slate-600 mt-1">WhatsApp integration for business communications</p>
        </div>
      </div>

      {/* Coming Soon Card */}
      <Card className="text-center py-16">
        <CardContent>
          <div className="max-w-md mx-auto">
            <div className="mb-8">
              <MessageSquare className="h-24 w-24 mx-auto text-slate-300 mb-4" />
              <h2 className="text-4xl font-bold text-slate-800 mb-4">Coming Soon</h2>
              <p className="text-lg text-slate-600 mb-6">
                We're working on an amazing WhatsApp integration that will revolutionize your business communications.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">What's Coming:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-700">
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Send & receive messages</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Customer management</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Automated responses</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Message templates</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Broadcast messaging</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Analytics & reports</span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                <strong>Stay tuned!</strong> This feature is in active development and will be available soon.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminSettings = () => {
  const { user, updateProfile, logout } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        name: user.name,
        email: user.email
      }));
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      // If attempting to change password, use the password change endpoint
      if (profileData.newPassword) {
        if (!profileData.currentPassword) {
          toast.error('Current password is required to change password');
          return;
        }
        if (profileData.newPassword !== profileData.confirmPassword) {
          toast.error('New passwords do not match');
          return;
        }
        if (profileData.newPassword.length < 6) {
          toast.error('New password must be at least 6 characters');
          return;
        }

        // Call the admin password change endpoint
        const passwordResponse = await axios.put(`${API}/auth/admin-change-password`, {
          currentPassword: profileData.currentPassword,
          newPassword: profileData.newPassword,
          confirmPassword: profileData.confirmPassword,
          adminEmail: user.email
        });

        if (passwordResponse.status === 200) {
          toast.success('Password changed successfully');
        }
      }

      // Update profile (name and email)
      const profileResult = await updateProfile({
        name: profileData.name,
        email: profileData.email,
        adminEmail: user.email
      });

      if (!profileResult.success) {
        toast.error(profileResult.error || 'Failed to update profile');
        return;
      }

      // Clear password fields
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

      setIsEditing(false);
      toast.success('Profile updated successfully');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update profile');
      }
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Admin Settings</h1>
        <Badge variant="default" className="bg-emerald-100 text-emerald-800 border-emerald-300">
          {user?.role}
        </Badge>
      </div>

      {/* Profile Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Profile Information</span>
          </CardTitle>
          <CardDescription>
            Update your personal information and account settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-slate-50' : ''}
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-slate-50' : ''}
                />
              </div>
            </div>

            {/* Password Change Section - Always Visible */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Change Password (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={profileData.currentPassword}
                      onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                      className="pl-10 pr-10"
                      placeholder="Enter current password"
                      disabled={!isEditing}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      disabled={!isEditing}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={profileData.newPassword}
                      onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                      className="pl-10 pr-10"
                      placeholder="Enter new password"
                      disabled={!isEditing}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      disabled={!isEditing}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                      className="pl-10 pr-10"
                      placeholder="Confirm new password"
                      disabled={!isEditing}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      disabled={!isEditing}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div className="space-x-2">
                {isEditing ? (
                  <>
                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                      Save Changes
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsEditing(false);
                        setProfileData(prev => ({
                          ...prev,
                          name: user.name,
                          email: user.email,
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        }));
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button 
                    type="button" 
                    onClick={() => setIsEditing(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Account Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-600">User ID</Label>
              <p className="text-slate-900">{user?.id}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-600">Role</Label>
              <p className="text-slate-900">{user?.role}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-600">Login Status</Label>
              <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-600">Last Login</Label>
              <p className="text-slate-900">{new Date().toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logout Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Danger Zone</CardTitle>
          <CardDescription>
            Logout from your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user?.isEmployee) {
    return <EmployeeDashboard />;
  } else if (user?.isClient) {
    return <ClientDashboard />;
  } else {
    return <Dashboard />;
  }
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  // If user is an employee (but not a manager), redirect to their dashboard
  if (user.isEmployee && !user.isManager && location.pathname === '/') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Employee Route Component - for employee-only pages
const EmployeeRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user || !user.isEmployee) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Admin Manager Route Component - for admin and manager-only pages (excludes employees)
const AdminManagerRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user || (user.isEmployee && !user.isManager)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const ManagerRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user?.isManager) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const ClientRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user?.isClient) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Update Navigation Component to include logout

// Leads List Component
const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddLeadDialogOpen, setIsAddLeadDialogOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [leadsPerPage] = useState(50);
  
  const [leadFormData, setLeadFormData] = useState({
    name: "",
    country: "",
    phone_number: "",
    email: "",
    product_name: "",
    customer_notes: "",
    proposal_value: ""
  });
  const [editingLead, setEditingLead] = useState(null);

  // Filtering state
  const [leadFilters, setLeadFilters] = useState({
    name: "",
    country: "",
    status: "",
    email: ""
  });

  useEffect(() => {
    fetchLeadsData();
  }, [currentPage]);

  const fetchLeadsData = async () => {
    try {
      setLoading(true);
      const [leadsRes, statusesRes, productsRes, countriesRes] = await Promise.all([
        axios.get(`${API}/leads/paginated?page=${currentPage}&limit=${leadsPerPage}`),
        axios.get(`${API}/lead-statuses`),
        axios.get(`${API}/products`),
        axios.get(`${API}/countries`)
      ]);
      
      // Handle paginated leads response
      if (leadsRes.data.leads) {
        setLeads(leadsRes.data.leads);
        setTotalPages(leadsRes.data.pagination.total_pages);
        setTotalLeads(leadsRes.data.pagination.total_leads);
      } else {
        setLeads(leadsRes.data); // Fallback for non-paginated response
      }
      
      setLeadStatuses(statusesRes.data);
      setProducts(productsRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error("Error fetching leads data:", error);
      toast.error("Failed to load leads data");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!leadFormData.name.trim()) {
      toast.error("Lead name is required");
      return;
    }
    if (!leadFormData.country) {
      toast.error("Please select a country");
      return;
    }
    if (!leadFormData.phone_number.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (!leadFormData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    
    try {
      // Sanitize data before sending to backend
      const submitData = {
        ...leadFormData,
        proposal_value: leadFormData.proposal_value === "" ? null : parseFloat(leadFormData.proposal_value)
      };
      
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, submitData);
        toast.success("Lead updated successfully");
      } else {
        await axios.post(`${API}/leads`, submitData);
        toast.success("Lead created successfully");
      }

      await fetchLeadsData();
      resetLeadForm();
      setIsAddLeadDialogOpen(false);
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error(error.response?.data?.detail || "Failed to save lead");
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setLeadFormData({
      name: lead.name,
      country: lead.country,
      phone_number: lead.phone_number,
      email: lead.email,
      product_name: lead.product_name || "",
      customer_notes: lead.customer_notes || "",
      proposal_value: lead.proposal_value ? lead.proposal_value.toString() : ""
    });
    setIsAddLeadDialogOpen(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted successfully");
      await fetchLeadsData();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  const resetLeadForm = () => {
    setLeadFormData({
      name: "",
      country: "",
      phone_number: "",
      email: "",
      product_name: "",
      customer_notes: "",
      proposal_value: ""
    });
    setEditingLead(null);
  };

  const getStatusColor = (statusName) => {
    const status = leadStatuses.find(s => s.name === statusName);
    return status ? status.color : "#6B7280";
  };

  // Filter and sort leads based on current filters
  const filteredLeads = leads
    .filter(lead => {
      return (
        (lead.name || "").toLowerCase().includes(leadFilters.name.toLowerCase()) &&
        (lead.country || "").toLowerCase().includes(leadFilters.country.toLowerCase()) &&
        (lead.status || "").toLowerCase().includes(leadFilters.status.toLowerCase()) &&
        (lead.email || "").toLowerCase().includes(leadFilters.email.toLowerCase())
      );
    })
    .sort((a, b) => {
      // Sort by created_at date, most recent first
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Leads Management</h1>
        <Button onClick={() => setIsAddLeadDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Lead Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filter Leads</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="lead-name-filter">Lead Name</Label>
              <Input
                id="lead-name-filter"
                placeholder="Search by name..."
                value={leadFilters.name}
                onChange={(e) => setLeadFilters({...leadFilters, name: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="lead-country-filter">Country</Label>
              <Input
                id="lead-country-filter"
                placeholder="Search by country..."
                value={leadFilters.country}
                onChange={(e) => setLeadFilters({...leadFilters, country: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="lead-status-filter">Status</Label>
              <Input
                id="lead-status-filter"
                placeholder="Search by status..."
                value={leadFilters.status}
                onChange={(e) => setLeadFilters({...leadFilters, status: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="lead-email-filter">Email</Label>
              <Input
                id="lead-email-filter"
                placeholder="Search by email..."
                value={leadFilters.email}
                onChange={(e) => setLeadFilters({...leadFilters, email: e.target.value})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle>Leads ({filteredLeads.length})</CardTitle>
          <CardDescription>
            Manage your sales leads and track their progress (sorted by most recent)
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">Loading leads...</div>
          ) : filteredLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="min-w-[1200px]">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-3 font-medium text-slate-700 w-24">Date</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-48">Name</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-32">Country</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-36">Phone Number</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-48">Email</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-24">Current Time</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-36">Product</th>
                      <th className="text-left p-3 font-medium text-slate-700 w-28">Status</th>
                      <th className="text-center p-3 font-medium text-slate-700 w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead, index) => (
                      <tr key={lead.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                        <td className="p-3 text-sm font-medium text-slate-600 whitespace-nowrap">
                          {formatDate(lead.created_at)}
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-slate-900 whitespace-nowrap">{lead.name}</div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center whitespace-nowrap">
                            <Globe className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0" />
                            <span>{lead.country}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center whitespace-nowrap">
                            <Phone className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0" />
                            <span>{lead.country_code} {lead.phone_number}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0" />
                            <span className="text-sm truncate max-w-[200px]" title={lead.email}>{lead.email}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center whitespace-nowrap">
                            <CalendarIcon className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0" />
                            <span className="text-sm font-medium">{lead.current_time}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm truncate max-w-[140px] block" title={lead.product_name || '-'}>
                            {lead.product_name || '-'}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge 
                            className="whitespace-nowrap text-xs"
                            style={{ 
                              backgroundColor: getStatusColor(lead.status),
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            {lead.status}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center space-x-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.location.href = `/leads/${lead.id}`}
                              className="text-blue-600 hover:text-blue-700 h-8 w-8 p-0"
                              title="View Lead"
                            >
                              <UserCheck className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditLead(lead)}
                              className="h-8 w-8 p-0"
                              title="Edit Lead"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteLead(lead.id)}
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                              title="Delete Lead"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No leads found. Add your first lead to get started.
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalLeads}
        itemsPerPage={leadsPerPage}
        itemName="leads"
      />

      {/* Add/Edit Lead Dialog */}
      <Dialog open={isAddLeadDialogOpen} onOpenChange={setIsAddLeadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingLead ? "Edit Lead" : "Add New Lead"}
            </DialogTitle>
            <DialogDescription>
              {editingLead ? "Update the lead details" : "Create a new lead in your pipeline"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit} className="space-y-4">
            <div>
              <Label htmlFor="lead-name">Lead Name *</Label>
              <Input
                id="lead-name"
                value={leadFormData.name}
                onChange={(e) => setLeadFormData({...leadFormData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="lead-country">Country *</Label>
              <Select 
                value={leadFormData.country} 
                onValueChange={(value) => setLeadFormData({...leadFormData, country: value})}
                required
              >
                <SelectTrigger id="lead-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(country => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="lead-phone">Phone Number *</Label>
              <Input
                id="lead-phone"
                value={leadFormData.phone_number}
                onChange={(e) => setLeadFormData({...leadFormData, phone_number: e.target.value})}
                placeholder="Phone number (without country code)"
                required
              />
            </div>
            <div>
              <Label htmlFor="lead-email">Email *</Label>
              <Input
                id="lead-email"
                type="email"
                value={leadFormData.email}
                onChange={(e) => setLeadFormData({...leadFormData, email: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="lead-product">Product Name (Optional)</Label>
              <Select value={leadFormData.product_name || "none"} onValueChange={(value) => setLeadFormData({...leadFormData, product_name: value === "none" ? "" : value})}>
                <SelectTrigger id="lead-product">
                  <SelectValue placeholder="Select product (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Product Selected</SelectItem>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.name}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="lead-notes">Customer Notes</Label>
              <Textarea
                id="lead-notes"
                value={leadFormData.customer_notes}
                onChange={(e) => setLeadFormData({...leadFormData, customer_notes: e.target.value})}
                rows={3}
                placeholder="Any initial notes about the customer..."
              />
            </div>
            <div>
              <Label htmlFor="proposal-value">Potential Sales Value ($)</Label>
              <Input
                id="proposal-value"
                type="number"
                step="0.01"
                min="0"
                value={leadFormData.proposal_value}
                onChange={(e) => setLeadFormData({...leadFormData, proposal_value: e.target.value})}
                placeholder="e.g., 5000.00"
              />
              <p className="text-xs text-slate-500 mt-1">
                Estimated potential value of this lead
              </p>
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                {editingLead ? "Update Lead" : "Create Lead"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsAddLeadDialogOpen(false);
                  resetLeadForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LeadStatusManagement = () => {
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [statusFormData, setStatusFormData] = useState({
    name: "",
    color: "#6B7280",
    is_default: false
  });
  const [editingStatus, setEditingStatus] = useState(null);

  useEffect(() => {
    fetchLeadStatuses();
  }, []);

  const fetchLeadStatuses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/lead-statuses`);
      setLeadStatuses(response.data);
    } catch (error) {
      console.error("Error fetching lead statuses:", error);
      toast.error("Failed to load lead statuses");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStatus) {
        await axios.put(`${API}/lead-statuses/${editingStatus.id}`, statusFormData);
        toast.success("Status updated successfully");
      } else {
        await axios.post(`${API}/lead-statuses`, statusFormData);
        toast.success("Status created successfully");
      }

      await fetchLeadStatuses();
      resetStatusForm();
      setIsStatusDialogOpen(false);
    } catch (error) {
      console.error("Error saving status:", error);
      toast.error(error.response?.data?.detail || "Failed to save status");
    }
  };

  const handleEditStatus = (status) => {
    setEditingStatus(status);
    setStatusFormData({
      name: status.name,
      color: status.color,
      is_default: status.is_default
    });
    setIsStatusDialogOpen(true);
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm("Are you sure you want to delete this status?")) return;
    
    try {
      await axios.delete(`${API}/lead-statuses/${statusId}`);
      toast.success("Status deleted successfully");
      await fetchLeadStatuses();
    } catch (error) {
      console.error("Error deleting status:", error);
      toast.error(error.response?.data?.detail || "Failed to delete status");
    }
  };

  const resetStatusForm = () => {
    setStatusFormData({
      name: "",
      color: "#6B7280",
      is_default: false
    });
    setEditingStatus(null);
  };

  const commonColors = [
    "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", 
    "#F97316", "#06B6D4", "#84CC16", "#EC4899", "#6B7280"
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Lead Status Management</h1>
        <Button onClick={() => setIsStatusDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Status
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lead Statuses</CardTitle>
          <CardDescription>
            Configure the different stages of your lead pipeline
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading statuses...</div>
          ) : leadStatuses.length > 0 ? (
            <div className="space-y-4">
              {leadStatuses.map((status) => (
                <div key={status.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: status.color }}
                    />
                    <div>
                      <h3 className="font-medium">{status.name}</h3>
                      <p className="text-sm text-slate-600">Color: {status.color}</p>
                    </div>
                    {status.is_default && (
                      <Badge variant="default" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                        Default
                      </Badge>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditStatus(status)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!status.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteStatus(status.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No statuses found. Add your first status to get started.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingStatus ? "Edit Status" : "Add New Status"}
            </DialogTitle>
            <DialogDescription>
              {editingStatus ? "Update the status details" : "Create a new lead status"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStatusSubmit} className="space-y-4">
            <div>
              <Label htmlFor="status-name">Status Name</Label>
              <Input
                id="status-name"
                value={statusFormData.name}
                onChange={(e) => setStatusFormData({...statusFormData, name: e.target.value})}
                placeholder="e.g., Qualified, Contacted"
                required
              />
            </div>
            <div>
              <Label htmlFor="status-color">Color</Label>
              <div className="space-y-2">
                <Input
                  id="status-color"
                  type="color"
                  value={statusFormData.color}
                  onChange={(e) => setStatusFormData({...statusFormData, color: e.target.value})}
                  className="w-full h-10"
                />
                <div className="flex flex-wrap gap-2">
                  {commonColors.map(color => (
                    <button
                      key={color}
                      type="button"
                      className="w-8 h-8 rounded border-2 border-slate-300 hover:border-slate-500"
                      style={{ backgroundColor: color }}
                      onClick={() => setStatusFormData({...statusFormData, color})}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-default"
                checked={statusFormData.is_default}
                onChange={(e) => setStatusFormData({...statusFormData, is_default: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is-default">Set as default status for new leads</Label>
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                {editingStatus ? "Update Status" : "Create Status"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsStatusDialogOpen(false);
                  resetStatusForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LeadDetail = () => {
  const [lead, setLead] = useState(null);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [newNote, setNewNote] = useState("");
  const [newNoteType, setNewNoteType] = useState("lead_note");
  const [proposalValue, setProposalValue] = useState("");
  const [proposalDescription, setProposalDescription] = useState("");
  const [isEditingProposal, setIsEditingProposal] = useState(false);
  const [whatsappData, setWhatsappData] = useState(null);

  // Get lead ID from URL
  const leadId = window.location.pathname.split('/leads/')[1];

  useEffect(() => {
    if (leadId) {
      fetchLeadDetail();
      fetchWhatsAppData();
    }
  }, [leadId]);

  const fetchLeadDetail = async () => {
    try {
      setLoading(true);
      const [leadRes, statusesRes] = await Promise.all([
        axios.get(`${API}/leads/${leadId}`),
        axios.get(`${API}/lead-statuses`)
      ]);
      
      setLead(leadRes.data);
      setLeadStatuses(statusesRes.data);
      setProposalValue(leadRes.data.proposal_value ? leadRes.data.proposal_value.toString() : "");
      setProposalDescription(leadRes.data.proposal_description || "");
    } catch (error) {
      console.error("Error fetching lead details:", error);
      toast.error("Failed to load lead details");
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsAppData = async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/whatsapp-number`);
      setWhatsappData(response.data);
    } catch (error) {
      console.error("Error fetching WhatsApp data:", error);
    }
  };

  const handleProposalValueUpdate = async () => {
    try {
      const value = proposalValue ? parseFloat(proposalValue) : null;
      await axios.put(`${API}/leads/${leadId}`, { 
        proposal_value: value,
        proposal_description: proposalDescription.trim() || null
      });
      setLead(prev => ({ 
        ...prev, 
        proposal_value: value,
        proposal_description: proposalDescription.trim() || null
      }));
      setIsEditingProposal(false);
      toast.success("Proposal value updated successfully");
    } catch (error) {
      console.error("Error updating proposal value:", error);
      toast.error("Failed to update proposal value");
    }
  };

  const handleWhatsAppClick = () => {
    if (whatsappData && whatsappData.whatsapp_url) {
      window.open(whatsappData.whatsapp_url, '_blank');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      setLead(prev => ({ ...prev, status: newStatus }));
      toast.success("Lead status updated successfully");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API}/leads/${leadId}/notes`, {
        note: newNote,
        note_type: newNoteType
      });
      
      // Refresh lead data to get updated notes
      await fetchLeadDetail();
      setNewNote("");
      toast.success("Note added successfully");
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  const getStatusColor = (statusName) => {
    const status = leadStatuses.find(s => s.name === statusName);
    return status ? status.color : "#6B7280";
  };

  const formatDateTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-lg text-slate-600">Loading lead details...</div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-lg text-red-600">Lead not found</div>
          <Button 
            onClick={() => window.location.href = '/leads/list'} 
            className="mt-4"
          >
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{lead.name}</h1>
          <p className="text-slate-600 mt-1">Lead Profile & Management</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/leads/list'}
          className="flex items-center space-x-2"
        >
          <Users className="h-4 w-4" />
          <span>Back to Leads</span>
        </Button>
      </div>

      {/* Lead Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Current Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={lead.status} onValueChange={handleStatusUpdate}>
              <SelectTrigger className="w-full">
                <SelectValue>
                  <Badge 
                    className="text-sm px-2 py-1"
                    style={{ 
                      backgroundColor: getStatusColor(lead.status),
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    {lead.status}
                  </Badge>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {leadStatuses.map((status) => (
                  <SelectItem key={status.id} value={status.name}>
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      <span>{status.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">Click to update status</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Local Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {lead.current_time}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {lead.country} ({lead.country_code})
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Proposal Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {lead.proposal_value ? `$${lead.proposal_value.toFixed(2)}` : 'Not Set'}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Potential sales value
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Total Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {lead.lead_notes?.length || 0}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Conversation entries
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="details">Lead Details</TabsTrigger>
          <TabsTrigger value="proposal">Proposal Value</TabsTrigger>
          <TabsTrigger value="notes">Notes & Timeline</TabsTrigger>
        </TabsList>

        {/* Lead Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Lead Name</label>
                    <div className="text-lg font-semibold text-slate-900">{lead.name}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Email</label>
                    <div className="text-slate-900 flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      {lead.email}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Phone Number</label>
                    <div className="text-slate-900 flex items-center space-x-2">
                      <Phone className="h-4 w-4 mr-2" />
                      <span>{lead.country_code} {lead.phone_number}</span>
                      {whatsappData && (
                        <button
                          onClick={handleWhatsAppClick}
                          className="ml-2 p-1 rounded-full hover:bg-green-100 transition-colors"
                          title={`Open WhatsApp: ${whatsappData.whatsapp_number}`}
                        >
                          <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Country</label>
                    <div className="text-slate-900 flex items-center">
                      <Globe className="h-4 w-4 mr-2" />
                      {lead.country}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Lead Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Product Interest</label>
                    <div className="text-slate-900">
                      {lead.product_name || 'No specific product'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Customer Notes</label>
                    <div className="text-slate-900 p-3 bg-slate-50 rounded-lg">
                      {lead.customer_notes || 'No initial notes'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Lead Created</label>
                    <div className="text-slate-900">
                      {formatDateTime(lead.created_at)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Last Updated</label>
                    <div className="text-slate-900">
                      {formatDateTime(lead.updated_at)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Proposal & Value Tab */}
        <TabsContent value="proposal">
          <Card>
            <CardHeader>
              <CardTitle>Proposal & Sales Value</CardTitle>
              <CardDescription>
                Manage the potential sales value and proposal information for this lead
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Current Proposal Value */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">Current Proposal Value</h3>
                      <div className="text-3xl font-bold text-green-600 mt-2">
                        {lead.proposal_value ? `$${lead.proposal_value.toFixed(2)}` : 'Not Set'}
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        {lead.proposal_value ? 'Estimated potential sales value' : 'No proposal value set for this lead'}
                      </p>
                    </div>
                    <div className="text-green-600">
                      <DollarSign className="h-12 w-12" />
                    </div>
                  </div>
                </div>

                {/* Edit Proposal Value */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-slate-800 mb-3">Update Proposal Value</h4>
                  {isEditingProposal ? (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="proposal-input" className="text-sm">Proposal Value ($)</Label>
                        <Input
                          id="proposal-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={proposalValue}
                          onChange={(e) => setProposalValue(e.target.value)}
                          placeholder="Enter proposal value (e.g., 15000.00)"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="proposal-description" className="text-sm">Description</Label>
                        <Textarea
                          id="proposal-description"
                          value={proposalDescription}
                          onChange={(e) => setProposalDescription(e.target.value)}
                          placeholder="Enter proposal description (e.g., Custom development for CRM system with advanced reporting features)"
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button onClick={handleProposalValueUpdate} size="sm">
                          Save
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setIsEditingProposal(false);
                            setProposalValue(lead.proposal_value ? lead.proposal_value.toString() : "");
                            setProposalDescription(lead.proposal_description || "");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Enter the estimated potential sales value and description for this lead
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">
                          {lead.proposal_value 
                            ? `Current value: $${lead.proposal_value.toFixed(2)}` 
                            : 'No proposal value has been set for this lead.'}
                        </p>
                        {lead.proposal_description && (
                          <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded border">
                            <strong>Description:</strong> {lead.proposal_description}
                          </p>
                        )}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsEditingProposal(true)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {lead.proposal_value ? 'Update Value' : 'Set Value'}
                      </Button>
                    </div>
                  )}
                </div>


              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes & Timeline Tab */}
        <TabsContent value="notes">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Add New Note */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>Add New Note</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="note-type">Note Type</Label>
                      <Select value={newNoteType} onValueChange={setNewNoteType}>
                        <SelectTrigger id="note-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead_note">Lead Note</SelectItem>
                          <SelectItem value="customer_note">Customer Note</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="new-note">Note Content</Label>
                      <Textarea
                        id="new-note"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={4}
                        placeholder="Enter your note here..."
                      />
                    </div>
                    <Button 
                      onClick={handleAddNote} 
                      className="w-full"
                      disabled={!newNote.trim()}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Note
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Notes Timeline */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Notes Timeline</CardTitle>
                  <CardDescription>
                    Conversation history and lead notes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {lead.lead_notes && lead.lead_notes.length > 0 ? (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {lead.lead_notes
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((note, index) => (
                          <div key={note.id || index} className="border-l-4 border-blue-200 pl-4 pb-4">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant={note.note_type === 'customer_note' ? 'default' : 'secondary'}>
                                {note.note_type === 'customer_note' ? 'Customer Note' : 'Lead Note'}
                              </Badge>
                              <span className="text-sm text-slate-500">
                                {formatDateTime(note.created_at)} by {note.created_by}
                              </span>
                            </div>
                            <p className="text-slate-700 bg-slate-50 p-3 rounded">
                              {note.note}
                            </p>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      No notes yet. Add the first note to start tracking conversations.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Original Sales Management Component (Updated)
const Sales = () => {
  // State for products
  const [products, setProducts] = useState([]);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    pricing_type: "one_time",
    price: "",
    billing_frequency: "monthly"
  });

  // State for sales
  const [sales, setSales] = useState([]);
  const [isSaleDialogOpen, setIsSaleDialogOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({
    date: new Date(),
    product_id: "",
    client_id: "",
    sale_price: "",
    pricing_type: "one_time",
    billing_frequency: "monthly",
    next_billing_date: new Date(),
    notes: ""
  });

  // State for sales expenses
  const [salesExpenses, setSalesExpenses] = useState([]);
  const [isSalesExpenseDialogOpen, setIsSalesExpenseDialogOpen] = useState(false);
  const [salesExpenseForm, setSalesExpenseForm] = useState({
    date: new Date(),
    expense_type: "tool",
    name: "",
    amount: "",
    notes: "",
    related_sale_id: ""
  });

  // Other state
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState("products");
  const [loading, setLoading] = useState(false);

  // Sales filtering and pagination
  const [salesFilters, setSalesFilters] = useState({
    dateFrom: "",
    dateTo: "",
    client: "all",
    product: "all"
  });
  const [salesExpenseFilters, setSalesExpenseFilters] = useState({
    dateFrom: "",
    dateTo: "",
    expenseType: "all",
    name: ""
  });
  const [salesPage, setSalesPage] = useState(1);
  const [salesExpensePage, setSalesExpensePage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchSalesData();
    fetchClients();
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      const [productsRes, salesRes, expensesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/sales`),
        axios.get(`${API}/sales-expenses`)
      ]);
      setProducts(productsRes.data);
      setSales(salesRes.data);
      setSalesExpenses(expensesRes.data);
    } catch (error) {
      console.error("Error fetching sales data:", error);
      toast.error("Failed to load sales data");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price)
      };
      await axios.post(`${API}/products`, data);
      toast.success("Product created successfully");
      setIsProductDialogOpen(false);
      resetProductForm();
      fetchSalesData();
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error("Failed to create product");
    }
  };

  const handleCreateSale = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...saleForm,
        date: format(saleForm.date, "yyyy-MM-dd"),
        sale_price: parseFloat(saleForm.sale_price),
        next_billing_date: saleForm.pricing_type === "subscription" ? format(saleForm.next_billing_date, "yyyy-MM-dd") : null
      };
      await axios.post(`${API}/sales`, data);
      toast.success("Sale recorded successfully");
      setIsSaleDialogOpen(false);
      resetSaleForm();
      fetchSalesData();
    } catch (error) {
      console.error("Error creating sale:", error);
      
      // Handle different types of errors
      let errorMessage = "Failed to record sale";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle validation errors array
            errorMessage = error.response.data.detail
              .map(err => typeof err === 'string' ? err : err.msg || err.message || 'Validation error')
              .join(', ');
          } else {
            errorMessage = "Validation error occurred";
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCreateSalesExpense = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...salesExpenseForm,
        date: format(salesExpenseForm.date, "yyyy-MM-dd"),
        amount: parseFloat(salesExpenseForm.amount)
      };
      await axios.post(`${API}/sales-expenses`, data);
      toast.success("Sales expense added successfully");
      setIsSalesExpenseDialogOpen(false);
      resetSalesExpenseForm();
      fetchSalesData();
    } catch (error) {
      console.error("Error creating sales expense:", error);
      
      // Handle different types of errors
      let errorMessage = "Failed to add sales expense";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle validation errors array
            errorMessage = error.response.data.detail
              .map(err => typeof err === 'string' ? err : err.msg || err.message || 'Validation error')
              .join(', ');
          } else {
            errorMessage = "Validation error occurred";
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: "",
      description: "",
      pricing_type: "one_time",
      price: "",
      billing_frequency: "monthly"
    });
  };

  const resetSaleForm = () => {
    setSaleForm({
      date: new Date(),
      product_id: "",
      client_id: "",
      sale_price: "",
      pricing_type: "one_time",
      billing_frequency: "monthly",
      next_billing_date: new Date(),
      notes: ""
    });
  };

  const resetSalesExpenseForm = () => {
    setSalesExpenseForm({
      date: new Date(),
      expense_type: "tool",
      name: "",
      amount: "",
      notes: "",
      related_sale_id: ""
    });
  };

  // Sales filtering functions
  const filterSales = (sales) => {
    return sales.filter(sale => {
      const clientMatch = !salesFilters.client || salesFilters.client === "all" || sale.client_id === salesFilters.client;
      const productMatch = !salesFilters.product || salesFilters.product === "all" || sale.product_id === salesFilters.product;
      const dateFromMatch = !salesFilters.dateFrom || sale.date >= salesFilters.dateFrom;
      const dateToMatch = !salesFilters.dateTo || sale.date <= salesFilters.dateTo;
      return clientMatch && productMatch && dateFromMatch && dateToMatch;
    });
  };

  const filterSalesExpenses = (expenses) => {
    return expenses.filter(expense => {
      const typeMatch = !salesExpenseFilters.expenseType || salesExpenseFilters.expenseType === "all" || expense.expense_type === salesExpenseFilters.expenseType;
      const nameMatch = !salesExpenseFilters.name || expense.name.toLowerCase().includes(salesExpenseFilters.name.toLowerCase());
      const dateFromMatch = !salesExpenseFilters.dateFrom || expense.date >= salesExpenseFilters.dateFrom;
      const dateToMatch = !salesExpenseFilters.dateTo || expense.date <= salesExpenseFilters.dateTo;
      return typeMatch && nameMatch && dateFromMatch && dateToMatch;
    });
  };

  const paginateData = (data, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (dataLength) => Math.ceil(dataLength / itemsPerPage);

  const resetSalesFilters = () => {
    setSalesFilters({
      dateFrom: "",
      dateTo: "",
      client: "all",
      product: "all"
    });
    setSalesPage(1);
  };

  const resetSalesExpenseFilters = () => {
    setSalesExpenseFilters({
      dateFrom: "",
      dateTo: "",
      expenseType: "all",
      name: ""
    });
    setSalesExpensePage(1);
  };

  const deleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await axios.delete(`${API}/products/${productId}`);
        toast.success("Product deleted successfully");
        fetchSalesData();
      } catch (error) {
        console.error("Error deleting product:", error);
        toast.error("Failed to delete product");
      }
    }
  };

  const deleteSale = async (saleId) => {
    if (window.confirm("Are you sure you want to delete this sale?")) {
      try {
        await axios.delete(`${API}/sales/${saleId}`);
        toast.success("Sale deleted successfully");
        fetchSalesData();
      } catch (error) {
        console.error("Error deleting sale:", error);
        toast.error("Failed to delete sale");
      }
    }
  };

  const deleteSalesExpense = async (expenseId) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      try {
        await axios.delete(`${API}/sales-expenses/${expenseId}`);
        toast.success("Sales expense deleted successfully");
        fetchSalesData();
      } catch (error) {
        console.error("Error deleting sales expense:", error);
        toast.error("Failed to delete sales expense");
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading sales data...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-slate-900 mb-8">Sales Management</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="expenses">Sales Expenses</TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Product Catalog</CardTitle>
                  <CardDescription>Manage your products with pricing information</CardDescription>
                </div>
                <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Product</DialogTitle>
                      <DialogDescription>Create a new product with pricing details</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4">
                      <div>
                        <Label htmlFor="product-name">Product Name</Label>
                        <Input
                          id="product-name"
                          value={productForm.name}
                          onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="product-description">Description</Label>
                        <Textarea
                          id="product-description"
                          value={productForm.description}
                          onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="pricing-type">Pricing Type</Label>
                        <Select 
                          value={productForm.pricing_type} 
                          onValueChange={(value) => setProductForm({...productForm, pricing_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="one_time">One-Time Payment</SelectItem>
                            <SelectItem value="subscription">Subscription</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="product-price">Price ($)</Label>
                        <Input
                          id="product-price"
                          type="number"
                          step="0.01"
                          value={productForm.price}
                          onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                          required
                        />
                      </div>
                      {productForm.pricing_type === "subscription" && (
                        <div>
                          <Label htmlFor="billing-frequency">Billing Frequency</Label>
                          <Select 
                            value={productForm.billing_frequency} 
                            onValueChange={(value) => setProductForm({...productForm, billing_frequency: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="annual">Annual</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                        Create Product
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {products.map((product) => (
                  <Card key={product.id}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{product.name}</h3>
                          {product.description && (
                            <p className="text-slate-600 mt-1">{product.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <Badge variant={product.pricing_type === "one_time" ? "default" : "secondary"}>
                              {product.pricing_type === "one_time" ? "One-Time" : "Subscription"}
                            </Badge>
                            {product.pricing_type === "subscription" && (
                              <Badge variant="outline">
                                {product.billing_frequency}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-green-600">
                              ${product.price}
                            </div>
                            {product.pricing_type === "subscription" && (
                              <div className="text-sm text-slate-500">
                                per {product.billing_frequency.slice(0, -2)}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteProduct(product.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {products.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No products added yet. Create your first product to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab - I'll continue this in the next part due to length */}
        <TabsContent value="sales">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Sales Records</CardTitle>
                  <CardDescription>Track your product sales and revenue</CardDescription>
                </div>
                <Dialog open={isSaleDialogOpen} onOpenChange={setIsSaleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Record Sale
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Record New Sale</DialogTitle>
                      <DialogDescription>Add a new sale transaction</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSale} className="space-y-4">
                      <div>
                        <Label>Sale Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {saleForm.date ? format(saleForm.date, "PPP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={saleForm.date}
                              onSelect={(date) => setSaleForm({...saleForm, date: date || new Date()})}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label htmlFor="sale-product">Product</Label>
                        <Select 
                          value={saleForm.product_id} 
                          onValueChange={(value) => {
                            const selectedProduct = products.find(p => p.id === value);
                            setSaleForm({
                              ...saleForm, 
                              product_id: value,
                              pricing_type: selectedProduct?.pricing_type || "one_time",
                              billing_frequency: selectedProduct?.billing_frequency || "monthly",
                              sale_price: selectedProduct?.price?.toString() || ""
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a product" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name} - ${product.price}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="sale-client">Client</Label>
                        <Select 
                          value={saleForm.client_id} 
                          onValueChange={(value) => setSaleForm({...saleForm, client_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.company_name} ({client.contact_person})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="sale-price">Sale Price ($)</Label>
                        <Input
                          id="sale-price"
                          type="number"
                          step="0.01"
                          value={saleForm.sale_price}
                          onChange={(e) => setSaleForm({...saleForm, sale_price: e.target.value})}
                          required
                        />
                      </div>
                      {saleForm.pricing_type === "subscription" && (
                        <div>
                          <Label>Next Billing Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {saleForm.next_billing_date ? format(saleForm.next_billing_date, "PPP") : <span>Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={saleForm.next_billing_date}
                                onSelect={(date) => setSaleForm({...saleForm, next_billing_date: date || new Date()})}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}
                      <div>
                        <Label htmlFor="sale-notes">Notes</Label>
                        <Textarea
                          id="sale-notes"
                          value={saleForm.notes}
                          onChange={(e) => setSaleForm({...saleForm, notes: e.target.value})}
                        />
                      </div>
                      <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                        Record Sale
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Sales Filters */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="sales-date-from">Date From</Label>
                      <Input
                        id="sales-date-from"
                        type="date"
                        value={salesFilters.dateFrom}
                        onChange={(e) => setSalesFilters({...salesFilters, dateFrom: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="sales-date-to">Date To</Label>
                      <Input
                        id="sales-date-to"
                        type="date"
                        value={salesFilters.dateTo}
                        onChange={(e) => setSalesFilters({...salesFilters, dateTo: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="sales-client-filter">Client</Label>
                      <Select 
                        value={salesFilters.client} 
                        onValueChange={(value) => setSalesFilters({...salesFilters, client: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All clients" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All clients</SelectItem>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.company_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="sales-product-filter">Product</Label>
                      <Select 
                        value={salesFilters.product} 
                        onValueChange={(value) => setSalesFilters({...salesFilters, product: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All products" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All products</SelectItem>
                          {products.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={resetSalesFilters} variant="outline" size="sm">
                      <X className="h-4 w-4 mr-1" />
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Sales Table */}
              {(() => {
                const filteredSales = filterSales(sales);
                const paginatedSales = paginateData(filteredSales, salesPage);
                const totalPages = getTotalPages(filteredSales.length);
                const totalAmount = filteredSales.reduce((sum, sale) => sum + sale.sale_price, 0);

                return (
                  <>
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle className="flex items-center gap-2">
                            <ShoppingCart className="h-5 w-5 text-green-600" />
                            Sales Records ({filteredSales.length} total)
                          </CardTitle>
                          <div className="text-lg font-bold text-green-600">
                            Total: +${totalAmount.toFixed(2)}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-3 font-semibold">Date</th>
                                <th className="text-left p-3 font-semibold">Product</th>
                                <th className="text-left p-3 font-semibold">Client</th>
                                <th className="text-left p-3 font-semibold">Type</th>
                                <th className="text-left p-3 font-semibold">Next Billing</th>
                                <th className="text-left p-3 font-semibold">Notes</th>
                                <th className="text-right p-3 font-semibold">Amount</th>
                                <th className="text-center p-3 font-semibold">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedSales.length > 0 ? paginatedSales.map((sale, index) => (
                                <tr key={sale.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                                  <td className="p-3">{sale.date}</td>
                                  <td className="p-3 font-medium">
                                    {products.find(p => p.id === sale.product_id)?.name || "Unknown Product"}
                                  </td>
                                  <td className="p-3">
                                    {clients.find(c => c.id === sale.client_id)?.company_name || "Unknown Client"}
                                  </td>
                                  <td className="p-3">
                                    <Badge variant={sale.pricing_type === "one_time" ? "default" : "secondary"}>
                                      {sale.pricing_type === "one_time" ? "One-Time" : "Subscription"}
                                    </Badge>
                                  </td>
                                  <td className="p-3">
                                    {sale.next_billing_date || '-'}
                                  </td>
                                  <td className="p-3 max-w-xs truncate" title={sale.notes}>
                                    {sale.notes || '-'}
                                  </td>
                                  <td className="p-3 text-right font-bold text-green-600">
                                    +${sale.sale_price.toFixed(2)}
                                  </td>
                                  <td className="p-3 text-center">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deleteSale(sale.id)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan="8" className="p-8 text-center text-slate-500">
                                    No sales found matching your filters
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="flex justify-between items-center mt-4 pt-4 border-t">
                            <div className="text-sm text-slate-600">
                              Showing {((salesPage - 1) * itemsPerPage) + 1} to {Math.min(salesPage * itemsPerPage, filteredSales.length)} of {filteredSales.length} sales
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSalesPage(prev => Math.max(prev - 1, 1))}
                                disabled={salesPage === 1}
                              >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                              </Button>
                              <span className="px-3 py-1 bg-slate-100 rounded text-sm">
                                {salesPage} of {totalPages}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSalesPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={salesPage === totalPages}
                              >
                                Next
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Sales Expenses</CardTitle>
                  <CardDescription>Track expenses related to tools and sales team</CardDescription>
                </div>
                <Dialog open={isSalesExpenseDialogOpen} onOpenChange={setIsSalesExpenseDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-orange-600 hover:bg-orange-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Expense
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Sales Expense</DialogTitle>
                      <DialogDescription>Record expenses related to sales activities</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSalesExpense} className="space-y-4">
                      <div>
                        <Label>Expense Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {salesExpenseForm.date ? format(salesExpenseForm.date, "PPP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={salesExpenseForm.date}
                              onSelect={(date) => setSalesExpenseForm({...salesExpenseForm, date: date || new Date()})}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label htmlFor="expense-type">Expense Type</Label>
                        <Select 
                          value={salesExpenseForm.expense_type} 
                          onValueChange={(value) => setSalesExpenseForm({...salesExpenseForm, expense_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tool">Tool/Software</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="expense-name">
                          {salesExpenseForm.expense_type === "tool" ? "Tool/Software Name" : "Employee"}
                        </Label>
                        {salesExpenseForm.expense_type === "tool" ? (
                          <Input
                            id="expense-name"
                            value={salesExpenseForm.name}
                            onChange={(e) => setSalesExpenseForm({...salesExpenseForm, name: e.target.value})}
                            placeholder="e.g., CRM Software, Email Tool"
                            required
                          />
                        ) : (
                          <Select 
                            value={salesExpenseForm.name} 
                            onValueChange={(value) => setSalesExpenseForm({...salesExpenseForm, name: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an employee" />
                            </SelectTrigger>
                            <SelectContent>
                              {employees.map((employee) => (
                                <SelectItem key={employee.id} value={employee.name}>
                                  {employee.name} - {employee.position}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="expense-amount">Amount ($)</Label>
                        <Input
                          id="expense-amount"
                          type="number"
                          step="0.01"
                          value={salesExpenseForm.amount}
                          onChange={(e) => setSalesExpenseForm({...salesExpenseForm, amount: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="expense-notes">Notes</Label>
                        <Textarea
                          id="expense-notes"
                          value={salesExpenseForm.notes}
                          onChange={(e) => setSalesExpenseForm({...salesExpenseForm, notes: e.target.value})}
                          placeholder="Additional details about this expense..."
                        />
                      </div>
                      <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700">
                        Add Expense
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Sales Expenses Filters */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="expense-date-from">Date From</Label>
                      <Input
                        id="expense-date-from"
                        type="date"
                        value={salesExpenseFilters.dateFrom}
                        onChange={(e) => setSalesExpenseFilters({...salesExpenseFilters, dateFrom: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="expense-date-to">Date To</Label>
                      <Input
                        id="expense-date-to"
                        type="date"
                        value={salesExpenseFilters.dateTo}
                        onChange={(e) => setSalesExpenseFilters({...salesExpenseFilters, dateTo: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="expense-type-filter">Expense Type</Label>
                      <Select 
                        value={salesExpenseFilters.expenseType} 
                        onValueChange={(value) => setSalesExpenseFilters({...salesExpenseFilters, expenseType: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All types</SelectItem>
                          <SelectItem value="tool">Tool/Software</SelectItem>
                          <SelectItem value="employee">Employee/Commission</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="expense-name-filter">Name</Label>
                      <Input
                        id="expense-name-filter"
                        placeholder="Filter by name..."
                        value={salesExpenseFilters.name}
                        onChange={(e) => setSalesExpenseFilters({...salesExpenseFilters, name: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={resetSalesExpenseFilters} variant="outline" size="sm">
                      <X className="h-4 w-4 mr-1" />
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Sales Expenses Table */}
              {(() => {
                const filteredExpenses = filterSalesExpenses(salesExpenses);
                const paginatedExpenses = paginateData(filteredExpenses, salesExpensePage);
                const totalPages = getTotalPages(filteredExpenses.length);
                const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

                return (
                  <>
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle className="flex items-center gap-2">
                            <TrendingDown className="h-5 w-5 text-red-600" />
                            Sales Expenses ({filteredExpenses.length} total)
                          </CardTitle>
                          <div className="text-lg font-bold text-red-600">
                            Total: -${totalAmount.toFixed(2)}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-3 font-semibold">Date</th>
                                <th className="text-left p-3 font-semibold">Name</th>
                                <th className="text-left p-3 font-semibold">Type</th>
                                <th className="text-left p-3 font-semibold">Emp Name</th>
                                <th className="text-left p-3 font-semibold">Notes</th>
                                <th className="text-right p-3 font-semibold">Amount</th>
                                <th className="text-center p-3 font-semibold">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedExpenses.length > 0 ? paginatedExpenses.map((expense, index) => (
                                <tr key={expense.id} className={`border-b ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-100`}>
                                  <td className="p-3">{expense.date}</td>
                                  <td className="p-3 font-medium">{expense.name}</td>
                                  <td className="p-3">
                                    <Badge variant={expense.expense_type === "employee" ? "default" : "secondary"}>
                                      {expense.expense_type === "employee" ? "Employee" :
                                       expense.expense_type === "tool" ? "Tool/Software" : 
                                       expense.expense_type}
                                    </Badge>
                                  </td>
                                  <td className="p-3 font-medium">
                                    {expense.employee_name || '-'}
                                  </td>
                                  <td className="p-3 max-w-xs truncate" title={expense.notes}>
                                    {expense.notes || '-'}
                                  </td>
                                  <td className="p-3 text-right font-bold text-red-600">
                                    -${expense.amount.toFixed(2)}
                                  </td>
                                  <td className="p-3 text-center">
                                    {expense.source !== 'employee_payment' && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => deleteSalesExpense(expense.id)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {expense.source === 'employee_payment' && (
                                      <span className="text-xs text-slate-500 italic">Employee Payment</span>
                                    )}
                                  </td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan="6" className="p-8 text-center text-slate-500">
                                    No sales expenses found matching your filters
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="flex justify-between items-center mt-4 pt-4 border-t">
                            <div className="text-sm text-slate-600">
                              Showing {((salesExpensePage - 1) * itemsPerPage) + 1} to {Math.min(salesExpensePage * itemsPerPage, filteredExpenses.length)} of {filteredExpenses.length} expenses
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSalesExpensePage(prev => Math.max(prev - 1, 1))}
                                disabled={salesExpensePage === 1}
                              >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                              </Button>
                              <span className="px-3 py-1 bg-slate-100 rounded text-sm">
                                {salesExpensePage} of {totalPages}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSalesExpensePage(prev => Math.min(prev + 1, totalPages))}
                                disabled={salesExpensePage === totalPages}
                              >
                                Next
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Reusable Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, itemName = "items" }) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div className="flex justify-between flex-1 sm:hidden">
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          variant="outline"
          size="sm"
        >
          Previous
        </Button>
        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
      
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalItems}</span> {itemName}
          </p>
        </div>
        
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <Button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            {generatePageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={index} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  onClick={() => onPageChange(page)}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    currentPage === page
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </Button>
              )
            ))}
            
            <Button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Analytics Component
const Analytics = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const projectsPerPage = 50;
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [reportsSummary, setReportsSummary] = useState(null);
  
  // Breakdown dialog states
  const [isRevenueBreakdownOpen, setIsRevenueBreakdownOpen] = useState(false);
  const [isExpenseBreakdownOpen, setIsExpenseBreakdownOpen] = useState(false);
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);
  const [expenseBreakdown, setExpenseBreakdown] = useState(null);
  
  // Analytics data state
  const [analyticsData, setAnalyticsData] = useState({
    monthlySpending: [],
    categoryBreakdown: [],
    employeeSpending: [],
    toolSpending: [],
    totalSpending: 0,
    avgMonthlySpending: 0
  });

  useEffect(() => {
    fetchMonthlyAnalytics();
    fetchProjects();
  }, [currentMonth]);

  const fetchProjects = async (page = 1) => {
    try {
      setLoading(true);
      
      // Fetch paginated projects
      const projectsResponse = await axios.get(`${API}/projects/paginated`, {
        params: { page, limit: projectsPerPage }
      });
      
      if (projectsResponse.data) {
        setProjects(projectsResponse.data.projects);
        setCurrentPage(projectsResponse.data.pagination.current_page);
        setTotalPages(projectsResponse.data.pagination.total_pages);
        setTotalProjects(projectsResponse.data.pagination.total_projects);
      }
      
      // Fetch reports summary
      const reportsResponse = await axios.get(`${API}/projects/reports/summary`);
      if (reportsResponse.data) {
        setReportsSummary(reportsResponse.data);
      }
      
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyAnalytics = async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await axios.get(`${API}/analytics/monthly/${year}/${month}`);
      setMonthlyData(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Generate months dropdown options
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Generate last 24 months and next 12 months
    for (let i = -24; i <= 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      options.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: format(date, "MMMM yyyy"),
        date: date
      });
    }
    return options;
  };

  const handleMonthChange = (value) => {
    const [year, month] = value.split('-');
    setCurrentMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
  };

  const generateRevenueBreakdown = () => {
    if (!monthlyData) return null;
    
    // Calculate projected revenue breakdown by projects
    const projectRevenues = projects.map(project => {
      let projectedAmount = 0;
      let reasoning = "";
      
      switch (project.billing_type) {
        case "monthly_retainer":
          projectedAmount = project.budget || 0;
          reasoning = `Monthly retainer of $${project.budget?.toFixed(2)} based on project contract`;
          break;
        case "annual_fee":
          projectedAmount = (project.budget || 0) / 12;
          reasoning = `Annual fee of $${project.budget?.toFixed(2)} divided by 12 months`;
          break;
        case "one_time":
          // Check if project is nearing completion
          projectedAmount = project.status === "active" ? (project.budget || 0) * 0.3 : 0;
          reasoning = project.status === "active" 
            ? `30% of one-time project budget expected this month`
            : `One-time project not expected to generate revenue this month`;
          break;
        default:
          projectedAmount = monthlyData.total_revenue * 0.05; // 5% growth default
          reasoning = "Based on 5% growth trend from historical data";
      }
      
      return {
        projectName: project.name,
        amount: projectedAmount,
        reasoning: reasoning,
        billingType: project.billing_type
      };
    });

    // Get sales data for current month (this would come from API in real implementation)
    // For now, calculating based on subscription sales
    const subscriptionSales = []; // This would be populated from sales API
    const salesRevenues = subscriptionSales.map(sale => ({
      productName: sale.product_name || "Product Sales",
      amount: sale.sale_price || 0,
      reasoning: `Recurring ${sale.billing_frequency} subscription revenue`,
      type: "subscription_sale"
    }));

    // Add category-based projections  
    const categoryProjections = monthlyData.revenue_by_category.map(cat => ({
      categoryName: formatCategory(cat.category),
      amount: cat.total_amount * 1.05, // 5% growth
      reasoning: `5% growth projected based on ${cat.category} historical performance`,
      type: "category"
    }));

    // Add sales growth projection
    const salesGrowthProjection = {
      categoryName: "Product Sales Growth",
      amount: (monthlyData.total_sales_revenue || 0) * 1.1, // 10% growth for sales
      reasoning: "10% growth projected based on product sales momentum and new client acquisition",
      type: "sales_growth"
    };

    const totalProjected = (monthlyData.total_revenue + (monthlyData.total_sales_revenue || 0)) * 1.05;
    
    return {
      total: totalProjected,
      projects: projectRevenues.filter(p => p.amount > 0),
      sales: salesRevenues,
      categories: [...categoryProjections, salesGrowthProjection],
      reasoning: "Projection based on project contracts, sales momentum, and historical growth patterns"
    };
  };

  const generateExpenseBreakdown = () => {
    if (!monthlyData) return null;

    const expenseProjections = monthlyData.expenses_by_category.map(cat => {
      let projectedAmount = cat.total_amount * 0.95; // 5% reduction target
      let reasoning = "";
      
      switch (cat.category) {
        case "employee_salary":
          projectedAmount = cat.total_amount; // Fixed cost
          reasoning = "Fixed employee salaries - no reduction expected";
          break;
        case "office_supplies":
          projectedAmount = cat.total_amount * 0.8; // 20% reduction possible
          reasoning = "20% reduction target through bulk purchasing and optimization";
          break;
        case "marketing":
          projectedAmount = cat.total_amount * 1.1; // Increase marketing
          reasoning = "10% increase planned for growth initiatives";
          break;
        default:
          reasoning = "5% reduction target through cost optimization";
      }
      
      return {
        categoryName: formatCategory(cat.category),
        currentAmount: cat.total_amount,
        targetAmount: projectedAmount,
        reasoning: reasoning,
        change: ((projectedAmount - cat.total_amount) / cat.total_amount * 100).toFixed(1)
      };
    });

    // Add sales expenses projections
    const salesExpensesProjection = {
      categoryName: "Sales & Marketing Tools",
      currentAmount: monthlyData.total_sales_expenses || 0,
      targetAmount: (monthlyData.total_sales_expenses || 0) * 1.05, // 5% increase for sales growth
      reasoning: "5% increase in sales tools and commission expenses to support growth targets",
      change: "5.0"
    };

    const totalTarget = (monthlyData.total_expenses + (monthlyData.total_sales_expenses || 0)) * 0.95;
    const totalCurrent = monthlyData.total_expenses + (monthlyData.total_sales_expenses || 0);
    
    return {
      currentTotal: totalCurrent,
      targetTotal: totalTarget,
      categories: [...expenseProjections, salesExpensesProjection],
      reasoning: "Target expenses based on optimization strategies, business requirements, and sales growth investments"
    };
  };

  const handleShowRevenueBreakdown = () => {
    setRevenueBreakdown(generateRevenueBreakdown());
    setIsRevenueBreakdownOpen(true);
  };

  const handleShowExpenseBreakdown = () => {
    setExpenseBreakdown(generateExpenseBreakdown());
    setIsExpenseBreakdownOpen(true);
  };

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
  const formatCategory = (category) => category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Process expense data for analytics
  const processAnalyticsData = (expenses) => {
    if (!expenses || expenses.length === 0) {
      setAnalyticsData({
        monthlySpending: [],
        categoryBreakdown: [],
        employeeSpending: [],
        toolSpending: [],
        totalSpending: 0,
        avgMonthlySpending: 0
      });
      return;
    }

    // Calculate total spending
    const totalSpending = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

    // Monthly spending breakdown
    const monthlyMap = {};
    const categoryMap = {};
    const employeeMap = {};
    const toolMap = {};

    expenses.forEach(expense => {
      const date = new Date(expense.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      
      // Monthly totals
      if (!monthlyMap[monthKey]) {
        monthlyMap[monthKey] = { month: monthName, total: 0, categories: {} };
      }
      monthlyMap[monthKey].total += expense.amount || 0;
      
      // Category breakdown within months
      const category = expense.expense_type || 'other';
      if (!monthlyMap[monthKey].categories[category]) {
        monthlyMap[monthKey].categories[category] = 0;
      }
      monthlyMap[monthKey].categories[category] += expense.amount || 0;

      // Overall category breakdown
      if (!categoryMap[category]) {
        categoryMap[category] = { name: category, total: 0, percentage: 0 };
      }
      categoryMap[category].total += expense.amount || 0;

      // Employee spending breakdown
      if (category === 'employee' || category === 'employee_payment') {
        const employeeName = expense.employee_name || expense.name || 'Unknown Employee';
        if (!employeeMap[employeeName]) {
          employeeMap[employeeName] = { name: employeeName, total: 0, transactions: 0 };
        }
        employeeMap[employeeName].total += expense.amount || 0;
        employeeMap[employeeName].transactions += 1;
      }

      // Tool spending breakdown
      if (category === 'tool' || category === 'software' || category === 'subscription') {
        const toolName = expense.name || expense.description || 'Unknown Tool';
        if (!toolMap[toolName]) {
          toolMap[toolName] = { name: toolName, total: 0, transactions: 0, type: category };
        }
        toolMap[toolName].total += expense.amount || 0;
        toolMap[toolName].transactions += 1;
      }
    });

    // Calculate percentages for categories
    Object.values(categoryMap).forEach(cat => {
      cat.percentage = totalSpending > 0 ? ((cat.total / totalSpending) * 100).toFixed(1) : 0;
    });

    // Convert maps to sorted arrays
    const monthlySpending = Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));
    const categoryBreakdown = Object.values(categoryMap).sort((a, b) => b.total - a.total);
    const employeeSpending = Object.values(employeeMap).sort((a, b) => b.total - a.total);
    const toolSpending = Object.values(toolMap).sort((a, b) => b.total - a.total);

    const avgMonthlySpending = monthlySpending.length > 0 ? totalSpending / monthlySpending.length : 0;

    setAnalyticsData({
      monthlySpending,
      categoryBreakdown,
      employeeSpending,
      toolSpending,
      totalSpending,
      avgMonthlySpending
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading analytics...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Business Analytics</h1>
        <div className="flex items-center space-x-4">
          <Label htmlFor="month-selector" className="text-sm font-medium">Select Month:</Label>
          <Select 
            value={`${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`}
            onValueChange={handleMonthChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {generateMonthOptions().map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {monthlyData && (
        <div className="space-y-6">
          {/* Monthly Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(monthlyData.total_revenue)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Expenses</CardTitle>
                <TrendingDown className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(monthlyData.total_expenses)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
                <Target className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  monthlyData.profit >= 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {formatCurrency(monthlyData.profit)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Category</CardTitle>
                <CardDescription>Breakdown of revenue sources</CardDescription>
              </CardHeader>
              <CardContent>
                {monthlyData.revenue_by_category.length > 0 ? (
                  <div className="space-y-3">
                    {monthlyData.revenue_by_category.map((cat, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-sm font-medium">
                            {formatCategory(cat.category)}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-green-600">
                            {formatCurrency(cat.total_amount)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {cat.percentage.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No revenue data for this month</p>
                )}
              </CardContent>
            </Card>

            {/* Expense Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Expenses by Category</CardTitle>
                <CardDescription>Breakdown of business expenses</CardDescription>
              </CardHeader>
              <CardContent>
                {monthlyData.expenses_by_category.length > 0 ? (
                  <div className="space-y-3">
                    {monthlyData.expenses_by_category.map((cat, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-sm font-medium">
                            {formatCategory(cat.category)}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-red-600">
                            {formatCurrency(cat.total_amount)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {cat.percentage.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No expense data for this month</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Insights and Forecasting */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Key Insights</CardTitle>
                <CardDescription>Business performance metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Profit Margin:</span>
                  <span className={`font-medium ${
                    monthlyData.total_revenue > 0 && (monthlyData.profit / monthlyData.total_revenue) >= 0.2 
                      ? "text-green-600" : "text-yellow-600"
                  }`}>
                    {monthlyData.total_revenue > 0 
                      ? ((monthlyData.profit / monthlyData.total_revenue) * 100).toFixed(1) + '%'
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Revenue Growth:</span>
                  <span className="font-medium text-blue-600">
                    Track with more historical data
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Expense Ratio:</span>
                  <span className="font-medium text-slate-800">
                    {monthlyData.total_revenue > 0 
                      ? ((monthlyData.total_expenses / monthlyData.total_revenue) * 100).toFixed(1) + '%'
                      : 'N/A'
                    }
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Next Month Forecast</CardTitle>
                <CardDescription>Predictions based on current trends and project data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-center py-4 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={handleShowRevenueBreakdown}>
                  <div className="text-2xl font-bold text-blue-600">
                    {formatCurrency(monthlyData.total_revenue * 1.05)}
                  </div>
                  <p className="text-sm text-slate-600">Projected Revenue (+5%)</p>
                  <p className="text-xs text-blue-600 mt-1">Click for detailed breakdown</p>
                </div>
                <Separator />
                <div className="text-center py-4 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={handleShowExpenseBreakdown}>
                  <div className="text-2xl font-bold text-orange-600">
                    {formatCurrency(monthlyData.total_expenses * 0.95)}
                  </div>
                  <p className="text-sm text-slate-600">Target Expenses (-5%)</p>
                  <p className="text-xs text-orange-600 mt-1">Click for detailed breakdown</p>
                </div>
                <Separator />
                <div className="text-center py-4">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency((monthlyData.total_revenue * 1.05) - (monthlyData.total_expenses * 0.95))}
                  </div>
                  <p className="text-sm text-slate-600">Projected Profit</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      
      {/* Revenue Breakdown Dialog */}
      <Dialog open={isRevenueBreakdownOpen} onOpenChange={setIsRevenueBreakdownOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Revenue Projection Breakdown
            </DialogTitle>
            <DialogDescription>
              Detailed breakdown of how next month's revenue projection is calculated
            </DialogDescription>
          </DialogHeader>
          
          {revenueBreakdown && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {formatCurrency(revenueBreakdown.total)}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{revenueBreakdown.reasoning}</p>
                </div>
              </div>
              
              {/* Project-Based Projections */}
              {revenueBreakdown.projects.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Project-Based Revenue</h3>
                  <div className="space-y-3">
                    {revenueBreakdown.projects.map((project, index) => (
                      <Card key={index}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium">{project.projectName}</h4>
                              <p className="text-sm text-slate-600 mt-1">{project.reasoning}</p>
                              <Badge variant="outline" className="mt-2">
                                {formatCategory(project.billingType)}
                              </Badge>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-600">
                                {formatCurrency(project.amount)}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Category-Based Projections */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Category-Based Growth</h3>
                <div className="space-y-2">
                  {revenueBreakdown.categories.map((category, index) => (
                    <div key={index} className="flex justify-between items-center p-3 border rounded">
                      <div>
                        <span className="font-medium">{category.categoryName}</span>
                        <p className="text-xs text-slate-500">{category.reasoning}</p>
                      </div>
                      <span className="font-bold text-green-600">
                        {formatCurrency(category.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Expense Breakdown Dialog */}
      <Dialog open={isExpenseBreakdownOpen} onOpenChange={setIsExpenseBreakdownOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-orange-600" />
              Target Expenses Breakdown
            </DialogTitle>
            <DialogDescription>
              Detailed breakdown of expense optimization targets for next month
            </DialogDescription>
          </DialogHeader>
          
          {expenseBreakdown && (
            <div className="space-y-6">
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-sm text-slate-600">Current: {formatCurrency(expenseBreakdown.currentTotal)}</div>
                  <div className="text-3xl font-bold text-orange-600">
                    {formatCurrency(expenseBreakdown.targetTotal)}
                  </div>
                  <div className="text-sm text-green-600 font-medium">
                    Savings: {formatCurrency(expenseBreakdown.currentTotal - expenseBreakdown.targetTotal)}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{expenseBreakdown.reasoning}</p>
                </div>
              </div>
              
              {/* Category Breakdown */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Category Optimization Targets</h3>
                <div className="space-y-3">
                  {expenseBreakdown.categories.map((category, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium">{category.categoryName}</h4>
                            <p className="text-sm text-slate-600 mt-1">{category.reasoning}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">
                                {category.change > 0 ? '+' : ''}{category.change}% change
                              </Badge>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-slate-500">
                              Current: {formatCurrency(category.currentAmount)}
                            </div>
                            <div className={`text-lg font-bold ${
                              category.targetAmount < category.currentAmount 
                                ? 'text-green-600' 
                                : 'text-orange-600'
                            }`}>
                              Target: {formatCurrency(category.targetAmount)}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;